// TimeTracker Pro - v2.1.0 - Unified Resource View (By Team / By Project)
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Clock, Plus, Trash2, Edit2, X, BarChart3, Calendar, DollarSign as _DollarSign, ChevronLeft, ChevronRight, Briefcase, Zap, RefreshCw, Link, Unlink, Filter, LogOut, Users, Receipt, TrendingUp, ChevronDown, Radio, Newspaper, MoreHorizontal } from 'lucide-react'; // eslint-disable-line no-unused-vars
import BrioMark from './components/BrioMark';
import './styles/brio-styles.css';
import './styles/brioProjectColors.css';
import AnalyticsDashboard from './pages/analytics/AnalyticsDashboardPage';
import DonutTimeTracker from './pages/tracker/DonutTimeTrackerPage';
import HybridFocusBoard from './pages/planning/HybridFocusBoardPage';
import ProjectTasksPage from './pages/planning/ProjectTasksPage';


// ========================================================================
// EXTRACTED ACCOUNTING PAGE  COMPONENTS
// ========================================================================
import SalaryMatrixPage from './pages/accounting/SalaryMatrixPage';
import SalaryMatrixNLPage from './pages/accounting/SalaryMatrixNLPage';
import ExpenseClaimsPage from './pages/accounting/ExpenseClaimsPage';
import ExpenseAdminPage from './pages/accounting/ExpenseAdminPage';
import ExpenseAnalyticsPage from './pages/accounting/ExpenseAnalyticsPage';
import TaxReconciliationPage from './pages/accounting/TaxReconciliationPage';
import ProjectPlanningPage from './pages/accounting/ProjectPlanningPage';
import MarkupFactorPage from './pages/accounting/MarkupFactorPage';
import SalaryCostPage from './pages/accounting/SalaryCostPage';
import TeamsPage from './pages/team/TeamsPage';
import TeamMembersPage from './pages/team/TeamMembersPage';
import HiringPage from './pages/team/HiringPage';
import RoleFrameworkPage from './pages/team/RoleFrameworkPage';
import TravelPage from './pages/team/TravelPage';
import VofStatementPage from './pages/accounting/VofStatementPage';
import AdminTeamOverviewPage from './pages/team/AdminTeamOverviewPage';
import ManageProjectsPage from './pages/projects/ManageProjectsPage';
import ProjectsOverviewPage from './pages/projects/ProjectsOverviewPage';
import ClientsPage from './pages/projects/ClientsPage';
import ContactsDirectoryPage from './pages/projects/ContactsDirectoryPage';
import InquiriesPage from './pages/projects/InquiriesPage';
import StagesPage from './pages/projects/StagesPage';
import TimelinePage from './pages/projects/TimelinePage';
import InvoicesPage from './pages/projects/InvoicesPage';
import ProjectMapPage from './pages/projects/ProjectMapPage';
import TimelinesPage from './pages/projects/TimelinesPage';
import MOMDirectoryPage from './pages/projects/MOMDirectoryPage';
import ContractsScopePage from './pages/projects/ContractsScopePage';
import ContractsPage from './pages/projects/ContractsPage';
import useContractScopes from './hooks/useContractScopes';
import useScopeTemplates from './hooks/useScopeTemplates';
import TrackerPage from './pages/time/TrackerPage';
import CalendarPage from './pages/time/CalendarPage';
import MsCalendarPage from './pages/time/MsCalendarPage';
import TimeOffRequestsPage from './pages/time/TimeOffRequestsPage';
import LeavePolicyPage from './pages/time/LeavePolicyPage';
import LeaveEntitlementPage from './pages/time/LeaveEntitlementPage';
import LeaveSummaryPage from './pages/time/LeaveSummaryPage';
import TimesheetOverviewPage from './pages/time/TimesheetOverviewPage';
import FreelancerManagementPage from './pages/time/FreelancerManagementPage';
import AccountingSettingsPage from './pages/accounting/AccountingSettingsPage';
import ProjectCostLedgerPage from './pages/accounting/ProjectCostLedgerPage';
import ProjectProfitabilityPage from './pages/accounting/ProjectProfitabilityPage';
import StyleGuidePage from './pages/StyleGuidePage';
import RadarDashboardPage from './pages/radar/RadarDashboardPage';
import CommsPage from './pages/comms/CommsPage';


// ========================================================================
// CENTRAL SETTINGS HOOK
// ========================================================================
import { useSettings } from './hooks/useSettings';
import { setExcelStyles } from './utils/excelStyles';
import { computeProjectLoadedCostRecords } from './utils/projectLoadedCost';
import { employedMonthFraction } from './utils/timesheetHelpers';
import { useRadarSettings } from './hooks/useRadarSettings';

// ========================================================================
// BRIO DESIGN SYSTEM IMPORTS
// ========================================================================
// eslint-disable-next-line no-unused-vars
import {
  BrioCard,
  BrioPageHeader,
  BrioPageSubHeader,
  BrioKpiBlock,
  BrioButton,
  BrioInfoBanner,
  BrioListItem,
  BrioListWrapper,
  BrioEmptyState,
  BrioModal,
  BrioProgressBar,
  BrioBadge,
  BrioInput,
  BrioSelect,
  BrioGrid,
  BrioSection,
  BrioActionGroup,
  BrioAnalyticsSurface,
  BrioByCode,
  BrioTable,
  getTablePropsFromCode,
  BrioFrame,
  BrioFrameHeader,
  BrioDatePicker,
  BrioMonthPicker
} from './components/BrioComponents';

// ========================================================================
// SUPABASE IMPORTS - Added for database integration
// ========================================================================
import { supabase, testSupabaseConnection } from './supabaseClient';
import {
  useSupabaseUsers,
  useSupabaseClients,
  useSupabaseProjects,
  useSupabaseTasks,
  useSupabaseTimeEntries,
  useSupabaseExpenses,
  useSupabaseHolidays,
  useSupabaseOfficeOverheads,
  useSupabaseOfficeOverheadsMonthly,
  useSupabaseSalaryMatrix,
  useSupabaseSalaryMatrixNL,
  useSupabaseCalendarEvents,
  useSupabaseAllocations,
  useSupabaseTeams,
  useSupabaseTeamProjects,
  useSupabaseTimeOff,
  useSupabaseInquiries,
  useSupabaseProjectLoadedCosts,
  useSupabaseVendors,
  // View fetchers for Analytics & Reports (RLS-gated)
  supabaseViews,
  isViewPermissionDenied,
  // ID normalization helpers (bigint strings → numbers)
  normalizeId,
  normalizedIncludes,
  // Admin functions for user management
  adminResetPassword,
  // MS Calendar integration status (admin-only master calendar)
  getMsCalendarIntegrationStatus,
  getMsReauthRequired,
  updateMsCalendarIntegrationStatus
} from './hooks/useSupabase';
import { useSupabaseHiring, useLocalHiring } from './hooks/useSupabaseHiring';
import useSupabaseReimbursements from './hooks/useSupabaseReimbursements';

// ========================================================================
// HFB DATA HOOKS - For Team Members page allocation ring
// ========================================================================
import { useHFBProjectAllocations, useHFBTeamAssignments, useHFBTeamAssignmentsForWeeks, useHFBProjectAllocationsForDonut, useUserProjectHistory } from './hooks/useHFBData';

// ========================================================================
// STAGE STATUS HOOKS - Partial invoicing & closure tracking
// ========================================================================
import { useProjectStageStatus, useProjectStageEntries } from './hooks/useProjectStages';
import { useSupabaseInvoices } from './hooks/useSupabaseInvoices';
import { useSupabaseInquiryIntake } from './hooks/useSupabaseInquiryIntake';
import { useSupabaseFeeRevisions } from './hooks/useSupabaseFeeRevisions';
import { useSalaryLedger } from './hooks/useSalaryLedger';
import { useApprovedFreelancerHoursMap } from './hooks/useFreelancerHours';
import { ensureCalendar as ensureCalendarGraph, getCalendars as getCalendarsGraph } from './utils/msCalendarHelpers';
import { getCategoryNameForEventType, seedMsMasterCategories, getCalendarColorForEventType } from './utils/msCategoryColors';
import { validateSalaryConservation } from './utils/validateSalaryConservation';

// ========================================================================
// AUTH IMPORTS - Supabase Auth for RLS
// ========================================================================
import LoginPage from './pages/auth/LoginPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import { useAuth } from './hooks/useAuth';

// ========================================================================
// AUTH WRAPPER - Handles authentication gate and user mapping
// Fetches the employee record linked to the authenticated Supabase user
// ========================================================================
export default function AppWithAuth() {
  const { session, loading: authLoading, isAuthenticated, signOut, authError } = useAuth();
  const [mappedUser, setMappedUser] = React.useState(null);
  const [mappingLoading, setMappingLoading] = React.useState(false);
  const [mappingError, setMappingError] = React.useState(null);

  // Check if we're on the reset-password route (handles recovery flow separately)
  const isResetPasswordRoute = window.location.pathname === '/reset-password';

  // When session changes, fetch the mapped employee record
  // NOTE: This hook must be called unconditionally (before any early returns)
  React.useEffect(() => {
    // Skip fetching if on reset-password route
    if (isResetPasswordRoute) {
      return;
    }

    const fetchMappedUser = async () => {
      if (!session?.user?.id) {
        setMappedUser(null);
        setMappingError(null);
        return;
      }

      setMappingLoading(true);
      setMappingError(null);

      try {
        console.log('[Auth] Fetching employee record for auth_user_id:', session.user.id);
        
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('auth_user_id', session.user.id)
          .maybeSingle();

        if (error) {
          console.error('[Auth] Error fetching mapped user:', error);
          
          // HARDENED: Check for auth-related errors (401, token issues)
          if (error.status === 401 || 
              error.message?.toLowerCase().includes('jwt') ||
              error.message?.toLowerCase().includes('token') ||
              error.message?.toLowerCase().includes('expired')) {
            console.log('[Auth] Auth error detected in DB query, signing out...');
            setMappingError('SESSION_EXPIRED');
            await signOut();
            return;
          }
          
          setMappingError('Database error: ' + error.message);
          setMappedUser(null);
          return;
        }

        let userRecord = data;

        // Fallback: if no match by auth_user_id, try email-based auto-linking via RPC
        if (!userRecord) {
          console.log('[Auth] No match by auth_user_id, trying email-based auto-link for:', session.user.email);
          const { data: linkResult, error: linkErr } = await supabase
            .rpc('link_auth_account_by_email');

          if (linkErr || !linkResult?.ok) {
            const reason = linkErr?.message || linkResult?.reason || 'unknown';
            console.warn('[Auth] Auto-link failed:', reason);
            setMappingError('NO_MAPPING');
            setMappedUser(null);
            return;
          }

          console.log('[Auth] Auto-link succeeded, re-fetching user record');
          // Re-fetch the now-linked record
          const { data: linkedData, error: linkedErr } = await supabase
            .from('users')
            .select('*')
            .eq('auth_user_id', session.user.id)
            .maybeSingle();

          if (linkedErr || !linkedData) {
            console.error('[Auth] Re-fetch after auto-link failed:', linkedErr);
            setMappingError('NO_MAPPING');
            setMappedUser(null);
            return;
          }

          console.log('[Auth] Successfully auto-linked auth account for:', linkedData.email);
          userRecord = linkedData;
        }

        // Convert snake_case DB columns to camelCase for UI
        // IMPORTANT: Normalize id to number - Supabase returns bigint as string
        let mappedUserData = {
          id: typeof userRecord.id === 'string' ? parseInt(userRecord.id, 10) : userRecord.id,
          name: userRecord.name,
          email: userRecord.email,
          role: userRecord.role,
          status: userRecord.status || 'active', // Lifecycle status: active, inactive, invited
          jobRole: userRecord.job_role,
          jobLevel: userRecord.job_level,
          teamId: userRecord.team_id,
          markup: userRecord.markup,
          joinDate: userRecord.join_date,
          officeLocation: userRecord.office_location || 'NL',
          isActive: userRecord.is_active !== false,
          authUserId: userRecord.auth_user_id,
          tempAccessPages: Array.isArray(userRecord.temp_access_pages) ? userRecord.temp_access_pages : [],
          tempAccessExpiresAt: userRecord.temp_access_expires_at || null,
        };

        console.log('[Auth] User status:', mappedUserData.status);

        // ========================================================================
        // LIFECYCLE ENFORCEMENT
        // ========================================================================
        
        // Handle INACTIVE users - block access
        if (mappedUserData.status === 'inactive') {
          console.log('[Auth] Inactive user blocked:', mappedUserData.email);
          setMappingError('INACTIVE');
          setMappedUser(null);
          return;
        }
        
        // Handle INVITED users - auto-activate on first login
        if (mappedUserData.status === 'invited') {
          console.log('[Auth] Activating invited user...');
          try {
            const { data: activatedData, error: activationError } = await supabase
              .rpc('activate_current_user_if_invited');
            
            if (activationError) {
              console.error('[Auth] Activation RPC error:', activationError);
              setMappingError('Activation failed: ' + activationError.message);
              setMappedUser(null);
              return;
            }
            
            if (activatedData) {
              console.log('[Auth] Activated user:', activatedData);
              // Update mappedUserData with the activated record
              // IMPORTANT: Normalize id to number - Supabase returns bigint as string
              mappedUserData = {
                id: typeof activatedData.id === 'string' ? parseInt(activatedData.id, 10) : activatedData.id,
                name: activatedData.name,
                email: activatedData.email,
                role: activatedData.role,
                status: activatedData.status || 'active',
                jobRole: activatedData.job_role,
                jobLevel: activatedData.job_level,
                teamId: activatedData.team_id,
                markup: activatedData.markup,
                joinDate: activatedData.join_date,
                officeLocation: activatedData.office_location || 'NL',
                isActive: activatedData.is_active !== false,
                authUserId: activatedData.auth_user_id,
                tempAccessPages: Array.isArray(activatedData.temp_access_pages) ? activatedData.temp_access_pages : [],
                tempAccessExpiresAt: activatedData.temp_access_expires_at || null,
              };
            }
          } catch (activationErr) {
            console.error('[Auth] Activation exception:', activationErr);
            setMappingError('Activation failed: ' + activationErr.message);
            setMappedUser(null);
            return;
          }
        }

        console.log('[Auth] Mapped employee record:', mappedUserData.name, '- role:', mappedUserData.role);
        setMappedUser(mappedUserData);
        setMappingError(null);
      } catch (err) {
        console.error('[Auth] Unexpected error fetching mapped user:', err);
        setMappingError('Unexpected error: ' + err.message);
        setMappedUser(null);
      } finally {
        setMappingLoading(false);
      }
    };

    fetchMappedUser();
  }, [session?.user?.id, signOut, isResetPasswordRoute]);

  // If on reset-password route, render that page directly (bypasses auth gate)
  if (isResetPasswordRoute) {
    return <ResetPasswordPage />;
  }

  // Auth gate: Show loading spinner while checking session
  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" style={{ fontFamily: 'Raleway, sans-serif' }}>
        <div className="text-center">
          <BrioMark size={48} className="mx-auto mb-4 animate-pulse" style={{ color: '#9F902A' }} />
          <p className="text-slate-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Auth gate: Show login page if not authenticated (with optional error message)
  if (!isAuthenticated) {
    return <LoginPage errorMessage={authError} />;
  }

  // Show loading while fetching mapped user
  if (mappingLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" style={{ fontFamily: 'Raleway, sans-serif' }}>
        <div className="text-center">
          <BrioMark size={48} className="mx-auto mb-4 animate-pulse" style={{ color: '#9F902A' }} />
          <p className="text-slate-600">Loading your profile...</p>
          <p className="text-xs text-slate-400 mt-2">{session.user.email}</p>
        </div>
      </div>
    );
  }

  // Show error if no employee mapping exists
  if (mappingError === 'NO_MAPPING') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" style={{ fontFamily: 'Raleway, sans-serif' }}>
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-6xl mb-4">🔗</div>
          <h1 className="text-xl font-bold text-slate-800 mb-2">Account Not Linked</h1>
          <p className="text-slate-600 mb-4">
            Your Supabase account (<strong>{session.user.email}</strong>) is not linked to an employee record in the system.
          </p>
          <p className="text-sm text-slate-500 mb-6">
            Please contact your administrator to link your account to your employee profile.
          </p>
          <div className="space-y-2">
            <button
              onClick={async () => {
                await signOut();
              }}
              className="brio-btn brio-btn-primary brio-btn--full"
            >
              Sign out
            </button>
            <p className="text-xs text-slate-400">
              Auth ID: {session.user.id}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show error if user account is inactive (lifecycle enforcement)
  if (mappingError === 'INACTIVE') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" style={{ fontFamily: 'Raleway, sans-serif' }}>
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-6xl mb-4">🚫</div>
          <h1 className="text-xl font-bold text-slate-800 mb-2">Account Inactive</h1>
          <p className="text-slate-600 mb-4">
            Your account is currently inactive. You cannot access the application.
          </p>
          <p className="text-sm text-slate-500 mb-6">
            Please contact your administrator if you believe this is an error.
          </p>
          <div className="space-y-2">
            <button
              onClick={async () => {
                await signOut();
              }}
              className="w-full px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors"
            >
              Sign out
            </button>
            <p className="text-xs text-slate-400">
              {session.user.email}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show other errors
  if (mappingError) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" style={{ fontFamily: 'Raleway, sans-serif' }}>
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-6xl mb-4">⚠️</div>
          <h1 className="text-xl font-bold text-red-800 mb-2">Error Loading Profile</h1>
          <p className="text-slate-600 mb-4">{mappingError}</p>
          <button
            onClick={async () => {
              await signOut();
            }}
            className="px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors"
          >
            Sign out and try again
          </button>
        </div>
      </div>
    );
  }

  // Final guard: session exists but mappedUser not yet loaded
  if (session && !mappedUser) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" style={{ fontFamily: 'Raleway, sans-serif' }}>
        <div className="text-center">
          <BrioMark size={48} className="mx-auto mb-4 animate-pulse" style={{ color: '#9F902A' }} />
          <p className="text-slate-600">Loading your profile...</p>
          <p className="text-xs text-slate-400 mt-2">{session.user.email}</p>
        </div>
      </div>
    );
  }

  // Authenticated and mapped: Render main app with mapped user
  return <TimeTrackerPro session={session} signOut={signOut} mappedUser={mappedUser} />;
}

// ========================================================================
// CALENDAR EVENT TOOLTIP
// ----------------------------------------
// Calendar event boxes truncate their text; we need a tooltip that exposes
// the full content across input modalities — mouse hover (desktop), pen /
// trackpad hover (iPad with Pencil or pointing device), keyboard focus,
// and tap (touch-only). The native `title` attribute only fires on mouse
// hover and doesn't work on touch devices, so we render a custom popover
// via portal so it can escape any clipping ancestor (calendar cells, the
// weekly timeline scroll container, etc.).
// ========================================================================
function useCalendarTooltip() {
  const [active, setActive] = useState(null); // { rect, text, key }

  useEffect(() => {
    if (!active) return;
    const close = () => setActive(null);
    const onDocPointerDown = (e) => {
      if (!e.target.closest || !e.target.closest('[data-cal-tooltip-trigger="1"]')) close();
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    const onScroll = () => close();
    document.addEventListener('pointerdown', onDocPointerDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('pointerdown', onDocPointerDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
    };
  }, [active]);

  const getTriggerProps = useCallback((key, text) => ({
    'data-cal-tooltip-trigger': '1',
    tabIndex: 0,
    onPointerEnter: (e) => {
      if (e.pointerType === 'mouse' || e.pointerType === 'pen') {
        setActive({ rect: e.currentTarget.getBoundingClientRect(), text, key });
      }
    },
    onPointerLeave: (e) => {
      if (e.pointerType === 'mouse' || e.pointerType === 'pen') {
        setActive((prev) => (prev && prev.key === key ? null : prev));
      }
    },
    onPointerDown: (e) => {
      if (e.pointerType === 'touch') {
        e.stopPropagation();
        setActive((prev) =>
          prev && prev.key === key
            ? null
            : { rect: e.currentTarget.getBoundingClientRect(), text, key }
        );
      }
    },
    onFocus: (e) => {
      setActive({ rect: e.currentTarget.getBoundingClientRect(), text, key });
    },
    onBlur: () => setActive((prev) => (prev && prev.key === key ? null : prev)),
  }), []);

  return { active, getTriggerProps };
}

function CalendarTooltipPortal({ active }) {
  if (!active || !active.text) return null;
  const { rect, text } = active;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
  const placeAbove = rect.bottom + 80 > vh;

  // Clamp the horizontal anchor so the centred tooltip stays inside the viewport.
  const anchorLeft = Math.max(8, Math.min(vw - 8, rect.left + rect.width / 2));
  const top = placeAbove ? rect.top - 6 : rect.bottom + 6;
  const transform = placeAbove ? 'translate(-50%, -100%)' : 'translateX(-50%)';

  const style = {
    position: 'fixed',
    left: anchorLeft,
    top,
    transform,
    maxWidth: Math.min(360, vw - 16),
    padding: '6px 10px',
    background: '#1f2937',
    color: '#fff',
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1.35,
    borderRadius: 4,
    boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
    zIndex: 10000,
    pointerEvents: 'none',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  };
  return createPortal(<div role="tooltip" style={style}>{text}</div>, document.body);
}

// ========================================================================
// MAIN APP COMPONENT - Only rendered when authenticated and mapped
// ========================================================================
function TimeTrackerPro({ session, signOut, mappedUser }) {
  const loadData = (key, def) => {
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : def;
    } catch { return def; }
  };

  // ========================================================================
  // SAFE NUMERIC INPUT HELPER - Prevents NaN in numeric inputs
  // ========================================================================
  // Converts any value to a safe value for <input type="number">
  // Returns: finite number unchanged, or "" for NaN/null/undefined
  const safeNumericInputValue = (val) => {
    if (val === null || val === undefined || val === '') return '';
    const num = typeof val === 'number' ? val : parseFloat(val);
    return Number.isFinite(num) ? num : '';
  };

  // Parses input value for storage: returns finite number or null
  const parseNumericInputValue = (val) => {
    if (val === null || val === undefined || val === '') return null;
    const num = parseFloat(val);
    return Number.isFinite(num) ? num : null;
  };

  // Normalizes a leave policy object's numeric fields
  const normalizeLeavePolicyNumericFields = (policy) => {
    if (!policy) return policy;
    return {
      ...policy,
      baseLeave: parseNumericInputValue(policy.baseLeave) ?? 15,
      roleAdditionalLeave: parseNumericInputValue(policy.roleAdditionalLeave) ?? 0,
      tenureIncrementPerYear: parseNumericInputValue(policy.tenureIncrementPerYear) ?? 0.5,
      effectiveYear: parseNumericInputValue(policy.effectiveYear) ?? new Date().getFullYear()
    };
  };

  // Normalizes a notice policy object's numeric fields (mirrors leave normalizer)
  // Normalizes a notice policy object's numeric fields (months, not days)
  const normalizeNoticePolicyNumericFields = (policy) => {
    if (!policy) return policy;
    return {
      ...policy,
      baseNotice: parseNumericInputValue(policy.baseNotice) ?? 1,
      roleAdditionalNotice: parseNumericInputValue(policy.roleAdditionalNotice) ?? 0,
      tenureIncrementPerYear: parseNumericInputValue(policy.tenureIncrementPerYear) ?? 0,
      effectiveYear: parseNumericInputValue(policy.effectiveYear) ?? new Date().getFullYear()
    };
  };

  // ========================================================================
  // BRIO PROJECT TYPE COLOR AUTO-ASSIGNMENT HELPERS
  // ========================================================================
  // Valid project types for the dropdown (base defaults; overridden by settingsHook below)
  const BRIO_PROJECT_TYPES_DEFAULT = [
    'Cultural', 'Educational', 'Houses', 'Housing', 
    'Hospitality', 'Institutional', 'Interior', 'NGO', 'Public', 'BRIO'
  ];

  // Centralized project status color mapping (base defaults; overridden by settingsHook below)
  const PROJECT_STATUS_COLORS_DEFAULT = {
    active: '#9f902a',    // BRIO gold
    paused: '#934948',    // BRIO red
    completed: '#689581', // BRIO green
    archived: '#f0d794'   // BRIO gold-light
  };

  // Fallback palettes (base defaults; overridden by settingsHook below)
  const FALLBACK_PALETTES_DEFAULT = {
    cultural: ['#DB6B5D', '#E28069', '#A66257', '#D3967A', '#994b41'],
    educational: ['#F1B476', '#5f7d6e', '#779c8a', '#92b0a1', '#adc4b9'],
    houses: ['#ECD288', '#a5935f', '#bda86d', '#f0dba0', '#f4e4b8'],
    housing: ['#c59b76', '#f6c293', '#f8cea9', '#fadabe', '#fbe7d4'],
    hospitality: ['#c0886e', '#f0aa89', '#f3bba1', '#f6ccb8', '#f9ddd0'],
    institutional: ['#854e46', '#a66257', '#b88179', '#caa19a', '#dbc0bc'],
    interior: ['#c39868', '#f4be82', '#f6cb9b', '#f8d8b4', '#fbe5cd'],
    ngo: ['#537767', '#689581', '#86aa9a', '#a4bfb3', '#c3d5cd'],
    public: ['#969e78', '#bcc596', '#c9d1ab', '#d7dcc0', '#e4e8d5'],
    brio: ['#714035', '#5a332a', '#53312c', '#7a5f41', '#77523b']
  };

  // Get CSS variable name for a project type
  const getBrioPaletteForType = (type) => {
    if (!type) return null;
    const normalizedType = type.toLowerCase().trim();
    return `--brio-${normalizedType}-palette`;
  };

  // Read palette from CSS variable, returns array of hex strings
  const readPaletteFromCSS = (varName) => {
    try {
      if (typeof window === 'undefined' || !document?.documentElement) {
        return null; // SSR or no DOM
      }
      const value = getComputedStyle(document.documentElement).getPropertyValue(varName);
      if (!value || !value.trim()) return null;
      return value.trim().split(',').map(c => c.trim()).filter(c => c.startsWith('#'));
    } catch (e) {
      console.warn('Could not read CSS palette:', varName, e);
      return null;
    }
  };

  // DJB2 hash - simple deterministic hash returning positive integer
  const stableHash = (str) => {
    if (!str) return 0;
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) + hash) + str.charCodeAt(i);
      hash = hash >>> 0; // Convert to unsigned 32-bit integer
    }
    return hash;
  };

  // Pick a stable color from palette based on project ID or name
  const pickColorFromPalette = ({ palette, projectId, projectName }) => {
    const DEFAULT_COLOR = '#3b82f6';
    if (!palette || !Array.isArray(palette) || palette.length === 0) {
      return DEFAULT_COLOR;
    }
    // Use projectId if available and is a number/string, else projectName
    const hashInput = projectId != null ? String(projectId) : (projectName || 'default');
    const index = stableHash(hashInput) % palette.length;
    return palette[index] || DEFAULT_COLOR;
  };

  // Get palette for a project type (from CSS or fallback)
  const getPaletteForType = (type) => {
    if (!type) return null;
    const normalizedType = type.toLowerCase().trim();
    const varName = getBrioPaletteForType(type);
    const cssPalette = readPaletteFromCSS(varName);
    if (cssPalette && cssPalette.length > 0) {
      return cssPalette;
    }
    // Fallback to hardcoded palette
    return FALLBACK_PALETTES[normalizedType] || null;
  };

  // Auto-pick color for a project based on its type and ID/name
  const autoPickProjectColor = ({ type, projectId, projectName }) => {
    const palette = getPaletteForType(type);
    return pickColorFromPalette({ palette, projectId, projectName });
  };

  // Keywords for auto-detecting project type from name
  const PROJECT_TYPE_KEYWORDS = {
    cultural: ['museum', 'gallery', 'cultural', 'theater', 'theatre', 'arts', 'exhibition', 'heritage', 'memorial'],
    educational: ['school', 'university', 'campus', 'library', 'learning', 'college', 'academy', 'institute'],
    houses: ['villa', 'house', 'residence', 'home', 'private', 'estate', 'bungalow', 'cottage'],
    housing: ['apartment', 'condos', 'condominium', 'multi-family', 'housing', 'tower', 'flats', 'residential'],
    hospitality: ['hotel', 'resort', 'restaurant', 'cafe', 'café', 'bar', 'lodge', 'inn', 'hostel'],
    institutional: ['hospital', 'government', 'civic', 'police', 'court', 'prison', 'embassy', 'consulate'],
    interior: ['interior', 'renovation', 'fit-out', 'fitout', 'refurb', 'refurbishment', 'remodel'],
    ngo: ['ngo', 'non-profit', 'nonprofit', 'charity', 'community', 'welfare', 'foundation', 'trust'],
    public: ['infrastructure', 'park', 'public', 'transport', 'transportation', 'metro', 'station', 'terminal', 'bridge'],
    brio: ['model', 'modelmaking', 'press', 'portfolio', 'acquisition', 'acquisitions', 'admin', 'internal', 'brio']
  };

  // Suggest project type based on project name keywords
  const suggestProjectTypeFromName = (projectName) => {
    if (!projectName) return '';
    const nameLower = projectName.toLowerCase();
    for (const [type, keywords] of Object.entries(PROJECT_TYPE_KEYWORDS)) {
      for (const keyword of keywords) {
        if (nameLower.includes(keyword)) {
          // Return with proper capitalization (BRIO stays uppercase, others title case)
          if (type === 'brio') return 'BRIO';
          return type.charAt(0).toUpperCase() + type.slice(1);
        }
      }
    }
    return '';
  };

  // ========================================================================
  // ROLE-BASED ACCESS CONTROL - Centralized visibility rules
  // ========================================================================
  // Rules:
  // - user: CANNOT see Reports (admin & supervisor only)
  // - supervisor: sees Reports → Project Profitability only (filtered to supervised projects)
  // - user & supervisor: CANNOT see Leave Policy, Leave Entitlement, Leave Summary (admin only)
  // - user: CANNOT see Team pages (admin & supervisor only)
  // - admin: sees everything
  // - admin-pa: like admin but restricted from specific Reports/Analytics pages
  const canAccessPage = (role, pageKey) => {
    // RBAC HARDENING: Normalize unknown roles to 'user' (least privilege)
    let normalizedRole = role;
    if (!['admin', 'admin-pa', 'supervisor', 'user'].includes(role)) {
      normalizedRole = 'user';
    }

    // Admin can access everything
    if (normalizedRole === 'admin') return true;

    // Contacts Directory access is governed entirely by the per-user grants set
    // via the page's "Give access" control (plus role defaults), computed in
    // getContactsAccessLevel. This lets admins both extend access (to a user)
    // and revoke it (from a supervisor). Full admins already returned true above.
    if (pageKey === 'contacts-directory') {
      return getContactsAccessLevel(currentUser) !== 'none';
    }

    // admin-pa: full access EXCEPT specific Reports and Analytics pages
    if (normalizedRole === 'admin-pa') {
      const adminPaDenied = [
        'projectProfitability', 'projectCostLedger', 'profitability',  // Reports
        'overview', 'timesheets', 'allocation', 'financial-overview',  // Analytics
        'analytics-projects', 'invoicing', 'salary',                     // Analytics
        'style-guide'
      ];
      return !adminPaDenied.includes(pageKey);
    }

    // Pages restricted from BOTH users AND supervisors (admin/admin-pa only)
    const adminOnlyPages = [
      // Inquiries (admin / admin-pa only)
      'inquiries',
      // Leave admin pages
      'leave-policy',
      'leave-entitlement',
      'leave-summary',
      // Report sub-tabs (admin-only, supervisor only sees projectProfitability)
      'salaryCost',
      'projectCostLedger',
      'profitability',
      'vofStatement',  // VOF hours statement (intercompany, admin and admin-pa only)
      'travel',        // Travel log, feeds the VOF statement (admin and admin-pa only)
      // Accounting pages (admin-only subset)
      'expense-analytics',
      'tax-reconciliation',
      'project-planning',
      'salary-matrix',
      'salary-matrix-nl',
      'markup-factor',
      'settings',
      // Analytics
      'analytics',
      // Style Guide (temporary)
      'style-guide',
      // Admin team overview
      'admin-team-overview',
      // Freelancer Management (admin only — controls auto-allocation and
      // monthly hour approvals that drive salary cost)
      'freelancer-management'
    ];

    // Pages restricted from users only (admin & supervisor can access)
    const supervisorAndAdminOnlyPages = [
      'teams',
      'team-members',
      'hiring',        // Hiring candidate tracking (admin + supervisor)
      'hybrid-focus-board', // Team Allocation board (admin + supervisor)
      'timeline',      // Project Tracking & Invoicing
      'tracking',      // subPage key that maps to timeline view
      'invoices',      // Invoice management (admin + supervisor only)
      'contracts-scope', // Contracts & Scope (admin + supervisor)
      'contracts',     // Contracts management (admin + supervisor)
      'radar',         // Radar operational dashboard (admin + supervisor)
      'comms',         // Comms & Outreach (admin + supervisor)
      'expense-admin', // Expense Administration (supervisors see project expenses only)
      'contacts-directory', // Vendors, contractors, suppliers directory
      'projects-overview', // Projects Overview table (admin + supervisor)
      'timesheet-overview', // Timesheet Overview (admin + supervisor)
      'reports',       // Reports main category (supervisor sees Project Profitability only)
      'dashboard',     // dashboard is the view name for reports
    ];

    // Check restrictions for supervisor role
    if (normalizedRole === 'supervisor') {
      if (adminOnlyPages.includes(pageKey)) {
        return hasTempPageAccess(currentUser, pageKey);
      }
    }

    // Check restrictions for user role (and any unknown roles due to normalization)
    if (normalizedRole === 'user') {
      if (adminOnlyPages.includes(pageKey)) {
        return hasTempPageAccess(currentUser, pageKey);
      }
      if (supervisorAndAdminOnlyPages.includes(pageKey)) {
        return hasTempPageAccess(currentUser, pageKey);
      }
    }

    return true;
  };

  // Admin-granted temporary page access (overrides role restrictions for the
  // current user only, until temp_access_expires_at). Backed by users.temp_access_pages
  // + users.temp_access_expires_at and mirrored in RLS for the affected tables.
  const hasTempPageAccess = (user, pageKey) => {
    if (!user || !pageKey) return false;
    const pages = user.tempAccessPages;
    if (!Array.isArray(pages) || !pages.includes(pageKey)) return false;
    const expiresAt = user.tempAccessExpiresAt;
    if (!expiresAt) return false;
    const expiry = new Date(expiresAt);
    if (Number.isNaN(expiry.getTime())) return false;
    return expiry.getTime() > Date.now();
  };
  
  // Helper to get default safe page for a role
  const getDefaultPageForRole = (role) => {
    if (role === 'admin' || role === 'admin-pa') {
      return { mainCategory: 'team', subPage: 'hybrid-focus-board' };
    }
    return { mainCategory: 'time', subPage: 'donut-tracker' };
  };

  const safeSetItem = (key, value) => {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (e) {
      if (e.name === 'QuotaExceededError') {
        console.error(`❌ Storage quota exceeded for ${key}`);
        
        // Calculate size
        const sizeKB = (value.length / 1024).toFixed(2);
        const sizeMB = (value.length / (1024 * 1024)).toFixed(2);
        console.log(`   Data size: ${sizeKB} KB (${sizeMB} MB)`);
        console.log(`   Tip: Use "Cleanup Old Data" or "Data Manager > Export Recent Only"`);
        
        // Show user-friendly error (only once per session, not during bulk imports)
        if (!window.quotaErrorShown && !window.bulkImportInProgress) {
          window.quotaErrorShown = true;
          setTimeout(() => {
            alert(
              `⚠️ Storage Limit Reached!\n\n` +
              `Item: ${key}\n` +
              `Size: ${sizeKB} KB (${sizeMB} MB)\n\n` +
              `Data is too large for browser storage (limit ~5-10 MB).\n\n` +
              `Recommended Actions:\n` +
              `1. Use "Cleanup Old Data" to remove old entries\n` +
              `2. Export to CSV and keep only recent data\n` +
              `3. Use "Data Manager > Export Recent Only" for smaller backups\n\n` +
              `Note: Current session works, but data won't persist after refresh.`
            );
          }, 100);
        }
        
        return false;
      }
      console.error(`Error saving to localStorage (${key}):`, e);
      return false;
    }
  };

  // ========================================================================
  // USER STATE SEPARATION:
  // - currentUser: The AUTHENTICATED user from Supabase mappedUser (controls permissions)
  //   AUTHORITATIVE SOURCE: mappedUser from Supabase is the ONLY source of truth.
  //   This is NEVER read from or persisted to localStorage.
  // - selectedUser: The user selected in UI for viewing/filtering (UI-only, demo purposes)
  // 
  // IMPORTANT: Permission checks (canAccessPage, admin pages) use currentUser.role
  // Viewing/filtering logic uses selectedUser.id
  // ========================================================================
  
  // currentUser = authenticated user from Supabase mapping (NEVER overwritten by UI or localStorage)
  const [currentUser, setCurrentUser] = useState(mappedUser);
  
  // RBAC Helper: admin-pa treated as admin for most features.
  // Page access is restricted via canAccessPage() (Reports/Analytics subset denied).
  // Use isAdmin for general admin permission checks throughout the app.
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'admin-pa';
  
  // selectedUser = user selected in UI for viewing/filtering (can be changed for demo)
  const [selectedUser, setSelectedUser] = useState(mappedUser);
  
  // Sync both states when mappedUser changes (e.g., on re-auth or profile update)
  useEffect(() => {
    if (mappedUser) {
      console.log('[App] Syncing from mappedUser:', mappedUser.name, '- role:', mappedUser.role);
      // Always update currentUser to match authenticated user
      setCurrentUser(mappedUser);
      // Also reset selectedUser to authenticated user
      setSelectedUser(mappedUser);
    }
  }, [mappedUser]);

  // SUPABASE-ONLY: Local state is UI-only placeholder, actual data comes from Supabase hooks
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [rateHistory, setRateHistory] = useState([]);
  const [salaryMatrix, setSalaryMatrix] = useState({});
  const [salaryMatrixHistory, setSalaryMatrixHistory] = useState([]);
  const [salaryMatrixHistoryNL, setSalaryMatrixHistoryNL] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  // SUPABASE-ONLY: Tasks come from Supabase hooks
  const [tasks, setTasks] = useState([]);
  // SUPABASE-ONLY: Time entries come from Supabase hooks
  const [timeEntries, setTimeEntries] = useState([]);
  // SUPABASE-ONLY: These come from Supabase or are UI-only state
  const [repeatingEntries, setRepeatingEntries] = useState([]);
  const [timeOff, setTimeOff] = useState(() => loadData('tt_timeoff', []));
  const [holidays, setHolidays] = useState([]);
  
  // === LEAVE MANAGEMENT MODULE ===
  // SUPABASE-ONLY: Leave policy comes from Supabase, initialized with defaults for UI
  const DEFAULT_LEAVE_POLICY = [
    { roleLevel: 'Intern', baseLeave: 15, roleAdditionalLeave: 0, tenureIncrementPerYear: 0.5, effectiveYear: 2025 },
    { roleLevel: 'Junior Architect', baseLeave: 15, roleAdditionalLeave: 0, tenureIncrementPerYear: 0.5, effectiveYear: 2025 },
    { roleLevel: 'Medior Architect', baseLeave: 15, roleAdditionalLeave: 2, tenureIncrementPerYear: 0.5, effectiveYear: 2025 },
    { roleLevel: 'Project Architect', baseLeave: 15, roleAdditionalLeave: 3, tenureIncrementPerYear: 0.5, effectiveYear: 2025 },
    { roleLevel: 'Senior Architect', baseLeave: 15, roleAdditionalLeave: 5, tenureIncrementPerYear: 0.5, effectiveYear: 2025 },
    { roleLevel: 'Associate', baseLeave: 15, roleAdditionalLeave: 7, tenureIncrementPerYear: 0.5, effectiveYear: 2025 },
    { roleLevel: 'Partner', baseLeave: 15, roleAdditionalLeave: 10, tenureIncrementPerYear: 0.5, effectiveYear: 2025 },
    { roleLevel: 'Director', baseLeave: 15, roleAdditionalLeave: 12, tenureIncrementPerYear: 0.5, effectiveYear: 2025 },
    { roleLevel: 'Support', baseLeave: 15, roleAdditionalLeave: 0, tenureIncrementPerYear: 0.5, effectiveYear: 2025 },
    { roleLevel: 'Model Maker', baseLeave: 15, roleAdditionalLeave: 0, tenureIncrementPerYear: 0.5, effectiveYear: 2025 },
    { roleLevel: 'Visualiser', baseLeave: 15, roleAdditionalLeave: 0, tenureIncrementPerYear: 0.5, effectiveYear: 2025 },
    { roleLevel: 'Others', baseLeave: 15, roleAdditionalLeave: 0, tenureIncrementPerYear: 0.5, effectiveYear: 2025 }
  ];
  const [leavePolicyMatrix, setLeavePolicyMatrix] = useState(DEFAULT_LEAVE_POLICY);
  const [employeeLeaveEntitlement, setEmployeeLeaveEntitlement] = useState([]);
  const [leaveAuditLog, setLeaveAuditLog] = useState([]);
  const [selectedLeaveYear, setSelectedLeaveYear] = useState(new Date().getFullYear());

  // ========================================================================
  // NOTICE POLICY DEFAULTS & STATE (mirrors Leave Policy — 3-component model)
  // notice_total_months = min(baseNotice + roleAdditionalNotice + (years × tenureIncrementPerYear), 6)
  // All values are in MONTHS
  // ========================================================================
  const DEFAULT_NOTICE_POLICY = [
    { roleLevel: 'Intern', baseNotice: 1, roleAdditionalNotice: 0, tenureIncrementPerYear: 0, effectiveYear: 2025 },
    { roleLevel: 'Junior Architect', baseNotice: 1, roleAdditionalNotice: 0, tenureIncrementPerYear: 0, effectiveYear: 2025 },
    { roleLevel: 'Medior Architect', baseNotice: 1, roleAdditionalNotice: 0, tenureIncrementPerYear: 0, effectiveYear: 2025 },
    { roleLevel: 'Project Architect', baseNotice: 1, roleAdditionalNotice: 1, tenureIncrementPerYear: 0, effectiveYear: 2025 },
    { roleLevel: 'Senior Architect', baseNotice: 1, roleAdditionalNotice: 1, tenureIncrementPerYear: 0, effectiveYear: 2025 },
    { roleLevel: 'Associate', baseNotice: 1, roleAdditionalNotice: 2, tenureIncrementPerYear: 0, effectiveYear: 2025 },
    { roleLevel: 'Partner', baseNotice: 1, roleAdditionalNotice: 2, tenureIncrementPerYear: 0, effectiveYear: 2025 },
    { roleLevel: 'Director', baseNotice: 1, roleAdditionalNotice: 2, tenureIncrementPerYear: 0, effectiveYear: 2025 },
    { roleLevel: 'Support', baseNotice: 1, roleAdditionalNotice: 0, tenureIncrementPerYear: 0, effectiveYear: 2025 },
    { roleLevel: 'Model Maker', baseNotice: 1, roleAdditionalNotice: 0, tenureIncrementPerYear: 0, effectiveYear: 2025 },
    { roleLevel: 'Visualiser', baseNotice: 1, roleAdditionalNotice: 0, tenureIncrementPerYear: 0, effectiveYear: 2025 },
    { roleLevel: 'Others', baseNotice: 1, roleAdditionalNotice: 0, tenureIncrementPerYear: 0, effectiveYear: 2025 }
  ];
  const [noticePolicyMatrix, setNoticePolicyMatrix] = useState(DEFAULT_NOTICE_POLICY);
  const [noticePolicySaving, setNoticePolicySaving] = useState(false);
  const [noticePolicyDirty, setNoticePolicyDirty] = useState(false);
  const [noticePolicySnapshot, setNoticePolicySnapshot] = useState(null);
  const [noticePolicySupabaseLoaded, setNoticePolicySupabaseLoaded] = useState(false);
  // SUPABASE-ONLY: Allocations come from Supabase hooks
  const [allocations, setAllocations] = useState([]);
  const [invoices, setInvoices] = useState([]);
  // REMOVED: const [settings, setSettings] = useState(() => loadData('tt_settings', {...}))
  // Settings (roundingInterval, essentialProfitPercentage, manualSalaryMarkup) are now
  // centralized in useSettings hook (settingsHook). See line ~1084.
  
  // SUPABASE-ONLY: Accounting data comes from Supabase hooks
  const [projectExpenses, setProjectExpenses] = useState([]);
  const [officeOverheads, setOfficeOverheads] = useState([]);
  const [salaryVarianceHistory, setSalaryVarianceHistory] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [activeScenario, setActiveScenario] = useState(null);
  
  // Two-row navigation state
  // Role-based landing: admin/admin-pa → HFB, user/supervisor → donut tracker
  const isAdminRole = mappedUser?.role === 'admin' || mappedUser?.role === 'admin-pa';
  // Persist the last-visited page (per user) so a hard refresh — or reopening the
  // tab — reopens where the user was instead of the role-based landing page.
  // Saved by the effect further down; the navigation access guard below corrects
  // any restored page the user can no longer access.
  const navStorageKey = `tt_last_nav:${mappedUser?.id ?? 'anon'}`;
  const [mainCategory, setMainCategory] = useState(() => {
    const saved = loadData(navStorageKey, null);
    return saved?.mainCategory || (isAdminRole ? 'team' : 'time');
  });
  const [subPage, setSubPage] = useState(() => {
    const saved = loadData(navStorageKey, null);
    // subPage is intentionally null for Reports/Analytics, so distinguish a stored
    // null from "nothing stored" to restore those sections correctly.
    return saved ? (saved.subPage ?? null) : (isAdminRole ? 'hybrid-focus-board' : 'donut-tracker');
  });

  // Mobile navigation state. The desktop nav needs about 1280px, so anything
  // narrower than 1024px (phones in landscape, small tablets) gets the mobile nav.
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 1024 : false);
  const [mobileSubpageSheetOpen, setMobileSubpageSheetOpen] = useState(false);
  const [mobileMoreSheetOpen, setMobileMoreSheetOpen] = useState(false);
  
  // Legacy view state - MUST be declared BEFORE useEffects that use it
  const [view, setView] = useState('tracker');
  const [modal, setModal] = useState(null);
  // Weekly timesheet submit lock: a second Submit while a save is in flight
  // re-read cells without entryIds and inserted every row a second time.
  const timesheetSavingRef = useRef(false);
  const [timesheetSaving, setTimesheetSaving] = useState(false);
  const [confirmReject, setConfirmReject] = useState(null); // Store pending rejection
  const [editLeave, setEditLeave] = useState(null); // Store leave being edited
  const [confirmCancel, setConfirmCancel] = useState(null); // Store leave to cancel
  
  // Sync new navigation with old view system
  useEffect(() => {
    // Map new navigation to old view
    const getViewFromNavigation = () => {
      if (mainCategory === 'time') {
        if (subPage === 'donut-tracker') return 'donut-tracker';
        if (subPage === 'tracker') return 'tracker';
        if (subPage === 'calendar') return 'calendar';
        if (subPage === 'ms-calendar') return 'ms-calendar';
        if (subPage === 'time-off-requests') return 'timeoff';
        if (subPage === 'leave-policy') return 'leave-policy';
        if (subPage === 'leave-entitlement') return 'leave-entitlement';
        if (subPage === 'leave-summary') return 'leave-summary';
        if (subPage === 'timesheet-overview') return 'timesheet-overview';
        if (subPage === 'freelancer-management') return 'freelancer-management';
      }
      if (mainCategory === 'projects') {
        if (subPage === 'manage-projects') return 'manage-projects';
        if (subPage === 'projects-overview') return 'projects-overview';
        if (subPage === 'clients') return 'clients';
        if (subPage === 'inquiries') return 'inquiries';
        if (subPage === 'stages') return 'stages';
        if (subPage === 'tracking') return 'timeline';
        if (subPage === 'invoices') return 'invoices';
        if (subPage === 'project-map') return 'project-map';
        if (subPage === 'timelines') return 'timelines';
        if (subPage === 'mom-directory') return 'mom-directory';
        if (subPage === 'contracts-scope') return 'contracts-scope';
        if (subPage === 'contracts') return 'contracts';
        if (subPage === 'contacts-directory') return 'contacts-directory';
      }
      if (mainCategory === 'team') {
        if (subPage === 'teams') return 'teams';
        if (subPage === 'team-members') return 'team-members';
        if (subPage === 'hiring') return 'hiring';
        if (subPage === 'role-framework') return 'role-framework';
        if (subPage === 'travel') return 'travel';
        if (subPage === 'hybrid-focus-board') return 'hybrid-focus-board';
        if (subPage === 'project-tasks') return 'project-tasks';
        if (subPage === 'admin-team-overview') return 'admin-team-overview';
      }
      if (mainCategory === 'accounting') {
        if (subPage === 'expense-claims') return 'expense-claims';
        if (subPage === 'expense-admin') return 'expense-admin';
        if (subPage === 'expense-analytics') return 'expense-analytics';
        if (subPage === 'tax-reconciliation') return 'tax-reconciliation';
        if (subPage === 'project-planning') return 'project-planning';
        if (subPage === 'salary-matrix') return 'salary-matrix';
        if (subPage === 'salary-matrix-nl') return 'salary-matrix-nl';
        if (subPage === 'markup-factor') return 'markup-factor';
        if (subPage === 'settings') return 'settings';
      }
      if (mainCategory === 'reports') return 'dashboard';
      if (mainCategory === 'analytics') return 'analytics';
      if (mainCategory === 'radar') return 'radar';
      if (mainCategory === 'styleguide') return 'style-guide';
      if (mainCategory === 'comms') return subPage || 'comms-practice';
      return 'donut-tracker';
    };
    
    const newView = getViewFromNavigation();
    if (newView !== view) {
      setView(newView);
    }
  }, [mainCategory, subPage]);

  // Guard against unauthorized navigation - redirect to safe page if user cannot access current page
  useEffect(() => {
    // Check both mainCategory and subPage against access rules
    const pageKey = subPage || mainCategory;
    if (!canAccessPage(currentUser.role, mainCategory) || !canAccessPage(currentUser.role, pageKey)) {
      const defaultPage = getDefaultPageForRole(currentUser.role);
      console.log(`Access denied: ${currentUser.role} cannot access ${mainCategory}/${subPage}. Redirecting to ${defaultPage.mainCategory}/${defaultPage.subPage}.`);
      setMainCategory(defaultPage.mainCategory);
      setSubPage(defaultPage.subPage);
    }
  }, [currentUser.role, mainCategory, subPage]);

  // Guard against inaccessible default report/analytics tabs for restricted roles (e.g. admin-pa)
  useEffect(() => {
    if (mainCategory === 'reports') {
      const currentTab = form.reportTab || 'profitability';
      if (!canAccessPage(currentUser.role, currentTab)) {
        const allowed = ['projectProfitability', 'salaryCost', 'projectCostLedger', 'profitability', 'vofStatement']
          .find(t => canAccessPage(currentUser.role, t));
        if (allowed && allowed !== form.reportTab) {
          setForm(prev => ({ ...prev, reportTab: allowed }));
        }
      }
    }
    if (mainCategory === 'analytics') {
      const currentTab = form.analyticsTab || 'overview';
      if (!canAccessPage(currentUser.role, currentTab)) {
        const allowed = ['overview', 'timesheets', 'allocation', 'financial-overview', 'analytics-projects', 'invoicing', 'cashflow', 'salary', 'stage-intelligence', 'capacity-intelligence']
          .find(t => canAccessPage(currentUser.role, t));
        if (allowed && allowed !== form.analyticsTab) {
          setForm(prev => ({ ...prev, analyticsTab: allowed }));
        }
      }
    }
  }, [currentUser.role, mainCategory]);

  // ========================================================================
  // SUPABASE DATABASE MODE - Toggle between localStorage and Supabase
  // ========================================================================
  const useSupabaseDB = true; // HARDENED: Supabase is the only source of truth
  const [supabaseReady, setSupabaseReady] = useState(false);
  
  // Leave Policy Supabase loading state
  const [leavePolicySupabaseLoaded, setLeavePolicySupabaseLoaded] = useState(false);
  // Leave Policy save state - for explicit Save button
  const [leavePolicySaving, setLeavePolicySaving] = useState(false);
  const [leavePolicyDirty, setLeavePolicyDirty] = useState(false);
  const [leavePolicySnapshot, setLeavePolicySnapshot] = useState(null); // Snapshot before edits for audit
  
  // Project status filter (for admins) - persisted in localStorage
  const [projectStatusFilter, setProjectStatusFilter] = useState(() => 
    loadData('tt_project_status_filter', 'default') // 'default', 'all', 'active', 'paused', 'completed', 'archived'
  );
  
  // Filter dropdown menu visibility state
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  
  // === NAVIGATION HEIGHT TRACKING ===
  // Ref for the fixed navigation container to measure its actual height
  const navRef = useRef(null);
  const [navHeight, setNavHeight] = useState(73); // Default to main header only
  
  // ========================================================================
  // FULLSCREEN DETECTION — Cross-platform (Fullscreen API + F11/kiosk)
  //
  // Mac Chrome: requestFullscreen() sets document.fullscreenElement ✓
  // Windows Chrome F11 / kiosk: does NOT set fullscreenElement, does NOT
  //   fire fullscreenchange. The only signal is that the viewport size
  //   matches (or nearly matches) the screen size.
  //
  // Strategy: combine both signals with a small tolerance for Windows
  //   taskbar, DPI scaling, and browser chrome.
  // ========================================================================
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    // Heuristic: viewport fills the screen within N px (handles taskbar, scaling)
    const TOLERANCE = 5;
    const checkFs = () => {
      // Fullscreen API (Mac Chrome, programmatic requestFullscreen)
      if (document.fullscreenElement) return true;
      // F11 / kiosk heuristic (Windows Chrome)
      const hMatch = Math.abs(window.innerHeight - window.screen.height) <= TOLERANCE;
      const wMatch = Math.abs(window.innerWidth - window.screen.width) <= TOLERANCE;
      return hMatch && wMatch;
    };

    const update = () => setIsFullscreen(checkFs());

    // Listen to both Fullscreen API events and resize (covers F11)
    document.addEventListener('fullscreenchange', update);
    window.addEventListener('resize', update);
    // Initial check (covers page load inside kiosk / already-fullscreen)
    update();

    return () => {
      document.removeEventListener('fullscreenchange', update);
      window.removeEventListener('resize', update);
    };
  }, []);
  
  // === CALENDAR MODULE STATES ===
  // Cross-device tooltip for truncated event-box titles (mouse hover, pen
  // hover, keyboard focus, touch tap). See useCalendarTooltip definition
  // above for the input-modality behaviour matrix.
  const { active: calTooltipActive, getTriggerProps: getCalTooltipProps } = useCalendarTooltip();
  // SUPABASE-ONLY: Calendar events come from Supabase hooks
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showDayEventsModal, setShowDayEventsModal] = useState(null); // For monthly view "+ more"
  const [eventForm, setEventForm] = useState({});
  const [editingEvent, setEditingEvent] = useState(null); // For editing existing events
  // Every category on by default. These control what the calendar DRAWS and
  // nothing else: the push to Outlook is built from the database and never
  // reads them (getUnsyncedBrioExportItems / bulkPushEventsToMicrosoft), so a
  // category being off has never meant "don't sync it", only "don't show it".
  //
  // clientBirthdays and clientAnniversaries were missing from this object
  // entirely while CalendarPage rendered toggles for them, so they read as
  // undefined, their sections never ran, and the chips sat permanently greyed.
  // That is why a client birthday could not be found in the app even though it
  // was correctly in the database and in Outlook.
  const [categoryFilters, setCategoryFilters] = useState({
    holidays: true,            // Public holidays
    leaves: true,              // Team time off (approved leaves)
    pendingLeaves: true,       // Pending leave requests (admin/supervisor only)
    milestones: true,          // Project milestones (target/contract/actual dates)
    invoicing: true,           // Invoice/payment dates
    events: true,              // Custom calendar events
    msCalendar: true,          // Microsoft Calendar events (default Outlook calendar)
    anniversaries: true,       // Work anniversaries + join dates + leaving dates
    birthdays: true,           // Team member birthdays (from user_profiles.dob)
    clientBirthdays: true,     // Client + client contact birthdays (from clients.dob)
    clientAnniversaries: true  // Client anniversaries (from clients.anniversary)
  });
  
  // === MICROSOFT CALENDAR INTEGRATION ===
  const [msCalendarConnected, setMsCalendarConnected] = useState(() => loadData('tt_ms_connected', false));
  // MS tokens live in memory only — the refresh token is server-side (ms-auth);
  // the browser holds just a short-lived access token cache, re-fetched from the
  // server as needed. Nothing token-related is persisted to localStorage.
  const [msAccessToken, setMsAccessToken] = useState(null);
  const [msTokenExpiry, setMsTokenExpiry] = useState(null);
  const [msCalendarEvents, setMsCalendarEvents] = useState(() => loadData('tt_ms_events', []));
  const [msLastSync, setMsLastSync] = useState(() => loadData('tt_ms_last_sync', null));
  // Ids of the Outlook events this app created, from ms_synced_items. Used to
  // tell its own output apart from events a person typed into a BRIO calendar.
  // `loaded` gates that: an unread or failed ledger must never be read as
  // "nothing is tracked", or every synced event renders as a stray copy.
  const [msSyncedEventIds, setMsSyncedEventIds] = useState(() => loadData('tt_ms_synced_ids', []));
  const [msLedgerLoaded, setMsLedgerLoaded] = useState(false);
  const [msSyncInProgress, setMsSyncInProgress] = useState(false);
  const [msAutoSync, setMsAutoSync] = useState(() => loadData('tt_ms_auto_sync', false));
  const [msSyncSettings, setMsSyncSettings] = useState(() => loadData('tt_ms_sync_settings', {
    syncPastDays: 30,
    syncFutureDays: 90,
    autoCreateEntries: false,
    onlyBillableCategories: [],
    defaultProjectId: null,
    defaultTaskId: null
  }));
  
  // Webhook subscription for event-based sync
  const [msWebhookSubscription, setMsWebhookSubscription] = useState(() => loadData('tt_ms_webhook', null));
  const [msWebhookEnabled, setMsWebhookEnabled] = useState(() => loadData('tt_ms_webhook_enabled', false));
  // Synchronous guard so rapid repeated clicks can't spawn duplicate subscriptions
  const msWebhookCreatingRef = useRef(false);
  
  // Admin-managed master calendar name (derived from integration status)
  const [msCalendarName, setMsCalendarName] = useState(null);
  
  // Non-blocking reconnect flag — set when refresh token is truly invalid
  // Shown as a banner on the MS Calendar page instead of interrupt alerts
  const [msAuthNeedsReconnect, setMsAuthNeedsReconnect] = useState(false);
  
  const [form, setForm] = useState(() => {
    // Restore the Reports/Analytics sub-tab (stored on `form`) alongside the nav.
    const saved = loadData(navStorageKey, null);
    const init = {};
    if (saved?.reportTab) init.reportTab = saved.reportTab;
    if (saved?.analyticsTab) init.analyticsTab = saved.analyticsTab;
    return init;
  });

  // Persist the current page so a hard refresh returns the user to where they were.
  // Reports/Analytics keep their active sub-tab too; we only overwrite the stored
  // tab when one is set, so transient `setForm({})` resets elsewhere don't wipe it.
  useEffect(() => {
    try {
      const prev = loadData(navStorageKey, {}) || {};
      const next = { mainCategory, subPage };
      const reportTab = form.reportTab || prev.reportTab;
      const analyticsTab = form.analyticsTab || prev.analyticsTab;
      if (reportTab) next.reportTab = reportTab;
      if (analyticsTab) next.analyticsTab = analyticsTab;
      safeSetItem(navStorageKey, JSON.stringify(next));
    } catch { /* ignore persistence failures (e.g. storage disabled) */ }
  }, [navStorageKey, mainCategory, subPage, form.reportTab, form.analyticsTab]);

  // Flush pending edits before the page unloads. Many fields across the app — the
  // inline Comms fields, and other onBlur-to-save inputs — commit only when they
  // lose focus. A hard refresh (or closing/navigating away) while a field is still
  // focused would otherwise discard the edit, because the blur never fires.
  // Blurring the active element here runs that same onBlur save path, so the field
  // commits on its way out. Best-effort: the browser may not keep an in-flight
  // write alive through a hard reload, but the request is dispatched synchronously,
  // which reliably covers closing/navigating and usually lands on refresh too.
  useEffect(() => {
    const flushActiveField = () => {
      const el = document.activeElement;
      if (el && typeof el.blur === 'function' &&
          (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) {
        el.blur();
      }
    };
    window.addEventListener('beforeunload', flushActiveField);
    window.addEventListener('pagehide', flushActiveField);
    return () => {
      window.removeEventListener('beforeunload', flushActiveField);
      window.removeEventListener('pagehide', flushActiveField);
    };
  }, []);

  // Date range picker state for Time Off modal
  const [dateRangeDragging, setDateRangeDragging] = useState(false);
  const [dateRangeDragAnchor, setDateRangeDragAnchor] = useState(null); // YYYY-MM-DD
  const [dateRangeDragHover, setDateRangeDragHover] = useState(null);   // YYYY-MM-DD
  const [dateRangeMonth, setDateRangeMonth] = useState(() => new Date());
  
  const [selected, setSelected] = useState([]);
  const [editing, setEditing] = useState(null);
  const [isEditingMatrix, setIsEditingMatrix] = useState(false);
  const [tempSalaryMatrix, setTempSalaryMatrix] = useState(null);
  const [calDate, setCalDate] = useState(new Date());
  const [calView, setCalView] = useState('week');
  const [resourceView, setResourceView] = useState('team'); // 'team' or 'project'
  const [selectedResourceProject, setSelectedResourceProject] = useState(null);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    // Get Monday of current week inline
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(today.setDate(diff));
    return monday.toISOString().split('T')[0];
  });
  const [filters, setFilters] = useState({
    userId: (isAdmin || currentUser.role === 'supervisor') ? 'all' : currentUser.id,
    projectId: 'all', clientId: 'all', billable: 'all', startDate: '', endDate: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage] = useState(50);
  const [selectedTimelineProject, setSelectedTimelineProject] = useState(null);
  const [pendingTimelineAction, setPendingTimelineAction] = useState(null); // nav params from RADAR → Timeline
  const [pendingHiringAction, setPendingHiringAction] = useState(null); // nav params from RADAR → Hiring
  // Inquiries → Comms → Pitches. CommsPage keeps its open deck in component
  // state rather than in the route, so the inquiry travels as state and the
  // Pitches tab opens on the decks list for it.
  const [pitchDeckInquiry, setPitchDeckInquiry] = useState(null);
  const [lastCalendarSync, setLastCalendarSync] = useState(() => loadData('tt_last_sync', null));
  
  // Test data generation state
  const [testDataProgress, setTestDataProgress] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);

  // ========================================================================
  // VIEW DATA STATE - For Analytics & Reports (RLS-gated via views)
  // These use dedicated Supabase views instead of base tables to allow
  // Admin-PA blocking without breaking operational pages.
  // ========================================================================
  
  // Analytics Dashboard view data
  const [analyticsViewRows, setAnalyticsViewRows] = useState([]);
  const [analyticsViewLoading, setAnalyticsViewLoading] = useState(false);
  const [analyticsViewError, setAnalyticsViewError] = useState(null);
  
  // Profitability Report view data
  const [profitViewRows, setProfitViewRows] = useState([]);
  const [profitViewLoading, setProfitViewLoading] = useState(false);
  const [profitViewError, setProfitViewError] = useState(null);
  
  // Salary Cost Report view data
  const [salaryReportViewRows, setSalaryReportViewRows] = useState([]);
  const [salaryReportViewLoading, setSalaryReportViewLoading] = useState(false);
  const [salaryReportViewError, setSalaryReportViewError] = useState(null);

  // ── Contract Scopes (initialized after settingsHook below) ──

  // ── Salary Ledger (Phase 2) ──
  const salaryLedgerHook = useSalaryLedger();

  // ── Freelancer monthly hours overrides ──
  // Source-of-truth for contracted hours when a user's overlapping role
  // history row has is_freelancer = true. Salary cost, project profitability
  // and project cost ledger all consult this map before falling back to the
  // role-history value.
  const { approvedMap: freelancerApprovedMap, pendingMap: freelancerPendingMap } = useApprovedFreelancerHoursMap();
  const [salaryLedgerYear, setSalaryLedgerYear] = useState(null);
  const [salaryLedgerData, setSalaryLedgerData] = useState([]);
  const [salaryLedgerLoading, setSalaryLedgerLoading] = useState(false);

  // ── Full Salary Ledger for Profitability (all years) ──
  // Structure: { authUserId: { "YYYY-MM": { base_salary, bonus, source } } }
  const [fullSalaryLedger, setFullSalaryLedger] = useState({});
  const [fullLedgerLoaded, setFullLedgerLoaded] = useState(false);

  // ── Paid invoice income by project (for profitability calculations) ──
  const [paidIncomeByProject, setPaidIncomeByProject] = useState({});
  const [paidIncomeLoaded, setPaidIncomeLoaded] = useState(false);

  // ── Full Salary Ledger fetch (all years, for profitability) ──
  const fetchFullSalaryLedger = useCallback(async () => {
    if (!useSupabaseDB) return;
    try {
      // Supabase caps results at 1000 rows (server-side apiMaxRows).
      // Paginate with .range() to fetch the complete ledger.
      const PAGE = 1000;
      let allRows = [];
      let from = 0;
      let done = false;
      while (!done) {
        const { data, error } = await supabase
          .from('salary_monthly_ledger')
          .select('auth_user_id, month, base_salary, bonus, source')
          .range(from, from + PAGE - 1);
        if (error) {
          console.error('[SalaryLedger] Full fetch error:', error);
          return;
        }
        allRows = allRows.concat(data || []);
        if (!data || data.length < PAGE) {
          done = true;
        } else {
          from += PAGE;
        }
      }
      const map = {};
      allRows.forEach(r => {
        if (!map[r.auth_user_id]) map[r.auth_user_id] = {};
        let ym = '';
        if (typeof r.month === 'string') {
          ym = r.month.substring(0, 7);
        } else if (r.month instanceof Date) {
          ym = `${r.month.getFullYear()}-${String(r.month.getMonth() + 1).padStart(2, '0')}`;
        }
        if (ym) map[r.auth_user_id][ym] = r;
      });
      setFullSalaryLedger(map);
      setFullLedgerLoaded(true);
    } catch (err) {
      console.error('[SalaryLedger] Full fetch exception:', err);
    }
  }, [useSupabaseDB]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch paid invoice totals by project ──
  const fetchPaidIncomeByProject = useCallback(async () => {
    if (!useSupabaseDB || paidIncomeLoaded) return;
    try {
      const { data, error } = await supabase
        .from('v_invoices_reporting')
        .select('project_id, net_amount')
        .is('deleted_at', null)
        .eq('paid_confirmed', true);
      if (error) throw error;
      const map = {};
      (data || []).forEach(r => {
        const pid = r.project_id;
        map[pid] = (map[pid] || 0) + (r.net_amount || 0);
      });
      setPaidIncomeByProject(map);
      setPaidIncomeLoaded(true);
    } catch (err) {
      console.error('[PaidIncome] Fetch exception:', err);
    }
  }, [useSupabaseDB, paidIncomeLoaded]);

  // HARDENED: In Supabase-only mode, we don't persist business data to localStorage
  // Supabase is the only source of truth - localStorage is NOT used as persistence
  // REMOVED: Identity persistence to localStorage - mappedUser from Supabase is authoritative
  // useEffect(() => { safeSetItem('tt_user', JSON.stringify(currentUser)); }, [currentUser]);
  
  // ========================================================================
  // SUPABASE CONNECTION TEST & INITIALIZATION
  // ========================================================================
  useEffect(() => {
    const checkConnection = async () => {
      try {
        const connected = await testSupabaseConnection();
        setSupabaseReady(connected);
        if (connected) {
          console.log('✅ Supabase is ready! Toggle available in header.');
        } else {
          console.log('⚠️  Supabase connection failed. Check .env file.');
        }
      } catch (error) {
        console.error('Supabase connection error:', error);
        setSupabaseReady(false);
      }
    };
    checkConnection();
  }, []);

  // ========================================================================
  // SUPABASE HOOKS - Fetch data from Supabase when enabled
  // ========================================================================
  const supabaseUsers = useSupabaseUsers();
  const supabaseClients = useSupabaseClients();
  const supabaseProjects = useSupabaseProjects();
  const supabaseTasks = useSupabaseTasks();
  const supabaseTimeEntries = useSupabaseTimeEntries({ initialScopeUserId: currentUser?.id ?? null });
  const supabaseExpenses = useSupabaseExpenses();
  const supabaseHolidays = useSupabaseHolidays();
  const supabaseOfficeOverheads = useSupabaseOfficeOverheads({ enabled: mainCategory === 'accounting' || mainCategory === 'reports' || mainCategory === 'analytics' });
  const supabaseOfficeOverheadsMonthly = useSupabaseOfficeOverheadsMonthly({ enabled: mainCategory === 'accounting' || mainCategory === 'reports' || (mainCategory === 'team' && subPage === 'hybrid-focus-board') });
  const supabaseSalaryMatrix = useSupabaseSalaryMatrix();
  const supabaseSalaryMatrixNL = useSupabaseSalaryMatrixNL();
  const supabaseCalendarEvents = useSupabaseCalendarEvents({ enabled: subPage === 'calendar' || subPage === 'ms-calendar' });
  const supabaseAllocations = useSupabaseAllocations();
  const supabaseTeams = useSupabaseTeams();
  const supabaseTeamProjects = useSupabaseTeamProjects();
  const supabaseTimeOff = useSupabaseTimeOff();
  const supabaseInquiries = useSupabaseInquiries({ enabled: mainCategory === 'projects' && (subPage === 'inquiries' || subPage === 'contracts-scope') });
  // Lightweight count of email-intake messages awaiting admin review — powers
  // the "needs review" badge on the Inquiries nav item. Admin-only (Inquiries
  // is an admin page); countOnly avoids pulling the full intake list.
  const supabaseInquiryIntakeCount = useSupabaseInquiryIntake({ enabled: isAdmin, countOnly: true });

  // Cached per-project loaded cost — read by Inquiries Fee Intelligence,
  // written by Project Profitability + the manual Refresh button. Enable
  // on every view that consumes or writes it.
  const supabaseProjectLoadedCosts = useSupabaseProjectLoadedCosts({
    enabled: (mainCategory === 'projects' && subPage === 'inquiries')
          || mainCategory === 'accounting'
          || mainCategory === 'reports',
  });

  // Hiring candidate & interview management hooks
  const supabaseHiring = useSupabaseHiring({ enabled: mainCategory === 'team' && subPage === 'hiring' });
  const localHiring = useLocalHiring();
  const hiringHook = useSupabaseDB ? supabaseHiring : localHiring;

  // Reimbursement tracking hook
  const supabaseReimbursements = useSupabaseReimbursements({ enabled: mainCategory === 'accounting' && (subPage || '').startsWith('expense') });

  // Vendors / Contacts Directory
  const supabaseVendors = useSupabaseVendors({ enabled: mainCategory === 'projects' && subPage === 'contacts-directory' });

  // Stage status & entries hooks (partial invoicing / closure)
  const supabaseStageStatus = useProjectStageStatus();
  const supabaseStageEntries = useProjectStageEntries(supabaseStageStatus);

  // Invoice management hook
  const supabaseInvoices = useSupabaseInvoices();

  // Fee revision management hook
  const supabaseFeeRevisions = useSupabaseFeeRevisions();

  // ========================================================================
  // CENTRAL SETTINGS HOOK - Timezone, Currency, FX, Categories, Theme
  // ========================================================================
  const settingsHook = useSettings();

  // ── Contacts Directory access grants ────────────────────────────────────
  // Admins grant per-user access to the Contacts page via its "Give access"
  // control. Grants persist in app_settings under key 'contactsAccess' as a
  // map of { [userId]: 'read' | 'full' | 'none' }. Access levels:
  //   full → view + edit (same capability as a supervisor)
  //   read → view / expand / filter only, no edits (plus screenshot deterrents)
  //   none → no access
  // Admins/admin-pa always keep full access and cannot be downgraded here.
  // Supervisors keep full access unless explicitly set to 'read'.
  const contactsAccessMap = React.useMemo(() => {
    const raw = settingsHook.appSettings?.contactsAccess;
    return (raw && typeof raw === 'object' && !Array.isArray(raw)) ? raw : {};
  }, [settingsHook.appSettings]);

  const getContactsAccessLevel = React.useCallback((user) => {
    if (!user) return 'none';
    // Inactive users have no access, regardless of role or explicit grant.
    if (user.isActive === false || user.status === 'inactive') return 'none';
    if (user.role === 'admin' || user.role === 'admin-pa') return 'full';
    const granted = contactsAccessMap[String(user.id)];
    // An explicit grant (including 'none') overrides the role default, so an
    // admin can both extend access to a user and revoke it from a supervisor.
    if (granted === 'read' || granted === 'full' || granted === 'none') return granted;
    if (user.role === 'supervisor') return 'full';
    return 'none';
  }, [contactsAccessMap]);
  const contractScopesHook = useContractScopes(settingsHook);
  const scopeTemplatesHook = useScopeTemplates(settingsHook);

  // Sync Excel export styles into the module-level cache used by export utilities
  useEffect(() => {
    setExcelStyles(settingsHook.excelExportStyles);
  }, [settingsHook.excelExportStyles]);

  // ========================================================================
  // RADAR SETTINGS HOOK - Rule thresholds, item status tracking
  // ========================================================================
  const radarSettingsHook = useRadarSettings();

  // ========================================================================
  // SETTINGS-AWARE OVERRIDES - Read from Supabase settings, fall back to defaults
  // These override the _DEFAULT constants defined earlier in the component.
  // ========================================================================
  const BRIO_PROJECT_TYPES = (settingsHook.activeCategoryLabels && settingsHook.activeCategoryLabels.length > 0)
    ? settingsHook.activeCategoryLabels
    : BRIO_PROJECT_TYPES_DEFAULT;

  const PROJECT_STATUS_COLORS = (settingsHook.themeConfig && settingsHook.themeConfig.projectStatus)
    ? settingsHook.themeConfig.projectStatus
    : PROJECT_STATUS_COLORS_DEFAULT;

  const FALLBACK_PALETTES = (settingsHook.themeConfig && settingsHook.themeConfig.categoryPalettes && Object.keys(settingsHook.themeConfig.categoryPalettes).length > 0)
    ? settingsHook.themeConfig.categoryPalettes
    : FALLBACK_PALETTES_DEFAULT;

  // Use either Supabase or localStorage data based on mode
  const activeUsersRaw = useSupabaseDB ? supabaseUsers.users : users;
  const activeClients = useSupabaseDB ? supabaseClients.clients : clients;
  const activeProjects = useSupabaseDB ? supabaseProjects.projects : projects;
  const activeTasks = useSupabaseDB ? supabaseTasks.tasks : tasks;
  const activeTimeEntries = useSupabaseDB ? supabaseTimeEntries.timeEntries : timeEntries;
  const activeExpenses = useSupabaseDB ? supabaseExpenses.expenses : projectExpenses;
  const activeHolidays = supabaseHolidays.holidays;
  // Holidays filtered for the current user's office (NULL office = all offices)
  const userHolidays = activeHolidays.filter(
    h => !h.office || h.office === currentUser?.officeLocation
  );
  const activeOfficeOverheads = useSupabaseDB ? supabaseOfficeOverheads.overheads : officeOverheads;
  const activeSalaryMatrixHistory = useSupabaseDB ? supabaseSalaryMatrix.history : salaryMatrixHistory;
  const activeCalendarEvents = useSupabaseDB ? supabaseCalendarEvents.calendarEvents : calendarEvents;
  const activeAllocations = useSupabaseDB
    ? (supabaseAllocations.initialLoading ? allocations : supabaseAllocations.allocations)
    : allocations;

  // Whether the current (non-manager) user can access ANY project's timeline —
  // i.e. has effective access somewhere: an explicit Give Access grant (read/full),
  // or is assigned to a project via the Team Allocation list (default read),
  // minus any explicit 'No access'. Managers (admin/supervisor) don't need this.
  // Drives the Timelines nav item + page gate; TimelinesPage then scopes the
  // visible projects to exactly this accessible set.
  const hasAnyTimelineAccess = React.useMemo(() => {
    if (!currentUser || isAdmin || currentUser.role === 'supervisor') return false;
    const uid = Number(currentUser.id);
    const assignedProjectIds = new Set();
    (activeAllocations || []).forEach(a => {
      if (Number(a.userId) === uid && (a.allocatedHours || 0) > 0) assignedProjectIds.add(Number(a.projectId));
    });
    return (activeProjects || []).some(p => {
      const g = p.stageTimeline && p.stageTimeline._grantedAccess;
      let explicit = null;
      if (Array.isArray(g)) explicit = g.map(Number).includes(uid) ? 'full' : null;
      else if (g && typeof g === 'object') { const v = g[String(uid)]; explicit = (v === 'read' || v === 'full' || v === 'none') ? v : null; }
      const level = explicit || (assignedProjectIds.has(Number(p.id)) ? 'read' : 'none');
      return level !== 'none';
    });
  }, [currentUser, isAdmin, activeProjects, activeAllocations]);
  const activeTeams = useSupabaseDB ? supabaseTeams.teams : teams;
  const activeTimeOff = useSupabaseDB ? supabaseTimeOff.timeOffRequests : timeOff;
  const timesheetDataLoading = useSupabaseDB && (
    supabaseUsers.loading ||
    supabaseProjects.loading ||
    supabaseTimeEntries.loading ||
    supabaseHolidays.loading ||
    supabaseTeams.loading ||
    supabaseTimeOff.loading
  );

  // ── Public holiday hours per office per month ─────────────────────────────
  // Used by getEffectiveContractedHours to deduct non-working days from the
  // contracted-hours denominator in cost/overhead calculations.
  // { 'NL': { 'YYYY-MM': 8 }, 'IN': { 'YYYY-MM': 16 }, ... }
  const holidayHoursByOfficeMonth = useMemo(() => {
    const map = {};
    activeHolidays.forEach(h => {
      if (!h.date) return;
      const ym = h.date.substring(0, 7);
      // h.office null means applies to all offices
      const offices = h.office ? [h.office] : ['NL', 'IN'];
      offices.forEach(o => {
        if (!map[o]) map[o] = {};
        map[o][ym] = (map[o][ym] || 0) + 8;
      });
    });
    return map;
  }, [activeHolidays]);

  // ── Approved leave hours per user per month ───────────────────────────────
  // Counts 8h for each weekday within an approved leave period, split by month.
  // { userId: { 'YYYY-MM': 40 }, ... }
  const approvedLeaveHoursByUserMonth = useMemo(() => {
    const map = {};
    activeTimeOff
      .filter(t => t.status === 'approved' && t.startDate && t.endDate)
      .forEach(t => {
        const uid = t.userId;
        const cur = new Date(t.startDate + 'T00:00:00');
        const end = new Date(t.endDate + 'T00:00:00');
        while (cur <= end) {
          const dow = cur.getDay();
          if (dow !== 0 && dow !== 6) { // weekdays only
            const ym = cur.toISOString().substring(0, 7);
            if (!map[uid]) map[uid] = {};
            map[uid][ym] = (map[uid][ym] || 0) + 8;
          }
          cur.setDate(cur.getDate() + 1);
        }
      });
    return map;
  }, [activeTimeOff]);

  // ========================================================================
  // USER PROFILES ENRICHMENT
  // Merge dob & leavingDate from user_profiles into activeUsers for calendar UI.
  // The users table doesn't have these fields — they live in user_profiles.
  // ========================================================================
  const [userProfilesMap, setUserProfilesMap] = React.useState({});
  React.useEffect(() => {
    if (!useSupabaseDB) return;
    (async () => {
      try {
        const { data } = await supabase
          .from('user_profiles')
          .select('user_id, dob, leaving_date');
        const map = {};
        (data || []).forEach(p => {
          map[p.user_id] = {
            dob: p.dob || null,
            leavingDate: p.leaving_date || null,
          };
        });
        setUserProfilesMap(map);
      } catch (_) { /* non-critical */ }
    })();
  }, [useSupabaseDB, activeUsersRaw.length]); // Re-fetch when users change

  // Enriched activeUsers: merge dob + leavingDate from user_profiles
  const activeUsers = React.useMemo(() => {
    if (Object.keys(userProfilesMap).length === 0) return activeUsersRaw;
    return activeUsersRaw.map(u => ({
      ...u,
      dob: u.dob || userProfilesMap[u.id]?.dob || null,
      leavingDate: u.leavingDate || userProfilesMap[u.id]?.leavingDate || null,
    }));
  }, [activeUsersRaw, userProfilesMap]);

  // ========================================================================
  // HFB ALLOCATIONS - For Team Members page allocation ring
  // Uses same ISO week format as HybridFocusBoard (YYYY-Www)
  // ========================================================================
  const currentHFBWeekId = (() => {
    const today = new Date();
    const d = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const weekNum = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    return d.getUTCFullYear() + '-W' + String(weekNum).padStart(2, '0');
  })();

  // ========================================================================
  // HELPER: Convert date string (YYYY-MM-DD) to ISO week ID (YYYY-Www)
  // Used for looking up HFB team assignments for a specific date
  // Robust ISO week calculation (Monday-based, handles year boundaries)
  // ========================================================================
  const getWeekIdFromDateStr = useCallback((dateStr) => {
    if (!dateStr) return currentHFBWeekId;
    try {
      const parts = dateStr.split('-');
      const d = new Date(Date.UTC(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2])));
      const dayNum = d.getUTCDay() || 7;
      d.setUTCDate(d.getUTCDate() + 4 - dayNum);
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      const weekNum = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
      return d.getUTCFullYear() + '-W' + String(weekNum).padStart(2, '0');
    } catch (e) {
      return currentHFBWeekId;
    }
  }, [currentHFBWeekId]);
  
  // ========================================================================
  // ANTI-REGRESSION GUARDRAIL: getEffectiveTeamIdForRequest
  // ========================================================================
  // Returns the effective team ID for a time-off request based on HFB assignments.
  // Returns null if no team assignment found for the request's week (unroutable).
  // This is used for diagnostics and soft warnings - does NOT affect routing.
  // ========================================================================
  const getEffectiveTeamIdForRequest = useCallback((userId, startDate, hfbAssignments, users) => {
    if (!userId || !startDate) return { teamId: null, weekId: null, source: 'invalid_input' };
    
    const weekId = getWeekIdFromDateStr(startDate);
    const normalizedUserId = String(userId);
    
    // PRIMARY: Check HFB weekly_team_assignments for the request's week
    if (hfbAssignments && hfbAssignments.length > 0) {
      const assignment = hfbAssignments.find(a => 
        String(a.userId) === normalizedUserId && 
        a.weekId === weekId
      );
      
      if (assignment && assignment.teamId) {
        return { 
          teamId: String(assignment.teamId), 
          weekId, 
          source: 'hfb_assignment' 
        };
      }
    }
    
    // SECONDARY (diagnostic only - NOT used for routing): Check user.teamId
    const user = users?.find(u => String(u.id) === normalizedUserId);
    if (user && user.teamId) {
      return { 
        teamId: String(user.teamId), 
        weekId, 
        source: 'user_teamId_legacy' 
      };
    }
    
    // No team found - request is unroutable
    return { teamId: null, weekId, source: 'none' };
  }, [getWeekIdFromDateStr]);
  
  // Fetch HFB allocations for current week (percentage-based, NOT hours-based)
  const { allocations: hfbAllocations } = useHFBProjectAllocations(currentHFBWeekId);
  
  // Fetch HFB team assignments for current week (for supervisor visibility)
  const { assignments: hfbTeamAssignments } = useHFBTeamAssignments(currentHFBWeekId);

  // ── Weekly timesheet: compute week start for addTime modal's allocation lookup ──
  const timesheetWeekStart = useMemo(() => {
    if (modal !== 'addTime') return null;
    const ws = form.weekStart ? new Date(form.weekStart) : (() => {
      const today = new Date();
      const day = today.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      const monday = new Date(today);
      monday.setDate(today.getDate() + diff);
      return monday;
    })();
    const y = ws.getFullYear();
    const m = String(ws.getMonth() + 1).padStart(2, '0');
    const d = String(ws.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, [modal, form.weekStart]);

  const timesheetAllocationsHook = useHFBProjectAllocationsForDonut(
    useSupabaseDB ? timesheetWeekStart : null
  );

  // Fetch all project IDs the current user has ever logged time on.
  // Used by the addTime modal to include previously-worked-on projects.
  const { projectIds: userHistoricalProjectIds } = useUserProjectHistory(
    useSupabaseDB ? currentUser?.id : null
  );

  // ========================================================================
  // HFB MULTI-WEEK ASSIGNMENTS - For supervisor visibility of time-off requests
  // Collects all unique weekIds from pending requests and fetches assignments
  // ========================================================================
  const pendingRequestWeekIds = useMemo(() => {
    if (!useSupabaseDB) return [];
    
    // Get all pending time-off requests that supervisors might need to see
    const pendingRequests = activeTimeOff.filter(to => 
      to.status === 'pending' || to.status === 'pending_supervisor' || to.status === 'pending_admin'
    );
    
    // Collect unique weekIds from these requests
    const weekIdSet = new Set([currentHFBWeekId]); // Always include current week
    pendingRequests.forEach(req => {
      if (req.startDate) {
        weekIdSet.add(getWeekIdFromDateStr(req.startDate));
      }
    });
    
    return Array.from(weekIdSet);
  }, [useSupabaseDB, activeTimeOff, currentHFBWeekId, getWeekIdFromDateStr]);

  // Fetch team assignments for all relevant weeks
  const { assignments: hfbMultiWeekAssignments } = useHFBTeamAssignmentsForWeeks(pendingRequestWeekIds);

  // ========================================================================
  // ANTI-REGRESSION GUARDRAIL: checkRequestRoutability
  // ========================================================================
  // Checks if a time-off request can be routed to a supervisor.
  // Returns routing info and whether to show a warning.
  // NOTE: Must be defined AFTER hfbMultiWeekAssignments to avoid temporal dead zone.
  // ========================================================================
  const checkRequestRoutability = useCallback((userId, startDate) => {
    const result = getEffectiveTeamIdForRequest(
      userId, 
      startDate, 
      hfbMultiWeekAssignments, 
      activeUsers
    );
    
    const isUnroutable = result.teamId === null || result.source === 'none';
    const isLegacyRouting = result.source === 'user_teamId_legacy';
    
    return {
      ...result,
      isUnroutable,
      isLegacyRouting,
      warningMessage: isUnroutable 
        ? `Warning: No team assignment found for week ${result.weekId}. This request may not appear to any supervisor until the user is assigned in HFB.`
        : isLegacyRouting
        ? `Note: Using legacy team assignment (user.teamId) for week ${result.weekId}. Consider updating HFB assignments.`
        : null
    };
  }, [getEffectiveTeamIdForRequest, hfbMultiWeekAssignments, activeUsers]);

  // Log mode changes
  useEffect(() => {
    if (useSupabaseDB) {
      console.log('🌐 Switched to Supabase mode');
    } else {
      console.log('💾 Switched to localStorage mode');
    }
  }, [useSupabaseDB]);
  
  // Sync salary matrix from Supabase when in Supabase mode
  // Uses the revision matching the current date (not just the latest)
  useEffect(() => {
    if (useSupabaseDB && supabaseSalaryMatrix.history.length > 0) {
      setSalaryMatrixHistory(supabaseSalaryMatrix.history);
      // Find the revision whose date range covers today
      const today = new Date().toISOString().split('T')[0];
      const currentPeriodMatrix = supabaseSalaryMatrix.history.find(
        h => h.effectiveDate <= today && (!h.endDate || h.endDate >= today)
      );
      const matrixToUse = currentPeriodMatrix?.matrix || supabaseSalaryMatrix.history[0]?.matrix;
      if (matrixToUse) {
        setSalaryMatrix(matrixToUse);
        console.log('✅ Salary matrix synced from Supabase' + (currentPeriodMatrix ? ` (current period: ${currentPeriodMatrix.effectiveDate})` : ' (latest fallback)'));
      }
    }
  }, [useSupabaseDB, supabaseSalaryMatrix.history]);

  // Sync NL salary matrix history from Supabase
  useEffect(() => {
    if (useSupabaseDB && supabaseSalaryMatrixNL.history.length > 0) {
      setSalaryMatrixHistoryNL(supabaseSalaryMatrixNL.history);
    }
  }, [useSupabaseDB, supabaseSalaryMatrixNL.history]);


  // salary_settings table DEPRECATED — markup now lives in app_settings.manualSalaryMarkup
  // Read via settingsHook.manualSalaryMarkup (year→numeric map).

  // ========================================================================
  // VIEW DATA FETCHING - Analytics & Reports (RLS-gated via views)
  // ========================================================================
  // These useEffects fetch data from dedicated Supabase views when the user
  // navigates to Analytics or Reports pages. Views have their own RLS policies
  // that can block specific roles (e.g., Admin-PA) without affecting operational pages.
  // ========================================================================

  // Fetch Analytics Dashboard view data when navigating to analytics
  useEffect(() => {
    const fetchAnalyticsView = async () => {
      // Only fetch when in Supabase mode, on analytics view, and supabase is ready
      if (!useSupabaseDB || view !== 'analytics' || !supabaseReady) {
        return;
      }
      
      console.log('[VIEWS][ANALYTICS] Starting fetch for analytics view...');
      setAnalyticsViewLoading(true);
      setAnalyticsViewError(null);
      
      try {
        const { data, error } = await supabaseViews.fetchAnalyticsDashboardView();
        
        if (error) {
          console.error('[VIEWS][ANALYTICS] Fetch error:', error);
          setAnalyticsViewError(error);
          setAnalyticsViewRows([]);
        } else {
          setAnalyticsViewRows(data || []);
          setAnalyticsViewError(null);
        }
      } catch (err) {
        console.error('[VIEWS][ANALYTICS] Exception during fetch:', err);
        setAnalyticsViewError(err);
        setAnalyticsViewRows([]);
      } finally {
        setAnalyticsViewLoading(false);
      }
    };
    
    fetchAnalyticsView();
  }, [useSupabaseDB, view, supabaseReady]);

  // Fetch Profitability Report view data when navigating to reports (profitability tab)
  useEffect(() => {
    const fetchProfitView = async () => {
      // Only fetch when in Supabase mode, on dashboard view (reports), and profitability tab is active
      const isProfitabilityTab = (form.reportTab || 'projectProfitability') === 'profitability';
      if (!useSupabaseDB || view !== 'dashboard' || !isProfitabilityTab || !supabaseReady) {
        return;
      }
      
      console.log('[VIEWS][PROFIT] Starting fetch for profitability view...');
      setProfitViewLoading(true);
      setProfitViewError(null);
      
      try {
        const { data, error } = await supabaseViews.fetchProfitabilityReportView();
        
        if (error) {
          console.error('[VIEWS][PROFIT] Fetch error:', error);
          setProfitViewError(error);
          setProfitViewRows([]);
        } else {
          setProfitViewRows(data || []);
          setProfitViewError(null);
        }
      } catch (err) {
        console.error('[VIEWS][PROFIT] Exception during fetch:', err);
        setProfitViewError(err);
        setProfitViewRows([]);
      } finally {
        setProfitViewLoading(false);
      }
    };
    
    fetchProfitView();
  }, [useSupabaseDB, view, form.reportTab, supabaseReady]);

  // Fetch full salary ledger when reports tabs or analytics view needs it.
  // The Reports section defaults to 'projectProfitability' when no tab is explicitly selected,
  // so we resolve reportTab to that default before checking.
  useEffect(() => {
    const resolvedTab = form.reportTab || 'projectProfitability';
    const needsLedger = view === 'analytics'
      || (view === 'dashboard' && (resolvedTab === 'profitability'
        || resolvedTab === 'projectProfitability'
        || resolvedTab === 'projectCostLedger'));
    // (Inquiries no longer needs the salary ledger to render — it reads
    // per-project loaded cost from the project_loaded_costs cache. Only
    // a manual "Refresh cost data" click loads the ledger on demand.)
    if (useSupabaseDB && needsLedger && supabaseReady && !fullLedgerLoaded) {
      fetchFullSalaryLedger();
    }
  }, [useSupabaseDB, view, form.reportTab, supabaseReady, fullLedgerLoaded, fetchFullSalaryLedger]);

  // Fetch ALL time entries when cost ledger, profitability, analytics, or timesheet overview needs them.
  // Profitability uses the same data set as cost ledger, so it must trigger the same fetch eagerly —
  // otherwise the page renders empty rows until the page-level effect catches up.
  useEffect(() => {
    const resolvedTab = form.reportTab || 'projectProfitability';
    const needsAllEntries = (view === 'dashboard' && (resolvedTab === 'projectCostLedger' || resolvedTab === 'projectProfitability'))
      || view === 'analytics'
      || view === 'timesheet-overview';
    // (Inquiries reads from the project_loaded_costs cache and only
    // loads time entries when the user explicitly clicks "Refresh
    // cost data" — see refreshProjectLoadedCosts.)
    if (useSupabaseDB && needsAllEntries
        && supabaseReady && !supabaseTimeEntries.allLoaded) {
      supabaseTimeEntries.fetchAllTimeEntries();
    }
  }, [useSupabaseDB, view, form.reportTab, supabaseReady, supabaseTimeEntries.allLoaded, supabaseTimeEntries.fetchAllTimeEntries]);

  // When the donut tracker opens, ensure the current user's own entries for the last
  // 60 days are present in state. The global paginated fetch (limit 200) covers the
  // whole team ordered by date and may not reach a user's entries if they haven't
  // logged recently — causing those dates to appear as pending in the donut.
  useEffect(() => {
    if (useSupabaseDB && view === 'donut-tracker' && currentUser?.id && supabaseReady) {
      supabaseTimeEntries.fetchEntriesForUser(currentUser.id, 60);
    }
  }, [useSupabaseDB, view, currentUser?.id, supabaseReady, supabaseTimeEntries.fetchEntriesForUser]);

  // Scope time entry fetches to the current user for all roles.
  // Every user — including admin and supervisor — sees only their own entries on the time log.
  // The timesheet overview page exists for cross-user review.
  useEffect(() => {
    if (!useSupabaseDB || !currentUser?.id) return;
    supabaseTimeEntries.initializeScope(currentUser.id);
  }, [useSupabaseDB, currentUser?.id, supabaseTimeEntries.initializeScope]);

  // When navigating back to the tracker view from admin views (which load global data via
  // fetchAllTimeEntries), explicitly refetch scoped data. initializeScope is idempotent
  // and won't trigger a refetch when the scope is already correct, so refetchScoped is
  // used here to reset allLoaded and replace state with only the current user's entries.
  useEffect(() => {
    if (!useSupabaseDB || !supabaseReady || !currentUser?.id) return;
    if (view === 'tracker') {
      supabaseTimeEntries.refetchScoped();
    }
  }, [view, useSupabaseDB, supabaseReady, currentUser?.id, supabaseTimeEntries.refetchScoped]);

  // Fetch per-user last entry date summary when Radar opens (avoids full table scan in frontend)
  useEffect(() => {
    if (useSupabaseDB && view === 'radar' && supabaseReady) {
      supabaseTimeEntries.fetchUserLastEntryDates();
      supabaseTimeEntries.fetchUserMonthHours();
    }
  }, [useSupabaseDB, view, supabaseReady, supabaseTimeEntries.fetchUserLastEntryDates, supabaseTimeEntries.fetchUserMonthHours]);

  // Fetch paid invoice income when profitability tab or analytics view needs it
  useEffect(() => {
    const needsIncome = view === 'analytics'
      || view === 'home'
      || (view === 'dashboard' && (form.reportTab || 'projectProfitability') === 'profitability');
    if (useSupabaseDB && needsIncome && supabaseReady && !paidIncomeLoaded) {
      fetchPaidIncomeByProject();
    }
  }, [useSupabaseDB, view, form.reportTab, supabaseReady, paidIncomeLoaded, fetchPaidIncomeByProject]);

  // Fetch Salary Cost Report view data when navigating to reports (salaryCost tab)
  useEffect(() => {
    const fetchSalaryView = async () => {
      // Only fetch when in Supabase mode, on dashboard view (reports), and salary cost tab is active
      if (!useSupabaseDB || view !== 'dashboard' || form.reportTab !== 'salaryCost' || !supabaseReady) {
        return;
      }
      
      // Optional: filter by selected year
      const params = {};
      if (form.salaryReportYear) {
        params.year = form.salaryReportYear;
      }
      
      console.log('[VIEWS][SALARY] Starting fetch for salary cost view with params:', params);
      setSalaryReportViewLoading(true);
      setSalaryReportViewError(null);
      
      try {
        const { data, error } = await supabaseViews.fetchSalaryCostReportView(params);
        
        if (error) {
          console.error('[VIEWS][SALARY] Fetch error:', error);
          setSalaryReportViewError(error);
          setSalaryReportViewRows([]);
        } else {
          setSalaryReportViewRows(data || []);
          setSalaryReportViewError(null);
        }
      } catch (err) {
        console.error('[VIEWS][SALARY] Exception during fetch:', err);
        setSalaryReportViewError(err);
        setSalaryReportViewRows([]);
      } finally {
        setSalaryReportViewLoading(false);
      }
    };
    
    fetchSalaryView();
  }, [useSupabaseDB, view, form.reportTab, form.salaryReportYear, supabaseReady]);

  // ========================================================================
  // SUPABASE LEAVE POLICY - Fetch from Supabase when mode enabled
  // ========================================================================
  
  // Fetch leave policy data from Supabase
  const fetchLeavePolicyFromSupabase = async () => {
    if (!useSupabaseDB) return;
    
    try {
      console.log('[LeavePolicy] Fetching from Supabase...');
      
      // Fetch leave policies (single row with JSONB policy array)
      const { data: policyData, error: policyError } = await supabase
        .from('leave_policies')
        .select('*')
        .limit(1)
        .maybeSingle();
      
      if (policyError) {
        console.error('[LeavePolicy] Error fetching policy:', policyError);
      }

      // Fetch employee entitlements
      const { data: entitlementData, error: entitlementError } = await supabase
        .from('employee_leave_entitlements')
        .select('*');
      
      if (entitlementError) {
        console.error('[LeavePolicy] Error fetching entitlements:', entitlementError);
      }

      // Fetch audit log (ordered by created_at desc, limit 200)
      const { data: auditData, error: auditError } = await supabase
        .from('leave_audit_log')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(200);
      
      if (auditError) {
        console.error('[LeavePolicy] Error fetching audit log:', auditError);
      }

      // Update state with fetched data
      if (policyData?.policy && Array.isArray(policyData.policy)) {
        const normalizedPolicy = policyData.policy.map(normalizeLeavePolicyNumericFields);
        setLeavePolicyMatrix(normalizedPolicy);
      }

      if (entitlementData && Array.isArray(entitlementData)) {
        // Map from snake_case (DB) to camelCase (App)
        // NOTE: DB stores: user_id, year, manual_override, notice_override
        // Other fields (baseLeave, calculatedTotalLeave, etc.) are computed client-side
        const mappedEntitlements = entitlementData.map(e => ({
          userId: e.user_id,
          year: e.year,
          manualOverride: e.manual_override,
          noticeOverride: e.notice_override ?? null
        }));
        setEmployeeLeaveEntitlement(mappedEntitlements);
        console.log('[LeavePolicy] Loaded entitlements from Supabase:', mappedEntitlements.length);
      }

      if (auditData && Array.isArray(auditData)) {
        // Map from snake_case (DB) to camelCase (App)
        // DB has: id, action, actor_user_id, target_user_id, payload, created_at
        const mappedAuditLog = auditData.map(a => ({
          id: a.id,
          timestamp: a.created_at,
          action: a.action,
          performedBy: a.actor_user_id,
          userId: a.target_user_id,
          // Extract additional fields from payload if present
          userName: a.payload?.userName,
          roleLevel: a.payload?.roleLevel,
          field: a.payload?.field,
          year: a.payload?.year,
          oldValue: a.payload?.oldValue,
          newValue: a.payload?.newValue,
          details: a.payload?.details,
          payload: a.payload
        }));
        setLeaveAuditLog(mappedAuditLog);
      }

      console.log('[LeavePolicy] Fetched from Supabase', { 
        hasPolicyRow: !!policyData, 
        entitlementsCount: entitlementData?.length || 0, 
        auditCount: auditData?.length || 0 
      });
      
      setLeavePolicySupabaseLoaded(true);
    } catch (err) {
      console.error('[LeavePolicy] Unexpected error fetching from Supabase:', err);
    }
  };

  // Save leave policy matrix to Supabase
  const saveLeavePolicyToSupabase = async (policyMatrix, beforePolicy) => {
    if (!useSupabaseDB) return { success: false };
    
    try {
      // Check if a policy row exists
      const { data: existing } = await supabase
        .from('leave_policies')
        .select('id')
        .limit(1)
        .maybeSingle();

      let result;
      if (existing) {
        // Update existing row
        result = await supabase
          .from('leave_policies')
          .update({ 
            policy: policyMatrix, 
            updated_by: currentUser.id,
            updated_at: new Date().toISOString() 
          })
          .eq('id', existing.id);
      } else {
        // Insert new row
        result = await supabase
          .from('leave_policies')
          .insert([{ 
            policy: policyMatrix,
            updated_by: currentUser.id
          }]);
      }

      if (result.error) {
        console.error('[LeavePolicy] Error saving policy:', result.error);
        return { success: false, error: result.error };
      }

      // Insert audit log entry - with safeguard for missing columns
      const auditEntry = {
        action: 'POLICY_UPDATED',
        actor_user_id: currentUser.id,
        payload: { before: beforePolicy, after: policyMatrix }
      };
      
      console.log('[LeavePolicy] Attempting audit insert with payload keys:', Object.keys(auditEntry));

      const { error: auditError } = await supabase
        .from('leave_audit_log')
        .insert([auditEntry]);

      if (auditError) {
        // Check for PGRST204 (missing column) error
        if (auditError.code === 'PGRST204' || auditError.message?.includes('Could not find')) {
          console.warn('[LeavePolicy] leave_audit_log schema mismatch — skipping audit insert. Error:', auditError.message);
        } else {
          console.warn('[LeavePolicy] Failed to insert audit log:', auditError);
        }
        // Don't fail the whole operation just because audit failed
      }

      console.log('[LeavePolicy] Saved to Supabase', { policySaved: true });
      return { success: true };
    } catch (err) {
      console.error('[LeavePolicy] Unexpected error saving policy:', err);
      return { success: false, error: err };
    }
  };

  // ========================================================================
  // NOTICE POLICY SUPABASE FUNCTIONS (mirrors saveLeavePolicyToSupabase)
  // ========================================================================

  // Fetch notice policy from Supabase (single row with JSONB policy array)
  const fetchNoticePolicyFromSupabase = async () => {
    if (!useSupabaseDB) return;
    try {
      console.log('[NoticePolicy] Fetching from Supabase...');
      const { data: policyData, error: policyError } = await supabase
        .from('notice_policies')
        .select('*')
        .limit(1)
        .maybeSingle();

      if (policyError) {
        console.error('[NoticePolicy] Error fetching policy:', policyError);
      }

      if (policyData?.policy && Array.isArray(policyData.policy)) {
        const normalizedPolicy = policyData.policy.map(normalizeNoticePolicyNumericFields);
        setNoticePolicyMatrix(normalizedPolicy);
      }

      console.log('[NoticePolicy] Fetched from Supabase', { hasPolicyRow: !!policyData });
      setNoticePolicySupabaseLoaded(true);
    } catch (err) {
      console.error('[NoticePolicy] Unexpected error fetching from Supabase:', err);
    }
  };

  // Save notice policy matrix to Supabase (mirrors saveLeavePolicyToSupabase)
  const saveNoticePolicyToSupabase = async (policyMatrix, beforePolicy) => {
    if (!useSupabaseDB) return { success: false };
    try {
      // Check if a policy row exists
      const { data: existing } = await supabase
        .from('notice_policies')
        .select('id')
        .limit(1)
        .maybeSingle();

      let result;
      if (existing) {
        // Update existing row
        result = await supabase
          .from('notice_policies')
          .update({
            policy: policyMatrix,
            updated_by: currentUser.id,
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id);
      } else {
        // Insert new row
        result = await supabase
          .from('notice_policies')
          .insert([{
            policy: policyMatrix,
            updated_by: currentUser.id
          }]);
      }

      if (result.error) {
        console.error('[NoticePolicy] Error saving policy:', result.error);
        return { success: false, error: result.error };
      }

      // Insert audit log entry (reuse leave_audit_log table with NOTICE_POLICY_UPDATED action)
      const auditEntry = {
        action: 'NOTICE_POLICY_UPDATED',
        actor_user_id: currentUser.id,
        payload: { before: beforePolicy, after: policyMatrix }
      };

      console.log('[NoticePolicy] Attempting audit insert with payload keys:', Object.keys(auditEntry));

      const { error: auditError } = await supabase
        .from('leave_audit_log')
        .insert([auditEntry]);

      if (auditError) {
        if (auditError.code === 'PGRST204' || auditError.message?.includes('Could not find')) {
          console.warn('[NoticePolicy] leave_audit_log schema mismatch — skipping audit insert. Error:', auditError.message);
        } else {
          console.warn('[NoticePolicy] Failed to insert audit log:', auditError);
        }
      }

      console.log('[NoticePolicy] Saved to Supabase', { policySaved: true });
      return { success: true };
    } catch (err) {
      console.error('[NoticePolicy] Unexpected error saving policy:', err);
      return { success: false, error: err };
    }
  };


  // Save employee entitlement to Supabase
  // NOTE: DB table employee_leave_entitlements has: user_id, year, manual_override, notice_override
  // All other fields (base_leave, calculated_total_leave, etc.) are computed client-side
  const saveEntitlementToSupabase = async (entitlement, beforeEntitlement) => {
    if (!useSupabaseDB) return { success: false };
    
    try {
      // Only send columns that exist in the DB table
      // The table likely only has: user_id, year, manual_override (or annual_leave_entitlement)
      const dbData = {
        user_id: entitlement.userId,
        year: entitlement.year,
        manual_override: entitlement.manualOverride
      };
      
      console.log('[LeavePolicy] Attempting entitlement upsert with payload:', JSON.stringify(dbData));

      // Upsert by user_id + year
      const { error } = await supabase
        .from('employee_leave_entitlements')
        .upsert([dbData], { onConflict: 'user_id,year' });

      if (error) {
        // Log full error for debugging
        console.error('[LeavePolicy] Error saving entitlement:', error.code, error.message, error.details);
        return { success: false, error };
      }

      // Insert audit log entry - with safeguard for missing columns
      const auditEntry = {
        action: 'ENTITLEMENT_UPDATED',
        actor_user_id: currentUser.id,
        target_user_id: entitlement.userId,
        payload: { userId: entitlement.userId, year: entitlement.year, before: beforeEntitlement?.manualOverride, after: entitlement.manualOverride }
      };
      
      console.log('[LeavePolicy] Attempting entitlement audit insert with payload keys:', Object.keys(auditEntry));

      const { error: auditError } = await supabase
        .from('leave_audit_log')
        .insert([auditEntry]);

      if (auditError) {
        // Check for PGRST204 (missing column) error
        if (auditError.code === 'PGRST204' || auditError.message?.includes('Could not find')) {
          console.warn('[LeavePolicy] leave_audit_log schema mismatch — skipping audit insert. Error:', auditError.message);
        } else {
          console.warn('[LeavePolicy] Failed to insert audit log:', auditError);
        }
        // Don't fail the whole operation just because audit failed
      }

      console.log('[LeavePolicy] Entitlement saved to Supabase', { entitlementSaved: true, userId: entitlement.userId });
      return { success: true };
    } catch (err) {
      console.error('[LeavePolicy] Unexpected error saving entitlement:', err);
      return { success: false, error: err };
    }
  };

  // Batch save entitlements to Supabase (for recalculateAll)
  // NOTE: DB table 'employee_leave_entitlements' only has: user_id, year, manual_override
  // All other fields (base_leave, calculated_total_leave, etc.) are computed client-side
  const batchSaveEntitlementsToSupabase = async (entitlements, year) => {
    if (!useSupabaseDB) return { success: false };
    
    try {
      // Only save entitlements that have a manual override set
      // (the rest are calculated client-side and don't need DB persistence)
      const entitlementsWithOverride = entitlements.filter(e => e.manualOverride !== null && e.manualOverride !== undefined);
      
      if (entitlementsWithOverride.length === 0) {
        console.log('[LeavePolicy] No manual overrides to save to Supabase');
        return { success: true };
      }
      
      // Only send columns that exist in the DB table
      const dbData = entitlementsWithOverride.map(e => ({
        user_id: e.userId,
        year: e.year,
        manual_override: e.manualOverride
      }));
      
      console.log('[LeavePolicy] Attempting batch entitlement upsert with payload:', JSON.stringify(dbData[0] || {}), 'count:', dbData.length);

      const { error } = await supabase
        .from('employee_leave_entitlements')
        .upsert(dbData, { onConflict: 'user_id,year' });

      if (error) {
        // Log full error for debugging
        console.error('[LeavePolicy] Error batch saving entitlements:', error.code, error.message, error.details);
        return { success: false, error };
      }

      // Single audit entry for batch operation - with safeguard for missing columns
      const auditEntry = {
        action: 'RECALCULATE_ALL',
        actor_user_id: currentUser.id,
        payload: { year: year, count: entitlements.length, overridesSaved: dbData.length }
      };
      
      console.log('[LeavePolicy] Attempting batch audit insert with payload keys:', Object.keys(auditEntry));

      const { error: auditError } = await supabase
        .from('leave_audit_log')
        .insert([auditEntry]);

      if (auditError) {
        // Check for PGRST204 (missing column) error
        if (auditError.code === 'PGRST204' || auditError.message?.includes('Could not find')) {
          console.warn('[LeavePolicy] leave_audit_log schema mismatch — skipping audit insert. Error:', auditError.message);
        } else {
          console.warn('[LeavePolicy] Failed to insert batch audit log:', auditError);
        }
        // Don't fail the whole operation just because audit failed
      }

      console.log('[LeavePolicy] Batch entitlements saved to Supabase', { count: dbData.length });
      return { success: true };
    } catch (err) {
      console.error('[LeavePolicy] Unexpected error batch saving:', err);
      return { success: false, error: err };
    }
  };

  // Insert audit log entry to Supabase
  const insertAuditLogToSupabase = async (auditEntry) => {
    if (!useSupabaseDB) return { success: false };
    
    try {
      // Use only columns that should exist: action, actor_user_id, target_user_id, payload
      const dbEntry = {
        action: auditEntry.action,
        actor_user_id: auditEntry.performedBy,
        target_user_id: auditEntry.userId || null,
        payload: {
          userName: auditEntry.userName,
          roleLevel: auditEntry.roleLevel,
          field: auditEntry.field,
          year: auditEntry.year,
          oldValue: auditEntry.oldValue,
          newValue: auditEntry.newValue,
          details: auditEntry.details,
          ...(auditEntry.payload || {})
        }
      };
      
      console.log('[LeavePolicy] Attempting generic audit insert with payload keys:', Object.keys(dbEntry));

      const { error } = await supabase
        .from('leave_audit_log')
        .insert([dbEntry]);

      if (error) {
        // Check for PGRST204 (missing column) error
        if (error.code === 'PGRST204' || error.message?.includes('Could not find')) {
          console.warn('[LeavePolicy] leave_audit_log schema mismatch — skipping audit insert. Error:', error.message);
          return { success: false, error, schemaError: true };
        }
        console.error('[LeavePolicy] Error inserting audit log:', error);
        return { success: false, error };
      }
      return { success: true };
    } catch (err) {
      console.error('[LeavePolicy] Unexpected error inserting audit:', err);
      return { success: false, error: err };
    }
  };

  // Fetch leave policy data when Supabase mode is enabled
  useEffect(() => {
    if (useSupabaseDB && !leavePolicySupabaseLoaded) {
      fetchLeavePolicyFromSupabase();
    }
    // Reset loaded flag when switching back to localStorage mode
    if (!useSupabaseDB) {
      setLeavePolicySupabaseLoaded(false);
    }
  }, [useSupabaseDB]);

  // Fetch notice policy data when Supabase mode is enabled (mirrors leave policy)
  useEffect(() => {
    if (useSupabaseDB && !noticePolicySupabaseLoaded) {
      fetchNoticePolicyFromSupabase();
    }
    if (!useSupabaseDB) {
      setNoticePolicySupabaseLoaded(false);
    }
  }, [useSupabaseDB]);

  // Also refetch when navigating to leave policy pages (ensures fresh data)
  useEffect(() => {
    if (view === 'leave-policy') {
      // Reset dirty state when entering the leave policy page
      setLeavePolicyDirty(false);
      setLeavePolicySnapshot(null);
      // Reset notice policy dirty state too (same page)
      setNoticePolicyDirty(false);
      setNoticePolicySnapshot(null);
    }
    if (useSupabaseDB && (view === 'leave-policy' || view === 'leave-entitlement' || view === 'leave-summary')) {
      // Only refetch if already loaded (avoid double-fetch on initial load)
      if (leavePolicySupabaseLoaded) {
        fetchLeavePolicyFromSupabase();
      }
      if (noticePolicySupabaseLoaded) {
        fetchNoticePolicyFromSupabase();
      }
    }
  }, [view, useSupabaseDB]);
  
  // REMOVED: Legacy one-time sync on mount
  // Identity now comes ONLY from mappedUser (Supabase auth), not from users array or localStorage
  // useEffect(() => {
  //   if (mappedUser) return; // Skip if using Supabase auth
  //   const userInArray = activeUsers.find(u => u.id === currentUser.id);
  //   if (userInArray && needsSync) setCurrentUser({...userInArray});
  // }, []);
  
  // UI preferences can still be persisted locally
  useEffect(() => { safeSetItem('tt_project_status_filter', JSON.stringify(projectStatusFilter)); }, [projectStatusFilter]);
  
  // === NAVIGATION HEIGHT MEASUREMENT ===
  // Measure the fixed navigation container height BEFORE first paint and keep it updated.
  // Uses useLayoutEffect to avoid flash of incorrectly positioned content.
  // ResizeObserver handles dynamic height changes (e.g., font loading, responsive adjustments).
  useLayoutEffect(() => {
    const measure = () => {
      const height = navRef.current?.offsetHeight || 73;
      setNavHeight(height);
    };
    
    // Measure immediately (before paint)
    measure();
    
    // Set up ResizeObserver if available
    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined' && navRef.current) {
      resizeObserver = new ResizeObserver(() => {
        measure();
      });
      resizeObserver.observe(navRef.current);
    }
    
    // Also measure on window resize (fallback and additional coverage)
    const handleResize = () => measure();
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [mainCategory, subPage]); // Re-run when navigation structure changes

  // === MOBILE BREAKPOINT DETECTION ===
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close mobile sheets when subPage changes
  useEffect(() => {
    setMobileSubpageSheetOpen(false);
    setMobileMoreSheetOpen(false);
  }, [subPage, mainCategory]);

  // REMOVED: useEffect(() => { safeSetItem('tt_settings', JSON.stringify(settings)); }, [settings]);
  // Settings are now persisted in Supabase via settingsHook.
  
  // Time Off requests - persist to localStorage when NOT using Supabase
  useEffect(() => {
    if (useSupabaseDB) return; // Supabase mode uses DB, skip localStorage
    safeSetItem('tt_timeoff', JSON.stringify(timeOff));
  }, [timeOff, useSupabaseDB]);
  
  // Microsoft Calendar Integration localStorage
  useEffect(() => { safeSetItem('tt_ms_connected', JSON.stringify(msCalendarConnected)); }, [msCalendarConnected]);
  // MS access/refresh tokens are no longer persisted to localStorage (server-side
  // now). Purge any left over from the previous SPA implementation, once.
  useEffect(() => {
    try {
      localStorage.removeItem('tt_ms_token');
      localStorage.removeItem('tt_ms_refresh');
      localStorage.removeItem('tt_ms_expiry');
    } catch (_) { /* ignore */ }
  }, []);
  useEffect(() => { safeSetItem('tt_ms_events', JSON.stringify(msCalendarEvents)); }, [msCalendarEvents]);
  useEffect(() => { safeSetItem('tt_ms_synced_ids', JSON.stringify(msSyncedEventIds)); }, [msSyncedEventIds]);
  useEffect(() => { safeSetItem('tt_ms_last_sync', JSON.stringify(msLastSync)); }, [msLastSync]);
  useEffect(() => { safeSetItem('tt_ms_auto_sync', JSON.stringify(msAutoSync)); }, [msAutoSync]);
  useEffect(() => { safeSetItem('tt_ms_sync_settings', JSON.stringify(msSyncSettings)); }, [msSyncSettings]);
  useEffect(() => { safeSetItem('tt_ms_webhook', JSON.stringify(msWebhookSubscription)); }, [msWebhookSubscription]);
  useEffect(() => { safeSetItem('tt_ms_webhook_enabled', JSON.stringify(msWebhookEnabled)); }, [msWebhookEnabled]);

  // Fetch admin-managed MS Calendar integration status on mount
  // For non-admin users, this is the ONLY source of msCalendarConnected
  useEffect(() => {
    if (!useSupabaseDB) return;
    const fetchIntegrationStatus = async () => {
      const status = await getMsCalendarIntegrationStatus();
      if (status.calendarName) setMsCalendarName(status.calendarName);
      // For non-admin users, derive msCalendarConnected from integration status
      // Admin's own connection state is managed via localStorage (tokens)
      const userIsAdmin = currentUser?.role === 'admin' || currentUser?.role === 'admin-pa';
      if (!userIsAdmin && status.isConnected !== undefined) {
        setMsCalendarConnected(status.isConnected);
        if (status.lastSyncAt) setMsLastSync(status.lastSyncAt);
      }

      // If the server-side renewal job has flagged this user's refresh token as
      // invalid (requires_reauth), surface the reconnect prompt. This covers
      // the case where the token died while the app was closed.
      if (currentUser?.id) {
        const needsReauth = await getMsReauthRequired(currentUser.id);
        if (needsReauth) setMsAuthNeedsReconnect(true);
      }
    };
    fetchIntegrationStatus();
  }, [useSupabaseDB, currentUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Date range picker: Global pointerup to finalize selection if released outside calendar
  useEffect(() => {
    if (!dateRangeDragging) return;
    
    const handleGlobalPointerUp = () => {
      if (dateRangeDragging && dateRangeDragAnchor) {
        const finalHover = dateRangeDragHover || dateRangeDragAnchor;
        commitDateRange(dateRangeDragAnchor, finalHover);
        resetDateRangePicker();
      }
    };
    
    window.addEventListener('pointerup', handleGlobalPointerUp);
    return () => window.removeEventListener('pointerup', handleGlobalPointerUp);
  }, [dateRangeDragging, dateRangeDragAnchor, dateRangeDragHover]);

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const newEntries = [];
    repeatingEntries.forEach((t, index) => {
      if (t.endDate && t.endDate < today) return;
      const last = t.lastCreated || t.startDate;
      const days = Math.floor((new Date(today) - new Date(last)) / 86400000);
      let create = false;
      if (t.frequency === 'daily' && days >= 1) create = true;
      if (t.frequency === 'weekly' && days >= 7) create = true;
      if (t.frequency === 'monthly' && days >= 30) create = true;
      if (create) {
        // Use timestamp + index for unique ID
        const uniqueId = Date.now() * 1000 + index;
        newEntries.push({
          id: uniqueId, userId: t.userId, projectId: t.projectId, taskId: t.taskId,
          description: t.description, duration: t.duration, date: today, billable: t.billable,
          notes: (t.notes || '') + ' (Recurring)', approved: false
        });
        t.lastCreated = today;
      }
    });
    if (newEntries.length > 0) {
      setTimeEntries(prev => [...prev, ...newEntries]);
      setRepeatingEntries([...repeatingEntries]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Export calendar events to .ics format
  const _exportToCalendar = () => {
    const events = [];
    
    // Helper to format date for .ics (YYYYMMDD)
    const formatICSDate = (dateStr) => {
      return dateStr.replace(/-/g, '');
    };
    
    // Helper to create UID
    const createUID = (type, projectId, stageKey) => {
      return `${type}-${projectId}-${stageKey}@architecturebrio.com`;
    };

    // Get all projects (admin sees all, supervisor sees supervised)
    const visibleProjects = currentUser.role === 'supervisor' ? getSupervisedProjects() : projects;
    
    const standardStages = [
      { key: 'advance', name: 'Advance' },
      { key: 'cd_ad', name: 'CD AD' },
      { key: 'cd_id', name: 'CD ID' },
      { key: 'sd_ad', name: 'SD AD' },
      { key: 'sd_id', name: 'SD ID' },
      { key: 'dd_ad', name: 'DD AD' },
      { key: 'dd_id', name: 'DD ID' },
      { key: 'aoc_ad', name: 'AoC AD' },
      { key: 'aoc_id', name: 'AoC ID' },
      { key: 'td_ad', name: 'TD AD' },
      { key: 'td_id', name: 'TD ID' },
      { key: 'gfc1', name: 'GFC 1' },
      { key: 'gfc2', name: 'GFC 2' },
      { key: 'gfc3', name: 'GFC 3' },
      { key: 'completion', name: 'Completion' },
      { key: 'cc', name: 'CC' }
    ];

    // Add project tracking dates
    visibleProjects.forEach(proj => {
      if (!proj.stageTimeline) return;
      
      const client = activeClients.find(c => c.id === proj.clientId);
      
      standardStages.forEach(stage => {
        const timeline = proj.stageTimeline[stage.key] || {};
        
        if (timeline.targetDate) {
          events.push({
            uid: createUID('target', proj.id, stage.key),
            summary: `${fmtProject(proj)} - ${stage.name} - Target Date`,
            description: `Project: ${fmtProject(proj)}\\nClient: ${client?.name || 'N/A'}\\nStage: ${stage.name}\\nType: Target Completion Date`,
            date: formatICSDate(timeline.targetDate),
            color: 'BLUE'
          });
        }
        
        if (timeline.actualDate) {
          events.push({
            uid: createUID('actual', proj.id, stage.key),
            summary: `${fmtProject(proj)} - ${stage.name} - Completed`,
            description: `Project: ${fmtProject(proj)}\\nClient: ${client?.name || 'N/A'}\\nStage: ${stage.name}\\nType: Actual Completion Date`,
            date: formatICSDate(timeline.actualDate),
            color: 'GREEN'
          });
        }
        
        if (timeline.clientApprovalDate) {
          events.push({
            uid: createUID('approval', proj.id, stage.key),
            summary: `${fmtProject(proj)} - ${stage.name} - Client Approval`,
            description: `Project: ${fmtProject(proj)}\\nClient: ${client?.name || 'N/A'}\\nStage: ${stage.name}\\nType: Client Approval Date`,
            date: formatICSDate(timeline.clientApprovalDate),
            color: 'PURPLE'
          });
        }
        
        if (timeline.invoiceDate) {
          events.push({
            uid: createUID('invoice', proj.id, stage.key),
            summary: `${fmtProject(proj)} - ${stage.name} - Invoice Sent`,
            description: `Project: ${fmtProject(proj)}\\nClient: ${client?.name || 'N/A'}\\nStage: ${stage.name}\\nType: Invoice Date`,
            date: formatICSDate(timeline.invoiceDate),
            color: 'ORANGE'
          });
        }
        
        if (timeline.paidDate) {
          events.push({
            uid: createUID('paid', proj.id, stage.key),
            summary: `${fmtProject(proj)} - ${stage.name} - Payment Received`,
            description: `Project: ${fmtProject(proj)}\\nClient: ${client?.name || 'N/A'}\\nStage: ${stage.name}\\nType: Payment Received Date`,
            date: formatICSDate(timeline.paidDate),
            color: 'GREEN'
          });
        }
      });
    });

    // Add holidays (filtered for current user's office)
    userHolidays.forEach(hol => {
      events.push({
        uid: `holiday-${hol.id}@architecturebrio.com`,
        summary: `🎉 ${hol.name}`,
        description: `Company Holiday: ${hol.name}`,
        date: formatICSDate(hol.date),
        color: 'RED'
      });
    });

    // Add approved time off
    activeTimeOff.filter(to => to.status === 'approved').forEach(to => {
      const user = activeUsers.find(u => u.id === to.userId);
      const startDate = new Date(to.startDate);
      const endDate = new Date(to.endDate);
      
      // Create event for each day in the range
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        const dateStr = d.toISOString().split('T')[0];
        events.push({
          uid: `timeoff-${to.id}-${dateStr}@architecturebrio.com`,
          summary: `${user?.name || 'Staff'} - ${to.type}`,
          description: `Time Off\\nEmployee: ${user?.name}\\nType: ${to.type}\\nDuration: ${to.days} days`,
          date: formatICSDate(dateStr),
          color: 'YELLOW'
        });
      }
    });

    // Generate .ics file content
    const now = new Date();
    const timestamp = now.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    
    let icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Architecture BRIO//Time Tracker Pro//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'X-WR-CALNAME:Architecture BRIO - Project Tracking',
      'X-WR-TIMEZONE:Asia/Kolkata',
      'X-WR-CALDESC:Project tracking dates, holidays, and time off from Time Tracker Pro'
    ];

    events.forEach(event => {
      icsContent.push('BEGIN:VEVENT');
      icsContent.push(`UID:${event.uid}`);
      icsContent.push(`DTSTAMP:${timestamp}`);
      icsContent.push(`DTSTART;VALUE=DATE:${event.date}`);
      icsContent.push(`SUMMARY:${event.summary}`);
      icsContent.push(`DESCRIPTION:${event.description}`);
      icsContent.push('STATUS:CONFIRMED');
      icsContent.push('TRANSP:TRANSPARENT');
      icsContent.push('END:VEVENT');
    });

    icsContent.push('END:VCALENDAR');

    // Create and download file
    const blob = new Blob([icsContent.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ArchitectureBRIO-Calendar-${new Date().toISOString().split('T')[0]}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // Update last sync time
    const syncTime = new Date().toISOString();
    setLastCalendarSync(syncTime);
    safeSetItem('tt_last_sync', JSON.stringify(syncTime));
    
    alert(`Calendar exported successfully!\n\n${events.length} events exported.\n\nTo import:\n1. Open Microsoft Outlook Calendar\n2. File → Open & Export → Import/Export\n3. Select "Import an iCalendar (.ics) file"\n4. Choose the downloaded file\n\nNote: For automatic sync, contact admin to set up Microsoft Graph API integration.`);
  };


  // Helper functions for supervisor permissions
  
  // Get team IDs that the current user supervises
  const getSupervisedTeamIds = () => {
    if (!currentUser || currentUser.role !== 'supervisor') return [];
    return activeTeams
      .filter(t => normalizedIncludes(t.supervisorIds, currentUser.id))
      .map(t => t.id);
  };
  
  // Get project IDs linked to teams the current user supervises (via team_projects)
  const getSupervisedProjectIds = () => {
    const supervisedTeamIds = getSupervisedTeamIds();
    if (supervisedTeamIds.length === 0) return [];
    
    // Get all project IDs linked to supervised teams via team_projects
    const projectIds = new Set();
    supervisedTeamIds.forEach(teamId => {
      const teamProjectIds = supabaseTeamProjects.getProjectIdsForTeam(teamId);
      teamProjectIds.forEach(pid => projectIds.add(pid));
    });
    return Array.from(projectIds);
  };
  
  const isSupervisorOfProject = (projectId) => {
    const pid = normalizeId(projectId);
    const project = activeProjects.find(p => normalizeId(p.id) === pid);
    // Primary: check project.supervisors array (direct assignment)
    if (normalizedIncludes(project?.supervisors, currentUser.id)) return true;
    // Fallback: check team_projects linkage
    const supervisedProjectIds = getSupervisedProjectIds();
    return normalizedIncludes(supervisedProjectIds, projectId);
  };

  const getSupervisedProjects = () => {
    let projectList = [];
    if (isAdmin) {
      projectList = activeProjects;
    } else if (currentUser.role === 'supervisor') {
      // Combine two sources:
      // 1. Direct: project.supervisors array contains currentUser.id
      // 2. Team-based: project linked to a team the user supervises (via team_projects)
      const supervisedProjectIds = getSupervisedProjectIds();
      projectList = activeProjects.filter(p =>
        normalizedIncludes(p.supervisors, currentUser.id) ||
        normalizedIncludes(supervisedProjectIds, p.id)
      );

      // DEV-ONLY: Debug log when supervisor has no supervised projects
      if (process.env.NODE_ENV !== 'production' && projectList.length === 0) {
        console.log('[SUPERVISOR DEBUG] currentUser.id:', currentUser.id, '(type:', typeof currentUser.id, ')');
        console.log(
          '[SUPERVISOR DEBUG] sample projects:',
          activeProjects.slice(0, 5).map(p => ({
            id: p.id,
            name: p.name,
            supervisors: p.supervisors
          }))
        );
        console.log(
          '[SUPERVISOR DEBUG] team route — supervisedProjectIds:', supervisedProjectIds,
          '| activeTeams:', (activeTeams || []).slice(0, 5).map(t => ({
            id: t.id, name: t.name, supervisorIds: t.supervisorIds
          }))
        );
      }
    }
    return [...projectList].sort((a, b) => (a.projectCode || a.name || '').localeCompare(b.projectCode || b.name || ''));
  };

  const canManageProject = (projectId) => {
    return isAdmin || isSupervisorOfProject(projectId);
  };

  // Check if a project has at least one valid supervisor assigned
  // Used by admin UI to warn about projects invisible to supervisors
  const hasSupervisorsAssigned = (project) => {
    const arr = Array.isArray(project?.supervisors) ? project.supervisors : [];
    return arr.map(normalizeId).filter(Boolean).length > 0;
  };

  const _canViewFinancials = () => {
    return isAdmin || currentUser.role === 'supervisor';
  };

  const getProjectsForDropdown = () => {
    let projectList = [];
    if (isAdmin) {
      // Admin sees all projects
      projectList = activeProjects;
    } else if (currentUser.role === 'supervisor') {
      // Combine direct project.supervisors + team_projects linkage
      const supervisedProjectIds = getSupervisedProjectIds();
      projectList = activeProjects.filter(p =>
        normalizedIncludes(p.supervisors, currentUser.id) ||
        normalizedIncludes(supervisedProjectIds, p.id)
      );
    } else {
      // Regular users: only projects they have time entry history on.
      // Falls back to all active projects for new users with no history yet.
      if (userHistoricalProjectIds && userHistoricalProjectIds.size > 0) {
        projectList = activeProjects.filter(p => userHistoricalProjectIds.has(String(p.id)));
      } else {
        projectList = activeProjects.filter(p => p.status === 'active');
      }
    }
    return [...projectList].sort((a, b) => (a.projectCode || a.name || '').localeCompare(b.projectCode || b.name || ''));
  };

  // Helper: Get projects sorted alphabetically by project code (applies status filter for Manage page)
  const getSortedProjects = () => {
    // Use filtered projects based on role and admin filter
    return [...getFilteredProjects()].sort((a, b) => (a.projectCode || a.name || '').localeCompare(b.projectCode || b.name || ''));
  };

  // BRIO Design System
  // Helper: read a CSS custom property from :root
  const cssVar = (name) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();

  const brioColors = {
    roles: {
      'Director': cssVar('--brio-role-director'), 'Partner': cssVar('--brio-role-partner'), 'Associate': cssVar('--brio-role-associate'),
      'Senior Architect': cssVar('--brio-role-senior-architect'), 'Project Architect': cssVar('--brio-role-project-architect'),
      'Medior Architect': cssVar('--brio-role-medior-architect'), 'Junior Architect': cssVar('--brio-role-junior-architect'),
      'Intern': cssVar('--brio-role-intern'), 'Support': cssVar('--brio-role-support'),
      'Model Maker': cssVar('--brio-role-model-maker'), 'Visualiser': cssVar('--brio-role-visualiser'), 'Others': cssVar('--brio-role-others')
    },
    teams: (settingsHook.themeConfig?.teams && Object.keys(settingsHook.themeConfig.teams).length > 0)
      ? Object.values(settingsHook.themeConfig.teams)
      : ['#5B7A99', '#CC9966', '#6B9B7B', '#B36B6B', '#8B7BA3', '#9B8B7A', '#7A9B9B', '#9B7A8B', '#8B9B7A', '#9B7A7A'],
    status: { optimal: '#ffffff', warning: '#FFF8E1', critical: '#FFEBEE' }
  };

  // ==========================================
  // MICROSOFT CALENDAR INTEGRATION 
  // ==========================================
  
  // OAuth Configuration
  const MS_CLIENT_ID = '4b3c690a-7d88-4a0a-a1cf-eb65d70a29c5'; // Your Azure AD Client ID
  const MS_REDIRECT_URI = window.location.origin + '/ms-oauth'; // Registered under the "Web" platform in Entra
  const MS_SCOPES = 'openid profile email Calendars.Read Calendars.ReadWrite offline_access';
  const MS_AUTHORITY = 'https://login.microsoftonline.com/common';

  // Generate code verifier and challenge for PKCE
  const generateCodeVerifier = () => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return base64UrlEncode(array);
  };

  const base64UrlEncode = (buffer) => {
    const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  };

  const generateCodeChallenge = async (verifier) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const hash = await crypto.subtle.digest('SHA-256', data);
    return base64UrlEncode(hash);
  };

  // Initiate OAuth flow with PKCE
  const initiateMsCalendarAuth = async () => {
    console.log('🔵 Connect MS Calendar button clicked!');
    console.log('Client ID:', MS_CLIENT_ID);
    console.log('Redirect URI:', MS_REDIRECT_URI);
    
    // Generate PKCE parameters
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    
    // Store code verifier for later use
    sessionStorage.setItem('ms_code_verifier', codeVerifier);
    
    const authUrl = `${MS_AUTHORITY}/oauth2/v2.0/authorize?` +
      `client_id=${MS_CLIENT_ID}` +
      `&response_type=code` + // Authorization code flow
      `&redirect_uri=${encodeURIComponent(MS_REDIRECT_URI)}` +
      `&scope=${encodeURIComponent(MS_SCOPES)}` +
      `&response_mode=query` +
      `&code_challenge=${codeChallenge}` +
      `&code_challenge_method=S256` + // PKCE
      `&prompt=select_account` + // Always show the account picker (supports switching between info@/projects@)
      `&state=${Date.now()}`;
    
    console.log('📍 Redirecting to Microsoft login...');
    console.log('Auth URL:', authUrl);
    
    // Use full page redirect (works better than popup for PKCE)
    window.location.href = authUrl;
  };

  // Exchange authorization code for tokens using PKCE
  const exchangeCodeForToken = async (code) => {
    try {
      console.log('🔄 Exchanging code for token...');
      
      const codeVerifier = sessionStorage.getItem('ms_code_verifier');
      if (!codeVerifier) {
        throw new Error('Code verifier not found. Please try connecting again.');
      }
      
      // Exchange the auth code SERVER-SIDE via the ms-auth edge function
      // (confidential "Web" client). The browser never receives or stores the
      // refresh token; the server stores it and returns a short-lived access
      // token. supabase.functions.invoke attaches the caller's session JWT so
      // the server can map to the right user.
      console.log('📤 Sending code to ms-auth for server-side exchange...');
      const { data: result, error: fnError } = await supabase.functions.invoke('ms-auth', {
        body: { action: 'exchange', code, code_verifier: codeVerifier, redirect_uri: MS_REDIRECT_URI }
      });

      // A refused or failed exchange comes back as a non-2xx, and supabase-js
      // reports that as fnError with data null — the server's explanation is in
      // the response body hanging off fnError.context. Without reading it the
      // user just sees "Edge Function returned a non-2xx status code", which
      // says nothing about, say, having signed in with the wrong mailbox.
      let payload = result;
      if (fnError?.context && typeof fnError.context.json === 'function') {
        payload = await fnError.context.json().catch(() => null) || result;
      }

      if (fnError || !payload || payload.error || !payload.access_token) {
        const msg = payload?.error_description || payload?.error || fnError?.message || 'Failed to exchange code for token';
        console.error('Token exchange failed:', msg, fnError || payload);
        throw new Error(msg);
      }

      const accessToken = result.access_token;
      console.log('✅ Connected (server-side token exchange).');

      // Cache the access token in memory only (no refresh token in the browser).
      setMsAccessToken(accessToken);
      setMsTokenExpiry(result.expires_at ? new Date(result.expires_at).getTime() : (Date.now() + 45 * 60 * 1000));
      setMsCalendarConnected(true);
      setMsAuthNeedsReconnect(false);

      // Clean up the verifier + strip the code/path from the URL.
      sessionStorage.removeItem('ms_code_verifier');
      window.history.replaceState({}, document.title, '/');

      alert('Successfully connected to Microsoft Calendar!');

      // Persist admin-managed integration status (non-secret) so non-admin users see "connected".
      if (useSupabaseDB) {
        updateMsCalendarIntegrationStatus({
          isConnected: true,
          connectedAt: new Date().toISOString()
        }).catch(err => {
          console.error('[MS Calendar] Failed to persist integration status:', err);
        });
      }

      // Initialize BRIO calendars in Outlook - pass token directly (state may not be set yet)
      console.log('🗓️ Auto-initializing Microsoft calendars after connection...');
      const calendarResult = await ensureMicrosoftCalendarsExist(currentUser.id, accessToken);
      if (calendarResult.success && calendarResult.created.length > 0) {
        console.log(`✓ Created ${calendarResult.created.length} BRIO calendar(s) in Outlook`);
        alert(`✓ Created ${calendarResult.created.length} BRIO calendar(s) in your Outlook:\n${calendarResult.created.map(c => `• ${c.calendarName}`).join('\n')}`);
      } else if (calendarResult.errors && calendarResult.errors.length > 0) {
        console.error('Calendar initialization errors:', calendarResult.errors);
      }

      syncMsCalendar(accessToken);
      
    } catch (error) {
      console.error('Token exchange error:', error);
      alert('Failed to connect to Microsoft Calendar: ' + error.message);
      sessionStorage.removeItem('ms_code_verifier');
    }
  };

  // Fetch a fresh access token from the ms-auth edge function. The server (a
  // confidential "Web" client) owns the refresh token and mints short-lived
  // access tokens — the browser never holds a refresh token, and SPA-token
  // cross-origin limits (AADSTS9002327) no longer apply. Returns the access
  // token, or null (disconnecting) if the server reports re-auth is required.
  const fetchServerAccessToken = async () => {
    if (!useSupabaseDB || !currentUser?.id) return null;
    try {
      const { data: result, error } = await supabase.functions.invoke('ms-auth', {
        body: { action: 'token' }
      });
      if (error) {
        // Transient (network / function error) — don't disconnect.
        console.warn('[MS Calendar] ms-auth token error (transient):', error.message || error);
        return null;
      }
      if (result?.requires_reauth) {
        console.warn('[MS Calendar] Server reports re-auth required — disconnecting');
        disconnectMsCalendar({ confirm: false, reason: 'Server requires re-authentication' });
        return null;
      }
      if (result?.access_token) {
        setMsAccessToken(result.access_token);
        setMsTokenExpiry(result.expires_at ? new Date(result.expires_at).getTime() : (Date.now() + 45 * 60 * 1000));
        setMsAuthNeedsReconnect(false);
        return result.access_token;
      }
      return null;
    } catch (err) {
      console.warn('[MS Calendar] ms-auth token exception (transient):', err);
      return null;
    }
  };

  // Get a valid access token: use the in-memory cache while it is comfortably
  // valid, otherwise fetch a fresh one from the server.
  const getValidToken = async () => {
    if (msAccessToken && msTokenExpiry && Date.now() < (msTokenExpiry - 300000)) {
      return msAccessToken;
    }
    const token = await fetchServerAccessToken();
    if (token) return token;
    // Server didn't return one; fall back to a still-valid cached token if any.
    if (msAccessToken && msTokenExpiry && Date.now() < msTokenExpiry) {
      console.warn('[MS Calendar] Server token fetch failed, using still-valid cached token');
      return msAccessToken;
    }
    return null;
  };

  // ========================================================================
  // CENTRALIZED MS GRAPH FETCH WRAPPER
  // All Microsoft Graph API calls MUST go through this wrapper.
  // It auto-attaches the Bearer token and retries once on 401.
  // ========================================================================
  const msGraphFetch = async (url, options = {}, { retryOn401 = true, accessToken: explicitToken = null } = {}) => {
    // 1. Get a valid access token. An explicit token bypasses React state
    //    (needed right after OAuth callback when setMsAccessToken hasn't
    //    committed yet — getValidToken() would see stale null).
    let token = explicitToken || await getValidToken();
    if (!token) {
      throw new Error('No valid Microsoft access token. Please reconnect.');
    }

    // 2. Merge Authorization header
    const mergedOptions = {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`
      }
    };

    // 3. Execute fetch
    let response = await fetch(url, mergedOptions);

    // 4. If 401 and retryOn401, force a fresh server-minted token and retry once.
    //    Skip when the caller supplied an explicit token (already fresh, e.g.
    //    right after the OAuth callback before state has committed).
    if (response.status === 401 && retryOn401 && !explicitToken) {
      console.warn('[msGraphFetch] 401 received, refreshing token and retrying…');
      const newToken = await fetchServerAccessToken();
      if (!newToken) {
        // Refresh failed — return the 401 response as-is
        return response;
      }
      // Retry with new token
      const retryOptions = {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': `Bearer ${newToken}`
        }
      };
      response = await fetch(url, retryOptions);
    }

    // 5. Log non-OK responses for debugging (but don't throw — callers handle errors)
    if (!response.ok && response.status !== 204 && response.status !== 404) {
      try {
        const cloned = response.clone();
        const errBody = await cloned.json().catch(() => cloned.text().catch(() => '(unreadable)'));
        console.error(`[msGraphFetch] ${options.method || 'GET'} ${url} → ${response.status}`, errBody);
      } catch (_) { /* best-effort logging */ }
    }

    return response;
  };

  // Fetch the connected MS account's userPrincipalName via Graph /me.
  // Used to tag ms_synced_items rows so an account switch (e.g. info@ → projects@)
  // can invalidate the previous account's ledger entries.
  // Returns null on failure — callers fall back to writing rows without a UPN tag.
  const fetchConnectedMsAccountUpn = async () => {
    try {
      const resp = await msGraphFetch('https://graph.microsoft.com/v1.0/me?$select=userPrincipalName,mail', {
        headers: { 'Content-Type': 'application/json' }
      });
      if (!resp.ok) {
        console.warn(`[MS Calendar] /me lookup failed (HTTP ${resp.status}) — proceeding without UPN tag`);
        return null;
      }
      const data = await resp.json();
      const upn = (data.userPrincipalName || data.mail || '').toLowerCase() || null;
      if (upn) console.log(`[MS Calendar] Connected account UPN: ${upn}`);
      return upn;
    } catch (err) {
      console.warn('[MS Calendar] /me lookup error — proceeding without UPN tag:', err.message);
      return null;
    }
  };

  // ========================================================================
  // MICROSOFT CALENDAR INITIALIZATION - Create BRIO calendars per event type
  // ========================================================================
  // 
  // CANONICAL EVENT TYPES - Single source of truth
  // These 7 event types are used throughout the app for:
  // - Outlook calendar creation (initializeMicrosoftCalendars)
  // - Supabase ms_calendar_mappings upserts
  // - MS event sync loops (syncMsCalendar)
  // - Calendar display filters (getUnifiedCalendarEvents)
  //
  // ⚠️ SUPABASE CONSTRAINT: The ms_calendar_mappings.event_type column must allow:
  //    'events', 'holidays', 'leaves', 'pendingLeaves', 'milestones', 'invoicing', 'anniversaries', 'birthdays'
  //    If you see error 23514 (ms_calendar_mappings_event_type_check), update the DB constraint.
  //
  // Event types and their corresponding Outlook calendar names:
  const MS_CALENDAR_TYPES = {
    holidays: 'BRIO — Public Holidays',
    leaves: 'BRIO — Team Leaves',
    pendingLeaves: 'BRIO — Pending Leaves',
    milestones: 'BRIO — Milestones',
    invoicing: 'BRIO — Invoicing',
    anniversaries: 'BRIO — Anniversaries',
    birthdays: 'BRIO — Birthdays',
    events: 'BRIO — General Events'
  };
  
  // Export as array for validation checks
  const VALID_MS_EVENT_TYPES = Object.keys(MS_CALENDAR_TYPES);

  // time_off_requests.status as this app actually writes it: new requests are
  // created 'pending_supervisor' (useSupabase.js), then move to 'pending_admin',
  // then 'approved' or 'rejected'. A bare 'pending' is never written. The MS
  // export used to test `status === 'pending'`, which therefore never matched,
  // so the Pending Leaves calendar received nothing from the day it shipped.
  // Every other status check in this file already tests all three spellings.
  const MS_PENDING_LEAVE_STATUSES = new Set(['pending', 'pending_supervisor', 'pending_admin']);

  // ========================================================================
  // SAFETY GUARD: Validate user_id before any ms_calendar_mappings operation
  // Ensures user_id is a valid positive number (Supabase bigint PKs can exceed 2B)
  // ========================================================================
  const validateMsCalendarUserId = (userId, callerName = 'unknown') => {
    if (userId == null || userId === undefined) {
      const msg = `[MS Calendar] ${callerName}: user_id is null/undefined — cannot proceed`;
      console.error(msg);
      throw new Error(msg);
    }
    const numericId = typeof userId === 'string' ? parseInt(userId, 10) : userId;
    if (isNaN(numericId)) {
      const msg = `[MS Calendar] ${callerName}: user_id "${userId}" is not a valid number`;
      console.error(msg);
      throw new Error(msg);
    }
    if (numericId <= 0) {
      const msg = `[MS Calendar] ${callerName}: user_id ${numericId} is not a valid positive integer`;
      console.error(msg);
      throw new Error(msg);
    }
    // NOTE: Supabase uses bigint (int8) for users.id — values like 1.7 trillion are legitimate.
    // Do NOT reject values > 2 billion; that would block valid employee PKs.
    return numericId;
  };

  // Ensure Microsoft calendars exist for each event type
  // Uses ensureCalendar() from msCalendarHelpers.js — the ONLY code path
  // that may POST /me/calendars. No direct fetch('POST /me/calendars') here.
  // CRITICAL: Verifies against Graph API, not just Supabase mappings
  const ensureMicrosoftCalendarsExist = async (userIdOrToken = null, tokenParam = null) => {
    // Support two call signatures:
    // 1. ensureMicrosoftCalendarsExist(userId, token) - explicit token
    // 2. ensureMicrosoftCalendarsExist(userId) - uses getValidToken()
    const rawUserId = (typeof userIdOrToken === 'number' || (typeof userIdOrToken === 'string' && !userIdOrToken.startsWith('ey'))) 
      ? userIdOrToken 
      : currentUser?.id;
    const explicitToken = tokenParam || (typeof userIdOrToken === 'string' && userIdOrToken.startsWith('ey') ? userIdOrToken : null);
    
    // SAFETY: Validate user_id before any Supabase operations
    const targetUserId = validateMsCalendarUserId(rawUserId, 'ensureMicrosoftCalendarsExist');
    
    if (!targetUserId) {
      console.error('ensureMicrosoftCalendarsExist: No user ID provided');
      return { success: false, error: 'No user ID' };
    }

    // Use explicit token if provided, otherwise try getValidToken()
    const validToken = explicitToken || await getValidToken();
    if (!validToken) {
      console.error('ensureMicrosoftCalendarsExist: No valid Microsoft token');
      return { success: false, error: 'Not connected to Microsoft' };
    }

    const expectedTypes = Object.keys(MS_CALENDAR_TYPES);
    console.log('🗓️ MS Calendar Initialization');
    console.log(`   User: ${targetUserId}`);
    console.log(`   Expected types (${expectedTypes.length}):`, expectedTypes);

    try {
      // Step 1: Fetch existing mappings from Supabase
      const { data: existingMappings, error: fetchError } = await supabase
        .from('ms_calendar_mappings')
        .select('*')
        .eq('user_id', targetUserId);

      if (fetchError) {
        console.error('Error fetching calendar mappings:', fetchError);
        throw fetchError;
      }

      const mappingsByType = {};
      (existingMappings || []).forEach(m => {
        mappingsByType[m.event_type] = m;
      });
      console.log(`   Supabase mappings found: ${Object.keys(mappingsByType).length}`);

      // Step 2: Fetch ALL calendars from Microsoft Graph (paginated via @odata.nextLink)
      // Uses getCalendarsGraph which follows pagination automatically
      console.log('📡 Fetching Microsoft calendars from Graph API (all pages)...');
      const msCalendars = await getCalendarsGraph(validToken);
      console.log(`   Graph calendars found: ${msCalendars.length}`);
      
      // Build ID lookup for verifying existing mappings
      const msCalendarsById = {};
      msCalendars.forEach(cal => {
        msCalendarsById[cal.id] = cal;
      });

      const results = {
        verified: [],      // Mapping exists AND calendar exists in Graph
        created: [],       // New calendar created
        reused: [],        // Found existing calendar by name, created mapping
        repaired: [],      // Mapping existed but calendar missing, recreated
        errors: []
      };

      // Step 3: For each event type, verify/create calendar
      for (const [eventType, calendarName] of Object.entries(MS_CALENDAR_TYPES)) {
        const existingMapping = mappingsByType[eventType];
        
        // Case A: Mapping exists - verify the calendar still exists in Graph
        if (existingMapping) {
          const graphCalendar = msCalendarsById[existingMapping.ms_calendar_id];
          
          if (graphCalendar) {
            // Calendar exists in Graph - verified!
            results.verified.push({ 
              eventType, 
              calendarId: existingMapping.ms_calendar_id, 
              calendarName: graphCalendar.name 
            });
            console.log(`   ✓ ${eventType}: Verified in Graph (${graphCalendar.name})`);
            continue;
          } else {
            // Mapping exists but calendar is MISSING from Graph - needs repair
            console.warn(`   ⚠️ ${eventType}: Mapping exists but calendar missing from Graph - will ensure`);
            // Fall through to ensureCalendar
          }
        }

        // Case B: No valid mapping/calendar — delegate to ensureCalendar()
        // This is the ONLY path that may create calendars. ensureCalendar:
        // 1. Lists all calendars (paginated, normalized name matching)
        // 2. Returns existing match if found
        // 3. Creates ONLY if truly not found
        // 4. Handles 409 race condition with retry
        let calendarId = null;
        let finalCalendarName = calendarName;
        let wasCreated = false;

        try {
          // Build extra fields (color) for creation — only used if calendar doesn't exist
          const extraFields = {};
          const calColor = getCalendarColor(eventType);
          const VALID_GRAPH_COLORS = ['auto','lightBlue','lightGreen','lightOrange','lightGray','lightYellow','lightTeal','lightPink','lightBrown','lightRed','maxColor'];
          if (calColor && VALID_GRAPH_COLORS.includes(calColor)) {
            extraFields.color = calColor;
          }

          console.log(`   📅 ${eventType}: ensureCalendar("${calendarName}")...`);
          const result = await ensureCalendarGraph(validToken, calendarName, extraFields);
          calendarId = result.id;
          finalCalendarName = result.name;
          wasCreated = result.created;

          if (wasCreated) {
            console.log(`   ✅ ${eventType}: Created calendar "${finalCalendarName}" (${calendarId})`);
            if (existingMapping) {
              results.repaired.push({ eventType, calendarId, calendarName: finalCalendarName });
            } else {
              results.created.push({ eventType, calendarId, calendarName: finalCalendarName });
            }
          } else {
            console.log(`   🔄 ${eventType}: Reusing existing calendar "${finalCalendarName}" (${calendarId})`);
            if (existingMapping) {
              results.repaired.push({ eventType, calendarId, calendarName: finalCalendarName });
            } else {
              results.reused.push({ eventType, calendarId, calendarName: finalCalendarName });
            }
          }
        } catch (calError) {
          console.error(`   ❌ ${eventType}: ensureCalendar failed:`, calError.message);
          results.errors.push({ eventType, error: calError.message });
          continue;
        }

        // Step 4: Upsert mapping into Supabase
        if (calendarId) {
          const { error: upsertError } = await supabase
            .from('ms_calendar_mappings')
            .upsert({
              user_id: targetUserId,
              event_type: eventType,
              ms_calendar_id: calendarId,
              ms_calendar_name: finalCalendarName,
              updated_at: new Date().toISOString()
            }, {
              onConflict: 'user_id,event_type'
            });

          if (upsertError) {
            console.error(`   ❌ ${eventType}: Supabase upsert failed:`, upsertError);
            results.errors.push({ eventType, error: 'Failed to save mapping: ' + upsertError.message });
          }
        }
      }

      // Step 5: Log summary
      console.log('📊 MS Calendar Initialization Summary:');
      console.log(`   Expected:  ${expectedTypes.length} calendars`);
      console.log(`   Verified:  ${results.verified.length} (mapping + Graph confirmed)`);
      console.log(`   Created:   ${results.created.length} (new calendars)`);
      console.log(`   Reused:    ${results.reused.length} (found by name, new mapping)`);
      console.log(`   Repaired:  ${results.repaired.length} (mapping existed, calendar missing)`);
      console.log(`   Errors:    ${results.errors.length}`);
      
      if (results.created.length > 0) {
        console.log('   Created calendars:', results.created.map(c => c.calendarName));
      }
      if (results.repaired.length > 0) {
        console.log('   Repaired calendars:', results.repaired.map(c => c.calendarName));
      }

      // Fetch final mappings
      const { data: finalMappings } = await supabase
        .from('ms_calendar_mappings')
        .select('*')
        .eq('user_id', targetUserId);
      
      return {
        success: results.errors.length === 0,
        mappings: finalMappings || [],
        verified: results.verified,
        created: results.created,
        reused: results.reused,
        repaired: results.repaired,
        errors: results.errors
      };

    } catch (error) {
      console.error('ensureMicrosoftCalendarsExist error:', error);
      return { success: false, error: error.message };
    }
  };

  // Helper: Get calendar color for event type (Microsoft Graph color enum)
  // See: https://learn.microsoft.com/en-us/graph/api/resources/calendar#properties
  // Microsoft Graph calendarColor enum — ONLY these values are valid:
  // auto, lightBlue, lightGreen, lightOrange, lightGray, lightYellow,
  // lightTeal, lightPink, lightBrown, lightRed, maxColor
  const getCalendarColor = (eventType) => {
    const colors = {
      holidays: 'lightPink',
      leaves: 'lightGreen',
      pendingLeaves: 'lightYellow',
      milestones: 'lightBlue',
      invoicing: 'lightOrange',
      anniversaries: 'lightTeal',
      birthdays: 'lightRed',       // No lightMauve/lightPurple in Graph enum; lightRed is closest
      events: 'lightGray'
    };
    return colors[eventType] || 'auto';
  };

  // Helper: Get Microsoft calendar ID for a given event type from ms_calendar_mappings
  const getMsCalendarIdForEventType = async (eventType) => {
    try {
      const validUserId = validateMsCalendarUserId(currentUser.id, 'getMsCalendarIdForEventType');
      const { data, error } = await supabase
        .from('ms_calendar_mappings')
        .select('ms_calendar_id')
        .eq('user_id', validUserId)
        .eq('event_type', eventType)
        .single();

      if (error || !data) {
        console.warn(`No MS calendar mapping found for event type "${eventType}"`);
        return null;
      }

      console.log(`📅 Found MS calendar ID for ${eventType}:`, data.ms_calendar_id);
      return data.ms_calendar_id;
    } catch (err) {
      console.error('Error fetching MS calendar mapping:', err);
      return null;
    }
  };

  // ========================================================================
  // UNIFIED EXPORT PIPELINE - Collect items from all BRIO sources for MS sync
  // ========================================================================
  
  // Helper: Get all BRIO calendar mappings for current user
  const getBrioCalendarMappings = async () => {
    try {
      const validUserId = validateMsCalendarUserId(currentUser.id, 'getBrioCalendarMappings');
      const { data, error } = await supabase
        .from('ms_calendar_mappings')
        .select('event_type, ms_calendar_id, ms_calendar_name')
        .eq('user_id', validUserId);
      
      if (error) {
        console.error('Error fetching BRIO calendar mappings:', error);
        return {};
      }
      
      // Convert to map: { eventType: { calendarId, calendarName } }
      const mappings = {};
      (data || []).forEach(m => {
        mappings[m.event_type] = {
          calendarId: m.ms_calendar_id,
          calendarName: m.ms_calendar_name
        };
      });
      return mappings;
    } catch (err) {
      console.error('getBrioCalendarMappings error:', err);
      return {};
    }
  };

  // Key for any in-memory map of ms_synced_items rows. The table is unique on
  // (user_id, event_type, source_key), and the user is fixed for any one read,
  // so these two identify a row. Keying such a map on source_key alone
  // collapses the two rows a leave has across its life — "pendingLeaves" while
  // awaiting approval, "leaves" once approved, under one unchanging source_key
  // — and silently drops whichever lost. Callers that need the bare source_key
  // read record.source_key, never the map key.
  const msLedgerKey = (eventType, sourceKey) => `${eventType}\u0000${sourceKey}`;

  // Helper: Get existing sync records from ms_synced_items, keyed by
  // msLedgerKey(event_type, source_key).
  // currentUpn: when provided, only records whose ms_account_upn equals this
  // value (or is NULL — pre-migration legacy rows) are returned. This prevents
  // the push from trusting ledger entries that point at events in a different
  // MS account after the user switches accounts (e.g. info@ → projects@).
  const getExistingSyncRecords = async (currentUpn = null) => {
    try {
      const validUserId = validateMsCalendarUserId(currentUser.id, 'getExistingSyncRecords');
      let query = supabase
        .from('ms_synced_items')
        .select('*')
        .eq('user_id', validUserId);
      if (currentUpn) {
        // Postgres null-or-equal: tolerate legacy NULL rows + match current account
        query = query.or(`ms_account_upn.is.null,ms_account_upn.eq.${currentUpn}`);
      }
      const { data, error } = await query;
      
      if (error) {
        // Table might not exist yet - return empty
        console.warn('Could not fetch ms_synced_items (table may not exist):', error.message);
        return {};
      }
      
      // Map keyed by event_type + source_key — see msLedgerKey above.
      const records = {};
      (data || []).forEach(r => {
        records[msLedgerKey(r.event_type, r.source_key)] = r;
      });
      return records;
    } catch (err) {
      console.error('getExistingSyncRecords error:', err);
      return {};
    }
  };

  // Helper: load just the MS event ids this app has created, for the connected
  // account. Separate from getExistingSyncRecords because that one returns {}
  // for both "no rows" and "query failed", and the calendar view has to be able
  // to tell those apart — see msLedgerLoaded.
  const loadMsSyncedEventIds = async () => {
    try {
      const validUserId = validateMsCalendarUserId(currentUser.id, 'loadMsSyncedEventIds');
      const currentUpn = await fetchConnectedMsAccountUpn();
      let query = supabase
        .from('ms_synced_items')
        .select('ms_event_id')
        .eq('user_id', validUserId)
        .not('ms_event_id', 'is', null);
      if (currentUpn) {
        query = query.or(`ms_account_upn.is.null,ms_account_upn.eq.${currentUpn}`);
      }
      const { data, error } = await query;
      if (error) {
        console.warn('[MS Calendar] Could not load synced item ids:', error.message);
        return { ids: [], ok: false };
      }
      return { ids: (data || []).map(r => r.ms_event_id).filter(Boolean), ok: true };
    } catch (err) {
      console.error('[MS Calendar] loadMsSyncedEventIds error:', err);
      return { ids: [], ok: false };
    }
  };

  // ========================================================================
  // MS SYNCED ITEMS — Allowed event_type values
  // Must match the CHECK constraint on ms_synced_items.event_type
  // DO NOT add values here without also updating the DB constraint.
  // ========================================================================
  const MS_SYNCED_ALLOWED_TYPES = new Set([
    'events', 'holidays', 'leaves', 'pendingLeaves',
    'milestones', 'invoicing', 'anniversaries'
  ]);

  /**
   * Map any internal event type to an allowed ms_synced_items event_type.
   * - 'birthdays' → 'anniversaries' (closest allowed category)
   * - 'msCalendar' → 'events' (external calendar events)
   * - unknown → 'events' (safe fallback)
   * @param {string} rawType - Internal event type string
   * @returns {string} An allowed ms_synced_items event_type
   */
  const toSyncedEventType = (rawType) => {
    if (MS_SYNCED_ALLOWED_TYPES.has(rawType)) return rawType;
    // Explicit mappings for known types that aren't in the constraint
    if (rawType === 'birthdays') return 'anniversaries';
    if (rawType === 'msCalendar') return 'events';
    // Unknown type — log and fall back
    console.warn(`[MS Sync] Unknown event_type "${rawType}" — mapping to "events"`);
    return 'events';
  };

  // Helper: Upsert sync record to ms_synced_items
  // msAccountUpn: the userPrincipalName of the currently-connected MS account.
  // Stored on every write so future account switches can invalidate stale rows.
  const upsertMsSyncedItem = async (eventType, sourceKey, msCalendarId, msEventId, contentHash = null, msAccountUpn = null) => {
    try {
      const validUserId = validateMsCalendarUserId(currentUser.id, 'upsertMsSyncedItem');
      // GUARD: Ensure event_type passes the DB CHECK constraint
      const safeEventType = toSyncedEventType(eventType);
      if (safeEventType !== eventType) {
        console.warn(`[MS Sync] upsertMsSyncedItem: mapped event_type "${eventType}" → "${safeEventType}" for source_key "${sourceKey}"`);
      }
      const row = {
        user_id: validUserId,
        event_type: safeEventType,
        source_key: sourceKey,
        ms_calendar_id: msCalendarId,
        ms_event_id: msEventId,
        content_hash: contentHash,
        last_synced_at: new Date().toISOString()
      };
      if (msAccountUpn) row.ms_account_upn = msAccountUpn;
      const { error } = await supabase
        .from('ms_synced_items')
        .upsert(row, {
          onConflict: 'user_id,event_type,source_key'
        });
      
      if (error) {
        console.error('Error upserting ms_synced_item:', error);
        return false;
      }
      return true;
    } catch (err) {
      console.error('upsertMsSyncedItem error:', err);
      return false;
    }
  };

  // Helper: Delete sync record from ms_synced_items
  const deleteMsSyncedItem = async (sourceKey) => {
    try {
      const validUserId = validateMsCalendarUserId(currentUser.id, 'deleteMsSyncedItem');
      const { error } = await supabase
        .from('ms_synced_items')
        .delete()
        .eq('user_id', validUserId)
        .eq('source_key', sourceKey);
      
      if (error) {
        console.error('Error deleting ms_synced_item:', error);
        return false;
      }
      return true;
    } catch (err) {
      console.error('deleteMsSyncedItem error:', err);
      return false;
    }
  };

  // Helper: Create content hash for change detection (Unicode-safe djb2 hash)
  // This function NEVER throws - returns null on any error
  // Content hash for MS sync change detection.
  // IMPORTANT: includes ALL fields that affect the Outlook event so that changing
  // any of them (title, description, times, dates, allDay) triggers an update.
  const createContentHash = (title, startDate, endDate, isAllDay, description, startTime, endTime) => {
    try {
      const str = [
        title || '',
        description || '',
        startDate || '',
        endDate || '',
        startTime || '',
        endTime || '',
        isAllDay ? '1' : '0',
        // All-day events are pushed with the reminder off (see
        // buildMsEventPayload). The marker changes their hash once so events
        // already in Outlook get re-PATCHed and lose the 23:45 reminder.
        // Must match ms-outbound-sync's createContentHash byte for byte.
        ...(isAllDay ? ['noreminder'] : [])
      ].join('|');
      
      // djb2 hash algorithm - simple, fast, works with any Unicode
      let hash = 5381;
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) + hash) ^ char; // hash * 33 ^ char
        hash = hash >>> 0; // Convert to unsigned 32-bit integer
      }
      
      // Convert to hex string (8 chars for 32-bit number)
      return hash.toString(16).padStart(8, '0');
    } catch (err) {
      // Hash should never fail, but if it does, return null (sync will still work)
      console.warn('createContentHash error (non-fatal):', err.message);
      return null;
    }
  };

  // ========================================================================
  // DATE RANGE PICKER HELPERS (for Time Off modal)
  // ========================================================================
  
  // Helper: Convert Date object to ISO string (YYYY-MM-DD) in local timezone
  const dateToISO = (dateObj) => {
    if (!dateObj) return null;
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper: Clamp two ISO date strings to sorted {start, end}
  const clampRange = (aISO, bISO) => {
    if (!aISO || !bISO) return { start: aISO || bISO, end: aISO || bISO };
    return aISO <= bISO 
      ? { start: aISO, end: bISO }
      : { start: bISO, end: aISO };
  };

  // Helper: Check if a day is within a range (inclusive)
  const isInRange = (dayISO, startISO, endISO) => {
    if (!dayISO || !startISO || !endISO) return false;
    return dayISO >= startISO && dayISO <= endISO;
  };

  // Helper: Get the current preview range for date picker
  const getDatePickerPreviewRange = () => {
    if (dateRangeDragging && dateRangeDragAnchor && dateRangeDragHover) {
      return clampRange(dateRangeDragAnchor, dateRangeDragHover);
    }
    if (form.timeOffStart && form.timeOffEnd) {
      return clampRange(form.timeOffStart, form.timeOffEnd);
    }
    return null;
  };

  // Helper: Build calendar grid for a month
  const buildCalendarGrid = (year, month) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    // Get the day of week for the first day (0 = Sunday, 1 = Monday, etc.)
    // Adjust so Monday = 0
    let startDayOfWeek = firstDay.getDay();
    startDayOfWeek = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1; // Convert Sunday=0 to Monday=0 system
    
    const days = [];
    
    // Add empty cells for days before the first of the month
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add the actual days
    for (let d = 1; d <= daysInMonth; d++) {
      const dateObj = new Date(year, month, d);
      days.push({
        date: dateObj,
        iso: dateToISO(dateObj),
        day: d
      });
    }
    
    return days;
  };

  // Helper: Commit date range from picker to form
  const commitDateRange = (startISO, endISO) => {
    const range = clampRange(startISO, endISO);
    setForm(prev => ({
      ...prev,
      timeOffStart: range.start,
      timeOffEnd: range.end
    }));
  };

  // Helper: Reset date range picker state
  const resetDateRangePicker = () => {
    setDateRangeDragging(false);
    setDateRangeDragAnchor(null);
    setDateRangeDragHover(null);
  };

  // ========================================================================
  // COLLECT EXPORT CANDIDATES FROM ALL BRIO SOURCES
  // ========================================================================
  
  // Main function: Collect all items that should be synced to MS Calendar
  const getUnsyncedBrioExportItems = async (pastDateStr, futureDateStr) => {
    const items = [];
    
    console.log('[MS EXPORT] Collecting items from all BRIO sources...');
    
    // ----------------------------------------
    // 1. CALENDAR_EVENTS (events category)
    // ----------------------------------------
    try {
      const { data: calendarEvents } = await supabase
        .from('calendar_events')
        .select('*')
        .is('deleted_at', null)
        .gte('date', pastDateStr)
        .lte('date', futureDateStr);
      
      (calendarEvents || []).forEach(e => {
        items.push({
          eventType: toSyncedEventType(e.category || 'events'),
          sourceKey: `calendar_events:${e.id}`,
          sourceTable: 'calendar_events',
          sourceId: e.id,
          payload: {
            title: e.title || 'Calendar Event',
            description: e.description || '',
            startDate: e.start_date || e.date,
            endDate: e.end_date || e.start_date || e.date,
            startTime: e.start_time,
            endTime: e.end_time,
            isAllDay: e.is_all_day || false
          },
          existingMsEventId: e.ms_calendar_event_id // calendar_events has this field
        });
      });
      console.log(`   - calendar_events: ${calendarEvents?.length || 0} items`);
    } catch (err) {
      console.error('Error fetching calendar_events:', err);
    }
    
    // ----------------------------------------
    // 2. TIME_OFF_REQUESTS (leaves, pendingLeaves)
    // ----------------------------------------
    try {
      // Fetch without FK join — users table relationship not available via PostgREST
      const { data: timeOffRequests } = await supabase
        .from('time_off_requests')
        .select('*')
        .gte('start_date', pastDateStr)
        .lte('end_date', futureDateStr);
      
      // Resolve user names client-side from activeUsers (consistent with rest of app)
      const userLookup = {};
      (activeUsers || []).forEach(u => { userLookup[u.id] = u.name; });
      
      (timeOffRequests || []).forEach(t => {
        const isPending = MS_PENDING_LEAVE_STATUSES.has(t.status);
        const isApproved = t.status === 'approved';
        
        // Only sync pending and approved leaves
        if (!isPending && !isApproved) return;
        
        const eventType = isPending ? 'pendingLeaves' : 'leaves';
        const userName = userLookup[t.user_id] || 'Team Member';
        const leaveType = t.leave_type || t.type || 'Leave';
        
        items.push({
          eventType,
          sourceKey: `time_off_requests:${t.id}`,
          sourceTable: 'time_off_requests',
          sourceId: t.id,
          payload: {
            title: `${userName} - ${leaveType}${isPending ? ' (Pending)' : ''}`,
            description: t.reason || '',
            startDate: t.start_date,
            endDate: t.end_date,
            startTime: null,
            endTime: null,
            isAllDay: true
          },
          existingMsEventId: null // Will check ms_synced_items
        });
      });
      console.log(`   - time_off_requests: ${timeOffRequests?.length || 0} items`);
    } catch (err) {
      console.error('Error fetching time_off_requests:', err);
    }
    
    // ----------------------------------------
    // 3. PUBLIC HOLIDAYS (holidays)
    // ----------------------------------------
    try {
      const { data: publicHolidays } = await supabase
        .from('holidays')
        .select('*')
        .gte('date', pastDateStr)
        .lte('date', futureDateStr);
      
      (publicHolidays || []).forEach(h => {
        items.push({
          eventType: 'holidays',
          sourceKey: `holidays:${h.id || h.date}`,
          sourceTable: 'holidays',
          sourceId: h.id,
          payload: {
            title: h.name || h.title || 'Public Holiday',
            description: h.description || '',
            startDate: h.date,
            endDate: h.date,
            startTime: null,
            endTime: null,
            isAllDay: true
          },
          existingMsEventId: null
        });
      });
      console.log(`   - holidays: ${publicHolidays?.length || 0} items`);
    } catch (err) {
      // Table might not exist
      console.log('   - holidays: table not found or empty');
    }
    
    // ----------------------------------------
    // 4 & 5. PROJECT MILESTONES + INVOICING from stageTimeline
    // ----------------------------------------
    // stageTimeline structure per project:
    //   { stageKey: { targetDate, contractDate, actualDate, clientApprovalDate,
    //                 invoiceDate, paidDate, invoicePercent,
    //                 partStages: [{ id, name, targetDate, contractDate, actualDate, ... }] } }
    //
    // Milestones: targetDate, contractDate, actualDate (event_type='milestones')
    // Invoicing:  invoiceDate, paidDate               (event_type='invoicing')
    //
    // source_key format:
    //   project:{id}:stage:{stageKey}:{field}
    //   project:{id}:stage:{stageKey}:part:{partId}:{field}
    //
    // projectScanOk gates the stale-milestone reconciliation in the deletion
    // stage (Source D). It is only set true once the projects fetch + item
    // build below complete without throwing, so a failed/empty fetch can never
    // be mistaken for "all project milestones removed" and trigger mass deletes.
    let projectScanOk = false;
    try {
      const { data: projects, error: projError } = await supabase
        .from('projects')
        .select('id, name, project_code, stage_timeline')
        .not('stage_timeline', 'is', null);
      
      if (projError) {
        console.error('[MS EXPORT] projects query error:', projError);
        throw projError;
      }
      console.log(`[MS EXPORT] Projects with stage_timeline: ${(projects || []).length}`);

      // Stage key → human-readable name
      const STAGE_NAMES = {
        advance: 'Advance',
        cd_ad: 'CD AD', cd_id: 'CD ID',
        sd_ad: 'SD AD', sd_id: 'SD ID',
        dd_ad: 'DD AD', dd_id: 'DD ID',
        aoc_ad: 'AoC AD', aoc_id: 'AoC ID',
        td_ad: 'TD AD', td_id: 'TD ID',
        gfc1: 'GFC 1', gfc2: 'GFC 2', gfc3: 'GFC 3',
        completion: 'Completion', cc: 'CC'
      };

      // Which date fields map to which event_type
      const MILESTONE_DATE_FIELDS = [
        { field: 'targetDate',   label: 'Target',   eventType: 'milestones' },
        { field: 'contractDate', label: 'Contract',  eventType: 'milestones' },
        { field: 'actualDate',   label: 'Completed', eventType: 'milestones' },
      ];
      const INVOICING_DATE_FIELDS = [
        { field: 'invoiceDate',  label: 'Invoice',  eventType: 'invoicing' },
        { field: 'paidDate',     label: 'Paid',     eventType: 'invoicing' },
      ];
      const ALL_DATE_FIELDS = [...MILESTONE_DATE_FIELDS, ...INVOICING_DATE_FIELDS];

      let milestoneCount = 0;
      let invoicingCount = 0;
      let skippedNoData = 0;
      let skippedNotObject = 0;
      let skippedNoDate = 0;
      let skippedOutOfRange = 0;

      (projects || []).forEach(p => {
        if (!p.stage_timeline) { skippedNoData++; return; }
        
        const timeline = typeof p.stage_timeline === 'string'
          ? JSON.parse(p.stage_timeline)
          : p.stage_timeline;
        
        const projLabel = p.project_code || p.name;
        const stageKeys = Object.keys(timeline);
        
        // Debug: log first project's structure
        if (milestoneCount === 0 && invoicingCount === 0 && stageKeys.length > 0) {
          const firstKey = stageKeys[0];
          const firstVal = timeline[firstKey];
          console.log(`[MS EXPORT] Sample project "${projLabel}" (id=${p.id}):`,
            `${stageKeys.length} stage keys, first="${firstKey}", type=${typeof firstVal}`,
            firstVal && typeof firstVal === 'object' ? `fields: ${Object.keys(firstVal).join(',')}` : `value: ${JSON.stringify(firstVal)?.substring(0, 80)}`
          );
        }
        
        Object.entries(timeline).forEach(([stageKey, stageData]) => {
          // stageData must be an object (not a string or null)
          if (!stageData || typeof stageData !== 'object') { skippedNotObject++; return; }
          
          const stageName = STAGE_NAMES[stageKey] || stageKey;
          
          // --- Process main stage date fields ---
          ALL_DATE_FIELDS.forEach(({ field, label, eventType }) => {
            const dateStr = stageData[field];
            if (!dateStr || typeof dateStr !== 'string') { skippedNoDate++; return; }
            // Date range filter
            if (dateStr < pastDateStr || dateStr > futureDateStr) { skippedOutOfRange++; return; }
            
            items.push({
              eventType,
              sourceKey: `project:${p.id}:stage:${stageKey}:${field}`,
              sourceTable: 'projects',
              sourceId: p.id,
              payload: {
                title: `${projLabel}: ${stageName} ${label}`,
                description: `${eventType === 'invoicing' ? 'Invoice' : 'Milestone'}: ${p.name} — ${stageName} ${label}`,
                startDate: dateStr,
                endDate: dateStr,
                startTime: null,
                endTime: null,
                isAllDay: true
              },
              existingMsEventId: null
            });
            if (eventType === 'milestones') milestoneCount++;
            else invoicingCount++;
          });
          
          // --- Process partStages (sub-stages within each stage) ---
          const parts = stageData.partStages;
          if (Array.isArray(parts)) {
            parts.forEach(part => {
              if (!part || !part.id) return;
              const partLabel = part.name || part.id;
              
              ALL_DATE_FIELDS.forEach(({ field, label, eventType }) => {
                const dateStr = part[field];
                if (!dateStr || typeof dateStr !== 'string') return;
                if (dateStr < pastDateStr || dateStr > futureDateStr) return;
                
                items.push({
                  eventType,
                  sourceKey: `project:${p.id}:stage:${stageKey}:part:${part.id}:${field}`,
                  sourceTable: 'projects',
                  sourceId: p.id,
                  payload: {
                    title: `${projLabel}: ${stageName} — ${partLabel} ${label}`,
                    description: `${eventType === 'invoicing' ? 'Invoice' : 'Milestone'}: ${p.name} — ${stageName} / ${partLabel} ${label}`,
                    startDate: dateStr,
                    endDate: dateStr,
                    startTime: null,
                    endTime: null,
                    isAllDay: true
                  },
                  existingMsEventId: null
                });
                if (eventType === 'milestones') milestoneCount++;
                else invoicingCount++;
              });
            });
          }
        });
      });
      
      console.log(`   - project milestones: ${milestoneCount} items`);
      console.log(`   - project invoicing: ${invoicingCount} items`);
      console.log(`   - skip stats: noData=${skippedNoData}, notObject=${skippedNotObject}, noDate=${skippedNoDate}, outOfRange=${skippedOutOfRange}`);
      if (milestoneCount + invoicingCount > 0) {
        // Log first few source_keys for debugging
        const sampleKeys = items
          .filter(i => i.sourceKey.startsWith('project:'))
          .slice(0, 3)
          .map(i => i.sourceKey);
        console.log(`   - sample source_keys:`, sampleKeys);
      }
      // Item build completed without throwing — safe to reconcile stale
      // project milestones against this `items` set in the deletion stage.
      projectScanOk = true;
    } catch (err) {
      console.error('Error fetching project milestones/invoicing:', err);
    }
    
    // ----------------------------------------
    // 6. ANNIVERSARIES (work anniversaries + join dates + leaving dates)
    // ----------------------------------------
    try {
      const { data: users } = await supabase
        .from('users')
        .select('id, name, join_date, is_active')
        .not('join_date', 'is', null);
      
      // Also fetch dob and leaving_date from user_profiles
      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('user_id, dob, leaving_date');
      
      const profileMap = {};
      (profiles || []).forEach(p => {
        profileMap[p.user_id] = {
          dob: p.dob || null,
          leavingDate: p.leaving_date || null,
        };
      });
      
      const currentYear = new Date().getFullYear();
      let anniversaryCount = 0;
      
      // Only include active employees (is_active defaults to true when null/undefined)
      (users || []).filter(u => u.is_active !== false).forEach(u => {
        if (!u.join_date) return;
        
        const joinDate = new Date(u.join_date);
        const yearsEmployed = currentYear - joinDate.getFullYear();
        
        // 6a. Work Anniversary (yearly recurring, only if >0 years)
        if (yearsEmployed > 0) {
          const anniversaryDate = `${currentYear}-${String(joinDate.getMonth() + 1).padStart(2, '0')}-${String(joinDate.getDate()).padStart(2, '0')}`;
          
          if (anniversaryDate >= pastDateStr && anniversaryDate <= futureDateStr) {
            items.push({
              eventType: 'anniversaries',
              sourceKey: `users:${u.id}:anniversary:${currentYear}`,
              sourceTable: 'users',
              sourceId: u.id,
              payload: {
                title: `${u.name} - ${yearsEmployed} Year${yearsEmployed > 1 ? 's' : ''} Anniversary`,
                description: `Work anniversary: ${yearsEmployed} year${yearsEmployed > 1 ? 's' : ''} at BRIO`,
                startDate: anniversaryDate,
                endDate: anniversaryDate,
                startTime: null,
                endTime: null,
                isAllDay: true
              },
              existingMsEventId: null
            });
            anniversaryCount++;
          }
        }
        
        // 6b. Join Date (one-time event)
        const joinDateStr = u.join_date.split('T')[0];
        if (joinDateStr >= pastDateStr && joinDateStr <= futureDateStr) {
          items.push({
            eventType: 'anniversaries',
            sourceKey: `users:${u.id}:joining`,
            sourceTable: 'users',
            sourceId: u.id,
            payload: {
              title: `${u.name} Joining`,
              description: `${u.name} joins BRIO`,
              startDate: joinDateStr,
              endDate: joinDateStr,
              startTime: null,
              endTime: null,
              isAllDay: true
            },
            existingMsEventId: null
          });
          anniversaryCount++;
        }
        
        // 6c. Leaving Date / Last Day (one-time event)
        const leavingDate = profileMap[u.id]?.leavingDate;
        if (leavingDate) {
          const leaveDateStr = leavingDate.split('T')[0];
          if (leaveDateStr >= pastDateStr && leaveDateStr <= futureDateStr) {
            items.push({
              eventType: 'anniversaries',
              sourceKey: `users:${u.id}:lastday`,
              sourceTable: 'user_profiles',
              sourceId: u.id,
              payload: {
                title: `${u.name} Last Day`,
                description: `${u.name}'s last day at BRIO`,
                startDate: leaveDateStr,
                endDate: leaveDateStr,
                startTime: null,
                endTime: null,
                isAllDay: true
              },
              existingMsEventId: null
            });
            anniversaryCount++;
          }
        }
      });
      console.log(`   - anniversaries (work anniv + joining + last day): ${anniversaryCount} items`);
    } catch (err) {
      console.error('Error fetching anniversaries:', err);
    }
    
    // ----------------------------------------
    // 7. BIRTHDAYS (yearly recurring from user_profiles.dob)
    // ----------------------------------------
    try {
      const { data: profilesForBdays } = await supabase
        .from('user_profiles')
        .select('user_id, dob');
      
      // Need user names + is_active to filter out inactive employees
      const { data: allUsers } = await supabase
        .from('users')
        .select('id, name, is_active');
      
      const userNameMap = {};
      const userActiveMap = {};
      (allUsers || []).forEach(u => {
        userNameMap[u.id] = u.name;
        userActiveMap[u.id] = u.is_active;
      });
      
      const currentYear = new Date().getFullYear();
      let birthdayCount = 0;
      
      (profilesForBdays || []).forEach(p => {
        if (!p.dob) return;
        // Only include active employees (is_active defaults to true when null/undefined)
        if (userActiveMap[p.user_id] === false) return;
        
        const dob = new Date(p.dob);
        if (isNaN(dob.getTime())) return;
        
        let birthMonth = dob.getMonth();
        let birthDay = dob.getDate();
        
        // Feb 29 handling: show on Feb 28 in non-leap years
        if (birthMonth === 1 && birthDay === 29) {
          const isLeapYear = (currentYear % 4 === 0 && currentYear % 100 !== 0) || (currentYear % 400 === 0);
          if (!isLeapYear) birthDay = 28;
        }
        
        const birthdayDate = `${currentYear}-${String(birthMonth + 1).padStart(2, '0')}-${String(birthDay).padStart(2, '0')}`;
        
        if (birthdayDate < pastDateStr || birthdayDate > futureDateStr) return;
        
        const userName = userNameMap[p.user_id] || 'Team Member';
        
        items.push({
          eventType: 'birthdays',
          sourceKey: `user_profiles:${p.user_id}:birthday:${currentYear}`,
          sourceTable: 'user_profiles',
          sourceId: p.user_id,
          payload: {
            title: `${userName} Birthday`,
            description: `${userName}'s birthday`,
            startDate: birthdayDate,
            endDate: birthdayDate,
            startTime: null,
            endTime: null,
            isAllDay: true
          },
          existingMsEventId: null
        });
        birthdayCount++;
      });
      console.log(`   - birthdays: ${birthdayCount} items`);
    } catch (err) {
      console.error('Error fetching birthdays:', err);
    }

    // ----------------------------------------
    // 8. CLIENT ANNIVERSARIES (yearly recurring from clients.anniversary
    //    + each entry in clients.additional_contacts[].anniversary).
    //    Pushed into the existing BRIO — Anniversaries calendar alongside
    //    employee anniversaries.
    // ----------------------------------------
    try {
      const { data: clientsForAnniv } = await supabase
        .from('clients')
        .select('id, name, anniversary, additional_contacts');

      const currentYear = new Date().getFullYear();
      let clientAnnivCount = 0;

      (clientsForAnniv || []).forEach(c => {
        const clientLabel = c.name || `Client ${c.id}`;
        const contacts = [];
        if (c.anniversary) {
          contacts.push({ name: clientLabel, anniversary: c.anniversary, key: 'primary' });
        }
        if (Array.isArray(c.additional_contacts)) {
          c.additional_contacts.forEach((ac, i) => {
            if (ac && ac.anniversary) {
              contacts.push({ name: ac.name || `Contact ${i + 1}`, anniversary: ac.anniversary, key: `c${i}` });
            }
          });
        }

        contacts.forEach(({ name, anniversary, key }) => {
          const dt = new Date(anniversary);
          if (isNaN(dt.getTime())) return;
          const yearsElapsed = currentYear - dt.getFullYear();
          if (yearsElapsed <= 0) return; // skip the original year itself
          let m = dt.getMonth();
          let d = dt.getDate();
          if (m === 1 && d === 29) {
            const isLeapYear = (currentYear % 4 === 0 && currentYear % 100 !== 0) || (currentYear % 400 === 0);
            if (!isLeapYear) d = 28;
          }
          const annDate = `${currentYear}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
          if (annDate < pastDateStr || annDate > futureDateStr) return;

          const yearLabel = `${yearsElapsed} Year${yearsElapsed > 1 ? 's' : ''} Anniversary`;
          const isPrimary = key === 'primary';
          const title = isPrimary
            ? `${name} ${yearLabel} (Client)`
            : `${name} (${clientLabel}) ${yearLabel}`;

          items.push({
            eventType: 'anniversaries',
            sourceKey: `clients:${c.id}:${key}:anniversary:${currentYear}`,
            sourceTable: 'clients',
            sourceId: c.id,
            payload: {
              title,
              description: `Client anniversary: ${name} (${clientLabel}) — ${yearLabel}`,
              startDate: annDate,
              endDate: annDate,
              startTime: null,
              endTime: null,
              isAllDay: true
            },
            existingMsEventId: null
          });
          clientAnnivCount++;
        });
      });
      console.log(`   - client anniversaries: ${clientAnnivCount} items`);
    } catch (err) {
      console.error('Error fetching client anniversaries:', err);
    }

    // ----------------------------------------
    // 9. CLIENT BIRTHDAYS (yearly recurring from clients.dob
    //    + each entry in clients.additional_contacts[].dob).
    //    Pushed into the existing BRIO — Birthdays calendar alongside
    //    employee birthdays.
    // ----------------------------------------
    try {
      const { data: clientsForBdays } = await supabase
        .from('clients')
        .select('id, name, dob, additional_contacts');

      const currentYear = new Date().getFullYear();
      let clientBdayCount = 0;

      (clientsForBdays || []).forEach(c => {
        const clientLabel = c.name || `Client ${c.id}`;
        const contacts = [];
        if (c.dob) {
          contacts.push({ name: clientLabel, dob: c.dob, key: 'primary' });
        }
        if (Array.isArray(c.additional_contacts)) {
          c.additional_contacts.forEach((ac, i) => {
            if (ac && ac.dob) {
              contacts.push({ name: ac.name || `Contact ${i + 1}`, dob: ac.dob, key: `c${i}` });
            }
          });
        }

        contacts.forEach(({ name, dob, key }) => {
          const dt = new Date(dob);
          if (isNaN(dt.getTime())) return;
          let m = dt.getMonth();
          let d = dt.getDate();
          if (m === 1 && d === 29) {
            const isLeapYear = (currentYear % 4 === 0 && currentYear % 100 !== 0) || (currentYear % 400 === 0);
            if (!isLeapYear) d = 28;
          }
          const bday = `${currentYear}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
          if (bday < pastDateStr || bday > futureDateStr) return;

          const isPrimary = key === 'primary';
          const title = isPrimary
            ? `${name} Birthday (Client)`
            : `${name} (${clientLabel}) Birthday`;

          items.push({
            eventType: 'birthdays',
            sourceKey: `clients:${c.id}:${key}:birthday:${currentYear}`,
            sourceTable: 'clients',
            sourceId: c.id,
            payload: {
              title,
              description: `Client birthday: ${name} (${clientLabel})`,
              startDate: bday,
              endDate: bday,
              startTime: null,
              endTime: null,
              isAllDay: true
            },
            existingMsEventId: null
          });
          clientBdayCount++;
        });
      });
      console.log(`   - client birthdays: ${clientBdayCount} items`);
    } catch (err) {
      console.error('Error fetching client birthdays:', err);
    }

    console.log(`[MS EXPORT] Total: ${items.length} items collected`);
    // projectScanOk lets the caller's deletion stage safely reconcile stale
    // project milestones — only true when the projects fetch + build above
    // completed without throwing.
    return { items, projectScanOk };
  };

  // Helper: Push event to Microsoft Calendar (create or update)
  // ========================================================================
  // SHARED HELPER: Build validated MS Graph event payload
  // Used by both pushEventToMsCalendar (POST) and updateMsCalendarEvent (PATCH)
  // Validates dates, normalizes times, ensures end > start, handles all-day events.
  // ========================================================================
  const buildMsEventPayload = (eventData) => {
    const { title, description, date, startDate, endDate, startTime, endTime, isAllDay, category } = eventData;
    const msTimeZone = 'W. Europe Standard Time';

    // Helper: Validate YYYY-MM-DD format
    const isValidDateStr = (str) => {
      if (!str || typeof str !== 'string') return false;
      const match = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!match) return false;
      const d = new Date(str + 'T12:00:00Z');
      return !isNaN(d.getTime());
    };

    // Helper: Ensure time string is HH:mm:ss format
    const normalizeTime = (timeStr, defaultTime = '09:00:00') => {
      if (!timeStr || typeof timeStr !== 'string') return defaultTime;
      if (/^\d{2}:\d{2}$/.test(timeStr)) return timeStr + ':00';
      if (/^\d{2}:\d{2}:\d{2}$/.test(timeStr)) return timeStr;
      return defaultTime;
    };

    // Determine start and end date strings
    let startDateStr = startDate || date;
    let endDateStr = endDate || startDate || date;

    // Validate date strings
    if (!isValidDateStr(startDateStr)) {
      throw new Error(`Invalid start date: ${startDateStr}`);
    }
    if (!isValidDateStr(endDateStr)) {
      console.warn('buildMsEventPayload: Invalid endDate, using startDate:', endDateStr);
      endDateStr = startDateStr;
    }

    let startObj, endObj;

    if (isAllDay) {
      // All-day events: use dateTime at midnight, end = day AFTER last day
      // Graph accepts dateTime with T00:00:00 for all-day events
      const endDateObj = new Date(endDateStr + 'T12:00:00Z');
      endDateObj.setDate(endDateObj.getDate() + 1);
      const endDatePlusOneStr = endDateObj.toISOString().split('T')[0];

      startObj = { dateTime: `${startDateStr}T00:00:00.0000000`, timeZone: msTimeZone };
      endObj = { dateTime: `${endDatePlusOneStr}T00:00:00.0000000`, timeZone: msTimeZone };
    } else {
      // Timed events
      const startTimeStr = normalizeTime(startTime, '09:00:00');
      const endTimeStr = normalizeTime(endTime, '10:00:00');

      const startDateTime = `${startDateStr}T${startTimeStr}`;
      let endDateTime = `${endDateStr}T${endTimeStr}`;

      // If end <= start on same day, bump end to next day
      if (endDateStr === startDateStr && endTimeStr <= startTimeStr) {
        console.warn('buildMsEventPayload: endTime <= startTime, bumping endDate to next day');
        const nextDay = new Date(startDateStr + 'T12:00:00Z');
        nextDay.setDate(nextDay.getDate() + 1);
        const nextDayStr = nextDay.toISOString().split('T')[0];
        endDateTime = `${nextDayStr}T${endTimeStr}`;
      }

      startObj = { dateTime: startDateTime, timeZone: msTimeZone };
      endObj = { dateTime: endDateTime, timeZone: msTimeZone };
    }

    const payload = {
      subject: title || 'Untitled Event',
      body: { contentType: 'text', content: description || '' },
      start: startObj,
      end: endObj,
      isAllDay: isAllDay === true
    };

    // Outlook gives an event with no reminder setting its default of 15
    // minutes, which for an all-day event starting at 00:00 rings at 23:45
    // the night before. Timed events keep the default.
    if (payload.isAllDay) {
      payload.isReminderOn = false;
    }

    const categoryName = getCategoryNameForEventType(category);
    if (categoryName) {
      payload.categories = [categoryName];
    }

    return payload;
  };

  const pushEventToMsCalendar = async (eventData, msCalendarId, token) => {
    const msEventPayload = buildMsEventPayload(eventData);

    console.log('MS Calendar: Creating event with payload:', JSON.stringify(msEventPayload, null, 2));

    const response = await msGraphFetch(`https://graph.microsoft.com/v1.0/me/calendars/${msCalendarId}/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(msEventPayload)
    });

    if (!response.ok) {
      let errorData = {};
      try { errorData = await response.json(); } catch (_) { /* empty */ }
      console.error('❌ MS Graph create event failed:', {
        status: response.status,
        error: errorData,
        payload: msEventPayload
      });
      throw new Error(errorData.error?.message || `Failed to create MS event (HTTP ${response.status})`);
    }

    const createdMsEvent = await response.json();
    console.log('✅ MS event created:', createdMsEvent.id);
    return createdMsEvent;
  };

  // Initialize Microsoft calendars (user-triggered or automatic)
  const initializeMicrosoftCalendars = async () => {
    if (!msCalendarConnected) {
      alert('Please connect to Microsoft Calendar first');
      return;
    }

    // Build calendar list dynamically from MS_CALENDAR_TYPES
    const calendarList = Object.values(MS_CALENDAR_TYPES).map(name => `• ${name}`).join('\n');
    const calendarCount = Object.keys(MS_CALENDAR_TYPES).length;
    
    const confirmed = window.confirm(
      `This will ensure ${calendarCount} BRIO calendars exist in your Microsoft Outlook:\n\n` +
      calendarList + '\n\n' +
      'Missing calendars will be created. Continue?'
    );

    if (!confirmed) return;

    try {
      // Get fresh token for this operation
      const token = await getValidToken();
      if (!token) {
        alert('Microsoft token expired. Please reconnect.');
        return;
      }
      
      const result = await ensureMicrosoftCalendarsExist(currentUser.id, token);
      
      if (result.success) {
        // Build detailed success message
        const parts = [];
        
        if (result.verified?.length > 0) {
          parts.push(`✓ Verified: ${result.verified.length} calendar(s) already exist`);
        }
        if (result.created?.length > 0) {
          parts.push(`✓ Created: ${result.created.length} new calendar(s):\n${result.created.map(c => `  • ${c.calendarName}`).join('\n')}`);
        }
        if (result.reused?.length > 0) {
          parts.push(`✓ Linked: ${result.reused.length} existing calendar(s):\n${result.reused.map(c => `  • ${c.calendarName}`).join('\n')}`);
        }
        if (result.repaired?.length > 0) {
          parts.push(`✓ Repaired: ${result.repaired.length} calendar(s) (mapping fixed):\n${result.repaired.map(c => `  • ${c.calendarName}`).join('\n')}`);
        }
        
        const totalInGraph = (result.verified?.length || 0) + (result.created?.length || 0) + 
                           (result.reused?.length || 0) + (result.repaired?.length || 0);
        
        if (parts.length === 0 || (result.verified?.length === calendarCount && parts.length === 1)) {
          alert(`✓ All ${calendarCount} BRIO calendars verified in Outlook.\n\nNo changes needed.`);
        } else {
          alert(`BRIO Calendar Initialization Complete\n\n${parts.join('\n\n')}\n\nTotal: ${totalInGraph}/${calendarCount} calendars ready.`);
        }
      } else {
        const errorMsg = result.errors?.length > 0
          ? result.errors.map(e => `• ${e.eventType}: ${e.error}`).join('\n')
          : result.error || 'Unknown error';
        alert('Some calendars could not be initialized:\n\n' + errorMsg);
      }
    } catch (error) {
      console.error('Initialize calendars error:', error);
      alert('Failed to initialize calendars: ' + error.message);
    }
  };

  // Seed BRIO master categories on the connected mailbox so events we push
  // with categories: [<name>] render in the BRIO color in Outlook. One-shot
  // admin action — re-run if categories are renamed or recoloured in Outlook.
  const seedMsCategoryColors = async () => {
    if (!msCalendarConnected) {
      alert('Please connect to Microsoft Calendar first');
      return;
    }

    const confirmed = window.confirm(
      'This will create or update the BRIO category colors on the connected Outlook mailbox.\n\n' +
      'Existing categories with the same name will be re-coloured to match BRIO.\n\n' +
      'Continue?'
    );
    if (!confirmed) return;

    try {
      const token = await getValidToken();
      if (!token) {
        alert('Microsoft token expired. Please reconnect.');
        return;
      }

      const result = await seedMsMasterCategories(msGraphFetch);

      const parts = [];
      if (result.created > 0) parts.push(`✓ Created: ${result.created}`);
      if (result.updated > 0) parts.push(`✓ Updated colour: ${result.updated}`);
      if (result.unchanged > 0) parts.push(`✓ Unchanged: ${result.unchanged}`);
      if (result.failed > 0) {
        const errs = result.errors.map(e => `  • ${e.name}: ${e.message}`).join('\n');
        parts.push(`✗ Failed: ${result.failed}\n${errs}`);
      }
      alert('BRIO Outlook colors\n\n' + (parts.join('\n') || 'No changes.'));
    } catch (error) {
      console.error('Seed MS category colors error:', error);
      alert('Failed to seed Outlook colors: ' + error.message);
    }
  };

  // Recolor each BRIO calendar in Outlook by PATCHing its `color` property
  // to the closest preset. This is what actually drives the sidebar dots
  // and the default week/month grid event color in Outlook — categories
  // only show up as tags or in "color by category" views.
  const recolorMsCalendars = async () => {
    if (!msCalendarConnected) {
      alert('Please connect to Microsoft Calendar first');
      return;
    }

    const confirmed = window.confirm(
      'Recolor each BRIO calendar in Outlook to its BRIO-mapped preset?\n\n' +
      'Outlook only supports 10 calendar colors, so each mapping is the closest available match. ' +
      'Safe to re-run.'
    );
    if (!confirmed) return;

    try {
      const token = await getValidToken();
      if (!token) {
        alert('Microsoft token expired. Please reconnect.');
        return;
      }

      const mappings = await getBrioCalendarMappings();
      const entries = Object.entries(mappings || {});
      if (entries.length === 0) {
        alert('No BRIO calendars found. Run "Initialize Calendars" first.');
        return;
      }

      const stats = { patched: 0, skipped: 0, failed: 0, notFound: 0 };
      const firstErrors = [];

      for (const [eventType, mapping] of entries) {
        const color = getCalendarColorForEventType(eventType);
        if (!color || !mapping?.calendarId) {
          stats.skipped += 1;
          continue;
        }
        try {
          const resp = await msGraphFetch(
            `https://graph.microsoft.com/v1.0/me/calendars/${mapping.calendarId}`,
            {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ color })
            }
          );
          if (resp.ok) {
            stats.patched += 1;
          } else if (resp.status === 404) {
            stats.notFound += 1;
          } else {
            stats.failed += 1;
            if (firstErrors.length < 3) {
              const txt = await resp.text().catch(() => '');
              firstErrors.push(`${eventType}: ${resp.status} ${txt.slice(0, 120)}`);
            }
          }
        } catch (err) {
          stats.failed += 1;
          if (firstErrors.length < 3) {
            firstErrors.push(`${eventType}: ${err.message}`);
          }
        }
      }

      const parts = [
        `✓ Recoloured: ${stats.patched}`,
        `Skipped (no mapping): ${stats.skipped}`,
        `Not found in Outlook (404): ${stats.notFound}`,
        `Failed: ${stats.failed}`
      ];
      if (firstErrors.length > 0) {
        parts.push('\nFirst errors:\n' + firstErrors.map(e => '  • ' + e).join('\n'));
      }
      alert('Recolor BRIO calendars\n\n' + parts.join('\n'));
    } catch (error) {
      console.error('Recolor MS calendars error:', error);
      alert('Failed to recolor calendars: ' + error.message);
    }
  };

  // Backfill BRIO categories onto events that were pushed to Outlook before
  // the categories field was added to buildMsEventPayload. Walks the
  // ms_synced_items ledger and PATCHes each MS event with the right category
  // for its event_type. Idempotent and safe to re-run.
  const backfillMsCategoryColors = async () => {
    if (!msCalendarConnected) {
      alert('Please connect to Microsoft Calendar first');
      return;
    }

    const confirmed = window.confirm(
      'Backfill BRIO categories onto every Outlook event we have a sync record for?\n\n' +
      'This will PATCH each event with its BRIO category so the color sticks in Outlook. ' +
      'Safe to re-run.'
    );
    if (!confirmed) return;

    try {
      const token = await getValidToken();
      if (!token) {
        alert('Microsoft token expired. Please reconnect.');
        return;
      }

      const currentUpn = await fetchConnectedMsAccountUpn();
      const records = await getExistingSyncRecords(currentUpn);
      const entries = Object.values(records).filter(r => r && r.ms_event_id && r.event_type);

      if (entries.length === 0) {
        alert('No synced events found to backfill.');
        return;
      }

      const stats = { patched: 0, skipped: 0, failed: 0, notFound: 0 };
      const firstErrors = [];

      for (const rec of entries) {
        const categoryName = getCategoryNameForEventType(rec.event_type);
        if (!categoryName) {
          stats.skipped += 1;
          continue;
        }
        try {
          const resp = await msGraphFetch(
            `https://graph.microsoft.com/v1.0/me/events/${rec.ms_event_id}`,
            {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ categories: [categoryName] })
            }
          );
          if (resp.ok) {
            stats.patched += 1;
          } else if (resp.status === 404) {
            stats.notFound += 1;
          } else {
            stats.failed += 1;
            if (firstErrors.length < 3) {
              const txt = await resp.text().catch(() => '');
              firstErrors.push(`${rec.event_type} ${rec.ms_event_id}: ${resp.status} ${txt.slice(0, 120)}`);
            }
          }
        } catch (err) {
          stats.failed += 1;
          if (firstErrors.length < 3) {
            firstErrors.push(`${rec.event_type} ${rec.ms_event_id}: ${err.message}`);
          }
        }
      }

      const parts = [
        `✓ Patched: ${stats.patched}`,
        `Skipped (no category mapping): ${stats.skipped}`,
        `Not found in Outlook (404): ${stats.notFound}`,
        `Failed: ${stats.failed}`
      ];
      if (firstErrors.length > 0) {
        parts.push('\nFirst errors:\n' + firstErrors.map(e => '  • ' + e).join('\n'));
      }
      alert('Backfill complete\n\n' + parts.join('\n'));
    } catch (error) {
      console.error('Backfill MS category colors error:', error);
      alert('Failed to backfill categories: ' + error.message);
    }
  };

  // State for bulk push progress
  const [bulkPushInProgress, setBulkPushInProgress] = useState(false);

  // Helper: Update existing MS Calendar event (PATCH)
  const updateMsCalendarEvent = async (eventData, msCalendarId, msEventId, token) => {
    const msEventPayload = buildMsEventPayload(eventData);

    // Calendar-agnostic: PATCH /me/events/{id} works regardless of which calendar the event is in.
    const response = await msGraphFetch(`https://graph.microsoft.com/v1.0/me/events/${msEventId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(msEventPayload)
    });

    if (!response.ok) {
      let errorData = {};
      try { errorData = await response.json(); } catch (_) { /* empty */ }
      console.error('❌ MS Graph update event failed:', {
        status: response.status,
        error: errorData,
        payload: msEventPayload
      });
      const err = new Error(errorData.error?.message || `Failed to update MS event (HTTP ${response.status})`);
      err.status = response.status;
      throw err;
    }

    return await response.json();
  };

  // Helper: Delete MS Calendar event
  // Calendar-agnostic: DELETE /me/events/{id} works regardless of which calendar owns the event.
  const deleteMsCalendarEvent = async (msCalendarId, msEventId, token) => {
    const response = await msGraphFetch(`https://graph.microsoft.com/v1.0/me/events/${msEventId}`, {
      method: 'DELETE'
    });

    // 204 No Content = success, 404 = already deleted (also ok)
    if (!response.ok && response.status !== 404) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || 'Failed to delete MS event');
    }
    return true;
  };

  // Bulk push/update/delete calendar events to Microsoft (PUSH: App → Outlook)
  // ========================================================================
  // BULK PUSH - Unified export of ALL BRIO sources to Microsoft Calendar
  // ========================================================================
  const bulkPushEventsToMicrosoft = async () => {
    if (!msCalendarConnected) {
      alert('Please connect to Microsoft Calendar first');
      return;
    }

    setBulkPushInProgress(true);

    try {
      const validToken = await getValidToken();
      if (!validToken) {
        alert('Microsoft token expired. Please reconnect.');
        setBulkPushInProgress(false);
        return;
      }

      // Calculate date window: 90 days past to 180 days future
      const pastDate = new Date();
      pastDate.setDate(pastDate.getDate() - 90);
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + 180);

      const pastDateStr = pastDate.toISOString().split('T')[0];
      const futureDateStr = futureDate.toISOString().split('T')[0];

      console.log('========================================');
      console.log('[MS PUSH] Starting unified export to Microsoft Calendar');
      console.log(`[MS PUSH] Date range: ${pastDateStr} to ${futureDateStr}`);
      console.log('========================================');

      // Step 1: Get all calendar mappings
      const mappings = await getBrioCalendarMappings();
      const mappedTypes = Object.keys(mappings);
      
      if (mappedTypes.length === 0) {
        alert('No BRIO calendars found. Please run "Initialize Calendars" first.');
        setBulkPushInProgress(false);
        return;
      }
      
      console.log(`[MS PUSH] Found ${mappedTypes.length} calendar mappings:`, mappedTypes);
      // Log which expected types are MISSING from mappings
      const EXPECTED_TYPES = ['events', 'holidays', 'leaves', 'pendingLeaves', 'milestones', 'invoicing', 'anniversaries', 'birthdays'];
      const missingTypes = EXPECTED_TYPES.filter(t => !mappings[t]);
      if (missingTypes.length > 0) {
        console.warn(`[MS PUSH] ⚠️ Missing mappings for: ${missingTypes.join(', ')} — these events will be SKIPPED. Run "Initialize Calendars" to fix.`);
      }

      // Step 2a: Identify the currently-connected MS account so the ledger
      // is filtered to rows that belong to this account (legacy NULL rows
      // are also returned — they're tagged with the current UPN on first write).
      const currentUpn = await fetchConnectedMsAccountUpn();

      // Step 2b: Get existing sync records from ms_synced_items (UPN-filtered)
      const existingSyncRecords = await getExistingSyncRecords(currentUpn);
      console.log(`[MS PUSH] Found ${Object.keys(existingSyncRecords).length} existing sync records (account=${currentUpn || 'unknown'})`);

      // Step 3: Collect all items from all BRIO sources
      const { items: exportItems, projectScanOk } = await getUnsyncedBrioExportItems(pastDateStr, futureDateStr);
      
      // Log per-type breakdown so we can see exactly what was collected
      const itemsByType = {};
      exportItems.forEach(i => { itemsByType[i.eventType] = (itemsByType[i.eventType] || 0) + 1; });
      console.log(`[MS PUSH] Export items by type:`, itemsByType);
      
      if (exportItems.length === 0) {
        console.log('[MS PUSH] No live items in date range — checking for deletions...');
      }

      // Step 4: Categorize items by sync status
      const toCreate = [];
      const toUpdate = [];
      
      for (const item of exportItems) {
        const syncRecord = existingSyncRecords[msLedgerKey(item.eventType, item.sourceKey)];
        const existingMsEventId = item.existingMsEventId || syncRecord?.ms_event_id;
        
        if (existingMsEventId) {
          // Has MS event ID - check if content changed
          const newHash = createContentHash(
            item.payload.title,
            item.payload.startDate,
            item.payload.endDate,
            item.payload.isAllDay,
            item.payload.description,
            item.payload.startTime,
            item.payload.endTime
          );
          
          if (syncRecord?.content_hash !== newHash) {
            toUpdate.push({ ...item, msEventId: existingMsEventId, newHash });
          }
          // If hash matches, skip (already synced and unchanged)
        } else {
          // No MS event ID - need to create
          toCreate.push(item);
        }
      }

      // Step 4b: Pre-scan for deleted calendar_events that need Outlook cleanup
      let pendingDeletionCount = 0;
      try {
        const { data: deletedCalEvents, error: delErr } = await supabase
          .from('calendar_events')
          .select('id, ms_calendar_event_id')
          .not('deleted_at', 'is', null);
        
        if (!delErr && deletedCalEvents) {
          // Count those with a known ms_event_id (either on the row or in sync records)
          const deletedWithMsId = deletedCalEvents.filter(e => e.ms_calendar_event_id);
          pendingDeletionCount = deletedWithMsId.length;
          
          // Also check sync records for additional mappings
          if (pendingDeletionCount === 0) {
            const deletedIds = new Set(deletedCalEvents.map(e => e.id));
            Object.values(existingSyncRecords).forEach(record => {
              const sk = record.source_key;
              if (!sk || !sk.startsWith('calendar_events:')) return;
              if (!record.ms_event_id) return;
              const eid = Number(sk.replace('calendar_events:', ''));
              if (deletedIds.has(eid)) pendingDeletionCount++;
            });
          }
        }
      } catch (_) { /* non-fatal pre-scan */ }

      // Step 4c: Pre-count stale project milestones (mirrors Source D in the
      // deletion stage). Without this, a sync whose only remaining work is
      // removing phantom milestones (cleared/out-of-window stage dates) would
      // see toCreate/toUpdate/pendingDeletionCount all zero, hit the
      // "All items are already synced" early-return below, and never reach the
      // deletion stage — so the stale Outlook events would never be cleaned up.
      // Gated on projectScanOk so a failed/empty export can't inflate the count.
      if (projectScanOk) {
        const desiredProjectKeys = new Set(
          exportItems
            .filter(i => typeof i.sourceKey === 'string' && i.sourceKey.startsWith('project:'))
            .map(i => i.sourceKey)
        );
        let staleProjectPreCount = 0;
        Object.values(existingSyncRecords).forEach(record => {
          const sourceKey = record.source_key;
          if (!sourceKey || !sourceKey.startsWith('project:')) return;
          if (!record.ms_event_id) return;
          if (desiredProjectKeys.has(sourceKey)) return;
          staleProjectPreCount++;
        });
        if (staleProjectPreCount > 0) {
          console.log(`[MS PUSH] Stale project milestones detected (pre-scan): ${staleProjectPreCount}`);
          pendingDeletionCount += staleProjectPreCount;
        }
      }

      const totalActions = toCreate.length + toUpdate.length + pendingDeletionCount;
      
      console.log(`[MS PUSH] Actions required:`);
      console.log(`   - To create: ${toCreate.length}`);
      console.log(`   - To update: ${toUpdate.length}`);
      console.log(`   - To delete: ~${pendingDeletionCount}`);

      if (totalActions === 0) {
        alert('All items are already synced. No changes needed.');
        setBulkPushInProgress(false);
        return;
      }

      // Build summary by event type
      const summaryByType = {};
      VALID_MS_EVENT_TYPES.forEach(t => { summaryByType[t] = { create: 0, update: 0 }; });
      toCreate.forEach(i => { summaryByType[i.eventType].create++; });
      toUpdate.forEach(i => { summaryByType[i.eventType].update++; });

      // Build confirmation message
      let confirmMsg = `MS Calendar Sync Summary:\n\n`;
      confirmMsg += `📊 By Category:\n`;
      VALID_MS_EVENT_TYPES.forEach(type => {
        const counts = summaryByType[type];
        if (counts.create > 0 || counts.update > 0) {
          const calName = MS_CALENDAR_TYPES[type];
          confirmMsg += `• ${calName}: `;
          const parts = [];
          if (counts.create > 0) parts.push(`${counts.create} new`);
          if (counts.update > 0) parts.push(`${counts.update} update`);
          confirmMsg += parts.join(', ') + '\n';
        }
      });
      confirmMsg += `\nTotal: ${toCreate.length} create, ${toUpdate.length} update`;
      if (pendingDeletionCount > 0) confirmMsg += `, ~${pendingDeletionCount} delete`;
      confirmMsg += `\nDate range: ${pastDateStr} to ${futureDateStr}\n\n`;
      confirmMsg += `Continue?`;

      if (!window.confirm(confirmMsg)) {
        setBulkPushInProgress(false);
        return;
      }

      // Step 5: Execute sync operations
      const stats = {
        created: 0, updated: 0, skipped: 0, failed: 0, deleted: 0, deleteFailed: 0,
        byCategory: {}
      };
      let firstError = null;

      const trackStat = (eventType, action) => {
        if (!stats.byCategory[eventType]) {
          stats.byCategory[eventType] = { created: 0, updated: 0, skipped: 0, failed: 0, deleted: 0, deleteFailed: 0 };
        }
        stats.byCategory[eventType][action]++;
      };

      // CREATE new items
      for (const item of toCreate) {
        try {
          const mapping = mappings[item.eventType];
          if (!mapping) {
            console.warn(`   ⚠️ No mapping for "${item.eventType}", skipping: ${item.payload.title}`);
            stats.skipped++;
            trackStat(item.eventType, 'skipped');
            continue;
          }

          const eventData = {
            title: item.payload.title,
            description: item.payload.description,
            date: item.payload.startDate,
            startDate: item.payload.startDate,
            endDate: item.payload.endDate,
            startTime: item.payload.startTime,
            endTime: item.payload.endTime,
            isAllDay: item.payload.isAllDay,
            category: item.eventType
          };

          // Step 1: Create event in MS Graph
          let msEvent;
          try {
            msEvent = await pushEventToMsCalendar(eventData, mapping.calendarId, validToken);
          } catch (graphErr) {
            stats.failed++;
            trackStat(item.eventType, 'failed');
            if (!firstError) firstError = `Graph create: ${graphErr.message}`;
            console.error(`   ❌ Graph create failed: ${item.payload.title} -`, graphErr.message);
            continue;
          }

          if (!msEvent?.id) {
            stats.failed++;
            trackStat(item.eventType, 'failed');
            if (!firstError) firstError = 'Graph returned no event ID';
            console.error(`   ❌ Graph create failed: ${item.payload.title} - No event ID returned`);
            continue;
          }

          // Step 2: Create content hash (safe - never throws)
          const contentHash = createContentHash(
            item.payload.title,
            item.payload.startDate,
            item.payload.endDate,
            item.payload.isAllDay,
            item.payload.description,
            item.payload.startTime,
            item.payload.endTime
          );
          
          // Step 3: Persist sync mapping (CRITICAL - must not fail silently)
          try {
            await upsertMsSyncedItem(
              item.eventType,
              item.sourceKey,
              mapping.calendarId,
              msEvent.id,
              contentHash,
              currentUpn
            );
          } catch (persistErr) {
            // Event was created in Outlook but mapping failed to save
            // Log error but count as created (it exists in Outlook)
            console.error(`   ⚠️ Persist mapping failed: ${item.payload.title} -`, persistErr.message);
            console.error(`      Event ${msEvent.id} exists in Outlook but may re-sync next time`);
          }

          // Step 4: Also update calendar_events if that's the source
          if (item.sourceTable === 'calendar_events') {
            try {
              await supabase
                .from('calendar_events')
                .update({
                  ms_calendar_event_id: msEvent.id,
                  ms_synced_at: new Date().toISOString()
                })
                .eq('id', item.sourceId);
            } catch (calEvtErr) {
              console.warn(`   ⚠️ calendar_events update failed (non-fatal):`, calEvtErr.message);
            }
          }

          stats.created++;
          trackStat(item.eventType, 'created');
          console.log(`   ✅ Created: ${item.payload.title} → ${MS_CALENDAR_TYPES[item.eventType]}`);
        } catch (err) {
          stats.failed++;
          trackStat(item.eventType, 'failed');
          if (!firstError) firstError = err.message;
          console.error(`   ❌ Create failed: ${item.payload.title} -`, err.message);
        }
      }

      // UPDATE existing items (with 404 repair logic)
      for (const item of toUpdate) {
        try {
          const mapping = mappings[item.eventType];
          if (!mapping) {
            stats.skipped++;
            trackStat(item.eventType, 'skipped');
            continue;
          }

          const eventData = {
            title: item.payload.title,
            description: item.payload.description,
            date: item.payload.startDate,
            startDate: item.payload.startDate,
            endDate: item.payload.endDate,
            startTime: item.payload.startTime,
            endTime: item.payload.endTime,
            isAllDay: item.payload.isAllDay,
            category: item.eventType
          };

          // Try to update existing event
          let updateSucceeded = false;
          let needsRecreate = false;
          
          try {
            await updateMsCalendarEvent(eventData, mapping.calendarId, item.msEventId, validToken);
            updateSucceeded = true;
          } catch (updateErr) {
            // Check if it's a 404 (event not found in Outlook) — use status code first, message as fallback
            const is404 = updateErr.status === 404 ||
                          updateErr.message?.includes('not found') || 
                          updateErr.message?.includes('Not Found') ||
                          updateErr.message?.includes('does not exist') ||
                          updateErr.message?.includes('404');
            
            if (is404) {
              console.warn(`   ⚠️ Update 404: ${item.payload.title} - Event not in Outlook, will recreate`);
              needsRecreate = true;
            } else {
              // Non-404 error - fail normally
              throw updateErr;
            }
          }

          if (updateSucceeded) {
            // Update sync record
            try {
              await upsertMsSyncedItem(
                item.eventType,
                item.sourceKey,
                mapping.calendarId,
                item.msEventId,
                item.newHash,
                currentUpn
              );
            } catch (persistErr) {
              console.warn(`   ⚠️ Persist mapping failed (non-fatal):`, persistErr.message);
            }

            stats.updated++;
            trackStat(item.eventType, 'updated');
            console.log(`   ✅ Updated: ${item.payload.title} → ${MS_CALENDAR_TYPES[item.eventType]}`);
          } else if (needsRecreate) {
            // 404 REPAIR: Delete stale mapping, create fresh event
            console.log(`   🔄 Repairing: ${item.payload.title} - Deleting stale mapping and recreating`);
            
            // Step 1: Delete stale sync record
            try {
              await deleteMsSyncedItem(item.sourceKey);
            } catch (delErr) {
              console.warn(`   ⚠️ Failed to delete stale mapping:`, delErr.message);
            }

            // Step 2: Create fresh event in Outlook
            let msEvent;
            try {
              msEvent = await pushEventToMsCalendar(eventData, mapping.calendarId, validToken);
            } catch (createErr) {
              stats.failed++;
              trackStat(item.eventType, 'failed');
              if (!firstError) firstError = `Repair create: ${createErr.message}`;
              console.error(`   ❌ Repair create failed: ${item.payload.title} -`, createErr.message);
              continue;
            }

            if (!msEvent?.id) {
              stats.failed++;
              trackStat(item.eventType, 'failed');
              console.error(`   ❌ Repair create failed: ${item.payload.title} - No event ID returned`);
              continue;
            }

            // Step 3: Save new sync mapping
            const newHash = createContentHash(
              item.payload.title,
              item.payload.startDate,
              item.payload.endDate,
              item.payload.isAllDay,
              item.payload.description,
              item.payload.startTime,
              item.payload.endTime
            );
            
            try {
              await upsertMsSyncedItem(
                item.eventType,
                item.sourceKey,
                mapping.calendarId,
                msEvent.id,
                newHash,
                currentUpn
              );
            } catch (persistErr) {
              console.warn(`   ⚠️ Persist new mapping failed:`, persistErr.message);
            }

            // Count as "repaired" (created due to 404)
            stats.created++;
            trackStat(item.eventType, 'created');
            console.log(`   ✅ Repaired (recreated): ${item.payload.title} → ${MS_CALENDAR_TYPES[item.eventType]}`);
          }
        } catch (err) {
          stats.failed++;
          trackStat(item.eventType, 'failed');
          if (!firstError) firstError = err.message;
          console.error(`   ❌ Update failed: ${item.payload.title} -`, err.message);
        }
      }

      // Refresh calendar events
      if (stats.created > 0 || stats.updated > 0) {
        await supabaseCalendarEvents.refetch();
      }

      // ========================================
      // DELETION STAGE: Remove Outlook events for app-deleted items
      // ========================================
      // Strategy: Query calendar_events with deleted_at IS NOT NULL, then
      // cross-reference with ms_synced_items to find Outlook events to delete.
      // Sources A–C target explicitly deleted / deactivated items. Source D
      // additionally reconciles project:* milestones against the freshly built
      // export set so cleared/out-of-window stage dates don't leave phantom
      // Outlook events. Source D is gated on projectScanOk to avoid the mass-
      // deletion hazard of treating an empty/failed export as "all removed".
      try {
        console.log('[MS PUSH] Starting deletion pass...');
        
        // 1. Find soft-deleted calendar_events that have an ms_calendar_event_id
        const { data: deletedCalEvents } = await supabase
          .from('calendar_events')
          .select('id, title, ms_calendar_event_id, deleted_at')
          .not('deleted_at', 'is', null);
        
        // 2. Also check ms_synced_items for any source_key starting with "calendar_events:"
        //    where the underlying calendar_event has been deleted
        const deletedEventIds = new Set((deletedCalEvents || []).map(e => e.id));
        
        // Build deletion candidates from both sources
        const toDelete = [];
        
        // Source A: calendar_events with ms_calendar_event_id set directly
        (deletedCalEvents || []).forEach(e => {
          if (e.ms_calendar_event_id) {
            toDelete.push({
              sourceKey: `calendar_events:${e.id}`,
              msEventId: e.ms_calendar_event_id,
              title: e.title || 'Unknown Event',
              eventType: 'events',
              source: 'calendar_events.ms_calendar_event_id'
            });
          }
        });
        
        // Source B: ms_synced_items for deleted calendar_events
        //   (handles cases where ms_calendar_event_id wasn't set on the row
        //    but the sync record exists)
        const existingRecordsForDeletion = await getExistingSyncRecords(currentUpn);
        Object.values(existingRecordsForDeletion).forEach(record => {
          const sourceKey = record.source_key;
          if (!sourceKey || !sourceKey.startsWith('calendar_events:')) return;
          if (!record.ms_event_id) return;
          // Extract the event ID from source_key format "calendar_events:{id}"
          const eventId = sourceKey.replace('calendar_events:', '');
          const numericId = Number(eventId);
          if (!deletedEventIds.has(numericId) && !deletedEventIds.has(eventId)) return;
          // Avoid duplicates (already added from Source A)
          if (toDelete.some(d => d.msEventId === record.ms_event_id)) return;
          toDelete.push({
            sourceKey,
            msEventId: record.ms_event_id,
            title: `Synced item ${sourceKey}`,
            eventType: record.event_type || 'events',
            source: 'ms_synced_items'
          });
        });

        // Source C: Inactive employees' birthday/anniversary/joining/lastday events
        //   If an employee becomes inactive, their previously synced events should be removed from Outlook.
        //   source_key patterns:
        //     users:{id}:anniversary:{year}     (work anniversary)
        //     users:{id}:joining                 (join date)
        //     users:{id}:lastday                 (leaving date)
        //     user_profiles:{id}:birthday:{year} (birthday)
        try {
          const { data: inactiveUsers } = await supabase
            .from('users')
            .select('id, name')
            .eq('is_active', false);
          
          if (inactiveUsers && inactiveUsers.length > 0) {
            const inactiveIds = new Set(inactiveUsers.map(u => u.id));
            const inactiveNameMap = {};
            inactiveUsers.forEach(u => { inactiveNameMap[u.id] = u.name; });
            
            // Scan ms_synced_items for any records matching inactive user source_keys
            Object.values(existingRecordsForDeletion).forEach(record => {
              const sourceKey = record.source_key;
              if (!sourceKey) return;
              if (!record.ms_event_id) return;
              // Already queued?
              if (toDelete.some(d => d.msEventId === record.ms_event_id)) return;
              
              // Match users:{id}:anniversary, users:{id}:joining, users:{id}:lastday
              const userMatch = sourceKey.match(/^users:(\d+):/);
              if (userMatch && inactiveIds.has(Number(userMatch[1]))) {
                toDelete.push({
                  sourceKey,
                  msEventId: record.ms_event_id,
                  title: `${inactiveNameMap[Number(userMatch[1])] || 'Inactive user'} (${sourceKey})`,
                  eventType: record.event_type || 'anniversaries',
                  source: 'inactive_user'
                });
                return;
              }
              
              // Match user_profiles:{id}:birthday
              const profileMatch = sourceKey.match(/^user_profiles:(\d+):/);
              if (profileMatch && inactiveIds.has(Number(profileMatch[1]))) {
                toDelete.push({
                  sourceKey,
                  msEventId: record.ms_event_id,
                  title: `${inactiveNameMap[Number(profileMatch[1])] || 'Inactive user'} (${sourceKey})`,
                  eventType: record.event_type || 'birthdays',
                  source: 'inactive_user'
                });
              }
            });
            console.log(`[MS PUSH] Inactive employees checked: ${inactiveUsers.length}`);
          }
        } catch (inactiveErr) {
          console.warn('[MS PUSH] Inactive user deletion scan failed (non-fatal):', inactiveErr.message);
        }

        // Source D: Stale project milestone/invoicing events
        //   The push only (re)creates/updates Outlook events for stage dates
        //   that are currently present AND within the export window. When a
        //   stage date is cleared to null (e.g. AAS), or moved outside the
        //   window (e.g. KK's CD AD target pushed back to a past date), the
        //   live calendar drops it but the Outlook event lingers — surfacing a
        //   phantom milestone the project tracker no longer has. Reconcile by
        //   deleting any project:* synced item the current export no longer
        //   produces. The desired set is keyed by source_key (field-based, not
        //   date-based), so an in-window date *change* updates the existing
        //   event rather than deleting it here.
        //
        //   Scoped strictly to source_key LIKE 'project:%' and gated on
        //   projectScanOk so a failed/empty projects fetch can never be read as
        //   "all milestones removed" and mass-delete the calendar.
        if (projectScanOk) {
          const desiredProjectKeys = new Set(
            exportItems
              .filter(i => typeof i.sourceKey === 'string' && i.sourceKey.startsWith('project:'))
              .map(i => i.sourceKey)
          );
          let staleProjectCount = 0;
          Object.values(existingRecordsForDeletion).forEach(record => {
            const sourceKey = record.source_key;
            if (!sourceKey || !sourceKey.startsWith('project:')) return;
            if (!record.ms_event_id) return;
            // Still a live, in-window milestone — leave it (handled by upsert).
            if (desiredProjectKeys.has(sourceKey)) return;
            // Avoid duplicates with earlier sources.
            if (toDelete.some(d => d.msEventId === record.ms_event_id)) return;
            toDelete.push({
              sourceKey,
              msEventId: record.ms_event_id,
              title: `Stale milestone (${sourceKey})`,
              eventType: record.event_type || 'milestones',
              source: 'stale_project_milestone'
            });
            staleProjectCount++;
          });
          console.log(`[MS PUSH] Stale project milestones queued for deletion: ${staleProjectCount}`);
        } else {
          console.warn('[MS PUSH] Skipping stale project milestone reconciliation (project scan did not complete)');
        }

        console.log(`[MS PUSH] Deletion candidates: ${toDelete.length}`);
        if (toDelete.length > 0) {
          console.log(`[MS PUSH] First 3:`, toDelete.slice(0, 3).map(d => `${d.sourceKey} → ${d.msEventId}`));
        }

        // 3. Execute deletions
        for (const item of toDelete) {
          try {
            console.log(`   🗑️ Deleting: ${item.title} (${item.msEventId})`);
            // Calendar-agnostic: DELETE /me/events/{id}
            await deleteMsCalendarEvent(null, item.msEventId, validToken);
            
            // Clean up sync record
            try {
              await deleteMsSyncedItem(item.sourceKey);
            } catch (_) { /* non-fatal */ }
            
            // Clear ms_calendar_event_id on calendar_events row
            if (item.source === 'calendar_events.ms_calendar_event_id') {
              const eventId = item.sourceKey.replace('calendar_events:', '');
              try {
                await supabase
                  .from('calendar_events')
                  .update({ ms_calendar_event_id: null })
                  .eq('id', eventId);
              } catch (_) { /* non-fatal */ }
            }
            
            stats.deleted++;
            trackStat(item.eventType, 'deleted');
            console.log(`   ✅ Deleted from Outlook: ${item.title}`);
          } catch (delErr) {
            stats.deleteFailed++;
            trackStat(item.eventType, 'deleteFailed');
            console.error(`   ❌ Delete failed: ${item.title} (${item.msEventId}):`, delErr.message);
            if (!firstError) firstError = `Graph delete: ${delErr.message}`;
          }
        }
        
        if (toDelete.length > 0) {
          console.log(`[MS PUSH] Deletion pass complete: ${stats.deleted} deleted, ${stats.deleteFailed} failed`);
        } else {
          console.log('[MS PUSH] No deletions needed');
        }
      } catch (delStageErr) {
        console.error('[MS PUSH] Deletion stage error:', delStageErr);
        // Don't fail the whole push for deletion errors
      }

      // Log summary
      console.log('========================================');
      console.log('[MS PUSH] Sync Complete');
      console.log('========================================');
      console.log(`Created: ${stats.created}`);
      console.log(`Updated: ${stats.updated}`);
      console.log(`Deleted: ${stats.deleted}`);
      console.log(`Skipped: ${stats.skipped}`);
      console.log(`Failed: ${stats.failed}${stats.deleteFailed > 0 ? ` (+ ${stats.deleteFailed} delete failures)` : ''}`);
      console.log('');
      console.log('[MS PUSH] By Calendar:');
      Object.entries(stats.byCategory).forEach(([eventType, catStats]) => {
        const calendarName = MS_CALENDAR_TYPES[eventType] || eventType;
        const parts = [];
        if (catStats.created > 0) parts.push(`${catStats.created} created`);
        if (catStats.updated > 0) parts.push(`${catStats.updated} updated`);
        if (catStats.deleted > 0) parts.push(`${catStats.deleted} deleted`);
        if (catStats.failed > 0) parts.push(`${catStats.failed} failed`);
        if (catStats.deleteFailed > 0) parts.push(`${catStats.deleteFailed} delete failed`);
        if (parts.length > 0) {
          console.log(`- ${calendarName}: ${parts.join(', ')}`);
        }
      });

      // Show summary alert
      let summary = `MS Calendar Sync Complete!\n\n`;
      summary += `✓ Created: ${stats.created}\n`;
      summary += `✓ Updated: ${stats.updated}\n`;
      summary += `🗑 Deleted: ${stats.deleted}\n`;
      summary += `⊘ Skipped: ${stats.skipped}\n`;
      summary += `✗ Failed: ${stats.failed}`;
      if (stats.deleteFailed > 0) {
        summary += ` (+ ${stats.deleteFailed} delete failures)`;
      }
      if (firstError) {
        summary += `\n\nFirst error: ${firstError}`;
      }
      alert(summary);

    } catch (error) {
      console.error('[MS PUSH] Error:', error);
      alert('Bulk push failed: ' + error.message);
    } finally {
      setBulkPushInProgress(false);
    }
  };

  // Sync calendar events from Microsoft (PULL: Outlook → App)
  const syncMsCalendar = async (token = null, { silent = false } = {}) => {
    setMsSyncInProgress(true);
    
    try {
      const validToken = token || await getValidToken();
      
      if (!validToken) {
        if (!silent) {
          alert('Please connect to Microsoft Calendar first');
        } else {
          console.warn('[MS Calendar] Background sync skipped — no valid token');
          setMsAuthNeedsReconnect(true);
        }
        setMsSyncInProgress(false);
        return;
      }

      const startDate = new Date();
      startDate.setDate(startDate.getDate() - msSyncSettings.syncPastDays);
      
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + msSyncSettings.syncFutureDays);

      const startDateTime = startDate.toISOString();
      const endDateTime = endDate.toISOString();

      console.log('📥 MS Calendar PULL Sync Starting...');
      console.log(`   Date range: ${startDateTime.split('T')[0]} to ${endDateTime.split('T')[0]}`);

      // Helper: Normalize MS Graph event dates (handles both dateTime and date formats)
      const normalizeMsEventDate = (dateObj) => {
        if (!dateObj) return null;
        
        // All-day events use "date" field: { date: "2025-01-29" }
        // Timed events use "dateTime" field: { dateTime: "2025-01-29T10:00:00.0000000", timeZone: "..." }
        let dateStr = dateObj.dateTime || dateObj.date || null;
        
        if (!dateStr) return null;
        
        // If it's a date-only string (all-day), add time component
        if (dateStr.length === 10) {
          dateStr = dateStr + 'T00:00:00Z';
        }
        
        // Add Z suffix if missing (MS returns dates without timezone suffix)
        if (dateStr && !dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('-', 10)) {
          dateStr = dateStr + 'Z';
        }
        
        const parsed = new Date(dateStr);
        return isNaN(parsed.getTime()) ? null : parsed;
      };

      // ========================================
      // STEP 1: Fetch from DEFAULT calendar (meetings/appointments)
      // ========================================
      const defaultUrl = `https://graph.microsoft.com/v1.0/me/calendarview?` +
        `startDateTime=${startDateTime}&endDateTime=${endDateTime}` +
        `&$select=subject,start,end,location,categories,isAllDay,organizer,attendees` +
        `&$orderby=start/dateTime` +
        `&$top=1000`;

      const defaultResponse = await msGraphFetch(defaultUrl, {
        headers: { 'Content-Type': 'application/json' }
      }, { accessToken: validToken });

      if (!defaultResponse.ok) {
        throw new Error(`Failed to fetch default calendar events (HTTP ${defaultResponse.status})`);
      }

      const defaultData = await defaultResponse.json();
      
      // Normalize default calendar events (category: 'msCalendar' for generic appointments)
      const defaultEvents = (defaultData.value || []).map(event => {
        const startDateObj = normalizeMsEventDate(event.start);
        const endDateObj = normalizeMsEventDate(event.end);
        
        if (!startDateObj || !endDateObj) {
          console.warn('MS Calendar: Invalid date for default event:', event.id, event.start, event.end);
          return null;
        }
        
        return {
          id: event.id,
          subject: event.subject,
          start: startDateObj.toISOString(),
          end: endDateObj.toISOString(),
          location: event.location?.displayName || '',
          categories: event.categories || [],
          isAllDay: event.isAllDay || false,
          organizer: event.organizer?.emailAddress?.name || '',
          attendees: event.attendees?.map(a => a.emailAddress?.name).filter(Boolean) || [],
          // Mark as generic MS calendar event
          category: 'msCalendar',
          source: 'ms',
          msCalendarId: 'default'
        };
      }).filter(Boolean);

      console.log(`   📅 Default calendar: ${defaultEvents.length} events`);

      // ========================================
      // STEP 2: Fetch BRIO category calendars from ms_calendar_mappings
      // ========================================
      let categoryEvents = [];
      const syncStats = { default: defaultEvents.length };
      
      try {
        // Get all calendar mappings for current user
        const validPullUserId = validateMsCalendarUserId(currentUser.id, 'syncMsCalendar:pullMappings');
        const { data: mappings, error: mappingsError } = await supabase
          .from('ms_calendar_mappings')
          .select('event_type, ms_calendar_id, ms_calendar_name')
          .eq('user_id', validPullUserId);

        if (mappingsError) {
          console.warn('   ⚠️ Could not fetch calendar mappings:', mappingsError);
        } else if (mappings && mappings.length > 0) {
          console.log(`   📋 Found ${mappings.length} BRIO calendar mappings`);
          
          // Fetch events from each mapped calendar
          for (const mapping of mappings) {
            try {
              const calendarUrl = `https://graph.microsoft.com/v1.0/me/calendars/${mapping.ms_calendar_id}/calendarview?` +
                `startDateTime=${startDateTime}&endDateTime=${endDateTime}` +
                `&$select=subject,start,end,location,categories,isAllDay,organizer,attendees` +
                `&$orderby=start/dateTime` +
                `&$top=500`;

              const calResponse = await msGraphFetch(calendarUrl, {
                headers: { 'Content-Type': 'application/json' }
              }, { accessToken: validToken });

              if (!calResponse.ok) {
                console.warn(`   ⚠️ ${mapping.event_type}: Failed to fetch (${calResponse.status})`);
                syncStats[mapping.event_type] = 'error';
                continue;
              }

              const calData = await calResponse.json();
              const calEvents = (calData.value || []).map(event => {
                const startDateObj = normalizeMsEventDate(event.start);
                const endDateObj = normalizeMsEventDate(event.end);
                
                if (!startDateObj || !endDateObj) {
                  return null;
                }
                
                return {
                  id: event.id,
                  subject: event.subject,
                  start: startDateObj.toISOString(),
                  end: endDateObj.toISOString(),
                  location: event.location?.displayName || '',
                  categories: event.categories || [],
                  isAllDay: event.isAllDay || false,
                  organizer: event.organizer?.emailAddress?.name || '',
                  attendees: event.attendees?.map(a => a.emailAddress?.name).filter(Boolean) || [],
                  // Set category to the BRIO event type so it shows under correct filter
                  category: mapping.event_type,
                  source: 'ms',
                  msCalendarId: mapping.ms_calendar_id,
                  msCalendarName: mapping.ms_calendar_name
                };
              }).filter(Boolean);

              syncStats[mapping.event_type] = calEvents.length;
              console.log(`   📅 ${mapping.event_type} (${mapping.ms_calendar_name}): ${calEvents.length} events`);
              categoryEvents.push(...calEvents);
            } catch (calError) {
              console.error(`   ❌ ${mapping.event_type}: Error -`, calError.message);
              syncStats[mapping.event_type] = 'error';
            }
          }
        } else {
          console.log('   ⚠️ No BRIO calendar mappings found. Run "Initialize Calendars" first.');
        }
      } catch (mappingFetchError) {
        console.error('   ❌ Error fetching mappings:', mappingFetchError);
      }

      // ========================================
      // STEP 3: Merge all events, avoiding duplicates
      // ========================================
      const allEvents = [...defaultEvents];
      const seenIds = new Set(defaultEvents.map(e => e.id));
      
      for (const event of categoryEvents) {
        if (!seenIds.has(event.id)) {
          allEvents.push(event);
          seenIds.add(event.id);
        }
      }

      setMsCalendarEvents(allEvents);
      setMsLastSync(new Date().toISOString());

      // Refresh the ledger in step with the events. The calendar view needs both
      // to decide which pulled events are this app's own output (already drawn
      // from the database) and which someone typed straight into a BRIO calendar.
      try {
        const { ids, ok } = await loadMsSyncedEventIds();
        setMsSyncedEventIds(ids);
        setMsLedgerLoaded(ok);
        console.log(`[MS SYNC] Ledger: ${ids.length} tracked event ids (ok=${ok})`);
      } catch (ledgerErr) {
        setMsLedgerLoaded(false);
        console.warn('[MS SYNC] Ledger load failed; BRIO calendars stay hidden:', ledgerErr.message);
      }
      
      // Persist last sync time to integration status for non-admin visibility
      if (useSupabaseDB) {
        updateMsCalendarIntegrationStatus({
          lastSyncAt: new Date().toISOString()
        }).catch(() => { /* silent — non-critical */ });
      }
      
      // Auto-create time entries if enabled (only for default calendar events)
      if (msSyncSettings.autoCreateEntries) {
        autoCreateEntriesFromEvents(defaultEvents);
      }
      
      // Log summary in required format
      console.log('[MS SYNC] Pulled:');
      Object.entries(syncStats).forEach(([type, count]) => {
        if (type !== 'default') {
          const calendarName = MS_CALENDAR_TYPES[type] || type;
          console.log(`- ${calendarName}: ${count}`);
        }
      });
      if (syncStats.default > 0) {
        console.log(`- Default Calendar: ${syncStats.default}`);
      }
      console.log(`Total: ${allEvents.length} events`);
      
    } catch (error) {
      console.error('Calendar sync error:', error);
      alert('Failed to sync calendar. Please try again or reconnect.');
    } finally {
      setMsSyncInProgress(false);
    }
  };

  // Auto-create time entries from calendar events
  const autoCreateEntriesFromEvents = (events) => {
    if (!msSyncSettings.defaultProjectId || !msSyncSettings.defaultTaskId) {
      console.log('Skipping auto-create: default project or task not configured');
      return;
    }

    const newEntries = [];
    
    events.forEach(event => {
      const existingEntry = timeEntries.find(e => e.msCalendarEventId === event.id);
      if (existingEntry) return;

      if (msSyncSettings.onlyBillableCategories.length > 0) {
        const hasMatchingCategory = event.categories.some(cat => 
          msSyncSettings.onlyBillableCategories.includes(cat)
        );
        if (!hasMatchingCategory) return;
      }

      if (event.isAllDay) return;

      const start = new Date(event.start);
      const end = new Date(event.end);
      const durationSeconds = Math.floor((end - start) / 1000);

      const entry = {
        id: Date.now() + Math.random(),
        userId: currentUser.id,
        projectId: msSyncSettings.defaultProjectId,
        taskId: msSyncSettings.defaultTaskId,
        description: event.subject,
        duration: durationSeconds,
        date: start.toISOString().split('T')[0],
        billable: true,
        notes: `Auto-imported from Microsoft Calendar\nLocation: ${event.location}\nAttendees: ${event.attendees.join(', ')}`,
        approved: false,
        msCalendarEventId: event.id,
        msCalendarSynced: true
      };

      newEntries.push(entry);
    });

    if (newEntries.length > 0) {
      setTimeEntries(prev => [...prev, ...newEntries]);
      console.log(`Auto-created ${newEntries.length} time entries from calendar events`);
      alert(`Created ${newEntries.length} time entries from your calendar!`);
    }
  };

  // Disconnect Microsoft Calendar
  // Disconnect Microsoft Calendar
  // Options:
  //   { confirm: true }  — user-initiated, shows confirm dialog (default)
  //   { confirm: false, reason: '...' } — programmatic, no dialogs
  const disconnectMsCalendar = ({ confirm: needsConfirm = true, reason = '' } = {}) => {
    if (needsConfirm && !window.confirm('Are you sure you want to disconnect Microsoft Calendar?')) {
      return;
    }
    
    if (!needsConfirm && reason) {
      console.warn(`[MS Calendar] Programmatic disconnect: ${reason}`);
    }
    
    setMsCalendarConnected(false);
    setMsAccessToken(null);
    setMsTokenExpiry(null);
    setMsCalendarEvents([]);
    setMsLastSync(null);
    setMsWebhookSubscription(null);
    setMsWebhookEnabled(false);
    setMsCalendarName(null);
    setMsAuthNeedsReconnect(true);
    // Update integration status in Supabase so non-admin users see disconnected
    if (useSupabaseDB) {
      updateMsCalendarIntegrationStatus({ isConnected: false }).catch(err => {
        console.error('[MS Calendar] Failed to update integration status on disconnect:', err);
      });
      // Clear the server-side stored tokens (fire-and-forget).
      if (currentUser?.id) {
        supabase.functions.invoke('ms-auth', { body: { action: 'disconnect' } })
          .catch(err => console.warn('[MS Calendar] Failed to clear server tokens on disconnect:', err));
      }
    }
    
    if (needsConfirm) {
      alert('Disconnected from Microsoft Calendar');
    }
  };

  // ==========================================
  // EVENT-BASED SYNC (WEBHOOKS)
  // ==========================================

  // Create webhook subscription for real-time calendar updates
  const createWebhookSubscription = async () => {
    // Guard 1: ignore overlapping calls from rapid repeated clicks. Set before
    // any await so it blocks synchronously within the same tick.
    if (msWebhookCreatingRef.current) {
      console.log('[Webhook] Creation already in progress — ignoring duplicate request');
      return;
    }
    // Guard 2: don't create a second subscription when one is already active.
    if (msWebhookEnabled && msWebhookSubscription?.id) {
      console.log('[Webhook] Subscription already active — skipping create');
      return;
    }
    msWebhookCreatingRef.current = true;
    try {
      const validToken = await getValidToken();

      if (!validToken) {
        alert('Please connect to Microsoft Calendar first');
        return;
      }

      // Guard 3: a subscription may already exist server-side (e.g. created in
      // another tab) even if local state is empty. Reuse it instead of adding a
      // duplicate. Best-effort — a query failure falls through to creation.
      if (useSupabaseDB && currentUser?.id) {
        try {
          const { data: existing } = await supabase
            .from('ms_graph_subscriptions')
            .select('id, expiration_datetime, client_state')
            .eq('user_id', currentUser.id)
            .eq('resource', '/me/events')
            .gt('expiration_datetime', new Date().toISOString())
            .order('expiration_datetime', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (existing?.id) {
            setMsWebhookSubscription({
              id: existing.id,
              expirationDateTime: existing.expiration_datetime,
              clientState: existing.client_state,
              createdAt: new Date().toISOString()
            });
            setMsWebhookEnabled(true);
            console.log('[Webhook] Reusing existing active subscription:', existing.id);
            return;
          }
        } catch (lookupErr) {
          console.warn('[Webhook] Existing-subscription lookup failed:', lookupErr);
        }
      }

      // Use environment variable for webhook URL (must be public HTTPS)
      const notificationUrl = process.env.REACT_APP_MS_WEBHOOK_URL || '';
      
      // Validate webhook URL before creating subscription
      if (!notificationUrl) {
        alert('Webhook URL not configured.\n\nSet REACT_APP_MS_WEBHOOK_URL in your environment to a public HTTPS endpoint.\n\nExample: https://yourproject.supabase.co/functions/v1/ms-webhook');
        return;
      }
      if (!notificationUrl.startsWith('https://')) {
        alert('Webhook URL must use HTTPS.\n\nMicrosoft Graph requires a publicly accessible HTTPS endpoint.\n\nCurrent URL: ' + notificationUrl);
        return;
      }
      if (notificationUrl.includes('localhost')) {
        alert('Webhook URL cannot use localhost.\n\nMicrosoft Graph requires a publicly accessible endpoint.\n\nCurrent URL: ' + notificationUrl);
        return;
      }
      
      // Create subscription that expires in 3 days (max allowed by Microsoft)
      const expirationDateTime = new Date();
      expirationDateTime.setDate(expirationDateTime.getDate() + 3);

      const clientState = crypto.randomUUID();
      const subscription = {
        changeType: 'created,updated,deleted',
        notificationUrl: notificationUrl,
        resource: '/me/events',
        expirationDateTime: expirationDateTime.toISOString(),
        clientState: clientState
      };

      const response = await msGraphFetch('https://graph.microsoft.com/v1.0/subscriptions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(subscription)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Failed to create webhook subscription');
      }

      const data = await response.json();
      
      // Store subscription in Supabase for server-side renewal
      if (useSupabaseDB) {
        try {
          await supabase.from('ms_graph_subscriptions').upsert({
            id: data.id,
            user_id: currentUser.id,
            resource: '/me/events',
            expiration_datetime: data.expirationDateTime,
            client_state: clientState,
            updated_at: new Date().toISOString()
          }, { onConflict: 'id' });
        } catch (dbErr) {
          console.warn('[Webhook] Failed to persist subscription to DB:', dbErr);
        }
      }
      
      setMsWebhookSubscription({
        id: data.id,
        expirationDateTime: data.expirationDateTime,
        clientState: clientState,
        createdAt: new Date().toISOString()
      });
      
      setMsWebhookEnabled(true);
      
      console.log('✅ Webhook subscription created:', data.id);
      alert('Event-based sync enabled! Calendar will update in real-time.');
      
    } catch (error) {
      console.error('Webhook subscription error:', error);
      alert(`Failed to enable event-based sync: ${error.message}`);
    } finally {
      msWebhookCreatingRef.current = false;
    }
  };

  // Renew webhook subscription (must be done before it expires)
  const renewWebhookSubscription = async () => {
    if (!msWebhookSubscription) return;

    try {
      const expirationDateTime = new Date();
      expirationDateTime.setDate(expirationDateTime.getDate() + 3);

      const response = await msGraphFetch(`https://graph.microsoft.com/v1.0/subscriptions/${msWebhookSubscription.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          expirationDateTime: expirationDateTime.toISOString()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to renew webhook subscription');
      }

      const data = await response.json();
      
      // Update in Supabase
      if (useSupabaseDB) {
        try {
          await supabase.from('ms_graph_subscriptions').update({
            expiration_datetime: data.expirationDateTime,
            updated_at: new Date().toISOString()
          }).eq('id', msWebhookSubscription.id);
        } catch (dbErr) {
          console.warn('[Webhook] Failed to update subscription in DB:', dbErr);
        }
      }
      
      setMsWebhookSubscription({
        ...msWebhookSubscription,
        expirationDateTime: data.expirationDateTime
      });
      
      console.log('✅ Webhook subscription renewed');
      
    } catch (error) {
      console.error('Webhook renewal error:', error);
      // If renewal fails, try to create a new subscription
      console.log('Attempting to create new subscription...');
      await deleteWebhookSubscription();
      await createWebhookSubscription();
    }
  };

  // Delete webhook subscription
  const deleteWebhookSubscription = async () => {
    if (!msWebhookSubscription) return;

    try {
      await msGraphFetch(`https://graph.microsoft.com/v1.0/subscriptions/${msWebhookSubscription.id}`, {
        method: 'DELETE'
      });

      // Also delete from Supabase
      if (useSupabaseDB) {
        try {
          await supabase.from('ms_graph_subscriptions').delete().eq('id', msWebhookSubscription.id);
        } catch (dbErr) {
          console.warn('[Webhook] Failed to delete subscription from DB:', dbErr);
        }
      }

      setMsWebhookSubscription(null);
      setMsWebhookEnabled(false);
      
      console.log('✅ Webhook subscription deleted');
      
    } catch (error) {
      console.error('Webhook deletion error:', error);
      // Even if deletion fails, clear local state
      setMsWebhookSubscription(null);
      setMsWebhookEnabled(false);
    }
  };

  // Handle incoming webhook notification (called by your backend)
  const _handleWebhookNotification = async (notification) => {
    try {
      console.log('📬 Webhook notification received:', notification);
      
      // Validate the notification
      if (notification.clientState !== msWebhookSubscription?.clientState) {
        console.error('Invalid clientState in webhook notification');
        return;
      }

      // Process each change
      for (const change of notification.value) {
        console.log(`Change type: ${change.changeType}, Resource: ${change.resource}`);
        
        // Instead of fetching the specific changed event,
        // do a quick targeted sync (more efficient than full sync)
        await syncMsCalendar();
      }
      
    } catch (error) {
      console.error('Webhook notification handling error:', error);
    }
  };

  // Auto-renew webhook subscription before it expires
  useEffect(() => {
    if (msWebhookEnabled && msWebhookSubscription) {
      const checkAndRenew = () => {
        const expirationDate = new Date(msWebhookSubscription.expirationDateTime);
        const now = new Date();
        const hoursUntilExpiration = (expirationDate - now) / (1000 * 60 * 60);
        
        // Renew if less than 12 hours until expiration
        if (hoursUntilExpiration < 12) {
          console.log('⏰ Webhook expiring soon, renewing...');
          renewWebhookSubscription();
        }
      };

      // Check every hour
      const interval = setInterval(checkAndRenew, 60 * 60 * 1000);
      
      // Check immediately on mount
      checkAndRenew();

      return () => clearInterval(interval);
    }
  }, [msWebhookEnabled, msWebhookSubscription]); // eslint-disable-line react-hooks/exhaustive-deps

  // ==========================================
  // END EVENT-BASED SYNC
  // ==========================================

  // Create Microsoft Calendar event from time entry
  const _createMsCalendarEvent = async (timeEntry) => {
    try {
      const validToken = await getValidToken();
      
      if (!validToken) {
        alert('Please connect to Microsoft Calendar first');
        return;
      }

      const project = activeProjects.find(p => p.id === timeEntry.projectId);
      const task = activeTasks.find(t => t.id === timeEntry.taskId);
      
      const startDate = new Date(timeEntry.date + 'T09:00:00');
      const endDate = new Date(startDate.getTime() + (timeEntry.duration * 1000));

      const event = {
        subject: `${project?.name || 'Project'} - ${task?.name || 'Task'}: ${timeEntry.description}`,
        body: {
          contentType: 'Text',
          content: timeEntry.notes || ''
        },
        start: {
          dateTime: startDate.toISOString(),
          timeZone: 'UTC'
        },
        end: {
          dateTime: endDate.toISOString(),
          timeZone: 'UTC'
        },
        categories: ['TimeTracker'],
        isReminderOn: false
      };

      const response = await msGraphFetch('https://graph.microsoft.com/v1.0/me/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(event)
      });

      if (!response.ok) throw new Error('Failed to create calendar event');

      const createdEvent = await response.json();
      
      if (useSupabaseDB) {
        await supabaseTimeEntries.updateTimeEntry(timeEntry.id, { 
          msCalendarEventId: createdEvent.id, 
          msCalendarSynced: true 
        });
      } else {
        setTimeEntries(prev => prev.map(e => 
          e.id === timeEntry.id 
            ? { ...e, msCalendarEventId: createdEvent.id, msCalendarSynced: true }
            : e
        ));
      }
      
      alert('Event created in Microsoft Calendar!');
      syncMsCalendar();
      
    } catch (error) {
      console.error('Create event error:', error);
      alert('Failed to create calendar event. Please try again.');
    }
  };

  // Check for OAuth callback on component mount (admin only)
  useEffect(() => {
    if (!isAdmin) return;
    // Check for authorization code in URL (PKCE flow)
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');
    
    if (error) {
      console.error('OAuth error:', error, errorDescription);
      alert('Microsoft login failed: ' + (errorDescription || error));
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }
    
    // Only process the code if we initiated the OAuth flow (verifier present in
    // sessionStorage). The verifier is set in initiateMsCalendarAuth and removed
    // in exchangeCodeForToken, so its presence is a reliable per-flow signal.
    // Using msCalendarConnected here would block account switching (e.g. info@ → projects@).
    if (code && sessionStorage.getItem('ms_code_verifier')) {
      console.log('🔑 Authorization code received!');
      // Clean up URL before exchanging (to prevent double processing)
      window.history.replaceState({}, document.title, window.location.pathname);
      // Exchange code for token
      exchangeCodeForToken(code);
    }
  }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-sync calendar if enabled (admin only)
  useEffect(() => {
    if (!isAdmin) return;
    // No longer gated on msAccessToken (it's null until the first server fetch);
    // syncMsCalendar -> getValidToken fetches an access token from the server.
    if (msAutoSync && msCalendarConnected) {
      const interval = setInterval(() => {
        console.log('Auto-syncing Microsoft Calendar...');
        syncMsCalendar(null, { silent: true });
      }, 15 * 60 * 1000); // Every 15 minutes

      return () => clearInterval(interval);
    }
  }, [isAdmin, msAutoSync, msCalendarConnected]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-sync MS Calendar when any BRIO category filter is enabled and data is stale (admin only)
  useEffect(() => {
    if (!isAdmin) return;
    // Check if any MS-related filter is enabled
    const msFiltersEnabled = categoryFilters.msCalendar || 
      (msCalendarConnected && (
        categoryFilters.holidays || 
        categoryFilters.leaves || 
        categoryFilters.pendingLeaves || 
        categoryFilters.milestones || 
        categoryFilters.invoicing || 
        categoryFilters.anniversaries
      ));
    
    if (msFiltersEnabled && msCalendarConnected && !msSyncInProgress) {
      // Check if we need to sync (no events or stale data)
      const needsSync = msCalendarEvents.length === 0 || 
        !msLastSync || 
        (Date.now() - new Date(msLastSync).getTime()) > 5 * 60 * 1000; // 5 minutes stale threshold
      
      if (needsSync) {
        console.log('MS Calendar: Filter enabled - triggering auto-sync');
        syncMsCalendar(null, { silent: true });
      }
    }
  }, [isAdmin, categoryFilters.msCalendar, categoryFilters.holidays, categoryFilters.leaves, categoryFilters.pendingLeaves, categoryFilters.milestones, categoryFilters.invoicing, categoryFilters.anniversaries, msCalendarConnected]); // eslint-disable-line react-hooks/exhaustive-deps

  // Admin-only: redirect non-admin away from ms-calendar view
  useEffect(() => {
    if (!isAdmin && view === 'ms-calendar') {
      console.warn('[MS Calendar] Non-admin user on ms-calendar view — redirecting to calendar');
      setView('calendar');
    }
  }, [isAdmin, view]); // eslint-disable-line react-hooks/exhaustive-deps

  // ==========================================
  // END MICROSOFT CALENDAR INTEGRATION
  // ==========================================

  const fmtTime = (s) => `${Math.floor(s/3600)}h ${Math.floor((s%3600)/60)}m`;
  
  // ========================================================================
  // CURRENCY DISPLAY HELPERS (Option 1: Display-only conversion)
  // ========================================================================
  // All stored amounts are in baseCurrency (INR). When displayCurrency differs,
  // we convert using FX rates at display time only.
  // ========================================================================
  
  // Get the FX anchor date for a project (for consistent conversion)
  const getProjectFxDate = (project) => {
    if (!project) return null;
    return project.advanceStartDate || project.contractDate || project.startDate || project.created_at || null;
  };
  
  // Format money with conversion to display currency
  // sourceCurrency: the currency the amount is stored in (defaults to baseCurrency)
  // fxDate: date for FX rate lookup (null = use default rate)
  const money = (amount, sourceCurrency, fxDate) => {
    if (settingsHook && settingsHook.formatMoneyInDisplayCurrency) {
      return settingsHook.formatMoneyInDisplayCurrency(
        amount || 0, 
        sourceCurrency || settingsHook.baseCurrency, 
        fxDate
      );
    }
    // Fallback to INR formatting when settingsHook not yet loaded
    if (!Number.isFinite(amount)) amount = 0;
    const rounded = Math.round(amount / 100) * 100;
    return `₹${rounded.toLocaleString('en-IN')}`;
  };
  
  // Legacy fmtCurr wrapper - now uses full conversion
  // For amounts stored in base currency without a specific date
  const fmtCurr = (a, sourceCurrency, fxDate) => {
    return money(a, sourceCurrency, fxDate);
  };

  // Currency symbol helper for prose text (e.g. "per ₹1 salary")
  const currSymbol = (settingsHook && settingsHook.displayCurrency === 'EUR') ? '€' : '₹';
  
  // Format number with Indian thousand separators (for non-currency numbers)
  const _fmtNum = (n) => {
    return Math.round(n).toLocaleString('en-IN');
  };
  
  // Format project name with code prefix (e.g., "A.001 — Project Name")
  const fmtProject = (project) => {
    if (!project) return '';
    const code = project.projectCode || '';
    const name = project.name || '';
    return code ? `${code} \u2014 ${name}` : name;
  };
  
  // Get project status badge styling (uses centralized PROJECT_STATUS_COLORS)
  const getProjectStatusStyle = (status) => {
    const styles = {
      active: { 
        bg: 'bg-[#9f902a]/10', 
        text: 'text-[#9f902a]', 
        border: 'border-[#9f902a]/30', 
        label: 'Active',
        color: PROJECT_STATUS_COLORS.active
      },
      paused: { 
        bg: 'bg-[#934948]/10', 
        text: 'text-[#934948]', 
        border: 'border-[#934948]/30', 
        label: 'Paused',
        color: PROJECT_STATUS_COLORS.paused
      },
      completed: { 
        bg: 'bg-[#689581]/10', 
        text: 'text-[#689581]', 
        border: 'border-[#689581]/30', 
        label: 'Completed',
        color: PROJECT_STATUS_COLORS.completed
      },
      archived: { 
        bg: 'bg-[#f0d794]/20', 
        text: 'text-[#a08840]', 
        border: 'border-[#f0d794]', 
        label: 'Archived',
        color: PROJECT_STATUS_COLORS.archived
      }
    };
    return styles[status] || styles.active;
  };
  
  // Filter projects based on selected status filter (available to all roles)
  const filteredProjects = useMemo(() => {
    if (projectStatusFilter === 'default' || projectStatusFilter === 'all') {
      return activeProjects;
    }
    return activeProjects.filter(p => p.status === projectStatusFilter);
  }, [activeProjects, projectStatusFilter]);

  // Legacy function wrapper (used in several places)
  const getFilteredProjects = () => filteredProjects;
  
  // Check if user can add time entry to a project (based on status)
  const canAddTimeToProject = (projectId) => {
    const project = activeProjects.find(p => p.id === projectId);
    if (!project) return false;
    
    // Admins can add time to any project
    if (isAdmin) return true;
    
    // Non-admins can only add time to active projects
    return project.status === 'active';
  };
  
  // Determine if a project's time entries should default to billable.
  // Non-billable: projects with no client, or with client "BRIO" (internal).
  const isProjectBillable = (projectId) => {
    const project = activeProjects.find(p => p.id === projectId);
    if (!project || !project.clientId) return false;
    const client = activeClients.find(c => c.id === project.clientId);
    if (!client) return false;
    return client.name.toLowerCase() !== 'brio';
  };

  const round = (m) => Math.round(m / settingsHook.roundingInterval) * settingsHook.roundingInterval;

  // ============================================================
  // SALARY REVISION FUNCTIONS
  // ============================================================
  
  // Get effective salary for a user at a specific year and month
  const getEffectiveSalary = (userId, year, month) => {
    const user = activeUsers.find(u => u.id === userId);
    if (!user) return 0;
    
    // If no salary history, use current salary
    if (!user.salaryHistory || user.salaryHistory.length === 0) {
      return user.costRate || 0;
    }
    
    // Find the most recent salary revision that's <= the target date
    const targetDate = `${year}-${String(month).padStart(2, '0')}`;
    const applicableRevisions = user.salaryHistory
      .filter(h => h.effectiveDate <= targetDate)
      .sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate));
    
    if (applicableRevisions.length > 0) {
      return applicableRevisions[0].salary;
    }
    
    // If no applicable revisions, use current salary
    return user.costRate || 0;
  };
  
  // Get salary from matrix history at a specific date
  // Uses both effectiveDate (start) and endDate to find the revision whose
  // date range covers the target date. Falls back to the most-recent-before
  // logic when no endDate-bounded match is found.
  const getSalaryFromMatrixHistory = (jobRole, jobLevel, date) => {
    // If no history, use current matrix
    if (!salaryMatrixHistory || salaryMatrixHistory.length === 0) {
      const roleData = salaryMatrix[jobRole];
      if (!roleData || !roleData[jobLevel]) return 0;
      return roleData[jobLevel].salary || 0;
    }

    // Find revisions where the target date falls within [effectiveDate, endDate]
    // endDate === null means open-ended (still valid)
    const applicableMatrices = salaryMatrixHistory
      .filter(h => h.effectiveDate <= date && (!h.endDate || h.endDate >= date))
      .sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate));

    if (applicableMatrices.length > 0) {
      const matrix = applicableMatrices[0].matrix;
      const roleData = matrix[jobRole];
      if (!roleData || !roleData[jobLevel]) return 0;
      return roleData[jobLevel].salary || 0;
    }

    // Fall back to current matrix if no applicable history
    const roleData = salaryMatrix[jobRole];
    if (!roleData || !roleData[jobLevel]) return 0;
    return roleData[jobLevel].salary || 0;
  };
  
  // Extract NL schaal from role name: "Senior Architect (7)" → "7", "c_1" → "c_1"
  // Also resolves custom role display names (e.g. "Director" → "c_1") via matrix metadata.
  const extractNlSchaalFromRole = (roleName) => {
    if (!roleName) return null;
    // Already a bare schaal key ("7", "c_1", etc.)
    if (/^(c_)?[0-9]{1,2}$/.test(roleName)) return roleName;
    // Parenthesized: "Senior Architect (7)" → "7"
    const m = roleName.match(/\((\d{1,2})\)\s*$/);
    if (m) return m[1];
    // Trailing number after space: "Director 11" → "11"
    const m2 = roleName.match(/\s(\d{1,2})$/);
    if (m2) return m2[1];
    // Fall back: look up custom column names from the latest NL matrix revision
    if (salaryMatrixHistoryNL && salaryMatrixHistoryNL.length > 0) {
      const sorted = [...salaryMatrixHistoryNL].sort((a, b) =>
        (b.effectiveDate || '').localeCompare(a.effectiveDate || '')
      );
      const customCols = sorted[0]?.customColumns || [];
      const match = customCols.find(cc => cc.name === roleName);
      if (match) return match.id; // e.g. "c_1"
    }
    return null;
  };

  // Look up a salary from the NL CAO matrix history by schaal + trede + date.
  // Returns the monthly EUR amount, or 0 if not found.
  const getSalaryFromNLMatrixHistory = (schaal, trede, date) => {
    if (!schaal || trede == null) return 0;
    if (!salaryMatrixHistoryNL || salaryMatrixHistoryNL.length === 0) return 0;

    const applicable = salaryMatrixHistoryNL
      .filter(h => h.effectiveDate <= date && (!h.endDate || h.endDate >= date))
      .sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate));

    if (applicable.length === 0) return 0;

    const matrix = applicable[0].matrix;
    const amount = matrix?.[String(schaal)]?.[String(trede)];
    return typeof amount === 'number' ? amount : 0;
  };

  // Get user profile (jobRole, jobLevel, contractedHours) at a specific date
  const getUserProfileAtDate = (userId, date) => {
    const user = activeUsers.find(u => u.id === userId);
    if (!user) return {
      jobRole: 'Junior Architect',
      jobLevel: 0,
      nlSchaal: null,
      nlTrede: null,
      contractedHours: 176
    };

    // Start with effective profile (from user_role_history, filtered to today).
    // Prefer effectiveJobRole/Level over jobRole/Level because the latter comes
    // from users.job_role which may reflect FUTURE entries or be stale.
    // For NL users: nlSchaal/nlTrede are the source of truth for salary lookup.
    let profile = {
      jobRole: user.effectiveJobRole ?? user.jobRole ?? 'Junior Architect',
      jobLevel: user.effectiveJobLevel ?? user.jobLevel ?? 0,
      nlSchaal: user.effectiveNlSchaal || null,
      nlTrede: user.effectiveNlTrede ?? null,
      contractedHours: user.contractedHours || 176
    };
    
    // If no history, return current
    if (!user.profileHistory || user.profileHistory.length === 0) {
      return profile;
    }
    
    // Find the most recent profile change that's <= the target date
    const applicableChanges = user.profileHistory
      .filter(h => h.effectiveDate <= date)
      .sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate));
    
    if (applicableChanges.length > 0) {
      // Apply the most recent change
      const mostRecent = applicableChanges[0];
      Object.keys(mostRecent.changes).forEach(field => {
        profile[field] = mostRecent.changes[field].to;
      });
    }

    return profile;
  };

  // ── Legacy estimates (used when actual data is not yet in the database) ────
  // Public holidays: actual data exists in the DB from 2026-01 onwards.
  // Before that, use a standard annual estimate spread evenly across 12 months.
  // Leave:           time-off requests are used from 2026-04 onwards.
  // Before that, use company-policy annual leave entitlement (spread evenly).
  //   NL: 30 leave days/year   IN: 15 leave days/year
  // Public holiday estimates (days/year, spread across 12 months):
  //   NL: 11 official Dutch public holidays   IN: 12 standard company holidays
  const HOLIDAY_DB_CUTOFF  = '2026-01'; // YYYY-MM: first month with real holiday data
  const LEAVE_DB_CUTOFF    = '2026-04'; // YYYY-MM: first month with actual leave requests
  const LEGACY_HOLIDAY_DAYS = { NL: 11, IN: 12 }; // standard public holidays/year pre-2026
  const LEGACY_LEAVE_DAYS   = { NL: 30, IN: 15 }; // annual leave days/year before Apr 2026

  // Resolves contracted hours for a user-month, consulting the freelancer
  // override before role history. Returns { hours, source }:
  //   - 'freelancer_approved' → approved freelancer_monthly_hours value
  //   - 'freelancer_pending'  → logged hours snapshot (admin has not yet approved)
  //   - 'role_history'        → role-history contracted_hours (legacy default)
  // Used by all cost-math sites; display sites can keep reading raw values.
  const getContractedHoursForUserMonth = (userId, year, month) => {
    const ymKey = `${year}-${String(month).padStart(2, '0')}`;
    const mapKey = `${String(userId)}|${ymKey}`;
    const approved = freelancerApprovedMap?.get(mapKey);
    if (approved != null) return { hours: approved, source: 'freelancer_approved' };
    const pending = freelancerPendingMap?.get(mapKey);
    if (pending != null) return { hours: pending, source: 'freelancer_pending' };
    const profile = getUserProfileAtDate(userId, `${ymKey}-15`);
    return { hours: profile.contractedHours || 176, source: 'role_history' };
  };

  // Returns contracted hours for a user/month after deducting public holidays
  // and approved leave. Use this as the gap/overhead denominator instead of
  // raw contractedHours. Keep using getUserProfileAtDate().contractedHours for
  // part-time proration (FTE cost × base / 176) — that should not change.
  const getEffectiveContractedHours = (userId, year, month) => {
    const ymKey = `${year}-${String(month).padStart(2, '0')}`;
    const base = getContractedHoursForUserMonth(userId, year, month).hours || 176;

    const user = activeUsers.find(u => u.id === userId);
    const office = user?.officeLocation || 'NL';

    // Scale deductions proportionally for part-timers
    // (a 50% FTE gets half the public holiday benefit)
    const scale = base / 176;

    let holidayHours;
    if (ymKey < HOLIDAY_DB_CUTOFF) {
      // No holiday data in DB before 2026 — use standard annual estimate
      holidayHours = (LEGACY_HOLIDAY_DAYS[office] || 11) / 12 * 8 * scale;
    } else {
      holidayHours = (holidayHoursByOfficeMonth[office]?.[ymKey] || 0) * scale;
    }

    let leaveHours;
    if (ymKey < LEAVE_DB_CUTOFF) {
      // Time-off system not in use before Apr 2026 — use company-policy entitlement
      leaveHours = (LEGACY_LEAVE_DAYS[office] || 15) / 12 * 8 * scale;
    } else {
      leaveHours = (approvedLeaveHoursByUserMonth[userId]?.[ymKey] || 0) * scale;
    }

    return Math.max(0, base - holidayHours - leaveHours);
  };


  // ── Salary Ledger: fetch for year + group by user ──
  const fetchSalaryLedgerForYear = useCallback(async (year) => {
    if (!year) return;
    setSalaryLedgerLoading(true);
    try {
      const rows = await salaryLedgerHook.getAllForYear(year);
      setSalaryLedgerData(rows || []);
      setSalaryLedgerYear(year);
    } catch (err) {
      console.error('[SalaryLedger] Fetch error:', err);
      setSalaryLedgerData([]);
    } finally {
      setSalaryLedgerLoading(false);
    }
  }, [salaryLedgerHook]);

  // Auto-fetch when salary report year changes
  useEffect(() => {
    const year = form.salaryReportYear || new Date().getFullYear();
    if (year !== salaryLedgerYear) {
      fetchSalaryLedgerForYear(year);
    }
  }, [form.salaryReportYear]); // eslint-disable-line react-hooks/exhaustive-deps

  // Group ledger rows: { authUserId: { monthNum: row } }
  const salaryLedgerByUser = useMemo(() => {
    const map = {};
    salaryLedgerData.forEach(row => {
      if (!map[row.auth_user_id]) map[row.auth_user_id] = {};
      const monthNum = new Date(row.month).getMonth() + 1;
      map[row.auth_user_id][monthNum] = row;
    });
    return map;
  }, [salaryLedgerData]);

  // Calculate yearly salary cost with month-by-month breakdown
  const calculateYearlySalaryCost = (userId, year) => {
    const user = activeUsers.find(u => u.id === userId);
    if (!user) return {
      totalSalary: 0,
      totalBonuses: 0,
      totalCost: 0,
      monthlyBreakdown: [],
      bonusBreakdown: { q1: 0, q2: 0, q3: 0, q4: 0 }
    };
    
    let totalSalary = 0;
    const monthlyBreakdown = [];
    
    // Calculate for each month
    for (let month = 1; month <= 12; month++) {
      const salary = getEffectiveSalary(userId, year, month);
      totalSalary += salary;
      monthlyBreakdown.push({
        month,
        salary,
        effectiveDate: `${year}-${String(month).padStart(2, '0')}`
      });
    }
    
    // Get bonuses for the year
    const yearBonuses = user.bonuses?.[year] || { q1: 0, q2: 0, q3: 0, q4: 0 };
    const totalBonuses = (yearBonuses.q1 || 0) + (yearBonuses.q2 || 0) + (yearBonuses.q3 || 0) + (yearBonuses.q4 || 0);
    
    return {
      totalSalary,
      totalBonuses,
      totalCost: totalSalary + totalBonuses,
      monthlyBreakdown,
      bonusBreakdown: yearBonuses
    };
  };

  // ============================================================
  // DATA MANAGEMENT FUNCTIONS — REMOVED
  // The obsolete Data Manager (exportAllData / importAllData) has been removed.
  // ============================================================

  // ============================================================
  // ACCOUNTING SYSTEM CALCULATIONS
  // ============================================================
  
  // Calculate Salary Markup Factor (yearly, applies to all employees)
  // Formula: (Total Fixed Salaries + Total Overheads + Essential Profit) / Total Fixed Salaries
  // This expresses how much revenue we need per euro of salary to cover overheads and achieve essential profit.
  // Note: This does NOT correct inefficiency or overtime — it's purely for revenue targeting.
  const calculateSalaryMarkupFactor = (year) => {
    // Total annual fixed salaries — use salary ledger (same source as SalaryCostPage)
    let totalFixedSalaries = 0;

    if (fullLedgerLoaded && Object.keys(fullSalaryLedger).length > 0) {
      // Sum base_salary + bonus for every user-month in the ledger for this year
      const yearPrefix = String(year);
      Object.values(fullSalaryLedger).forEach(userMonths => {
        Object.entries(userMonths).forEach(([ym, row]) => {
          if (ym.startsWith(yearPrefix)) {
            totalFixedSalaries += (Number(row.base_salary) || 0) + (Number(row.bonus) || 0);
          }
        });
      });
    } else if (getInternalCostRate) {
      // Fallback: salary matrix — only count months the employee was employed
      const parseD = (val) => {
        if (!val) return null;
        const m = String(val).match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
        if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
        const d = new Date(val);
        return isNaN(d.getTime()) ? null : d;
      };
      totalFixedSalaries = activeUsers.reduce((sum, user) => {
        const empStart = parseD(user.joinDate || user.joiningDate);
        const empEnd   = parseD(user.endDate  || user.leavingDate);
        let annual = 0;
        for (let m = 1; m <= 12; m++) {
          if (empStart) {
            const monthStart = new Date(year, m - 1, 1);
            const monthEnd   = new Date(year, m, 0);
            if (empStart > monthEnd) continue;            // not yet joined
            if (empEnd && empEnd < monthStart) continue; // already left
          }
          const rate = getInternalCostRate(user.id, year, m);
          if (rate) annual += (rate.monthlySalary || 0) + (rate.monthlyBonusEquivalent || 0);
        }
        return sum + annual;
      }, 0);
    }

    if (totalFixedSalaries === 0) return 1; // Avoid division by zero

    // Total annual overheads for the year
    const totalOverheads = activeOfficeOverheads
      .filter(o => o.year === year)
      .reduce((sum, o) => sum + (o.amount || 0), 0);

    // Calculate target revenue based on essential profit percentage
    const essentialProfitPercentage = settingsHook.essentialProfitPercentage || 20;
    const targetRevenue = totalFixedSalaries + totalOverheads;
    const essentialProfit = targetRevenue * (essentialProfitPercentage / 100);

    // Salary Markup Factor
    const salaryMarkupFactor = (totalFixedSalaries + totalOverheads + essentialProfit) / totalFixedSalaries;

    return salaryMarkupFactor;
  };
  
  // Get the effective salary markup factor — reads from app_settings.manualSalaryMarkup only.
  // If no manual markup set for the year → returns 0 (not 1.0).
  // This makes it explicit when markup hasn't been configured.
  const getEffectiveSalaryMarkup = (year) => {
    const manualMarkup = settingsHook.manualSalaryMarkup?.[year];
    if (manualMarkup && manualMarkup > 0) {
      return manualMarkup;
    }
    return 0; // No manual markup for this year
  };
  
  // Get internal cost rate for a user (Monthly Salary × Salary Markup Factor)
  // This is for INTERNAL profitability tracking, NOT client billing
  const getInternalCostRate = (userId, year, month = null) => {
    const user = activeUsers.find(u => u.id === userId);
    if (!user) return {
      monthlySalary: 0,
      salaryMarkupFactor: 1,
      monthlyInternalCost: 0,
      hourlyInternalCost: 0,
      contractedHours: 176,
      quarterlyBonuses: { q1: 0, q2: 0, q3: 0, q4: 0 },
      totalAnnualBonus: 0,
      monthlyBonusEquivalent: 0,
      adjustedMonthlySalary: 0,
      source: 'none'
    };
    
    const salaryMarkupFactor = getEffectiveSalaryMarkup(year); // Use effective (manual or calculated)
    
    // Get the date for this calculation
    const targetDate = month !== null 
      ? `${year}-${String(month).padStart(2, '0')}-15` // Mid-month
      : `${year}-06-15`; // Mid-year if no month specified
    
    // Get user profile at this date (jobRole, jobLevel, contractedHours)
    const profile = getUserProfileAtDate(userId, targetDate);
    const contractedHours = profile.contractedHours;
    
    // Determine salary source and amount
    let monthlySalary;
    let salarySource;
    // Currency: NL salaries are in EUR, IN salaries are in INR (baseCurrency)
    const isNLUser = (user.officeLocation || 'NL') === 'NL';
    const salaryCurrency = isNLUser ? 'EUR' : 'INR';

    if (isNLUser) {
      // ── NL office: look up from the CAO matrix using schaal + trede ──
      // Prefer explicit nlSchaal/nlTrede; fall back to extracting from role name
      const schaal = profile.nlSchaal || extractNlSchaalFromRole(profile.jobRole);
      const trede = profile.nlTrede ?? profile.jobLevel;
      const matrixSalary = getSalaryFromNLMatrixHistory(schaal, trede, targetDate);
      if (matrixSalary > 0) {
        monthlySalary = matrixSalary;
        salarySource = 'salary_matrix_nl';
      } else {
        monthlySalary = user.costRate || 0;
        salarySource = 'user_base';
      }
    } else {
      // ── IN office: existing logic using IN salary matrix ──
      // Priority 1: Check for individual salary override in salary history
      if (month !== null && user.salaryHistory && user.salaryHistory.length > 0) {
        monthlySalary = getEffectiveSalary(userId, year, month);
        if (monthlySalary > 0 && monthlySalary !== user.costRate) {
          salarySource = 'individual_override';
        } else {
          const matrixSalary = getSalaryFromMatrixHistory(profile.jobRole, profile.jobLevel, targetDate);
          monthlySalary = matrixSalary || monthlySalary;
          salarySource = matrixSalary > 0 ? 'salary_matrix' : 'user_base';
        }
      } else {
        // Priority 2: Try salary matrix
        const matrixSalary = getSalaryFromMatrixHistory(profile.jobRole, profile.jobLevel, targetDate);
        if (matrixSalary > 0) {
          monthlySalary = matrixSalary;
          salarySource = 'salary_matrix';
        } else {
          // Priority 3: Fall back to user's current costRate
          monthlySalary = user.costRate || 0;
          salarySource = 'user_base';
        }
      }
    }
    
    // Get bonuses for the year
    const bonuses = user.bonuses?.[year] || { q1: 0, q2: 0, q3: 0, q4: 0 };
    const totalAnnualBonus = (bonuses.q1 || 0) + (bonuses.q2 || 0) + (bonuses.q3 || 0) + (bonuses.q4 || 0);
    const monthlyBonusEquivalent = totalAnnualBonus / 12;
    
    // Adjusted monthly salary includes bonus equivalent
    const adjustedMonthlySalary = monthlySalary + monthlyBonusEquivalent;
    const monthlyInternalCost = adjustedMonthlySalary * salaryMarkupFactor;
    
    // Convert to hourly rate using standard full-time hours (176)
    // Monthly salary from the matrix is always a full-time equivalent,
    // so hourly rate = monthly / 176 regardless of part-time contracted hours
    const hourlyInternalCost = monthlyInternalCost / 176;
    
    return {
      monthlySalary,
      salaryMarkupFactor,
      monthlyInternalCost,
      hourlyInternalCost,
      contractedHours,
      quarterlyBonuses: bonuses,
      totalAnnualBonus,
      monthlyBonusEquivalent,
      adjustedMonthlySalary,
      source: salarySource,
      currency: salaryCurrency,
      profile // Include profile for debugging
    };
  };

  // ════════════════════════════════════════════════════════════════
  // CENTRALISED MONTHLY COST — single source of truth for all pages
  // ════════════════════════════════════════════════════════════════
  // Resolution priority (same for NL and IN, all periods):
  //   1. Ledger override row (source = 'override') — always wins
  //   2. Non-zero ledger row (derived, legacy, etc.) — trust the DB
  //   3. getInternalCostRate (salary matrix fallback) — for months
  //      where the ledger has no data
  //
  // The salary ledger is rebuilt server-side with correct NL/IN matrix
  // lookups. The SalaryCostPage already shows correct data from it.
  // Frontend matrix lookup is only a last resort for months without
  // ledger coverage.
  //
  // Returns FTE (full-time equivalent) cost. Callers prorate for
  // part-timers:  proratedCost = totalCost × contractedHours / 176
  // ════════════════════════════════════════════════════════════════

  const calcMonthlyCost = useCallback((userId, year, month) => {
    const user = activeUsers.find(u => u.id === userId);
    const empty = { baseSalary: 0, bonus: 0, totalCost: 0, markup: 0, contractedHours: 176, source: 'none', reason: 'user_not_found' };
    if (!user) return empty;

    const markup = getEffectiveSalaryMarkup(year);
    const isNLUser = (user.officeLocation || 'NL') === 'NL';
    const ymKey = `${year}-${String(month).padStart(2, '0')}`;

    // Profile for contracted hours (freelancer override → role history → 176)
    const targetDate = `${ymKey}-15`;
    const profile = getUserProfileAtDate(userId, targetDate);
    const contractedHoursInfo = getContractedHoursForUserMonth(userId, year, month);
    const contractedHours = contractedHoursInfo.hours || 176;
    const contractedHoursSource = contractedHoursInfo.source;

    // Ledger lookup
    const authId = user.authUserId;
    const ledgerRow = (fullLedgerLoaded && authId) ? fullSalaryLedger[authId]?.[ymKey] : null;
    const hasLedgerSalary = ledgerRow && ((ledgerRow.base_salary || 0) > 0 || (ledgerRow.bonus || 0) > 0);

    let baseSalary = 0;
    let bonus = 0;
    let source = 'missing';
    let reason = null;

    // 1. Non-zero ledger row — trust the DB (handles NL + IN, overrides + derived + legacy)
    if (hasLedgerSalary) {
      baseSalary = ledgerRow.base_salary || 0;
      bonus = ledgerRow.bonus || 0;
      source = ledgerRow.source === 'override' ? 'override'
        : ledgerRow.source === 'legacy_import' ? 'legacy'
        : (ledgerRow.source || 'derived');
    } else {
      // 2. Fallback: frontend salary matrix for months without ledger data
      const internal = getInternalCostRate(userId, year, month);
      if (internal && (internal.monthlySalary > 0 || internal.monthlyBonusEquivalent > 0)) {
        baseSalary = internal.monthlySalary || 0;
        bonus = internal.monthlyBonusEquivalent || 0;
        source = internal.source || 'salary_matrix';
      } else {
        // Explain why everything failed
        if (!authId) {
          reason = 'no auth_user_id — cannot look up ledger';
        } else if (!fullLedgerLoaded) {
          reason = 'ledger not loaded yet';
        } else if (ledgerRow && !hasLedgerSalary) {
          reason = `ledger row exists but salary is 0 (source=${ledgerRow.source})`;
        } else {
          reason = `no ledger row for ${ymKey}`;
        }
        // Add matrix failure detail
        if (isNLUser) {
          const nlSchaal = profile.nlSchaal || extractNlSchaalFromRole(profile.jobRole);
          const nlTrede = profile.nlTrede ?? profile.jobLevel;
          if (!nlSchaal) {
            reason += `, NL: no schaal for role "${profile.jobRole}"`;
          } else if (!salaryMatrixHistoryNL || salaryMatrixHistoryNL.length === 0) {
            reason += ', NL: salary matrix not loaded';
          } else {
            reason += `, NL: no matrix entry for schaal=${nlSchaal} trede=${nlTrede}`;
          }
        } else {
          reason += `, IN: no matrix match for role="${profile.jobRole}" level=${profile.jobLevel}`;
        }
      }
    }

    if (markup === 0 && (baseSalary > 0 || bonus > 0)) {
      reason = `markup is 0 for year ${year} — check app_settings.manualSalaryMarkup`;
    }

    const totalCost = (baseSalary + bonus) * markup;
    return { baseSalary, bonus, totalCost, markup, contractedHours, contractedHoursSource, source, reason };
  // Note: getEffectiveSalaryMarkup, getInternalCostRate, getUserProfileAtDate,
  // getContractedHoursForUserMonth, extractNlSchaalFromRole are plain functions
  // (not useCallback) so they're intentionally excluded from deps to avoid
  // recomputing every render. They access current state via closure which is
  // always up-to-date. Including freelancerApprovedMap / freelancerPendingMap
  // in deps so a freelancer approval flip-triggers downstream memoised costs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUsers, fullLedgerLoaded, fullSalaryLedger, salaryMatrixHistoryNL, freelancerApprovedMap, freelancerPendingMap]);

  // ════════════════════════════════════════════════════════════════
  // EMPLOYER BURDEN (NL only, non-directors) — per-user pro-rata
  // ════════════════════════════════════════════════════════════════
  // NL employees carry employer pension + payroll tax (Loonbelasting).
  // These are stored as office-wide *quarterly* aggregates in
  // office_overheads_yearly under categories "Pension" and "Salary Tax".
  //
  // Distributing them via the office overhead pool (hour-share) is wrong:
  //   - IN users logging time would absorb part of Dutch pension cost
  //   - NL senior vs NL junior at the same desk-hour would absorb the same
  //     burden, but the senior actually generates 3x the pension
  //
  // The right home is the user's own monthly cost. For each NL non-director
  // user we pro-rate the quarterly burden by that user's salary share of all
  // eligible NL users' salaries in the same quarter, then split evenly
  // across the quarter's three months. Directors (job_role starts with
  // "Director") are excluded — per real-world arrangement they don't carry
  // employer-burden charges in this firm.
  // ════════════════════════════════════════════════════════════════

  const EMPLOYER_BURDEN_CATEGORIES = useMemo(() => new Set(['Pension', 'Salary Tax']), []);
  const isEmployerBurdenCategory = useCallback(
    (category) => EMPLOYER_BURDEN_CATEGORIES.has(category),
    [EMPLOYER_BURDEN_CATEGORIES]
  );

  const employerBurdenByUserMonth = useMemo(() => {
    // Index: { uid: { 'YYYY-MM': burdenInr } }
    if (!fullLedgerLoaded) return {};

    // 1. Pool quarterly NL pension + salary tax (INR-equivalent)
    const burdenByYQ = {};
    activeOfficeOverheads.forEach(o => {
      if ((o.officeLocation || 'NL') !== 'NL') return;
      if (!EMPLOYER_BURDEN_CATEGORIES.has(o.category)) return;
      const m = (o.subcategory || '').match(/Q([1-4])/i);
      if (!m) return;
      const key = `${o.year}|${m[1]}`;
      burdenByYQ[key] = (burdenByYQ[key] || 0) + (o.amountInInr ?? o.amount ?? 0);
    });

    // 2. Eligible NL non-director users
    const isDirector = (jobRole) => /^director/i.test(jobRole || '');
    const eligibleUsers = activeUsers.filter(u =>
      (u.officeLocation || 'NL') === 'NL' && !isDirector(u.jobRole) && u.authUserId
    );

    // 3. For every (year, quarter) with burden, compute each eligible user's
    //    share by salary and distribute evenly across that quarter's months.
    const idx = {};
    Object.keys(burdenByYQ).forEach(key => {
      const [yearStr, qStr] = key.split('|');
      const year = parseInt(yearStr, 10);
      const q = parseInt(qStr, 10);
      const burden = burdenByYQ[key];
      const startMonth = (q - 1) * 3 + 1;

      const userSalaries = {};
      let totalSalary = 0;
      eligibleUsers.forEach(u => {
        let sal = 0;
        for (let m = startMonth; m < startMonth + 3; m++) {
          const ymKey = `${year}-${String(m).padStart(2, '0')}`;
          const row = fullSalaryLedger[u.authUserId]?.[ymKey];
          sal += (Number(row?.base_salary) || 0) + (Number(row?.bonus) || 0);
        }
        userSalaries[u.id] = sal;
        totalSalary += sal;
      });
      if (totalSalary <= 0) return;

      eligibleUsers.forEach(u => {
        const userQuarterBurden = (burden * userSalaries[u.id]) / totalSalary;
        const perMonth = userQuarterBurden / 3;
        if (perMonth <= 0) return;
        if (!idx[u.id]) idx[u.id] = {};
        for (let m = startMonth; m < startMonth + 3; m++) {
          const ymKey = `${year}-${String(m).padStart(2, '0')}`;
          idx[u.id][ymKey] = (idx[u.id][ymKey] || 0) + perMonth;
        }
      });
    });

    return idx;
  }, [activeOfficeOverheads, activeUsers, fullSalaryLedger, fullLedgerLoaded, EMPLOYER_BURDEN_CATEGORIES]);

  const getEmployerBurdenShare = useCallback((userId, year, month) => {
    const ymKey = `${year}-${String(month).padStart(2, '0')}`;
    return employerBurdenByUserMonth[userId]?.[ymKey] || 0;
  }, [employerBurdenByUserMonth]);

  // ════════════════════════════════════════════════════════════════
  // TRUE MONTHLY COST (no markup) — for Project Profitability page
  // ════════════════════════════════════════════════════════════════
  // calcMonthlyCost (above) returns (base + bonus) × markup, which is the
  // *target revenue per FTE* the firm needs to charge clients to cover
  // overhead + essential profit. It is correct for the Salary Cost report
  // (a target-revenue view) but wrong for per-project cost attribution
  // because the same overhead is also separately allocated via Op. Overhead.
  //
  // calcMonthlyTrueCost returns the firm's actual cash labour cost per FTE
  // for the month:  base + bonus + pro-rata employer burden (NL only).
  // It is a drop-in replacement (same return shape) for callers that want
  // unmarked-up cost on the cost side of profit-and-loss math.
  // ════════════════════════════════════════════════════════════════

  const calcMonthlyTrueCost = useCallback((userId, year, month) => {
    const c = calcMonthlyCost(userId, year, month);
    const employerBurden = getEmployerBurdenShare(userId, year, month);
    const totalTrueCost = (c.baseSalary + c.bonus) + employerBurden;
    return {
      baseSalary: c.baseSalary,
      bonus: c.bonus,
      employerBurden,
      totalCost: totalTrueCost,
      markup: 1,
      contractedHours: c.contractedHours,
      source: c.source,
      reason: c.reason,
    };
  }, [calcMonthlyCost, getEmployerBurdenShare]);

  // ════════════════════════════════════════════════════════════════
  // Partial-month pro-ration for mid-month joiners / leavers
  // ════════════════════════════════════════════════════════════════
  // A salaried employee who starts or leaves mid-month is paid pro-rata for
  // that first / last month. getEmployedMonthFraction returns the fraction of
  // the month's working days they were employed (1 for full months). The two
  // *PM wrappers scale the cost basis AND the contracted-hours denominator by
  // the same fraction, so the Project Cost Ledger / Profitability / loaded-cost
  // reports charge partial months correctly and stay internally consistent.
  // (The Salary Cost page applies the same fraction in its own path; the salary
  // ledger deriver still books whole months.)
  const getEmployedMonthFraction = (userId, year, month) => {
    const user = activeUsers.find(u => u.id === userId);
    if (!user) return 1;
    const joinDate = user.joinDate || user.joiningDate || null;
    const endDate = user.endDate || user.leavingDate || null;
    return employedMonthFraction(joinDate, endDate, year, month);
  };

  const getEffectiveContractedHoursPM = (userId, year, month) =>
    getEffectiveContractedHours(userId, year, month) * getEmployedMonthFraction(userId, year, month);

  const calcMonthlyTrueCostPM = (userId, year, month) => {
    const cost = calcMonthlyTrueCost(userId, year, month);
    const frac = getEmployedMonthFraction(userId, year, month);
    return frac === 1 ? cost : { ...cost, totalCost: (cost.totalCost || 0) * frac };
  };

  // ════════════════════════════════════════════════════════════════
  // refreshProjectLoadedCosts — recompute per-project loaded cost
  // (direct labour + expenses + activity-allocated overhead) and write
  // it to project_loaded_costs. Same primitives as Project
  // Profitability: computeProjectLoadedCostRecords delegates to
  // computeProjectLabour + computeActivityOverheadEngine +
  // computeInternalCostRedistribution.
  //
  // Implementation: split into a synchronous "request" phase and an
  // effect that runs the actual compute when prereqs are fresh. This
  // avoids the stale-closure trap that bit the first version — when
  // the Inquiries Refresh button kicked off the loaders cold, the
  // captured booleans (supabaseTimeEntries.allLoaded, fullLedgerLoaded)
  // and the captured data (activeTimeEntries, fullSalaryLedger) would
  // stay stale through the awaits because React state updates land
  // only on the next render. The effect below sees fresh state on the
  // next render and drains the queued request.
  //
  // Public API stays the same: returns a Promise<{success, count}> or
  // Promise<{success: false, error}>. Profitability calls with no
  // args (data already loaded at call time → effect drains immediately
  // on the same render cycle). Inquiries calls with
  // { awaitDataLoads: true } — same shape, but the request waits for
  // the loaders to finish before draining.
  // ════════════════════════════════════════════════════════════════
  const loadedCostsRequestRef = useRef(null);
  const loadedCostsRequestRunningRef = useRef(false);
  const [loadedCostsRequestNonce, setLoadedCostsRequestNonce] = useState(0);

  const refreshProjectLoadedCosts = useCallback((opts = {}) => {
    const { awaitDataLoads = false } = opts;
    return new Promise((resolve) => {
      // Coalesce concurrent requests — most-recent caller wins so the
      // first invocation doesn't strand its promise.
      if (loadedCostsRequestRef.current) {
        loadedCostsRequestRef.current.resolve({ success: false, error: 'superseded' });
      }
      loadedCostsRequestRef.current = { resolve, awaitDataLoads, requestedAt: Date.now() };
      // Trigger the loaders synchronously; the effect waits for them.
      if (awaitDataLoads) {
        if (!supabaseTimeEntries.allLoaded && supabaseTimeEntries.fetchAllTimeEntries) {
          supabaseTimeEntries.fetchAllTimeEntries();
        }
        if (!fullLedgerLoaded) {
          fetchFullSalaryLedger();
        }
      }
      // Wake the effect.
      setLoadedCostsRequestNonce(n => n + 1);
    });
  }, [supabaseTimeEntries, fullLedgerLoaded, fetchFullSalaryLedger]);

  // The drain effect. Fires whenever a request is queued and prereqs
  // are ready. On each render it has the latest closed-over state, so
  // the compute reads fresh data + fresh helpers (calcMonthlyTrueCost
  // closes over fullSalaryLedger; getEffectiveContractedHours over
  // approved leave; etc.).
  useEffect(() => {
    const req = loadedCostsRequestRef.current;
    if (!req) return;
    if (loadedCostsRequestRunningRef.current) return;
    // For awaitDataLoads requests: wait until both loaders are done.
    if (req.awaitDataLoads && (!supabaseTimeEntries.allLoaded || !fullLedgerLoaded)) return;
    // For non-awaitDataLoads requests (e.g. Profitability on render):
    // if prereqs aren't ready, just resolve as a no-op so we don't
    // write a half-cooked cache.
    if (!req.awaitDataLoads && (!supabaseTimeEntries.allLoaded || !fullLedgerLoaded)) {
      loadedCostsRequestRef.current = null;
      req.resolve({ success: false, error: 'prerequisites_not_loaded' });
      return;
    }
    loadedCostsRequestRunningRef.current = true;
    (async () => {
      try {
        const records = computeProjectLoadedCostRecords({
          activeTimeEntries,
          activeExpenses,
          activeUsers,
          activeProjects,
          activeClients,
          monthlyOfficeOverheads: supabaseOfficeOverheadsMonthly.overheads || [],
          activeOfficeOverheads,
          fullSalaryLedger,
          calcMonthlyTrueCost: calcMonthlyTrueCostPM,
          getEffectiveContractedHours: getEffectiveContractedHoursPM,
          getUserProfileAtDate,
          isEmployerBurden: isEmployerBurdenCategory,
          // project_start_year + project_country_code are auto-filled
          // server-side by upsert_project_loaded_costs.
          projectStartYearByProjectId: {},
          projectCountryCodeByProjectId: {},
        });
        let res;
        if (records.length === 0) {
          res = { success: true, count: 0 };
        } else {
          res = await supabaseProjectLoadedCosts.upsertMany(records);
          if (res.success) await supabaseProjectLoadedCosts.refetch();
        }
        // Only resolve if this request is still the active one (it may
        // have been superseded by a later call, in which case that path
        // already resolved the displaced promise with 'superseded').
        if (loadedCostsRequestRef.current === req) {
          loadedCostsRequestRef.current = null;
          req.resolve(res);
        }
      } catch (err) {
        console.error('[refreshProjectLoadedCosts] failed:', err);
        if (loadedCostsRequestRef.current === req) {
          loadedCostsRequestRef.current = null;
          req.resolve({ success: false, error: err.message });
        }
      } finally {
        loadedCostsRequestRunningRef.current = false;
      }
    })();
  // helpers (getEffectiveContractedHours / getUserProfileAtDate) are
  // plain closures whose dependencies are already covered by upstream state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loadedCostsRequestNonce,
    supabaseTimeEntries.allLoaded, fullLedgerLoaded,
    activeTimeEntries, activeExpenses, activeUsers, activeProjects, activeClients,
    activeOfficeOverheads, fullSalaryLedger,
    supabaseOfficeOverheadsMonthly.overheads,
    calcMonthlyTrueCost, isEmployerBurdenCategory,
  ]);



  // Get client billing rate (stored separately, independent of internal costs)
  const getClientBillingRate = (userId) => {
    const user = activeUsers.find(u => u.id === userId);
    if (!user) return 0;
    
    // Client billing rate should be stored separately on user object
    // For now, return 0 if not set (to be added to user profile)
    return user.clientBillingRate || 0;
  };
  
  // 1. Calculate theoretical hourly cost
  const getTheoreticalHourlyCost = (userId, month) => {
    const user = activeUsers.find(u => u.id === userId);
    if (!user) return 0;
    
    // costRate is a full-time monthly salary; divide by standard 176h
    return user.costRate / 176;
  };

  // 2. Get recorded hours for a user in a month
  const getRecordedHoursForMonth = (userId, yearMonth) => {
    const entries = activeTimeEntries.filter(e => {
      const entryMonth = e.date.substring(0, 7); // YYYY-MM
      return e.userId === userId && entryMonth === yearMonth;
    });
    return entries.reduce((sum, e) => sum + (e.duration / 3600), 0);
  };

  // 3. Calculate salary variance for a user in a month
  const calculateSalaryVariance = (userId, yearMonth) => {
    const user = activeUsers.find(u => u.id === userId);
    if (!user) return { variance: 0, recordedHours: 0, contractedHours: 176 };

    const [vYear, vMonth] = yearMonth.split('-').map(s => parseInt(s, 10));
    const contractedHours = getContractedHoursForUserMonth(userId, vYear, vMonth).hours || 176;
    const recordedHours = getRecordedHoursForMonth(userId, yearMonth);
    const theoreticalHourlyCost = getTheoreticalHourlyCost(userId, yearMonth);

    // Prorate FTE monthly salary for part-timers: actual cost = FTE cost × (contracted / 176)
    const fixedMonthlySalaryCost = user.costRate * contractedHours / 176;
    const recordedHoursCost = recordedHours * theoreticalHourlyCost;
    const variance = recordedHoursCost - fixedMonthlySalaryCost;
    
    return {
      userId,
      yearMonth,
      recordedHours,
      contractedHours,
      theoreticalHourlyCost,
      fixedMonthlySalaryCost,
      recordedHoursCost,
      variance,
      interpretation: variance > 0 ? 'unpaid_overtime' : variance < 0 ? 'under_utilisation' : 'on_target'
    };
  };

  // 4. Apply tolerance band (10%)
  const applyToleranceBand = (salaryVariance) => {
    const toleranceHours = salaryVariance.contractedHours * 0.10;
    const toleranceCost = toleranceHours * salaryVariance.theoreticalHourlyCost;
    const excessVariance = Math.max(0, salaryVariance.variance - toleranceCost);
    
    return {
      ...salaryVariance,
      toleranceHours,
      toleranceCost,
      excessVariance,
      withinTolerance: excessVariance === 0
    };
  };

  // 5. Check persistence (3 consecutive months)
  const checkPersistence = (userId, currentYearMonth) => {
    // Get last 3 months including current
    const date = new Date(currentYearMonth + '-01');
    const months = [];
    for (let i = 0; i < 3; i++) {
      const d = new Date(date);
      d.setMonth(d.getMonth() - i);
      months.unshift(d.toISOString().substring(0, 7));
    }
    
    // Calculate variance for each month
    const variances = months.map(month => {
      const sv = calculateSalaryVariance(userId, month);
      return applyToleranceBand(sv);
    });
    
    // Check if all 3 months have positive excess variance
    const allPositive = variances.every(v => v.excessVariance > 0);
    const confirmed = allPositive;
    
    return {
      userId,
      months: variances,
      confirmed,
      confirmedExcessVariance: confirmed ? variances[2].excessVariance : 0
    };
  };

  // 6. Calculate office-level confirmed excess salary variance
  const getOfficeConfirmedExcessSalaryVariance = (yearMonth) => {
    return activeUsers.reduce((sum, user) => {
      const persistence = checkPersistence(user.id, yearMonth);
      return sum + persistence.confirmedExcessVariance;
    }, 0);
  };

  // 7. Calculate project expenses
  const getProjectExpenses = (projectId) => {
    const expenses = activeExpenses.filter(e => e.projectId === projectId);
    return expenses.reduce((sum, e) => {
      if (e.status === 'planned') return sum; // Don't count planned
      return sum + (e.amount || 0);
    }, 0);
  };

  // 8a. Get monthly compensation from salary ledger for a specific user+year+month
  const getLedgerMonthlyComp = (userId, year, month) => {
    const user = activeUsers.find(u => u.id === userId);
    if (!user?.authUserId) return { monthlySalary: 0, monthlyBonus: 0, monthlyComp: 0, found: false };
    const ym = `${year}-${String(month).padStart(2, '0')}`;
    const row = fullSalaryLedger[user.authUserId]?.[ym];
    if (row) {
      const monthlySalary = row.base_salary || 0;
      const monthlyBonus = row.bonus || 0;
      return { monthlySalary, monthlyBonus, monthlyComp: monthlySalary + monthlyBonus, found: true };
    }
    return { monthlySalary: 0, monthlyBonus: 0, monthlyComp: 0, found: false };
  };

  // 8b. Get hourly internal cost — uses centralised calcMonthlyCost
  const getHourlyInternalCostFromLedger = (userId, year, month) => {
    const cost = calcMonthlyCost(userId, year, month);
    if (cost.totalCost > 0) {
      // FTE hourly = totalCost / 176 standard hours
      return { hourly: cost.totalCost / 176, found: true };
    }

    // Fallback: latest prior month within same year (salary may not be set yet)
    for (let m = month - 1; m >= 1; m--) {
      const prior = calcMonthlyCost(userId, year, m);
      if (prior.totalCost > 0) {
        return { hourly: prior.totalCost / 176, found: false, reason: 'fallback_previous_month' };
      }
    }

    return { hourly: 0, found: false, reason: 'no_salary_found' };
  };

  // 8. Calculate actual labour cost for project (using ledger when available, with fallback tracking)
  const getProjectActualLabourCost = (projectId) => {
    const entries = activeTimeEntries.filter(e => e.projectId === projectId);
    let labourCostTotal = 0;
    let fallbackCount = 0;
    const missingLedgerMonths = [];
    const seen = new Set();

    entries.forEach(e => {
      const year = parseInt(e.date.substring(0, 4));
      const month = parseInt(e.date.substring(5, 7));
      const hours = e.duration / 3600;

      if (fullLedgerLoaded) {
        const { hourly, found, reason } = getHourlyInternalCostFromLedger(e.userId, year, month);
        labourCostTotal += hours * hourly;
        if (!found) {
          fallbackCount++;
          const key = `${e.userId}-${year}-${String(month).padStart(2, '0')}-${reason}`;
          if (!seen.has(key)) {
            seen.add(key);
            const u = activeUsers.find(x => x.id === e.userId);
            missingLedgerMonths.push({ userId: e.userId, userName: u?.name, year, month, reason });
          }
        }
      } else {
        // Ledger not loaded yet — use old method
        const internalCost = getInternalCostRate(e.userId, year);
        labourCostTotal += hours * internalCost.hourlyInternalCost;
      }
    });

    return { labourCostTotal, fallbackCount, missingLedgerMonths };
  };

  // 9. Calculate planned labour cost for project (using internal cost rate from matrix/ledger)
  const getProjectPlannedLabourCost = (projectId) => {
    const project = activeProjects.find(p => p.id === projectId);
    if (!project || !project.plannedHours) return 0;

    const currentYear = new Date().getFullYear();

    // Use average hourly internal cost rate from matrix/ledger (not stale u.costRate)
    let totalHourlyCost = 0;
    let count = 0;
    activeUsers.forEach(u => {
      const rate = getInternalCostRate(u.id, currentYear);
      if (rate.hourlyInternalCost > 0) {
        totalHourlyCost += rate.hourlyInternalCost;
        count++;
      }
    });
    const avgInternalCostRate = count > 0 ? totalHourlyCost / count : 0;

    return (project.plannedHours || 0) * avgInternalCostRate;
  };

  // 10. Calculate project realised profit (paid invoices minus costs)
  const getProjectRealisedProfit = (projectId) => {
    const income = paidIncomeByProject[projectId] || 0;
    const { labourCostTotal } = getProjectActualLabourCost(projectId);
    const actualProjectExpenses = getProjectExpenses(projectId);

    return income - labourCostTotal - actualProjectExpenses;
  };

  // 11. Calculate project cost variance
  const getProjectCostVariance = (projectId) => {
    const plannedLabourCost = getProjectPlannedLabourCost(projectId);
    const plannedExpenses = projectExpenses
      .filter(e => e.projectId === projectId && e.status === 'planned')
      .reduce((sum, e) => sum + (e.amount || 0), 0);
    const plannedProjectCost = plannedLabourCost + plannedExpenses;
    
    const { labourCostTotal: actualLabourCost } = getProjectActualLabourCost(projectId);
    const actualExpenses = getProjectExpenses(projectId);
    const actualProjectCost = actualLabourCost + actualExpenses;
    
    return {
      plannedProjectCost,
      actualProjectCost,
      variance: actualProjectCost - plannedProjectCost,
      interpretation: actualProjectCost > plannedProjectCost ? 'over_budget' : 'under_budget'
    };
  };

  // 12. Calculate office realised profit (paid invoices - costs - overheads)
  const getOfficeRealisedProfit = (yearMonth) => {
    const totalPaidIncome = Object.values(paidIncomeByProject).reduce((s, v) => s + v, 0);
    const totalDirectLabourCost = activeProjects.reduce((sum, p) => sum + getProjectActualLabourCost(p.id).labourCostTotal, 0);
    const totalProjectExpenses = activeProjects.reduce((sum, p) => sum + getProjectExpenses(p.id), 0);

    // Get yearly overheads and divide by 12 for monthly allocation
    const year = parseInt(yearMonth.substring(0, 4));
    const yearlyOverheads = activeOfficeOverheads
      .filter(o => o.year === year)
      .reduce((sum, o) => sum + (o.amount || 0), 0);
    const monthlyOverheads = yearlyOverheads / 12;

    return totalPaidIncome - totalDirectLabourCost - totalProjectExpenses - monthlyOverheads;
  };

  // 13. Calculate capacity-adjusted profit
  const getCapacityAdjustedProfit = (yearMonth) => {
    const officeRealisedProfit = getOfficeRealisedProfit(yearMonth);
    const confirmedExcess = getOfficeConfirmedExcessSalaryVariance(yearMonth);
    
    return {
      officeRealisedProfit,
      confirmedExcessSalaryVariance: confirmedExcess,
      capacityAdjustedProfit: officeRealisedProfit - confirmedExcess,
      adjustment: confirmedExcess
    };
  };

  // ════════════════════════════════════════════════════════════════
  // OVERHEAD COST ALLOCATION — distributes untracked salary costs
  // to projects for management, support, and flags project staff gaps.
  //
  // Role categories:
  //   Management (Partner, Director, Associate)                → distribute by revenue share
  //   Support (Support, Model Maker, Visualiser, Others)       → distribute by hours share
  //   Project Staff (all other roles)                          → flag only, not distributed
  // ════════════════════════════════════════════════════════════════

  const MANAGEMENT_ROLE_PREFIXES = ['Partner', 'Director', 'Associate'];
  const SUPPORT_ROLE_PREFIXES = ['Support', 'Model Maker', 'Visualiser', 'Others'];

  /** Classify a user's job role into management | support | project_staff.
   *  Uses prefix matching so "Director 11", "Associate 5" etc. are recognised. */
  const classifyUserRole = useCallback((userId, targetDate) => {
    const profile = getUserProfileAtDate(userId, targetDate);
    const role = profile.jobRole || '';
    if (MANAGEMENT_ROLE_PREFIXES.some(p => role === p || role.startsWith(p + ' '))) return 'management';
    if (SUPPORT_ROLE_PREFIXES.some(p => role === p || role.startsWith(p + ' '))) return 'support';
    return 'project_staff';
  }, [getUserProfileAtDate]);

  /**
   * Compute overhead allocation for a set of months.
   *
   * Returns {
   *   managementPool,        — total untracked cost from management roles
   *   supportPool,           — total untracked cost from support roles
   *   untrackedProjectStaff, — total untracked cost from project staff (flagged only)
   *   totalSalaryCost,       — total salary cost across all users for the period
   *   totalAllocatedCost,    — total cost already allocated via time entries
   *   costRecoveryPct,       — (allocated / total) × 100
   *   byProject: { [projectId]: { mgmtOverhead, supportOverhead, totalOverhead } },
   *   flaggedUsers: [ { userId, name, role, contractedHours, loggedHours, untrackedCost } ],
   *   usesLegacyHolidays: bool — true if any month is before the holiday DB cutoff (pre-2026)
   *   usesLegacyLeave:    bool — true if any month is before the leave DB cutoff (pre-Apr 2026)
   * }
   */
  const computeOverheadAllocation = useCallback((rawMonths) => {
    // The salary ledger begins 2018-04. Drop any earlier months so users aren't
    // flagged as having incomplete timesheets for periods that pre-date the
    // salary data (no cost to compare against, so no real gap to report).
    const months = (rawMonths || []).filter(m => m >= '2018-04');

    if (!months || months.length === 0) {
      return {
        managementPool: 0, supportPool: 0, untrackedProjectStaff: 0,
        totalSalaryCost: 0, totalAllocatedCost: 0, costRecoveryPct: 100,
        byProject: {}, flaggedUsers: [],
        usesLegacyHolidays: false, usesLegacyLeave: false,
      };
    }

    // Detect whether the period uses legacy (estimated) holiday/leave data
    const usesLegacyHolidays = months.some(m => m < '2026-01');
    const usesLegacyLeave    = months.some(m => m < '2026-04');

    const monthSet = new Set(months);

    // ── 1. Build total logged hours per user per month from time entries ──
    const loggedByUserMonth = {};
    const hoursByProjectMonth = {};   // { projectId: totalHours }

    activeTimeEntries.forEach(e => {
      const ymKey = e.date.substring(0, 7);
      if (!monthSet.has(ymKey)) return;
      const uid = e.userId;
      const hours = (e.duration || 0) / 3600;
      if (!loggedByUserMonth[uid]) loggedByUserMonth[uid] = {};
      loggedByUserMonth[uid][ymKey] = (loggedByUserMonth[uid][ymKey] || 0) + hours;
      hoursByProjectMonth[e.projectId] = (hoursByProjectMonth[e.projectId] || 0) + hours;
    });

    // ── 2. Revenue per project (for management distribution) ──
    // Use paidIncomeByProject (pre-fetched paid invoice totals per project).
    // This is cumulative, not monthly, but accurate for revenue share weighting.
    const totalProjectHours = Object.values(hoursByProjectMonth).reduce((a, b) => a + b, 0);
    const totalRevenue = Object.values(paidIncomeByProject).reduce((a, b) => a + b, 0);
    const useRevenueDist = totalRevenue > 0;

    // ── 3. Per-user: compute total salary cost vs allocated cost ──
    let managementPool = 0;
    let supportPool = 0;
    let untrackedProjectStaff = 0;
    let totalSalaryCost = 0;
    let totalAllocatedCost = 0;
    const flaggedUsers = [];
    const missingSalaryUsers = [];   // users where calcMonthlyCost returned 0

    activeUsers.forEach(user => {
      const uid = user.id;
      let userTotalCost = 0;
      let userLoggedHours = 0;
      let userContractedHours = 0;
      let userZeroCostMonths = 0;
      let userEmployedMonths = 0;
      let firstZeroReason = null;

      // Determine employment period for this user
      // End: users.end_date (authoritative), user_profiles.leaving_date (fallback)
      const userJoinYM = (user.joinDate || user.joiningDate) ? (user.joinDate || user.joiningDate).substring(0, 7) : null;
      const effectiveLeaveDate = user.endDate || user.leavingDate;
      const userLeaveYM = effectiveLeaveDate ? effectiveLeaveDate.substring(0, 7) : null;

      // Skip user entirely if employment period doesn't overlap the selected range
      const firstMonth = months[0];
      const lastMonth = months[months.length - 1];
      if (userJoinYM && userJoinYM > lastMonth) return;
      if (userLeaveYM && userLeaveYM < firstMonth) return;

      months.forEach(ymKey => {
        const p = parseYM(ymKey);
        if (!p) return;

        // Skip months outside the user's employment period
        if (userJoinYM && ymKey < userJoinYM) return;
        if (userLeaveYM && ymKey > userLeaveYM) return;

        // Use centralised salary resolution
        const costData = calcMonthlyCost(uid, p.year, p.month);
        // baseContractedHours: used for part-time proration (FTE cost × base / 176)
        // effectiveContractedHours: used for gap/overhead denominator, deducts holidays + leave
        const baseContractedHours = costData.contractedHours;
        const effectiveContractedHours = getEffectiveContractedHours(uid, p.year, p.month);
        userContractedHours += effectiveContractedHours;
        userEmployedMonths++;

        if (costData.totalCost === 0 && costData.reason) {
          userZeroCostMonths++;
          if (!firstZeroReason) firstZeroReason = costData.reason;
        }

        const logged = loggedByUserMonth[uid]?.[ymKey] || 0;
        userLoggedHours += logged;

        // Prorate FTE monthly cost for part-timers: actual cost = FTE cost × (base contracted / 176)
        const proratedMonthlyCost = costData.totalCost * baseContractedHours / 176;
        userTotalCost += proratedMonthlyCost;

        // Allocated cost — denominator is effective contracted hours (holidays + leave removed)
        const normalizedTotal = Math.max(logged, effectiveContractedHours);
        const allocatedRatio = normalizedTotal > 0 ? logged / normalizedTotal : 0;
        const allocatedCostForMonth = proratedMonthlyCost * allocatedRatio;
        totalAllocatedCost += allocatedCostForMonth;

        // Salary conservation integrity check (dev only)
        if (process.env.NODE_ENV !== 'production') {
          validateSalaryConservation(uid, ymKey, allocatedCostForMonth, proratedMonthlyCost, logged, effectiveContractedHours);
        }
      });

      // Track users with missing salary data
      if (userZeroCostMonths > 0 && userEmployedMonths > 0) {
        const zeroPct = Math.round((userZeroCostMonths / userEmployedMonths) * 100);
        if (zeroPct > 50) {  // Only flag if majority of months have no salary
          const isNL = (user.officeLocation || 'NL') === 'NL';
          missingSalaryUsers.push({
            userId: uid,
            name: user.name || `User ${uid}`,
            office: isNL ? 'NL' : 'IN',
            employedMonths: userEmployedMonths,
            zeroCostMonths: userZeroCostMonths,
            zeroPct,
            reason: firstZeroReason,
          });
        }
      }

      totalSalaryCost += userTotalCost;

      // Untracked cost = total salary cost − what was allocated to projects
      const allocatedRatio = userContractedHours > 0
        ? Math.min(userLoggedHours / userContractedHours, 1)
        : 0;
      const untrackedCost = userTotalCost * (1 - allocatedRatio);

      if (untrackedCost <= 0) return;

      // Use mid-period date for role classification
      const midMonth = months[Math.floor(months.length / 2)];
      const midP = parseYM(midMonth);
      const midDate = midP ? `${midP.year}-${String(midP.month).padStart(2, '0')}-15` : '';
      const category = classifyUserRole(uid, midDate);

      if (category === 'management') {
        managementPool += untrackedCost;
      } else if (category === 'support') {
        supportPool += untrackedCost;
      } else {
        untrackedProjectStaff += untrackedCost;
        // Flag project staff with significant gaps (>10% of contracted hours)
        const gapPct = userContractedHours > 0
          ? (1 - userLoggedHours / userContractedHours) * 100
          : 0;
        if (gapPct > 10) {
          const profile = getUserProfileAtDate(uid, midDate);
          flaggedUsers.push({
            userId: uid,
            name: user.name || `User ${uid}`,
            role: profile.jobRole || 'Unknown',
            contractedHours: userContractedHours,
            loggedHours: userLoggedHours,
            untrackedCost,
            gapPct: Math.round(gapPct),
          });
        }
      }
    });

    // ── 4. Distribute pools to projects ──
    const byProject = {};
    activeProjects.forEach(proj => {
      const pid = proj.id;
      const projHours = hoursByProjectMonth[pid] || 0;
      const projRevenue = paidIncomeByProject[pid] || 0;

      // Management: by revenue share (fallback to hours share)
      let mgmtOverhead = 0;
      if (useRevenueDist && totalRevenue > 0) {
        mgmtOverhead = managementPool * (projRevenue / totalRevenue);
      } else if (totalProjectHours > 0) {
        mgmtOverhead = managementPool * (projHours / totalProjectHours);
      }

      // Support: by hours share
      let supportOverhead = 0;
      if (totalProjectHours > 0) {
        supportOverhead = supportPool * (projHours / totalProjectHours);
      }

      if (mgmtOverhead > 0 || supportOverhead > 0) {
        byProject[pid] = {
          mgmtOverhead,
          supportOverhead,
          totalOverhead: mgmtOverhead + supportOverhead,
        };
      }
    });

    const costRecoveryPct = totalSalaryCost > 0
      ? (totalAllocatedCost / totalSalaryCost) * 100
      : 100;

    return {
      managementPool,
      supportPool,
      untrackedProjectStaff,
      totalSalaryCost,
      totalAllocatedCost,
      costRecoveryPct,
      byProject,
      flaggedUsers,
      missingSalaryUsers,
      usesLegacyHolidays,
      usesLegacyLeave,
    };
  // Note: getEffectiveContractedHours is a plain function (like getUserProfileAtDate)
  // so it is intentionally excluded from deps — it accesses current closure state.
  // holidayHoursByOfficeMonth and approvedLeaveHoursByUserMonth ARE included because
  // they are memos that change when holidays/timeOff data changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTimeEntries, activeUsers, activeProjects, paidIncomeByProject,
      calcMonthlyCost, classifyUserRole,
      holidayHoursByOfficeMonth, approvedLeaveHoursByUserMonth]);

  // Helper: parse "YYYY-MM" → { year, month } (re-used from various pages)
  const parseYM = (s) => {
    if (!s) return null;
    const m = String(s).match(/^(\d{4})-(\d{1,2})/);
    return m ? { year: parseInt(m[1]), month: parseInt(m[2]) } : null;
  };

  // 14. Get salary variance alerts for a user
  const getSalaryVarianceAlerts = (userId, yearMonth) => {
    const persistence = checkPersistence(userId, yearMonth);
    const currentMonth = persistence.months[2];
    
    if (!currentMonth || currentMonth.excessVariance === 0) return null;
    
    const consecutiveMonths = persistence.months.filter(m => m.excessVariance > 0).length;
    
    if (consecutiveMonths === 1) {
      return { level: 'yellow', month: 1, message: 'Excess detected - monitoring' };
    } else if (consecutiveMonths === 2) {
      return { level: 'orange', month: 2, message: 'Persistence warning - 2 consecutive months' };
    } else if (consecutiveMonths >= 3) {
      return { level: 'red', month: 3, message: 'Confirmed - capacity adjustment applied' };
    }
    
    return null;
  };



  // Get salary matrix data for a job role and level
  const getSalaryFromMatrix = (jobRole, jobLevel) => {
    if (!jobRole || jobLevel === undefined || jobLevel === null) {
      return { salary: 0, costRate: 0, markup: 0 };
    }
    const roleData = salaryMatrix[jobRole];
    if (!roleData || !roleData[jobLevel]) {
      return { salary: 0, costRate: 0, markup: 0 };
    }
    const data = roleData[jobLevel];
    const costRate = Math.round(data.salary / 176); // Monthly salary ÷ 176 hours
    return {
      salary: data.salary,
      costRate: costRate,
      markup: data.markup
    };
  };

  // Update salary matrix cell
  const _updateSalaryMatrix = (jobRole, level, field, value) => {
    setSalaryMatrix(prev => {
      const roleData = prev[jobRole] || Array.from({ length: 21 }, () => ({ salary: 0, markup: 0 }));
      const updated = Array.isArray(roleData)
        ? roleData.map((item, idx) => idx === level ? { ...item, [field]: parseFloat(value) || 0 } : item)
        : Object.fromEntries(
            Object.keys(roleData).map(k => [k, parseInt(k) === level ? { ...roleData[k], [field]: parseFloat(value) || 0 } : roleData[k]])
          );
      return { ...prev, [jobRole]: updated };
    });
  };

  // Job roles list
  const jobRoles = [
    'Intern',
    'Junior Architect',
    'Medior Architect',
    'Project Architect',
    'Senior Architect',
    'Associate',
    'Partner',
    'Director',
    'Support',
    'Model Maker',
    'Visualiser',
    'Others'
  ];

  // Role level colors for visual encoding (circle fill)
  const roleColors = {
    'Intern': '#9ca3af',           // Light grey
    'Junior Architect': '#93c5fd',  // Light blue
    'Medior Architect': '#3b82f6',  // Blue
    'Project Architect': '#14b8a6', // Teal
    'Senior Architect': '#1e40af',  // Dark blue
    'Associate': '#9333ea',         // Purple
    'Partner': '#eab308',           // Gold
    'Director': '#1f2937',          // Dark charcoal
    'Support': '#22c55e',           // Green
    'Model Maker': '#f59e0b',      // Amber
    'Visualiser': '#ec4899',       // Pink
    'Others': '#6b7280'            // Grey
  };

  // Helper: Get Monday of a given date's week
  const _getMonday = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    return new Date(d.setDate(diff));
  };

  // Helper: Format week for display
  const formatWeek = (mondayDate) => {
    const monday = new Date(mondayDate);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return `${monday.getDate()} ${monday.toLocaleDateString('en-US', {month: 'short'})} - ${sunday.getDate()} ${sunday.toLocaleDateString('en-US', {month: 'short'})}`;
  };

  // Helper: Get actual hours logged for user/project/week
  const getActualHours = (userId, projectId, weekStart) => {
    const monday = new Date(weekStart);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    return activeTimeEntries
      .filter(e => {
        if (e.userId !== userId || e.projectId !== projectId) return false;
        const entryDate = new Date(e.date);
        return entryDate >= monday && entryDate <= sunday;
      })
      .reduce((sum, e) => sum + (e.duration / 3600), 0);
  };

  // Helper: Get user's total allocated hours for a week
  const getUserWeekAllocated = (userId, weekStart) => {
    return activeAllocations
      .filter(a => a.userId === userId && a.weekStart === weekStart)
      .reduce((sum, a) => sum + a.allocatedHours, 0);
  };

  // Helper: Get user's total actual hours for a week
  const getUserWeekActual = (userId, weekStart) => {
    const monday = new Date(weekStart);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    return activeTimeEntries
      .filter(e => {
        if (e.userId !== userId) return false;
        const entryDate = new Date(e.date);
        return entryDate >= monday && entryDate <= sunday;
      })
      .reduce((sum, e) => sum + (e.duration / 3600), 0);
  };

  const getRate = (uid, date) => {
    const user = activeUsers.find(u => u.id === uid);
    if (!user) return { costRate: 0, markup: 0, revenueRate: 0 };
    const d = new Date(date);
    const ym = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
    const hist = rateHistory.filter(h => h.userId === uid && h.effectiveMonth <= ym).sort((a,b) => b.effectiveMonth.localeCompare(a.effectiveMonth));
    if (hist.length > 0) {
      const r = hist[0];
      return { costRate: r.costRate, markup: r.markup, revenueRate: r.costRate * (1 + r.markup/100) };
    }
    return { costRate: user.costRate, markup: user.markup, revenueRate: user.costRate * (1 + user.markup/100) };
  };

  const calcRev = (e) => (e.duration/3600) * getRate(e.userId, e.date).revenueRate;
  const calcCost = (e) => (e.duration/3600) * getRate(e.userId, e.date).costRate;

  // ── Ledger-based per-entry cost (matches ProjectCostLedgerPage formula) ──
  // Builds a total-logged-hours-per-user-month index, then computes:
  //   effective_hourly_rate = (base_salary + bonus) × markup / max(total_logged, contracted_hours)
  //   entry_cost = entry_hours × effective_hourly_rate
  const totalLoggedByUserMonth = useMemo(() => {
    const idx = {};
    activeTimeEntries.forEach(e => {
      const uid = e.userId;
      const ymKey = e.date.substring(0, 7);
      if (!idx[uid]) idx[uid] = {};
      idx[uid][ymKey] = (idx[uid][ymKey] || 0) + (e.duration || 0) / 3600;
    });
    return idx;
  }, [activeTimeEntries]);

  const calcLedgerCost = useCallback((e) => {
    const hours = (e.duration || 0) / 3600;
    if (hours === 0) return 0;
    const ymKey = e.date.substring(0, 7);
    const year = parseInt(e.date.substring(0, 4));
    const month = parseInt(e.date.substring(5, 7));

    // Get monthly salary cost with markup from ledger
    if (fullLedgerLoaded) {
      const { hourly, found } = getHourlyInternalCostFromLedger(e.userId, year, month);
      if (found) {
        // Use normalized denominator: max(total_logged, contracted_hours).
        // Contracted hours come from the freelancer override when applicable,
        // otherwise fall back to role-history value.
        const contractedHours = getContractedHoursForUserMonth(e.userId, year, month).hours || 176;
        const totalLogged = totalLoggedByUserMonth[e.userId]?.[ymKey] || 0;
        const salaryMarkupFactor = getEffectiveSalaryMarkup(year);
        const comp = getLedgerMonthlyComp(e.userId, year, month);
        if (comp.found) {
          const fteMonthlyCost = comp.monthlyComp * salaryMarkupFactor;
          // Prorate for part-timers: actual monthly cost = FTE cost × (contracted / 176)
          const monthlyCost = fteMonthlyCost * contractedHours / 176;
          const normalizedTotal = Math.max(totalLogged, contractedHours);
          const entryCost = normalizedTotal > 0 ? hours * (monthlyCost / normalizedTotal) : 0;

          // Salary conservation integrity check (dev only)
          // Validate the theoretical total for this user-month
          if (process.env.NODE_ENV !== 'production') {
            const totalAllocated = normalizedTotal > 0 ? totalLogged * (monthlyCost / normalizedTotal) : 0;
            validateSalaryConservation(e.userId, ymKey, totalAllocated, monthlyCost, totalLogged, contractedHours);
          }

          return entryCost;
        }
        // Ledger hourly found via fallback — use it directly (already divided by contractedHours)
        return hours * hourly;
      }
    }
    // Fallback: old method
    return calcCost(e);
  }, [fullLedgerLoaded, totalLoggedByUserMonth, getHourlyInternalCostFromLedger, getUserProfileAtDate, getEffectiveSalaryMarkup, getLedgerMonthlyComp, calcCost]);

  const filterEntries = () => {
    // Filter to the current user as a client-side safety net. The server-side scope
    // (initialScopeUserId) is the primary mechanism, but this guards against a stale
    // global fetch result landing in state (e.g. a race condition on initial load or
    // after returning from an admin view before the scoped refetch completes).
    let f = activeTimeEntries.filter(e => e.userId === currentUser.id);
    if (filters.projectId !== 'all') f = f.filter(e => e.projectId === parseInt(filters.projectId));
    if (filters.clientId !== 'all') {
      const cp = activeProjects.filter(p => p.clientId === parseInt(filters.clientId)).map(p => p.id);
      f = f.filter(e => normalizedIncludes(cp, e.projectId));
    }
    if (filters.billable !== 'all') f = f.filter(e => e.billable === (filters.billable === 'true'));
    if (filters.startDate && filters.endDate) f = f.filter(e => e.date >= filters.startDate && e.date <= filters.endDate);
    return f.sort((a,b) => new Date(b.date) - new Date(a.date));
  };

  const exportCSV = () => {
    const f = filterEntries();
    const h = ['Date','User','Email','Client','Project','Stage','Description','Start time','End time','Hours','Billable','Tags','Amount'];
    const rows = f.map(e => {
      const u = activeUsers.find(x => x.id === e.userId);
      const p = activeProjects.find(x => x.id === e.projectId);
      const c = activeClients.find(x => x.id === p?.clientId);
      const t = activeTasks.find(x => x.id === e.taskId);
      return [e.date, u?.name||'', u?.email||'', c?.name||'', fmtProject(p)||'', t?.name||'', e.description, '', '', (e.duration/3600).toFixed(2), e.billable?'Yes':'No', '', isAdmin?Math.round(calcRev(e)):''].map(x => `"${x}"`).join(',');
    });
    const csv = [h.join(','), ...rows].join('\n');
    const blob = new Blob([csv], {type:'text/csv'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `timetracker-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getStorageInfo = () => {
    const size = new Blob([JSON.stringify(timeEntries)]).size;
    const sizeKB = (size / 1024).toFixed(2);
    const sizeMB = (size / (1024 * 1024)).toFixed(2);
    
    const entryCount = timeEntries.length;
    const oldestEntry = timeEntries.reduce((oldest, e) => 
      !oldest || new Date(e.date) < new Date(oldest.date) ? e : oldest
    , null);
    const newestEntry = timeEntries.reduce((newest, e) => 
      !newest || new Date(e.date) > new Date(newest.date) ? e : newest
    , null);
    
    let totalLocalStorageSize = 0;
    for (const key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        totalLocalStorageSize += localStorage[key].length + key.length;
      }
    }
    const totalSizeKB = (totalLocalStorageSize / 1024).toFixed(2);
    const totalSizeMB = (totalLocalStorageSize / (1024 * 1024)).toFixed(2);
    
    alert(
      `📊 Storage Information\n\n` +
      `Time Entries: ${entryCount.toLocaleString()}\n` +
      `Entries Data Size: ${sizeKB} KB (${sizeMB} MB)\n` +
      `Total Storage Used: ${totalSizeKB} KB (${totalSizeMB} MB)\n` +
      `Date Range: ${oldestEntry?.date || 'N/A'} to ${newestEntry?.date || 'N/A'}\n\n` +
      `Browser Limit: ~5-10 MB\n` +
      `Usage: ${((totalLocalStorageSize / (5 * 1024 * 1024)) * 100).toFixed(0)}% of typical 5MB limit\n\n` +
      `💡 Tip: If approaching limit, use "Cleanup Old Data" to archive entries older than 12 months.`
    );
  };

  const cleanupOldEntries = () => {
    if (!isAdmin) {
      alert('Only admins can cleanup data');
      return;
    }
    
    const monthsToKeep = prompt(
      '🧹 Cleanup Old Data\n\n' +
      'How many months of data would you like to keep?\n\n' +
      '(Recommended: 12 months)\n' +
      'Older entries will be exported to CSV before deletion.',
      '12'
    );
    
    if (!monthsToKeep) return;
    
    const months = parseInt(monthsToKeep);
    if (isNaN(months) || months < 1) {
      alert('Please enter a valid number of months');
      return;
    }
    
    const cutoffDate = new Date();
    cutoffDate.setMonth(cutoffDate.getMonth() - months);
    const cutoffStr = cutoffDate.toISOString().split('T')[0];
    
    const oldEntries = timeEntries.filter(e => e.date < cutoffStr);
    const newEntries = timeEntries.filter(e => e.date >= cutoffStr);
    
    if (oldEntries.length === 0) {
      alert(`✅ No entries older than ${months} months found.\n\nYour data is already optimized!`);
      return;
    }
    
    const oldSizeKB = (new Blob([JSON.stringify(oldEntries)]).size / 1024).toFixed(2);
    
    const confirmMsg = 
      `Found ${oldEntries.length.toLocaleString()} entries older than ${months} months\n` +
      `(${oldSizeKB} KB of data)\n\n` +
      `These will be:\n` +
      `1. Exported to CSV (saved to Downloads)\n` +
      `2. Removed from the app\n\n` +
      `You'll keep ${newEntries.length.toLocaleString()} recent entries.\n\n` +
      `Continue?`;
    
    if (!window.confirm(confirmMsg)) return;
    
    // Export old entries first
    const h = ['Date','User','Email','Client','Project','Stage','Description','Start time','End time','Hours','Billable','Tags','Amount'];
    const rows = oldEntries.map(e => {
      const u = activeUsers.find(x => x.id === e.userId);
      const p = activeProjects.find(x => x.id === e.projectId);
      const c = activeClients.find(x => x.id === p?.clientId);
      const t = activeTasks.find(x => x.id === e.taskId);
      return [
        e.date, u?.name||'', u?.email||'', c?.name||'', fmtProject(p)||'', t?.name||'', 
        e.description, '', '', (e.duration/3600).toFixed(2), e.billable?'Yes':'No', '', 
        isAdmin?Math.round(calcRev(e)):''
      ].map(x => `"${x}"`).join(',');
    });
    const csv = [h.join(','), ...rows].join('\n');
    const blob = new Blob([csv], {type:'text/csv'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `archived-entries-before-${cutoffStr}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    // Remove old entries
    setTimeEntries(newEntries);
    
    alert(
      `✅ Cleanup Complete!\n\n` +
      `Removed: ${oldEntries.length.toLocaleString()} old entries\n` +
      `Kept: ${newEntries.length.toLocaleString()} recent entries\n` +
      `Space freed: ~${oldSizeKB} KB\n\n` +
      `Old entries exported to:\n` +
      `archived-entries-before-${cutoffStr}.csv`
    );
  };

  const importCSV = (evt) => {
    const file = evt.target.files[0];
    if (!file) return;
    
    // Show progress modal immediately
    setModal('importProgress');
    setForm({ 
      importProgress: 0, 
      importTotal: 0, 
      importMessage: 'Reading file...' 
    });
    
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const txt = e.target.result.replace(/^\uFEFF/, ''); // strip BOM
        const lines = txt.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter(line => line.trim());

        if (lines.length < 2) {
          setModal('importError');
          setForm({errorMessage: 'CSV file appears to be empty or has no data rows'});
          return;
        }

        // Log first two lines to console for debugging separator/header issues
        console.log('[CSV import] lines[0]:', JSON.stringify(lines[0]));
        console.log('[CSV import] lines[1]:', JSON.stringify(lines[1]));

        // Check header to determine CSV format
        const header = lines[0].toLowerCase();
        const isTopTrackerFormat = header.includes('activity') && header.includes('duration_seconds') && header.includes('start_time');

        // Legacy format: scan first 5 lines for the header row (handles title rows added by Numbers/Excel)
        let legacyHeaderIndex = -1;
        let legacySeparator = ',';
        let headerLegacyFields = [];
        for (let li = 0; li < Math.min(5, lines.length); li++) {
          const tabSplit = lines[li].split('\t').map(f => f.toLowerCase().trim());
          const semiSplit = lines[li].split(';').map(f => f.toLowerCase().trim());
          const commaSplit = lines[li].split(',').map(f => f.toLowerCase().trim());
          let sep, candidates;
          if (tabSplit.length > 1) { sep = '\t'; candidates = tabSplit; }
          else if (semiSplit.length > 1) { sep = ';'; candidates = semiSplit; }
          else { sep = ','; candidates = commaSplit; }
          if (candidates.includes('project') && candidates.includes('workers') &&
              candidates.includes('date') && candidates.includes('hours')) {
            legacyHeaderIndex = li;
            legacySeparator = sep;
            headerLegacyFields = candidates;
            break;
          }
        }
        const isLegacyFormat = !isTopTrackerFormat && legacyHeaderIndex >= 0;
        // Column indices for legacy format (resolved from header)
        const legacyProjectCol = headerLegacyFields.indexOf('project');
        const legacyWorkersCol = headerLegacyFields.indexOf('workers');
        const legacyDateCol = headerLegacyFields.indexOf('date');
        const legacyHoursCol = headerLegacyFields.indexOf('hours');
        console.log('[CSV import] isTopTrackerFormat:', isTopTrackerFormat, '| isLegacyFormat:', isLegacyFormat, '| legacyHeaderIndex:', legacyHeaderIndex, '| legacySeparator:', JSON.stringify(legacySeparator));

        const imported = [];
        const errors = [];
        const newProjects = new Map();
        const newTasks = new Map();
        const newUsers = new Map();

        // Parse CSV line handling quoted fields
        const parseCSVLine = (line) => {
          const result = [];
          let current = '';
          let inQuotes = false;
          
          for (let j = 0; j < line.length; j++) {
            const char = line[j];
            const nextChar = line[j + 1];
            
            if (char === '"') {
              if (inQuotes && nextChar === '"') {
                current += '"';
                j++; // Skip next quote
              } else {
                inQuotes = !inQuotes;
              }
            } else if (char === ',' && !inQuotes) {
              result.push(current);
              current = '';
            } else {
              current += char;
            }
          }
          result.push(current); // Push last field
          return result;
        };

        // Update total count
        setForm({ 
          importProgress: 0, 
          importTotal: lines.length - 1,
          importMessage: `Processing ${lines.length - 1} rows...` 
        });

        // BATCH PROCESSING - Process in chunks to prevent UI freeze
        const BATCH_SIZE = 100; // Process 100 rows at a time
        const totalRows = lines.length - 1;
        
        for (let batchStart = 1; batchStart < lines.length; batchStart += BATCH_SIZE) {
          const batchEnd = Math.min(batchStart + BATCH_SIZE, lines.length);
          
          // Process this batch
          for (let i = batchStart; i < batchEnd; i++) {
          try {
            const fields = parseCSVLine(lines[i]);

            if (isTopTrackerFormat) {
              // TopTracker CSV Format
              // Column A (0): activity = task
              // Column B (1): project = project
              // Column C (2): workers = users
              // Column D (3): duration: ignore
              // Column E (4): time: ignore
              // Column F (5): duration_seconds = time in seconds
              // Column G (6): keyboard_hits: ignore
              // Column H (7): mouse_clicks: ignore
              // Column I (8): screenshot_count: ignore
              // Column J (9): start_time: start Date and time
              // Column K (10): end_time: ignore

              const taskName = fields[0]?.trim() || 'General';
              const projectName = fields[1]?.trim();
              const workerName = fields[2]?.trim();
              const durationSeconds = parseInt(fields[5]) || 0;
              const startTimeStr = fields[9]?.trim();

              // Validate required fields
              if (!projectName || !workerName || durationSeconds === 0 || !startTimeStr) {
                errors.push(`Line ${i + 1}: Missing required data (project, worker, duration, or start_time)`);
                continue;
              }

              // Parse start_time to extract date (format: 2024-04-30T17:00:00+05:30)
              let entryDate = new Date().toISOString().split('T')[0];
              try {
                const dateMatch = startTimeStr.match(/^(\d{4}-\d{2}-\d{2})/);
                if (dateMatch) {
                  entryDate = dateMatch[1];
                } else {
                  errors.push(`Line ${i + 1}: Invalid start_time format "${startTimeStr}"`);
                }
              } catch (err) {
                errors.push(`Line ${i + 1}: Error parsing date from "${startTimeStr}"`);
              }

              // Find or create user
              let userId = currentUser.id;
              if (isAdmin && workerName) {
                let user = activeUsers.find(u => u.name.toLowerCase() === workerName.toLowerCase());
                
                if (!user) {
                  // Check if we've already created this user in this import
                  if (newUsers.has(workerName.toLowerCase())) {
                    userId = newUsers.get(workerName.toLowerCase());
                  } else {
                    // Create new user with unique ID using timestamp + counter
                    const newUserId = Date.now() * 1000 + newUsers.size;
                    newUsers.set(workerName.toLowerCase(), newUserId);
                    userId = newUserId;
                  }
                } else {
                  userId = user.id;
                }
              }

              // Find or create project
              let projectId = projects[0]?.id || 1;
              if (projectName) {
                let project = activeProjects.find(p => p.name.toLowerCase() === projectName.toLowerCase());
                
                if (!project) {
                  // Check if we've already created this project in this import
                  if (newProjects.has(projectName.toLowerCase())) {
                    projectId = newProjects.get(projectName.toLowerCase()).id;
                  } else {
                    // Create new project with unique ID using timestamp + counter
                    const newProjectId = Date.now() * 1000 + newProjects.size + 100;
                    newProjects.set(projectName.toLowerCase(), { id: newProjectId, name: projectName });
                    projectId = newProjectId;
                  }
                } else {
                  projectId = project.id;
                }
              }

              // Find or create task (only create if it matches approved list)
              let taskId = tasks[0]?.id || 1;
              if (taskName) {
                let task = activeTasks.find(t => t.name.toLowerCase() === taskName.toLowerCase());
                
                if (!task) {
                  // Map shorthand codes to specific stage IDs
                  const stageMapping = {
                    'cd': 2,      // CD AD
                    'sd': 4,      // SD AD
                    'dd': 6,      // DD AD
                    'td': 10,     // TD AD
                    'wd': 12,     // GFC 1
                    'id': 3,      // CD ID
                    'cc': 16      // CC
                  };
                  
                  const taskNameLower = taskName.toLowerCase().trim();
                  
                  // Check if it's a shorthand code
                  if (stageMapping[taskNameLower]) {
                    taskId = stageMapping[taskNameLower];
                  } else {
                    // List of full approved task names (case-insensitive)
                    const approvedTasks = [
                      'advance', 'cd ad', 'cd id', 'sd ad', 'sd id', 'dd ad', 'dd id', 
                      'aoc ad', 'aoc id', 'td ad', 'td id', 'gfc 1', 'gfc 2', 'gfc 3', 'completion', 'cc'
                    ];
                    
                    // Only create task if it matches approved list
                    if (approvedTasks.includes(taskNameLower)) {
                      // Check if we've already created this task in this import
                      if (newTasks.has(taskNameLower)) {
                        taskId = newTasks.get(taskNameLower);
                      } else {
                        // Find the matching default task by name
                        const matchingTask = activeTasks.find(t => t.name.toLowerCase() === taskNameLower);
                        if (matchingTask) {
                          taskId = matchingTask.id;
                        } else {
                          // Create new task with unique ID using timestamp + counter
                          const newTaskId = Date.now() * 1000 + newTasks.size + 200;
                          newTasks.set(taskNameLower, newTaskId);
                          taskId = newTaskId;
                        }
                      }
                    } else {
                      // Stage not in approved list, use default stage
                      taskId = tasks[0]?.id || 1;
                      errors.push(`Line ${i + 1}: Stage "${taskName}" not in approved list, using default stage`);
                    }
                  }
                } else {
                  taskId = task.id;
                }
              }

              // Convert duration from seconds to rounded minutes
              const durationMinutes = Math.round(durationSeconds / 60);
              const roundedDuration = round(durationMinutes) * 60; // Convert back to seconds

              // Generate unique ID using timestamp + index + random
              const uniqueId = Date.now() * 10000 + i * 10 + Math.floor(Math.random() * 10);
              
              imported.push({
                id: uniqueId,
                userId: userId,
                projectId: projectId,
                taskId: taskId,
                description: `${taskName} - ${projectName}`,
                duration: roundedDuration,
                date: entryDate,
                billable: true, // Default to billable for TopTracker imports
                notes: `Imported from TopTracker CSV (Worker: ${workerName})`,
                approved: false
              });

            } else if (isLegacyFormat) {
              // Legacy format: project | workers | MM-YYYY | (empty) | hours
              // Skip any rows at or before the detected header row (e.g. a Numbers table title)
              if (i <= legacyHeaderIndex) continue;
              const tabFields = legacySeparator === ','
                ? parseCSVLine(lines[i])
                : lines[i].split(legacySeparator).map(f => f.trim());

              const projectName = tabFields[legacyProjectCol]?.trim();
              const workerName = tabFields[legacyWorkersCol]?.trim();
              const dateStr = tabFields[legacyDateCol]?.trim();
              const hoursRaw = tabFields[legacyHoursCol]?.trim();

              // Skip blank/aggregate rows
              if (!projectName || !workerName || !dateStr || !hoursRaw) continue;

              const totalHours = parseFloat(hoursRaw.replace(',', '.')) || 0;
              if (totalHours <= 0) continue;

              // Parse date as MM-YYYY (e.g. 01-2021)
              let monthYearMonth = 0, monthYearYear = 0;
              try {
                const dateParts = dateStr.split('-');
                if (dateParts.length === 2) {
                  let [m, y] = dateParts.map(p => parseInt(p, 10));
                  if (y < 100) y += 2000;
                  if (m >= 1 && m <= 12 && y >= 2000) {
                    monthYearMonth = m;
                    monthYearYear = y;
                  } else {
                    errors.push(`Line ${i + 1}: Invalid month-year "${dateStr}", skipping row`);
                    continue;
                  }
                } else {
                  errors.push(`Line ${i + 1}: Expected MM-YYYY date format, got "${dateStr}", skipping row`);
                  continue;
                }
              } catch (err) {
                errors.push(`Line ${i + 1}: Error parsing date "${dateStr}"`);
                continue;
              }

              // Resolve user
              let userId = currentUser.id;
              if (isAdmin && workerName) {
                const user = activeUsers.find(u => u.name.toLowerCase() === workerName.toLowerCase());
                if (!user) {
                  if (newUsers.has(workerName.toLowerCase())) {
                    userId = newUsers.get(workerName.toLowerCase());
                  } else {
                    const newUserId = Date.now() * 1000 + newUsers.size;
                    newUsers.set(workerName.toLowerCase(), newUserId);
                    userId = newUserId;
                  }
                } else {
                  userId = user.id;
                }
              }

              // Resolve project
              let projectId = projects[0]?.id || 1;
              if (projectName) {
                const project = activeProjects.find(p => p.name.toLowerCase() === projectName.toLowerCase());
                if (!project) {
                  if (newProjects.has(projectName.toLowerCase())) {
                    projectId = newProjects.get(projectName.toLowerCase()).id;
                  } else {
                    const newProjectId = Date.now() * 1000 + newProjects.size + 100;
                    newProjects.set(projectName.toLowerCase(), { id: newProjectId, name: projectName });
                    projectId = newProjectId;
                  }
                } else {
                  projectId = project.id;
                }
              }

              // Collect working days (Mon–Fri) in the month, capped at 10h/day
              // e.g. 80h → 8 days at 10h each; 65h → 6 days at 10h + 1 day at 5h
              const allWorkingDays = [];
              const scanDay = new Date(monthYearYear, monthYearMonth - 1, 1);
              while (scanDay.getMonth() === monthYearMonth - 1) {
                const dow = scanDay.getDay();
                if (dow !== 0 && dow !== 6) {
                  const y = scanDay.getFullYear();
                  const mo = String(scanDay.getMonth() + 1).padStart(2, '0');
                  const d = String(scanDay.getDate()).padStart(2, '0');
                  allWorkingDays.push(`${y}-${mo}-${d}`);
                }
                scanDay.setDate(scanDay.getDate() + 1);
              }
              if (allWorkingDays.length === 0) continue;
              const totalSeconds = Math.round(totalHours * 3600);
              const maxSecsPerDay = 10 * 3600; // 10h cap per day
              const numDays = Math.min(Math.ceil(totalSeconds / maxSecsPerDay), allWorkingDays.length);
              const workingDays = allWorkingDays.slice(0, numDays);
              let remainingSecs = totalSeconds;
              workingDays.forEach((dayStr, idx) => {
                const isLast = idx === workingDays.length - 1;
                const duration = isLast ? remainingSecs : maxSecsPerDay;
                remainingSecs -= maxSecsPerDay;
                imported.push({
                  id: Date.now() * 10000 + i * 100 + idx,
                  userId: userId,
                  projectId: projectId,
                  taskId: tasks[0]?.id || 1,
                  description: projectName,
                  duration: duration,
                  date: dayStr,
                  billable: true,
                  notes: `Imported from legacy CSV (Worker: ${workerName}, total: ${totalHours}h)`,
                  approved: false
                });
              });

            } else {
              // Standard CSV Format (original code)
              if (fields.length < 10) {
                errors.push(`Line ${i + 1}: Insufficient columns (found ${fields.length}, need at least 10)`);
                continue;
              }

              const [date, uname, uemail, _cname, pname, tname, desc, _startTime, _endTime, hrs, bill] = fields;

              if (!date && !hrs && !desc) {
                errors.push(`Line ${i + 1}: Missing critical data (date, hours, or description)`);
                continue;
              }

              let uid = currentUser.id;
              if (isAdmin && (uname || uemail)) {
                const u = activeUsers.find(x => 
                  (uname && x.name.toLowerCase() === uname.toLowerCase()) || 
                  (uemail && x.email.toLowerCase() === uemail.toLowerCase())
                );
                if (u) uid = u.id;
              }

              let pid = projects[0]?.id || 1;
              if (pname) {
                const p = activeProjects.find(x => x.name.toLowerCase() === pname.toLowerCase());
                if (p) {
                  pid = p.id;
                } else {
                  // Check if we've already created this project in this import
                  if (newProjects.has(pname.toLowerCase())) {
                    pid = newProjects.get(pname.toLowerCase()).id;
                  } else {
                    // Create new project - use timestamp + counter for uniqueness
                    const newProjectId = Date.now() * 1000 + newProjects.size;
                    newProjects.set(pname.toLowerCase(), { id: newProjectId, name: pname });
                    pid = newProjectId;
                  }
                }
              }

              let tid = tasks[0]?.id || 1;
              if (tname) {
                const tnameLower = tname.toLowerCase().trim();
                
                // Map shorthand codes to specific stage IDs
                const stageMapping = {
                  'cd': 2,      // CD AD
                  'sd': 4,      // SD AD
                  'dd': 6,      // DD AD
                  'td': 10,     // TD AD
                  'wd': 12,     // GFC 1
                  'id': 3,      // CD ID
                  'cc': 16      // CC
                };
                
                // Check if it's a shorthand code first
                if (stageMapping[tnameLower]) {
                  tid = stageMapping[tnameLower];
                } else {
                  // Try to find by full name
                  const t = activeTasks.find(x => x.name.toLowerCase() === tnameLower);
                  if (t) {
                    tid = t.id;
                  } else {
                    errors.push(`Line ${i + 1}: Stage "${tname}" not found, using default`);
                  }
                }
              }

              let duration = 0;
              if (hrs) {
                const hoursNum = parseFloat(hrs);
                if (!isNaN(hoursNum) && hoursNum > 0) {
                  duration = hoursNum * 3600;
                } else {
                  errors.push(`Line ${i + 1}: Invalid hours value "${hrs}"`);
                  continue;
                }
              } else {
                errors.push(`Line ${i + 1}: Missing hours value`);
                continue;
              }

              const rdur = round(duration / 60) * 60;

              let entryDate = new Date().toISOString().split('T')[0];
              if (date) {
                const parsedDate = new Date(date);
                if (!isNaN(parsedDate.getTime())) {
                  entryDate = parsedDate.toISOString().split('T')[0];
                } else {
                  errors.push(`Line ${i + 1}: Invalid date "${date}", using today`);
                }
              }

              // Generate unique ID using timestamp + index + random
              const uniqueId = Date.now() * 10000 + i * 10 + Math.floor(Math.random() * 10);
              
              imported.push({
                id: uniqueId,
                userId: uid,
                projectId: pid,
                taskId: tid,
                description: desc || 'Imported entry',
                duration: rdur,
                date: entryDate,
                billable: bill?.toLowerCase() === 'yes' || bill?.toLowerCase() === 'true',
                notes: 'Imported from CSV',
                approved: false
              });
            }
          } catch (rowError) {
            errors.push(`Line ${i + 1}: ${rowError.message}`);
          }
        }
        
        // Update progress after each batch
        const progress = Math.round((batchEnd - 1) / totalRows * 100);
        setForm({ 
          importProgress: progress, 
          importTotal: totalRows,
          importMessage: `Processing: ${batchEnd - 1} / ${totalRows} rows (${progress}%)` 
        });
        
        // Allow UI to update between batches
        await new Promise(resolve => setTimeout(resolve, 0));
      }

        // Create new users, projects, and tasks
        if (isAdmin) {
          const usersToAdd = [];
          newUsers.forEach((userId, userName) => {
            // Generate email: firstname@architecturebrio.com
            const firstName = userName.trim().split(/\s+/)[0].toLowerCase();
            const email = `${firstName}@architecturebrio.com`;
            
            usersToAdd.push({
              id: userId,
              name: userName,
              email: email,
              role: 'user',
              jobRole: 'Junior Architect', // Default job role
              jobLevel: 0, // Default level
              teamId: null, // No team assigned yet
              costRate: 2900,
              markup: 70,
              contractedHours: 176
            });
          });
          if (usersToAdd.length > 0) {
            if (useSupabaseDB) {
              for (const user of usersToAdd) {
                const result = await supabaseUsers.createUser(user);
                if (result.success && result.data?.id) {
                  // Supabase generates its own ID — patch entries referencing the fake one
                  const realId = Number(result.data.id);
                  const fakeId = user.id;
                  for (const entry of imported) {
                    if (entry.userId === fakeId) entry.userId = realId;
                  }
                }
              }
            } else {
              setUsers(prev => [...prev, ...usersToAdd]);
            }
          }

          const projectsToAdd = [];
          newProjects.forEach((projectData, projectNameLower) => {
            // Generate project code from first 2 letters of project name (uppercase)
            const projectCode = projectData.name.replace(/[^A-Za-z0-9]/g, '').substring(0, 2).toUpperCase();
            
            projectsToAdd.push({
              id: projectData.id,
              name: projectData.name,
              projectCode: projectCode,
              clientId: clients[0]?.id || 1,
              color: '#' + Math.floor(Math.random()*16777215).toString(16).padStart(6, '0'),
              budget: 1000000,
              status: 'active',
              supervisors: [],
              budgetAllocation: {},
              retainerConfig: {
                monthlyAmount: 0,
                startDate: null,
                totalMonths: 12,
                status: 'inactive',
                paidMonths: 0
              }
            });
          });
          if (projectsToAdd.length > 0) {
            if (useSupabaseDB) {
              for (const project of projectsToAdd) {
                const result = await supabaseProjects.createProject(project);
                if (result.success && result.data?.id) {
                  // Supabase generates its own ID — patch entries referencing the fake one
                  const realId = Number(result.data.id);
                  const fakeId = project.id;
                  for (const entry of imported) {
                    if (entry.projectId === fakeId) entry.projectId = realId;
                  }
                }
              }
            } else {
              setProjects(prev => [...prev, ...projectsToAdd]);
            }
          }

          const tasksToAdd = [];
          newTasks.forEach((taskId, taskName) => {
            tasksToAdd.push({
              id: taskId,
              name: taskName
            });
          });
          if (tasksToAdd.length > 0) {
            if (useSupabaseDB) {
              for (const task of tasksToAdd) {
                await supabaseTasks.createTask(task);
              }
            } else {
              setTasks(prev => [...prev, ...tasksToAdd]);
            }
          }
        }

        // EFFICIENT UPDATE: Use functional update to avoid re-spreading
        if (imported.length > 0) {
          setForm({ 
            importProgress: 100, 
            importTotal: totalRows,
            importMessage: 'Saving entries...' 
          });
          
          // Small delay to show "Saving" message
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Save entries
          if (useSupabaseDB) {
            let successCount = 0;
            for (const entry of imported) {
              const result = await supabaseTimeEntries.createTimeEntry(entry);
              if (result.success) successCount++;
            }
            if (successCount < imported.length) {
              errors.push(`Warning: Only ${successCount}/${imported.length} entries saved to Supabase`);
            }
          } else {
            // Use functional update for better performance
            setTimeEntries(prevEntries => [...prevEntries, ...imported]);
          }
        }

        // Show detailed results in modal
        let message = `✅ Import Complete!\n\n`;
        message += `✓ Successfully imported: ${imported.length} entries\n`;
        
        if (newUsers.size > 0) {
          message += `✓ Created ${newUsers.size} new user(s)\n`;
        }
        if (newProjects.size > 0) {
          message += `✓ Created ${newProjects.size} new project(s)\n`;
        }
        if (newTasks.size > 0) {
          message += `✓ Created ${newTasks.size} new stage(s)\n`;
        }
        
        if (errors.length > 0) {
          message += `\n⚠️ Warnings/Errors: ${errors.length}\n\n`;
          message += `First few issues:\n${errors.slice(0, 5).join('\n')}`;
          if (errors.length > 5) {
            message += `\n... and ${errors.length - 5} more`;
          }
        }
        
        setForm({importMessage: message});
        setModal('importSuccess');
        evt.target.value = '';
      } catch (error) {
        console.error('Import error:', error);
        setForm({errorMessage: `Error importing CSV: ${error.message}\n\nPlease check that your CSV file matches the expected format.`});
        setModal('importError');
      }
    };
    reader.readAsText(file);
  };

  const _addEntry = async () => {
    if (!form.projectId || !form.taskId) { alert('Fill required fields (Project and Stage)'); return; }
    
    // Check if user can add time to this project (status validation)
    if (!canAddTimeToProject(parseInt(form.projectId))) {
      const project = activeProjects.find(p => p.id === parseInt(form.projectId));
      const statusLabel = getProjectStatusStyle(project?.status).label;
      alert(`⚠️ Cannot Add Time to ${statusLabel} Project\n\nOnly admins can add time entries to projects with status: ${statusLabel}.\n\nRegular users and supervisors can only add time to Active projects.`);
      return;
    }
    
    const dur = ((parseInt(form.hours)||0)*3600) + ((parseInt(form.minutes)||0)*60);
    if (dur === 0) { alert('Enter duration'); return; }
    const rdur = round(dur/60) * 60;
    const entryData = {
      id: Date.now(), // For localStorage
      userId: parseInt(form.userId) || currentUser.id, 
      projectId: parseInt(form.projectId),
      taskId: parseInt(form.taskId), 
      description: form.description || '',
      duration: rdur,
      date: form.date || new Date().toISOString().split('T')[0],
      billable: form.billable !== undefined ? form.billable : isProjectBillable(parseInt(form.projectId)),
      notes: form.notes || '',
      approved: false
    };
    
    if (useSupabaseDB) {
      // Use Supabase
      console.log('Creating time entry in Supabase...');
      const result = await supabaseTimeEntries.createTimeEntry(entryData);
      if (result.success) {
        console.log('✅ Time entry created in Supabase!');
        // Data will update automatically via real-time subscription
      } else {
        alert('Error creating entry: ' + result.error);
        return;
      }
    } else {
      // Use localStorage (existing code)
      setTimeEntries([entryData, ...timeEntries]);
    }
    
    // Handle repeating entries (localStorage only for now)
    if (form.isRepeating && !useSupabaseDB) {
      setRepeatingEntries([...repeatingEntries, {
        id: Date.now()+1, ...entryData, frequency: form.repeatFrequency, startDate: entryData.date,
        endDate: form.repeatEndDate || null, lastCreated: entryData.date
      }]);
    }
    
    setModal(null);
    setForm({});
  };

  const updateEntry = async () => {
    const dur = ((parseInt(editing.hours)||0)*3600) + ((parseInt(editing.minutes)||0)*60);
    const rdur = round(dur/60) * 60;
    
    if (useSupabaseDB) {
      // Only send DB-compatible fields, not UI-only fields like hours/minutes
      const updatePayload = {
        description: editing.description,
        duration: rdur,
        notes: editing.notes,
        projectId: editing.projectId,
        taskId: editing.taskId,
        billable: editing.billable
      };
      const result = await supabaseTimeEntries.updateTimeEntry(editing.id, updatePayload);
      if (!result.success) {
        alert('Error updating entry: ' + result.error);
        return;
      }
      // Refetch to ensure UI shows the persisted data
      await supabaseTimeEntries.refetch();
    } else {
      setTimeEntries(timeEntries.map(e => e.id === editing.id ? {...editing, duration: rdur} : e));
    }
    setEditing(null);
  };


  // === LEAVE MANAGEMENT HELPER FUNCTIONS ===
  
  const calculateYearsEmployed = (userId) => {
    const user = activeUsers.find(u => u.id === userId);
    if (!user || !user.joinDate) return 0;
    const joinDate = new Date(user.joinDate);
    const today = new Date();
    const years = (today - joinDate) / (365.25 * 24 * 60 * 60 * 1000);
    return Math.floor(years);
  };
  
  const getLeavePolicy = (roleLevel, year) => {
    const policies = leavePolicyMatrix
      .filter(p => p.roleLevel === roleLevel && p.effectiveYear <= year)
      .sort((a, b) => b.effectiveYear - a.effectiveYear);
    const foundPolicy = policies[0];
    // Always normalize to ensure no NaN values reach inputs
    return foundPolicy 
      ? normalizeLeavePolicyNumericFields(foundPolicy)
      : {
          roleLevel, 
          baseLeave: 15, 
          roleAdditionalLeave: 0, 
          tenureIncrementPerYear: 0.5, 
          effectiveYear: year
        };
  };
  
  const calculateLeaveEntitlement = (userId, year) => {
    const user = activeUsers.find(u => u.id === userId);
    if (!user) return null;
    const roleLevel = user.jobRole || 'Junior Architect';
    const yearsEmployed = calculateYearsEmployed(userId);
    const policy = getLeavePolicy(roleLevel, year);
    const calculatedTotal = policy.baseLeave + policy.roleAdditionalLeave + 
                           (yearsEmployed * policy.tenureIncrementPerYear);
    
    // Round up to full numbers (ceiling)
    const roundedTotal = Math.ceil(calculatedTotal);
    
    // Cap at maximum 28 days
    const cappedTotal = Math.min(roundedTotal, 28);
    
    return {
      userId, year, roleLevel, yearsEmployed,
      baseLeave: policy.baseLeave,
      roleAdditionalLeave: policy.roleAdditionalLeave,
      tenureIncrementPerYear: policy.tenureIncrementPerYear,
      calculatedTotalLeave: roundedTotal,
      manualOverride: null,
      finalTotalLeave: cappedTotal
    };
  };
  
  const getEmployeeEntitlement = (userId, year) => {
    let entitlement = employeeLeaveEntitlement.find(
      e => e.userId === userId && e.year === year
    );
    if (!entitlement) {
      entitlement = calculateLeaveEntitlement(userId, year);
    }
    return entitlement;
  };
  
  const recalculateAllEntitlements = async (year) => {
    const newEntitlements = activeUsers.map(user => {
      const existing = employeeLeaveEntitlement.find(
        e => e.userId === user.id && e.year === year
      );
      const calculated = calculateLeaveEntitlement(user.id, year);
      if (existing && existing.manualOverride !== null) {
        return {
          ...calculated,
          manualOverride: existing.manualOverride,
          finalTotalLeave: existing.manualOverride
        };
      }
      return calculated;
    });
    const otherYears = employeeLeaveEntitlement.filter(e => e.year !== year);
    
    // Optimistic local update
    setEmployeeLeaveEntitlement([...otherYears, ...newEntitlements]);
    
    // Save to Supabase if in Supabase mode
    if (useSupabaseDB) {
      await batchSaveEntitlementsToSupabase(newEntitlements, year);
    } else {
      // Only log to local audit in localStorage mode
      logLeaveAudit({
        action: 'recalculate_all', year: year, performedBy: currentUser.id,
        details: `Recalculated ${newEntitlements.length} employees`
      });
    }
  };
  
  const updateLeavePolicyMatrix = (roleLevel, field, value, year) => {
    // Parse value safely - store null for empty, finite number otherwise
    const parsedValue = parseNumericInputValue(value);
    // For storage, use the parsed value (may be null for empty input)
    // The UI will handle null/undefined via safeNumericInputValue
    const storageValue = parsedValue ?? 0; // Default to 0 if null for calculations
    
    // Take snapshot of current state BEFORE first edit (for audit comparison)
    if (!leavePolicyDirty && !leavePolicySnapshot) {
      setLeavePolicySnapshot([...leavePolicyMatrix]);
    }
    
    const existingPolicy = leavePolicyMatrix.find(
      p => p.roleLevel === roleLevel && p.effectiveYear === year
    );
    
    let newPolicyMatrix;
    if (existingPolicy) {
      newPolicyMatrix = leavePolicyMatrix.map(p => 
        p.roleLevel === roleLevel && p.effectiveYear === year
          ? { ...p, [field]: storageValue } : p
      );
    } else {
      const basePolicy = getLeavePolicy(roleLevel, year);
      newPolicyMatrix = [...leavePolicyMatrix, {
        ...basePolicy, [field]: storageValue, effectiveYear: year
      }];
    }
    
    // Update local state only - NO Supabase save here
    setLeavePolicyMatrix(newPolicyMatrix);
    setLeavePolicyDirty(true);
    
    // Note: Supabase persistence happens ONLY via handleSaveLeavePolicy (Save button)
    // localStorage mode still auto-saves via useEffect (existing behavior)
  };
  
  // Handler for explicit Save button - persists to Supabase
  const handleSaveLeavePolicy = async () => {
    if (leavePolicySaving) return; // Prevent double-save
    
    setLeavePolicySaving(true);
    console.log('[LeavePolicy] Save button clicked, saving to Supabase...');
    
    try {
      if (useSupabaseDB) {
        // Use the snapshot (before edits) for audit comparison
        const beforePolicy = leavePolicySnapshot || [];
        const result = await saveLeavePolicyToSupabase(leavePolicyMatrix, beforePolicy);
        
        if (result.success) {
          console.log('[LeavePolicy] ✅ Policy saved successfully');
          // Clear dirty state and snapshot after successful save
          setLeavePolicyDirty(false);
          setLeavePolicySnapshot(null);
          // Recalculate entitlements after save
          await recalculateAllEntitlements(selectedLeaveYear);
        } else {
          console.error('[LeavePolicy] ❌ Failed to save policy:', result.error);
        }
      } else {
        // localStorage mode - just log audit and recalculate
        logLeaveAudit({
          action: 'POLICY_UPDATED',
          performedBy: currentUser.id,
          details: 'Leave policy matrix updated'
        });
        setLeavePolicyDirty(false);
        setLeavePolicySnapshot(null);
        await recalculateAllEntitlements(selectedLeaveYear);
        console.log('[LeavePolicy] ✅ Policy saved (localStorage mode)');
      }
    } catch (err) {
      console.error('[LeavePolicy] ❌ Unexpected error saving policy:', err);
    } finally {
      setLeavePolicySaving(false);
    }
  };
  
  // ========================================================================
  // NOTICE POLICY HANDLERS (mirrors Leave Policy — 3-component model)
  // notice_total = baseNotice + roleAdditionalNotice + (years × tenureIncrementPerYear)
  // ========================================================================

  // Get notice policy for a role/year (mirrors getLeavePolicy)
  const getNoticePolicy = (roleLevel, year) => {
    const policies = noticePolicyMatrix
      .filter(p => p.roleLevel === roleLevel && p.effectiveYear <= year)
      .sort((a, b) => b.effectiveYear - a.effectiveYear);
    const foundPolicy = policies[0];
    // Always normalize to ensure no NaN values reach inputs
    return foundPolicy
      ? normalizeNoticePolicyNumericFields(foundPolicy)
      : {
          roleLevel,
          baseNotice: 1,
          roleAdditionalNotice: 0,
          tenureIncrementPerYear: 0,
          effectiveYear: year
        };
  };

  // Update a single field in the notice policy matrix (mirrors updateLeavePolicyMatrix)
  const updateNoticePolicyMatrix = (roleLevel, field, value, year) => {
    // Parse value safely - store null for empty, finite number otherwise
    const parsedValue = parseNumericInputValue(value);
    // For storage, use the parsed value (may be null for empty input)
    const storageValue = parsedValue ?? 0; // Default to 0 if null for calculations

    // Take snapshot of current state BEFORE first edit (for audit comparison)
    if (!noticePolicyDirty && !noticePolicySnapshot) {
      setNoticePolicySnapshot([...noticePolicyMatrix]);
    }

    const existingPolicy = noticePolicyMatrix.find(
      p => p.roleLevel === roleLevel && p.effectiveYear === year
    );

    let newPolicyMatrix;
    if (existingPolicy) {
      newPolicyMatrix = noticePolicyMatrix.map(p =>
        p.roleLevel === roleLevel && p.effectiveYear === year
          ? { ...p, [field]: storageValue } : p
      );
    } else {
      const basePolicy = getNoticePolicy(roleLevel, year);
      newPolicyMatrix = [...noticePolicyMatrix, {
        ...basePolicy, [field]: storageValue, effectiveYear: year
      }];
    }

    // Update local state only - NO Supabase save here
    setNoticePolicyMatrix(newPolicyMatrix);
    setNoticePolicyDirty(true);

    // Note: Supabase persistence happens ONLY via handleSaveNoticePolicy (Save button)
  };

  // Handler for explicit Save button - persists to Supabase (mirrors handleSaveLeavePolicy)
  const handleSaveNoticePolicy = async () => {
    if (noticePolicySaving) return; // Prevent double-save

    setNoticePolicySaving(true);
    console.log('[NoticePolicy] Save button clicked, saving to Supabase...');

    try {
      if (useSupabaseDB) {
        // Use the snapshot (before edits) for audit comparison
        const beforePolicy = noticePolicySnapshot || [];
        const result = await saveNoticePolicyToSupabase(noticePolicyMatrix, beforePolicy);

        if (result.success) {
          console.log('[NoticePolicy] ✅ Policy saved successfully');
          // Clear dirty state and snapshot after successful save
          setNoticePolicyDirty(false);
          setNoticePolicySnapshot(null);
        } else {
          console.error('[NoticePolicy] ❌ Failed to save policy:', result.error);
        }
      } else {
        // localStorage mode - just clear dirty state
        setNoticePolicyDirty(false);
        setNoticePolicySnapshot(null);
        console.log('[NoticePolicy] ✅ Policy saved (localStorage mode)');
      }
    } catch (err) {
      console.error('[NoticePolicy] ❌ Unexpected error saving policy:', err);
    } finally {
      setNoticePolicySaving(false);
    }
  };

  // ========================================================================
  // NOTICE ENTITLEMENT CALCULATION & OVERRIDES (mirrors Leave Entitlement)
  // Uses same calculateYearsEmployed, same user lookup, same override pattern
  // ========================================================================

  // Calculate notice period for a user (mirrors calculateLeaveEntitlement)
  const calculateNoticeEntitlement = (userId, year) => {
    const user = activeUsers.find(u => u.id === userId);
    if (!user) return null;
    const roleLevel = user.jobRole || 'Junior Architect';
    const yearsEmployed = calculateYearsEmployed(userId);
    const policy = getNoticePolicy(roleLevel, year);
    const calculatedTotal = policy.baseNotice + policy.roleAdditionalNotice +
                           (yearsEmployed * policy.tenureIncrementPerYear);

    // Round up to whole months (ceiling) — mirrors leave rounding
    const roundedTotal = Math.ceil(calculatedTotal);

    // Cap at maximum 6 months
    const cappedTotal = Math.min(roundedTotal, 6);

    return {
      userId, year, roleLevel, yearsEmployed,
      baseNotice: policy.baseNotice,
      roleAdditionalNotice: policy.roleAdditionalNotice,
      tenureIncrementPerYear: policy.tenureIncrementPerYear,
      calculatedTotalNotice: roundedTotal,
      manualOverride: null,
      finalTotalNotice: cappedTotal
    };
  };

  // Get employee notice entitlement (mirrors getEmployeeEntitlement)
  // Uses notice_override from employeeLeaveEntitlement (same table, no separate state)
  const getEmployeeNoticeEntitlement = (userId, year) => {
    const stored = employeeLeaveEntitlement.find(
      e => e.userId === userId && e.year === year
    );
    const calculated = calculateNoticeEntitlement(userId, year);
    if (!calculated) return calculated;

    if (stored && stored.noticeOverride !== null && stored.noticeOverride !== undefined) {
      return {
        ...calculated,
        manualOverride: stored.noticeOverride,
        finalTotalNotice: stored.noticeOverride
      };
    }

    return calculated;
  };

  // Set manual notice override (mirrors setManualLeaveOverride)
  // Writes notice_override column on employee_leave_entitlements
  const setManualNoticeOverride = async (userId, year, overrideValue) => {
    const stored = employeeLeaveEntitlement.find(
      e => e.userId === userId && e.year === year
    );
    const beforeOverride = stored?.noticeOverride ?? null;

    // Optimistic local update — add noticeOverride to the stored entitlement
    const updated = stored
      ? { ...stored, noticeOverride: overrideValue }
      : { userId, year, manualOverride: null, noticeOverride: overrideValue };

    const otherEntitlements = employeeLeaveEntitlement.filter(
      e => !(e.userId === userId && e.year === year)
    );
    setEmployeeLeaveEntitlement([...otherEntitlements, updated]);

    // Save to Supabase
    if (useSupabaseDB) {
      try {
        const dbData = {
          user_id: userId,
          year: year,
          notice_override: overrideValue
        };
        const { error } = await supabase
          .from('employee_leave_entitlements')
          .upsert([dbData], { onConflict: 'user_id,year' });

        if (error) {
          console.error('[NoticePolicy] Error saving notice override:', error);
        }

        // Audit log
        const auditEntry = {
          action: 'NOTICE_OVERRIDE_SET',
          actor_user_id: currentUser.id,
          target_user_id: userId,
          payload: { userId, year, before: beforeOverride, after: overrideValue }
        };
        const { error: auditError } = await supabase
          .from('leave_audit_log')
          .insert([auditEntry]);
        if (auditError) {
          console.warn('[NoticePolicy] Failed to insert audit log:', auditError);
        }
      } catch (err) {
        console.error('[NoticePolicy] Unexpected error saving notice override:', err);
      }
    }
  };

  // Remove manual notice override (mirrors removeManualLeaveOverride)
  const removeManualNoticeOverride = async (userId, year) => {
    const stored = employeeLeaveEntitlement.find(
      e => e.userId === userId && e.year === year
    );

    // Optimistic local update
    const updated = stored
      ? { ...stored, noticeOverride: null }
      : { userId, year, manualOverride: null, noticeOverride: null };

    const otherEntitlements = employeeLeaveEntitlement.filter(
      e => !(e.userId === userId && e.year === year)
    );
    setEmployeeLeaveEntitlement([...otherEntitlements, updated]);

    // Save to Supabase
    if (useSupabaseDB) {
      try {
        const dbData = {
          user_id: userId,
          year: year,
          notice_override: null
        };
        const { error } = await supabase
          .from('employee_leave_entitlements')
          .upsert([dbData], { onConflict: 'user_id,year' });

        if (error) {
          console.error('[NoticePolicy] Error removing notice override:', error);
        }

        // Audit log
        const auditEntry = {
          action: 'NOTICE_OVERRIDE_REMOVED',
          actor_user_id: currentUser.id,
          target_user_id: userId,
          payload: { userId, year }
        };
        const { error: auditError } = await supabase
          .from('leave_audit_log')
          .insert([auditEntry]);
        if (auditError) {
          console.warn('[NoticePolicy] Failed to insert audit log:', auditError);
        }
      } catch (err) {
        console.error('[NoticePolicy] Unexpected error removing notice override:', err);
      }
    }
  };

  const setManualLeaveOverride = async (userId, year, overrideValue) => {
    const entitlement = getEmployeeEntitlement(userId, year);
    const beforeEntitlement = { ...entitlement };
    const updated = {
      ...entitlement, manualOverride: overrideValue, finalTotalLeave: overrideValue
    };
    const otherEntitlements = employeeLeaveEntitlement.filter(
      e => !(e.userId === userId && e.year === year)
    );
    
    // Optimistic local update
    setEmployeeLeaveEntitlement([...otherEntitlements, updated]);
    
    // Save to Supabase if in Supabase mode
    if (useSupabaseDB) {
      await saveEntitlementToSupabase(updated, beforeEntitlement);
    } else {
      // Only log to local audit in localStorage mode
      const user = activeUsers.find(u => u.id === userId);
      logLeaveAudit({
        action: 'manual_override', userId, userName: user?.name, year,
        oldValue: entitlement.calculatedTotalLeave, newValue: overrideValue,
        performedBy: currentUser.id, reason: 'Manual adjustment by admin'
      });
    }
  };
  
  const removeManualLeaveOverride = async (userId, year) => {
    const entitlement = getEmployeeEntitlement(userId, year);
    const beforeEntitlement = { ...entitlement };
    const updated = {
      ...entitlement, manualOverride: null, finalTotalLeave: entitlement.calculatedTotalLeave
    };
    const otherEntitlements = employeeLeaveEntitlement.filter(
      e => !(e.userId === userId && e.year === year)
    );
    
    // Optimistic local update
    setEmployeeLeaveEntitlement([...otherEntitlements, updated]);
    
    // Save to Supabase if in Supabase mode
    if (useSupabaseDB) {
      await saveEntitlementToSupabase(updated, beforeEntitlement);
    } else {
      // Only log to local audit in localStorage mode
      const user = activeUsers.find(u => u.id === userId);
      logLeaveAudit({
        action: 'remove_override', userId, userName: user?.name, year,
        performedBy: currentUser.id
      });
    }
  };
  
  const logLeaveAudit = async (auditEntry) => {
    const entry = { id: Date.now(), timestamp: new Date().toISOString(), ...auditEntry };
    
    // Optimistic local update
    setLeaveAuditLog(prev => [entry, ...prev]);
    
    // Save to Supabase if in Supabase mode
    if (useSupabaseDB) {
      await insertAuditLogToSupabase(entry);
    }
  };
  
  // Annual cap on Emergency Leave (working days per calendar year). Beyond
  // this, requests still go through but skip supervisor and route directly
  // to admin.
  const EMERGENCY_LEAVE_ANNUAL_CAP = 5;

  const calculateLeaveTaken = (userId, year) => {
    const yearStart = `${year}-01-01`;
    const yearEnd = `${year}-12-31`;
    const approvedLeaves = activeTimeOff.filter(
      to => to.userId === userId && to.status === 'approved' &&
           to.startDate >= yearStart && to.startDate <= yearEnd
    );
    const byType = { sick: 0, vacation: 0, emergency: 0, wfh: 0, total: 0 };
    approvedLeaves.forEach(leave => {
      const days = leave.workingDays || countWorkingDays(leave.startDate, leave.endDate);
      // Legacy: "personal" rows (pre-migration 20260514000001) are folded
      // into vacation so historical totals match the new model.
      const effectiveType = leave.type === 'personal' ? 'vacation' : leave.type;
      byType[effectiveType] = (byType[effectiveType] || 0) + days;

      // Deduction policy:
      //   Vacation, Emergency  -> always counts against annual leave
      //   Sick, WFH            -> first 5 days/year free, beyond counts
      if (effectiveType === 'vacation' || effectiveType === 'emergency') {
        byType.total += days;
      } else if (effectiveType === 'wfh') {
        const wfhUsedSoFar = byType.wfh - days;
        const freeDaysRemaining = Math.max(0, 5 - wfhUsedSoFar);
        const chargeableDays = Math.max(0, days - freeDaysRemaining);
        byType.total += chargeableDays;
      } else if (effectiveType === 'sick') {
        const sickUsedSoFar = byType.sick - days;
        const freeDaysRemaining = Math.max(0, 5 - sickUsedSoFar);
        const chargeableDays = Math.max(0, days - freeDaysRemaining);
        byType.total += chargeableDays;
      }
    });
    return byType;
  };

  // Working-day count of Emergency Leave already approved or in flight
  // (pending_supervisor / pending_admin / pending) for the given user and
  // year. Used to decide whether a new emergency request skips supervisor.
  const getEmergencyUsageForYear = (userId, year) => {
    const yearStart = `${year}-01-01`;
    const yearEnd = `${year}-12-31`;
    return activeTimeOff
      .filter(to =>
        to.userId === userId &&
        to.type === 'emergency' &&
        (to.status === 'approved' || to.status === 'pending'
          || to.status === 'pending_supervisor' || to.status === 'pending_admin') &&
        to.startDate >= yearStart && to.startDate <= yearEnd
      )
      .reduce((sum, leave) => sum + (leave.workingDays || countWorkingDays(leave.startDate, leave.endDate)), 0);
  };

  // Returns {date:'YYYY-MM-DD', hour:0-23} for the current moment in IST.
  // Used to enforce Emergency Leave's 08:00 IST same-day cutoff.
  const getNowInIST = () => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(new Date());
    const get = (t) => parts.find(p => p.type === t)?.value || '';
    return {
      date: `${get('year')}-${get('month')}-${get('day')}`,
      hour: parseInt(get('hour'), 10) || 0,
    };
  };
  
  const getYearlyLeaveSummary = (year) => {
    return activeUsers.map(user => {
      const entitlement = getEmployeeEntitlement(user.id, year);
      const leaveTaken = calculateLeaveTaken(user.id, year);
      const remaining = entitlement.finalTotalLeave - leaveTaken.total;
      const utilizationPercent = entitlement.finalTotalLeave > 0 
        ? (leaveTaken.total / entitlement.finalTotalLeave * 100) : 0;
      return {
        user, entitlement, leaveTaken, remaining, utilizationPercent,
        isNearLimit: remaining < 2 && remaining >= 0,
        hasExceeded: remaining < 0
      };
    }).sort((a, b) => a.user.name.localeCompare(b.user.name));
  };

  // Check if current user is supervisor of the requesting user
  // === LEAVE VALIDATION FUNCTIONS ===
  
  // Count working days between two dates (excluding weekends and holidays)
  const countWorkingDays = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    let count = 0;
    const current = new Date(start);
    
    while (current <= end) {
      const dayOfWeek = current.getDay();
      const dateStr = current.toISOString().split('T')[0];
      
      // Check if it's not a weekend and not a holiday
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const isHoliday = userHolidays.some(h => h.date === dateStr);

      if (!isWeekend && !isHoliday) {
        count++;
      }

      current.setDate(current.getDate() + 1);
    }

    return count;
  };

  // Check if there are sufficient working days between two leave periods
  const countWorkingDaysBetween = (date1, date2) => {
    const start = new Date(Math.min(new Date(date1), new Date(date2)));
    const end = new Date(Math.max(new Date(date1), new Date(date2)));

    // Add one day to start to exclude the boundary
    start.setDate(start.getDate() + 1);
    // Subtract one day from end to exclude the boundary
    end.setDate(end.getDate() - 1);

    if (start > end) return 0;

    let count = 0;
    const current = new Date(start);

    while (current <= end) {
      const dayOfWeek = current.getDay();
      const dateStr = current.toISOString().split('T')[0];

      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const isHoliday = userHolidays.some(h => h.date === dateStr);
      
      if (!isWeekend && !isHoliday) {
        count++;
      }
      
      current.setDate(current.getDate() + 1);
    }
    
    return count;
  };
  
  // Validate maximum continuous holiday and interruption requirements
  const validateContinuousLeave = (userId, startDate, endDate, leaveType, isAdmin, editingLeaveId = null) => {
    // Admin can bypass all restrictions
    if (isAdmin) {
      console.log('Admin bypass - skipping continuous leave validation');
      return { valid: true };
    }
    
    // Vacation only. Emergency Leave has its own rules
    // (validateAdvanceNotice), Sick / WFH have none.
    if (leaveType !== 'vacation') {
      return { valid: true };
    }

    const requestedDays = countWorkingDays(startDate, endDate);

    // Rule 1: Maximum 15 continuous working days per request.
    if (requestedDays > 15) {
      return {
        valid: false,
        message: `⚠️ Maximum Continuous Vacation Exceeded\n\nYou are requesting ${requestedDays} working days, but the maximum continuous vacation is 15 working days.\n\nPlease split your leave into multiple requests with at least 5 working days between them.`
      };
    }

    // Rule 2: 5-working-day gap between vacation periods.
    const start = new Date(startDate);
    const end = new Date(endDate);

    const userLeaves = activeTimeOff.filter(leave =>
      leave.userId === userId &&
      // Gap rule is vacation-only. Personal is legacy (folded into vacation),
      // emergency is exempt.
      (leave.type === 'vacation' || leave.type === 'personal') &&
      (leave.status === 'approved' || leave.status === 'pending' ||
       leave.status === 'pending_supervisor' || leave.status === 'pending_admin') &&
      leave.id !== editingLeaveId
    );
    
    for (const existingLeave of userLeaves) {
      const existingStart = new Date(existingLeave.startDate);
      const existingEnd = new Date(existingLeave.endDate);
      
      // Check if new leave is before existing leave
      if (end < existingStart) {
        const daysBetween = countWorkingDaysBetween(end, existingStart);
        if (daysBetween < 5) {
          return {
            valid: false,
            message: `⚠️ Insufficient Gap Between Holidays\n\nYour requested leave (${startDate} to ${endDate}) is only ${daysBetween} working day${daysBetween === 1 ? '' : 's'} away from your existing leave (${existingLeave.startDate} to ${existingLeave.endDate}).\n\nYou must have at least 5 working days between consecutive holiday periods.\n\nPlease adjust your dates or cancel the conflicting leave request.`
          };
        }
      }
      
      // Check if new leave is after existing leave
      if (start > existingEnd) {
        const daysBetween = countWorkingDaysBetween(existingEnd, start);
        if (daysBetween < 5) {
          return {
            valid: false,
            message: `⚠️ Insufficient Gap Between Holidays\n\nYour requested leave (${startDate} to ${endDate}) is only ${daysBetween} working day${daysBetween === 1 ? '' : 's'} away from your existing leave (${existingLeave.startDate} to ${existingLeave.endDate}).\n\nYou must have at least 5 working days between consecutive holiday periods.\n\nPlease adjust your dates or cancel the conflicting leave request.`
          };
        }
      }
      
      // Check for overlapping dates
      if ((start >= existingStart && start <= existingEnd) || 
          (end >= existingStart && end <= existingEnd) ||
          (start <= existingStart && end >= existingEnd)) {
        return {
          valid: false,
          message: `⚠️ Overlapping Leave Dates\n\nYour requested leave (${startDate} to ${endDate}) overlaps with your existing ${existingLeave.type} leave (${existingLeave.startDate} to ${existingLeave.endDate}).\n\nPlease choose different dates or edit/cancel the conflicting leave.`
        };
      }
    }
    
    return { valid: true };
  };
  
  // Notice tiers for Vacation. Length is in working days, notice is in
  // calendar days before the start date.
  //   1–2 working days  -> 5 calendar days' notice
  //   3–5 working days  -> 7 calendar days' notice
  //   6+ working days   -> 21 calendar days' notice
  const getVacationNoticeRequirement = (workingDays) => {
    if (workingDays <= 2) return { days: 5, label: '1–2 working days' };
    if (workingDays <= 5) return { days: 7, label: '3–5 working days' };
    return { days: 21, label: '6+ working days' };
  };

  // Validate advance notice requirements
  const validateAdvanceNotice = (startDate, leaveType, workingDays, isAdmin) => {
    if (isAdmin) return { valid: true };

    // Sick / WFH have no notice requirement.
    if (leaveType === 'sick' || leaveType === 'wfh') return { valid: true };

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    const daysUntilLeave = Math.ceil((start - today) / (1000 * 60 * 60 * 24));

    if (leaveType === 'vacation' || leaveType === 'personal') {
      const tier = getVacationNoticeRequirement(workingDays);
      if (daysUntilLeave < tier.days) {
        return {
          valid: false,
          message: `⚠️ Vacation of ${tier.label} requires ${tier.days} days' notice.\n\nYou are requesting ${workingDays} working days with only ${daysUntilLeave} days notice.\n\nIf this is short-notice and unavoidable, use Emergency Leave instead (max 2 working days per request, max 5 per year, must be submitted before 08:00 IST on the day the leave starts).`
        };
      }
      return { valid: true };
    }

    if (leaveType === 'emergency') {
      // Emergency Leave window: submission allowed only within 5 calendar
      // days of the start date, and never after 08:00 IST on the start day.
      if (daysUntilLeave > 5) {
        return {
          valid: false,
          message: `⚠️ Emergency Leave can only be requested within 5 days of the start date.\n\nFor anything further out, please submit Vacation Leave instead.`
        };
      }

      const istNow = getNowInIST();
      if (istNow.date > startDate) {
        return {
          valid: false,
          message: `⚠️ Emergency Leave cannot be backdated.\n\nThe start date is in the past (Asia/Kolkata).`
        };
      }
      if (istNow.date === startDate && istNow.hour >= 8) {
        return {
          valid: false,
          message: `⚠️ Emergency Leave for today must be submitted before 08:00 IST.\n\nIt is already past the cutoff. Please contact your supervisor directly and, if needed, submit Emergency Leave for tomorrow.`
        };
      }

      return { valid: true };
    }

    return { valid: true };
  };

  // Working-day max length for a single Emergency Leave request.
  const EMERGENCY_LEAVE_MAX_LENGTH = 2;

  // Validate the additional Emergency-Leave-only rule: length per request.
  const validateEmergencyLength = (workingDays, isAdmin) => {
    if (isAdmin) return { valid: true };
    if (workingDays > EMERGENCY_LEAVE_MAX_LENGTH) {
      return {
        valid: false,
        message: `⚠️ Emergency Leave is limited to ${EMERGENCY_LEAVE_MAX_LENGTH} working days per request.\n\nYou are requesting ${workingDays} working days. For anything longer, please submit Vacation Leave with appropriate notice.`
      };
    }
    return { valid: true };
  };
  
  // Check if leave request exceeds available balance. May also flag
  // advisory outcomes (e.g. emergencyCapExceeded for over-cap Emergency Leave).
  const checkLeaveBalance = (userId, leaveType, workingDays, year) => {
    const entitlement = getEmployeeEntitlement(userId, year);
    if (!entitlement) {
      return {
        valid: true,
        warning: 'Could not calculate leave balance - user entitlement not found'
      };
    }

    if (leaveType === 'sick') {
      return { valid: true };
    }

    const leaveTaken = calculateLeaveTaken(userId, year);
    const remaining = entitlement.finalTotalLeave - leaveTaken.total;

    // Emergency cap: an advisory signal only. Over-cap requests still follow the
    // normal two-step route (supervisor first, then admin) — the cap is flagged
    // on the request so both approvers can see it.
    let emergencyCapExceeded = false;
    let emergencyCapNote = null;
    if (leaveType === 'emergency') {
      const emergencyUsed = getEmergencyUsageForYear(userId, year);
      const totalAfter = emergencyUsed + workingDays;
      if (totalAfter > EMERGENCY_LEAVE_ANNUAL_CAP) {
        emergencyCapExceeded = true;
        emergencyCapNote = `ℹ️ Emergency Leave cap: this request would bring your Emergency Leave usage to ${totalAfter} / ${EMERGENCY_LEAVE_ANNUAL_CAP} working days for ${year}. It still needs supervisor approval first, then admin sign-off.`;
      }
    }

    let chargeableDays = workingDays;
    if (leaveType === 'wfh') {
      const wfhUsed = leaveTaken.wfh || 0;
      const freeDaysRemaining = Math.max(0, 5 - wfhUsed);
      chargeableDays = Math.max(0, workingDays - freeDaysRemaining);
      if (chargeableDays === 0) {
        return { valid: true };
      }
    }

    if (chargeableDays > remaining) {
      if (leaveType === 'wfh') {
        const wfhUsed = leaveTaken.wfh || 0;
        const freeDaysRemaining = Math.max(0, 5 - wfhUsed);
        return {
          valid: false,
          message: `⚠️ Insufficient Leave Balance\n\nYou are requesting: ${workingDays} WFH days\nFree WFH days used: ${wfhUsed} / 5\nFree days remaining: ${freeDaysRemaining}\nDays counting against leave: ${chargeableDays}\n\nAvailable leave balance: ${remaining.toFixed(1)} days\nExceeds by: ${(chargeableDays - remaining).toFixed(1)} days\n\nNote: WFH is deductible beyond 5 days per year.`,
          autoReject: true
        };
      }
      return {
        valid: false,
        message: `⚠️ Insufficient Leave Balance\n\nYou are requesting: ${workingDays} working days\nAvailable balance: ${remaining.toFixed(1)} days\nExceeds by: ${(workingDays - remaining).toFixed(1)} days\n\nYour request cannot be processed as it exceeds your available leave entitlement for ${year}.`,
        autoReject: true
      };
    }

    if (chargeableDays > remaining - 2 && chargeableDays <= remaining) {
      const balanceWarning = `ℹ️ Note: After this request, you will have ${(remaining - chargeableDays).toFixed(1)} days remaining for ${year}.`;
      return {
        valid: true,
        warning: emergencyCapNote ? `${emergencyCapNote}\n\n${balanceWarning}` : balanceWarning,
        emergencyCapExceeded,
      };
    }

    return { valid: true, warning: emergencyCapNote || undefined, emergencyCapExceeded };
  };

  // DEBUG_TIMEOFF: Set to true to trace supervisor matching for time-off requests
  const DEBUG_TIMEOFF = true;
  
  // DEBUG_TIMEOFF_GUARDS: Set to true to enable anti-regression guardrails logging
  // This logs warnings for unroutable requests (no team assignment for request week)
  const DEBUG_TIMEOFF_GUARDS = true;
  
  // Track logged unroutable requests to avoid console spam (log once per request)
  const loggedUnroutableRequests = useRef(new Set());

  /**
   * Check if a user (supervisorId) is a supervisor of another user (requestingUserId).
   * 
   * In Supabase mode, uses HFB weekly_team_assignments to determine team membership
   * for a specific week (derived from opts.dateStr or opts.weekId).
   * Falls back to user.teamId if no HFB assignment exists for that week.
   * 
   * @param supervisorId - ID of the potential supervisor
   * @param requestingUserId - ID of the user being checked
   * @param opts - Optional: { dateStr: 'YYYY-MM-DD' } or { weekId: 'YYYY-Www' }
   * 
   * CRITICAL: This function handles ID type normalization to avoid string/number mismatches.
   */
  const isSupervisorOfUser = (supervisorId, requestingUserId, opts = {}) => {
    // Normalize IDs for consistent comparison (use String for safety)
    const normalizedSupervisorId = String(supervisorId);
    const normalizedRequestingUserId = String(requestingUserId);

    const requestingUser = activeUsers.find(u => String(u.id) === normalizedRequestingUserId);

    // Resolve team: user.teamId (synced by HFB) → HFB week lookup → fallback
    let effectiveTeamId = null;
    let teamSource = 'none';

    // PRIMARY: user.teamId — kept in sync by HFB assignTeam/unassign operations
    if (requestingUser && requestingUser.teamId) {
      effectiveTeamId = String(requestingUser.teamId);
      teamSource = 'user_teamId';
    }

    // SECONDARY: HFB weekly_team_assignments (for week-specific edge cases)
    if (!effectiveTeamId && useSupabaseDB) {
      const targetWeekId = opts.weekId || (opts.dateStr ? getWeekIdFromDateStr(opts.dateStr) : currentHFBWeekId);
      const assignmentsToSearch = hfbMultiWeekAssignments && hfbMultiWeekAssignments.length > 0
        ? hfbMultiWeekAssignments
        : hfbTeamAssignments;

      if (assignmentsToSearch && assignmentsToSearch.length > 0) {
        const assignment = assignmentsToSearch.find(a =>
          String(a.userId) === normalizedRequestingUserId &&
          a.weekId === targetWeekId
        ) || assignmentsToSearch.find(a =>
          String(a.userId) === normalizedRequestingUserId && a.teamId
        );

        if (assignment && assignment.teamId) {
          effectiveTeamId = String(assignment.teamId);
          teamSource = 'hfb_' + (assignment.weekId || 'any');
        }
      }
    }

    if (!effectiveTeamId) {
      if (DEBUG_TIMEOFF) {
        console.log('[TimeOff] isSupervisorOfUser: no team found for user', {
          supervisorId: normalizedSupervisorId,
          requestingUserId: normalizedRequestingUserId,
          userTeamId: requestingUser?.teamId
        });
      }
      return false;
    }

    // Find team → check supervisorIds
    const team = activeTeams.find(t => String(t.id) === effectiveTeamId);

    if (!team) {
      if (DEBUG_TIMEOFF) {
        console.log('[TimeOff] isSupervisorOfUser: team not found', {
          effectiveTeamId,
          teamSource,
          availableTeamIds: activeTeams.map(t => String(t.id))
        });
      }
      return false;
    }

    const supervisorIds = team.supervisorIds || [];
    const isSupervisor = supervisorIds.some(sid => String(sid) === normalizedSupervisorId);

    if (DEBUG_TIMEOFF) {
      console.log('[TimeOff] isSupervisorOfUser result:', {
        supervisorId: normalizedSupervisorId,
        requestingUserId: normalizedRequestingUserId,
        effectiveTeamId,
        teamSource,
        teamName: team.name,
        teamSupervisorIds: supervisorIds.map(String),
        isSupervisor
      });
    }

    return isSupervisor;
  };

  /**
   * Check if a user (supervisorId) is a supervisor for a specific time-off request.
   *
   * This is the AUTHORITATIVE function for supervisor visibility of time-off requests.
   * Simple chain: user.teamId → team.supervisorIds → supervisor match
   *
   * user.teamId is kept in sync by HFB (assignTeam writes to both
   * weekly_team_assignments AND users.team_id).
   *
   * @param supervisorId - ID of the potential supervisor
   * @param request - The time-off request object (must have userId and startDate)
   * @returns boolean
   */
  const isSupervisorOfRequest = (supervisorId, request) => {
    if (!request || !request.userId) {
      if (DEBUG_TIMEOFF) {
        console.log('[TimeOff] isSupervisorOfRequest: invalid request', { supervisorId, request });
      }
      return false;
    }

    const normalizedSupervisorId = String(supervisorId);
    const normalizedRequestUserId = String(request.userId);
    const requestingUser = activeUsers.find(u => String(u.id) === normalizedRequestUserId);

    // Collect ALL teams this user could belong to so every supervisor of every
    // candidate team is notified. Sources:
    //   - user.teamId (kept in sync by HFB assignTeam/unassign)
    //   - HFB weekly_team_assignments for the leave's start week
    //   - HFB weekly_team_assignments across other weeks (fallback)
    const candidateTeamIds = new Set();

    if (requestingUser && requestingUser.teamId) {
      candidateTeamIds.add(String(requestingUser.teamId));
    }

    if (useSupabaseDB) {
      const targetWeekId = request.startDate
        ? getWeekIdFromDateStr(request.startDate)
        : currentHFBWeekId;
      const assignmentsToSearch = hfbMultiWeekAssignments && hfbMultiWeekAssignments.length > 0
        ? hfbMultiWeekAssignments
        : hfbTeamAssignments;

      if (assignmentsToSearch && assignmentsToSearch.length > 0) {
        // Add the week-matched team (if any) and any other team this user is
        // assigned to in known weeks — union so every supervisor is notified.
        assignmentsToSearch.forEach(a => {
          if (String(a.userId) === normalizedRequestUserId && a.teamId) {
            if (!targetWeekId || a.weekId === targetWeekId) {
              candidateTeamIds.add(String(a.teamId));
            }
          }
        });

        if (candidateTeamIds.size === 0) {
          assignmentsToSearch.forEach(a => {
            if (String(a.userId) === normalizedRequestUserId && a.teamId) {
              candidateTeamIds.add(String(a.teamId));
            }
          });
        }
      }
    }

    if (candidateTeamIds.size === 0) {
      if (DEBUG_TIMEOFF) {
        console.log('[TimeOff] isSupervisorOfRequest: no team found', {
          supervisorId: normalizedSupervisorId,
          requestUserId: normalizedRequestUserId,
          userTeamId: requestingUser?.teamId
        });
      }

      // ANTI-REGRESSION GUARDRAIL: Log unroutable request (once per request)
      if (DEBUG_TIMEOFF_GUARDS && request.id) {
        const requestKey = `${request.id}-unroutable`;
        if (!loggedUnroutableRequests.current.has(requestKey)) {
          loggedUnroutableRequests.current.add(requestKey);
          console.warn('[TimeOff Guard] Unroutable request detected:', {
            requestId: request.id,
            userId: normalizedRequestUserId,
            userName: requestingUser?.name,
            startDate: request.startDate,
            reason: 'No team assignment found (user.teamId is null and no HFB assignment)',
            hint: 'Assign user to a team in HFB so a supervisor can review'
          });
        }
      }

      return false;
    }

    // Union of supervisorIds across all candidate teams
    const isSupervisor = Array.from(candidateTeamIds).some(teamId => {
      const team = activeTeams.find(t => String(t.id) === teamId);
      if (!team) return false;
      const supervisorIds = team.supervisorIds || [];
      return supervisorIds.some(sid => String(sid) === normalizedSupervisorId);
    });

    if (DEBUG_TIMEOFF) {
      console.log('[TimeOff] isSupervisorOfRequest:', {
        supervisorId: normalizedSupervisorId,
        requestUserId: normalizedRequestUserId,
        candidateTeamIds: Array.from(candidateTeamIds),
        isSupervisor
      });
    }

    return isSupervisor;
  };

  // Count time off requests that still need the current user's approval action.
  // Admins act on 'pending_admin'; supervisors act on 'pending_supervisor'/'pending'.
  // Requests the supervisor has already approved (now 'pending_admin') are excluded
  // from the badge — the supervisor's work is done on those.
  const pendingTimeOffCount = useMemo(() => {
    if (!currentUser) return 0;
    if (isAdmin) {
      return activeTimeOff.filter(to => to.status === 'pending_admin').length;
    }
    if (currentUser.role === 'supervisor') {
      return activeTimeOff.filter(to =>
        (to.status === 'pending_supervisor' || to.status === 'pending') &&
        isSupervisorOfRequest(currentUser.id, to)
      ).length;
    }
    return 0;
  }, [currentUser, isAdmin, activeTimeOff, hfbMultiWeekAssignments, hfbTeamAssignments, activeUsers, activeTeams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Count of bank payment events awaiting reconciliation (suggested + unmatched)
  // in the payment inbox. Powers the "payment received" badge on the Invoices
  // submenu item and the Radar main-menu button. Admin/supervisor only — regular
  // users never confirm payments, so they never see the badge.
  const canSeePaymentBadge = isAdmin || currentUser?.role === 'supervisor';
  const pendingPaymentCount = canSeePaymentBadge ? (supabaseInvoices?.pendingPaymentCount || 0) : 0;

  // Load the global pending-payment count once the user is known. Individual
  // inbox actions keep it fresh via refreshPaymentEvents inside the invoices hook.
  useEffect(() => {
    if (useSupabaseDB && canSeePaymentBadge) {
      supabaseInvoices?.fetchPendingPaymentCount?.();
    }
  }, [useSupabaseDB, canSeePaymentBadge]); // eslint-disable-line react-hooks/exhaustive-deps

  // Count of email-intake messages awaiting admin review — powers the badge on
  // the Inquiries nav item. Admin-only (Inquiries is an admin page).
  const inquiriesNeedsReviewCount = isAdmin ? (supabaseInquiryIntakeCount?.needsReviewCount || 0) : 0;

  // Refresh the count whenever the admin enters or leaves the Inquiries page, so
  // approvals/rejections made there are reflected in the badge. The Inquiries
  // page uses its own intake hook instance, so this is our cross-instance sync.
  const onInquiriesPage = mainCategory === 'projects' && subPage === 'inquiries';
  useEffect(() => {
    if (useSupabaseDB && isAdmin) {
      supabaseInquiryIntakeCount?.fetchNeedsReviewCount?.();
    }
  }, [useSupabaseDB, isAdmin, onInquiriesPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // === CALENDAR HELPER FUNCTIONS ===
  
  // Get all events for a specific day
  // ========================================================================
  // UNIFIED CALENDAR EVENTS - Single source of truth for all calendar data
  // ========================================================================
  // Event Type Mapping:
  // | Event Type      | Source                  | Filter Key      | Status Filter           |
  // |-----------------|-------------------------|-----------------|-------------------------|
  // | holidays        | activeHolidays          | holidays        | None (always shown)     |
  // | leaves          | timeOff                 | leaves          | status === 'approved'   |
  // | pendingLeaves   | timeOff                 | pendingLeaves   | status === 'pending*'   |
  // | milestones      | projects.stageTimeline  | milestones      | None                    |
  // | invoicing       | projects.stageTimeline  | invoicing       | None                    |
  // | events          | calendarEvents          | events          | None                    |
  // | msCalendar      | msCalendarEvents        | msCalendar      | msCalendarConnected     |
  // | anniversaries   | activeUsers.joinDate    | anniversaries   | None (work anniv + join + leave) |
  // | birthdays       | activeUsers.dob         | birthdays       | None (yearly recurring)         |
  // ========================================================================
  
  // Helper function to get BRIO-styled event colors
  // Returns inline style object with outline color and 50% opacity background
  const getBrioEventStyle = (eventType) => {
    // Using hex colors directly so we can append opacity.
    // Sourced from the BRIO team palette (DEFAULT_TEAM_PALETTE in
    // AccountingSettingsPage.jsx). Keep in sync with
    // CALENDAR_CATEGORY_COLORS in CalendarPage.jsx so filter buttons
    // and event boxes share the same hue per category.
    const colorMap = {
      holidays: '#B36B6B',           // muted red
      leaves: '#CC9966',             // tan / gold
      pendingLeaves: '#9B8B6B',      // ochre (sibling of leaves)
      milestones: '#6B9B7B',         // forest green
      invoicing: '#5B7A99',          // blue steel (financial / distinct from brown family)
      events: '#6B8B8B',             // teal (clearly outside the purple range)
      anniversaries: '#8B7BA3',      // muted purple (team-celebration family)
      msCalendar: '#9B8B7A',         // warm grey
      birthdays: '#7A6B8B',          // lavender (team-celebration family)
      clientBirthdays: '#9B7A8B',    // mauve (client-celebration family)
      clientAnniversaries: '#8B6B7A' // plum (client-celebration family)
    };
    const color = colorMap[eventType] || '#6B7280';
    return {
      borderColor: color,
      borderStyle: eventType === 'pendingLeaves' ? 'dashed' : 'solid',
      backgroundColor: color + '80',  // 80 in hex = 50% opacity
    };
  };

  // ========================================================================
  // Timezone-safe date helpers for calendar UI
  // Avoids the UTC-shift bug where new Date('YYYY-MM-DD') or
  // new Date(y,m,d).toISOString() can shift dates by ±1 day.
  // ========================================================================
  const pad2 = (n) => String(n).padStart(2, '0');
  /** Build 'YYYY-MM-DD' from local year, zero-based month, day — no Date object, no TZ shift */
  const ymdLocal = (y, m0, d) => `${y}-${pad2(m0 + 1)}-${pad2(d)}`;
  /** Parse 'YYYY-MM-DD' into { year, month0, day } without any Date conversion */
  const parseYMD = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') return null;
    const s = dateStr.split('T')[0]; // strip time portion if present
    const [y, m, d] = s.split('-').map(Number);
    if (!y || !m || !d) return null;
    return { year: y, month0: m - 1, day: d };
  };
  /** Local YYYY-MM-DD from a Date object (avoids UTC toISOString shift) */
  const dateToYmd = (dt) => ymdLocal(dt.getFullYear(), dt.getMonth(), dt.getDate());
  /** Return new Date shifted by n days */
  const addDays = (dt, n) => { const x = new Date(dt); x.setDate(x.getDate() + n); return x; };

  const getUnifiedCalendarEvents = (dateStr) => {
    const events = [];
    
    // Pre-filter: active employees only for user-derived system events (birthdays, anniversaries)
    // isActive !== false handles both true and null/undefined (default active)
    // status !== 'inactive' handles any legacy status field
    const activeEmployeesForSystemEvents = (activeUsers || []).filter(u =>
      u.isActive !== false && u.status !== 'inactive'
    );

    // ========================================
    // 1. PUBLIC HOLIDAYS (System events - always visible when filter enabled)
    // ========================================
    if (categoryFilters.holidays !== false) { // Default to showing holidays
      const holiday = userHolidays.find(h => h.date === dateStr);
      if (holiday) {
        events.push({
          id: `holiday-${holiday.id}`,
          eventType: 'holidays',
          title: holiday.name,
          category: 'holidays',
          color: 'bg-red-100 text-red-700 border-red-200',
          isAllDay: true,
          startDate: holiday.date,
          endDate: holiday.date,
          status: 'system', // System events don't have approval status
          sourceId: holiday.id,
          sourceTable: 'holidays'
        });
      }
    }
    
    // ========================================
    // 2. APPROVED TEAM LEAVES
    // ========================================
    if (categoryFilters.leaves) {
      const dayLeaves = activeTimeOff.filter(to => 
        to.status === 'approved' && 
        dateStr >= to.startDate && 
        dateStr <= to.endDate
      );
      
      // AUDIT[01]: All Tailwind color classes below should use BRIO tokens instead
      const leaveTypeConfig = {
        sick: { color: 'bg-red-100 text-red-800 border-red-300' }, // → --brio-red-light, --brio-red
        vacation: { color: 'bg-blue-100 text-blue-800 border-blue-300' }, // → needs --brio-blue token
        emergency: { color: 'bg-orange-100 text-orange-800 border-orange-300' }, // → needs --brio-orange token
        // Legacy: any leftover pre-migration "personal" rows render as vacation.
        personal: { color: 'bg-blue-100 text-blue-800 border-blue-300' },
        wfh: { color: 'bg-green-100 text-green-800 border-green-300' }
      };
      
      dayLeaves.forEach(leave => {
        const user = activeUsers.find(u => u.id === leave.userId);
        const config = leaveTypeConfig[leave.type] || { color: 'bg-gray-100 text-gray-800 border-gray-300' };
        
        events.push({
          id: `leave-${leave.id}`,
          eventType: 'leaves',
          title: `${user?.name || 'Unknown'} - ${leave.type.charAt(0).toUpperCase() + leave.type.slice(1)}`,
          category: 'leaves',
          subType: leave.type,
          color: config.color,
          isAllDay: true,
          startDate: leave.startDate,
          endDate: leave.endDate,
          status: leave.status,
          userId: leave.userId,
          sourceId: leave.id,
          sourceTable: 'time_off'
        });
      });
    }
    
    // ========================================
    // 3. PENDING LEAVE REQUESTS (Admin/Supervisor visibility)
    // ========================================
    if (categoryFilters.pendingLeaves && (isAdmin || currentUser.role === 'supervisor')) {
      const pendingLeaves = activeTimeOff.filter(to => 
        (to.status === 'pending' || to.status === 'pending_supervisor' || to.status === 'pending_admin') && 
        dateStr >= to.startDate && 
        dateStr <= to.endDate
      );
      
      pendingLeaves.forEach(leave => {
        const user = activeUsers.find(u => u.id === leave.userId);
        // For supervisors, only show their team members' pending requests
        // Uses isSupervisorOfRequest which checks HFB weekly_team_assignments
        if (currentUser.role === 'supervisor') {
          const isSupervisorOfLeaveUser = isSupervisorOfRequest(currentUser.id, leave);
          
          if (DEBUG_TIMEOFF) {
            console.log('[TimeOff] Calendar pending leave filter:', {
              currentUserId: currentUser.id,
              currentUserRole: currentUser.role,
              leaveUserId: leave.userId,
              leaveUserName: user?.name,
              leaveStatus: leave.status,
              leaveStartDate: leave.startDate,
              isSupervisorOfLeaveUser
            });
          }
          
          if (!isSupervisorOfLeaveUser) return;
        }
        
        events.push({
          id: `pending-leave-${leave.id}`,
          eventType: 'pendingLeaves',
          title: `${user?.name || 'Unknown'} - ${leave.type} (Pending)`,
          category: 'pendingLeaves',
          subType: leave.type,
          color: 'bg-yellow-100 text-yellow-800 border-yellow-400 border-dashed',
          isAllDay: true,
          startDate: leave.startDate,
          endDate: leave.endDate,
          status: leave.status,
          userId: leave.userId,
          sourceId: leave.id,
          sourceTable: 'time_off'
        });
      });
    }
    
    // ========================================
    // 4. PROJECT MILESTONES (Target & Actual dates)
    // ========================================
    if (categoryFilters.milestones && (isAdmin || currentUser.role === 'supervisor')) {
      const visibleProjects = currentUser.role === 'supervisor' ? getSupervisedProjects() : activeProjects;
      
      const stages = [
        { key: 'advance', name: 'Advance' },
        { key: 'cd_ad', name: 'CD AD' }, { key: 'cd_id', name: 'CD ID' },
        { key: 'sd_ad', name: 'SD AD' }, { key: 'sd_id', name: 'SD ID' },
        { key: 'dd_ad', name: 'DD AD' }, { key: 'dd_id', name: 'DD ID' },
        { key: 'aoc_ad', name: 'AoC AD' }, { key: 'aoc_id', name: 'AoC ID' },
        { key: 'td_ad', name: 'TD AD' }, { key: 'td_id', name: 'TD ID' },
        { key: 'gfc1', name: 'GFC 1' }, { key: 'gfc2', name: 'GFC 2' }, { key: 'gfc3', name: 'GFC 3' },
        { key: 'completion', name: 'Completion' }, { key: 'cc', name: 'CC' }
      ];

      // Date fields carried by a part stage, with the same wording and colours
      // the main-stage blocks below use.
      const MILESTONE_PART_FIELDS = [
        { field: 'targetDate', label: 'Target', subType: 'target', status: 'upcoming', color: 'bg-blue-50 text-blue-800 border-blue-300' },
        { field: 'contractDate', label: 'Contract', subType: 'contract', status: 'upcoming', color: 'bg-purple-50 text-purple-800 border-purple-300' },
        { field: 'actualDate', label: 'Done', subType: 'actual', status: 'completed', color: 'bg-green-50 text-green-800 border-green-300' }
      ];

      visibleProjects.forEach(proj => {
        if (!proj.stageTimeline) return;

        stages.forEach(stage => {
          const timeline = proj.stageTimeline[stage.key] || {};

          // Target Date (upcoming milestone)
          if (timeline.targetDate === dateStr) {
            events.push({
              id: `milestone-target-${proj.id}-${stage.key}`,
              eventType: 'milestones',
              title: `${proj.projectCode || proj.name}: ${stage.name} Target`,
              category: 'milestones',
              subType: 'target',
              color: 'bg-blue-50 text-blue-800 border-blue-300',
              isAllDay: true,
              startDate: timeline.targetDate,
              endDate: timeline.targetDate,
              status: 'upcoming',
              projectId: proj.id,
              stageKey: stage.key,
              sourceId: proj.id,
              sourceTable: 'projects'
            });
          }
          
          // Contract Date (contractual milestone)
          if (timeline.contractDate === dateStr) {
            events.push({
              id: `milestone-contract-${proj.id}-${stage.key}`,
              eventType: 'milestones',
              title: `${proj.projectCode || proj.name}: ${stage.name} Contract`,
              category: 'milestones',
              subType: 'contract',
              color: 'bg-purple-50 text-purple-800 border-purple-300',
              isAllDay: true,
              startDate: timeline.contractDate,
              endDate: timeline.contractDate,
              status: 'upcoming',
              projectId: proj.id,
              stageKey: stage.key,
              sourceId: proj.id,
              sourceTable: 'projects'
            });
          }
          
          // Actual Date (completed milestone)
          if (timeline.actualDate === dateStr) {
            events.push({
              id: `milestone-actual-${proj.id}-${stage.key}`,
              eventType: 'milestones',
              title: `${proj.projectCode || proj.name}: ${stage.name} Done`,
              category: 'milestones',
              subType: 'actual',
              color: 'bg-green-50 text-green-800 border-green-300',
              isAllDay: true,
              startDate: timeline.actualDate,
              endDate: timeline.actualDate,
              status: 'completed',
              projectId: proj.id,
              stageKey: stage.key,
              sourceId: proj.id,
              sourceTable: 'projects'
            });
          }

          // Part stages (sub-milestones under a stage, e.g. GFC1—MAH).
          // The MS push emits one event per part-stage date
          // (getUnsyncedBrioExportItems), so the calendar has to generate them
          // here too. Without this they reached the view only via the Outlook
          // pull-back, which is why they survived when that overlay was removed.
          if (Array.isArray(timeline.partStages)) {
            timeline.partStages.forEach(part => {
              if (!part || !part.id) return;
              const partLabel = part.name || String(part.id);
              MILESTONE_PART_FIELDS.forEach(({ field, label, subType, status, color }) => {
                if (part[field] !== dateStr) return;
                events.push({
                  id: `milestone-${subType}-${proj.id}-${stage.key}-part-${part.id}`,
                  eventType: 'milestones',
                  title: `${proj.projectCode || proj.name}: ${stage.name} — ${partLabel} ${label}`,
                  category: 'milestones',
                  subType,
                  color,
                  isAllDay: true,
                  startDate: part[field],
                  endDate: part[field],
                  status,
                  projectId: proj.id,
                  stageKey: stage.key,
                  partStageId: part.id,
                  sourceId: proj.id,
                  sourceTable: 'projects'
                });
              });
            });
          }
        });
      });
    }
    
    // ========================================
    // 5. INVOICING & PAYMENT DATES
    // ========================================
    if (categoryFilters.invoicing && (isAdmin || currentUser.role === 'supervisor')) {
      const visibleProjects = currentUser.role === 'supervisor' ? getSupervisedProjects() : activeProjects;
      
      const stages = [
        { key: 'advance', name: 'Advance' },
        { key: 'cd_ad', name: 'CD AD' }, { key: 'cd_id', name: 'CD ID' },
        { key: 'sd_ad', name: 'SD AD' }, { key: 'sd_id', name: 'SD ID' },
        { key: 'dd_ad', name: 'DD AD' }, { key: 'dd_id', name: 'DD ID' },
        { key: 'aoc_ad', name: 'AoC AD' }, { key: 'aoc_id', name: 'AoC ID' },
        { key: 'td_ad', name: 'TD AD' }, { key: 'td_id', name: 'TD ID' },
        { key: 'gfc1', name: 'GFC 1' }, { key: 'gfc2', name: 'GFC 2' }, { key: 'gfc3', name: 'GFC 3' },
        { key: 'completion', name: 'Completion' }, { key: 'cc', name: 'CC' }
      ];
      
      visibleProjects.forEach(proj => {
        if (!proj.stageTimeline) return;
        
        stages.forEach(stage => {
          const timeline = proj.stageTimeline[stage.key] || {};
          
          // Invoice Date
          if (timeline.invoiceDate === dateStr) {
            events.push({
              id: `invoice-${proj.id}-${stage.key}`,
              eventType: 'invoicing',
              title: `${proj.projectCode || proj.name}: ${stage.name} Invoice`,
              category: 'invoicing',
              subType: 'invoice',
              color: 'bg-orange-50 text-orange-800 border-orange-300',
              isAllDay: true,
              startDate: timeline.invoiceDate,
              endDate: timeline.invoiceDate,
              status: timeline.paidDate ? 'paid' : 'pending',
              projectId: proj.id,
              stageKey: stage.key,
              sourceId: proj.id,
              sourceTable: 'projects'
            });
          }
          
          // Paid Date
          if (timeline.paidDate === dateStr) {
            events.push({
              id: `paid-${proj.id}-${stage.key}`,
              eventType: 'invoicing',
              title: `${proj.projectCode || proj.name}: ${stage.name} Paid`,
              category: 'invoicing',
              subType: 'paid',
              color: 'bg-emerald-50 text-emerald-800 border-emerald-300',
              isAllDay: true,
              startDate: timeline.paidDate,
              endDate: timeline.paidDate,
              status: 'paid',
              projectId: proj.id,
              stageKey: stage.key,
              sourceId: proj.id,
              sourceTable: 'projects'
            });
          }

          // Part stages — same reasoning as the milestone section above.
          if (Array.isArray(timeline.partStages)) {
            timeline.partStages.forEach(part => {
              if (!part || !part.id) return;
              const partLabel = part.name || String(part.id);

              if (part.invoiceDate === dateStr) {
                events.push({
                  id: `invoice-${proj.id}-${stage.key}-part-${part.id}`,
                  eventType: 'invoicing',
                  title: `${proj.projectCode || proj.name}: ${stage.name} — ${partLabel} Invoice`,
                  category: 'invoicing',
                  subType: 'invoice',
                  color: 'bg-orange-50 text-orange-800 border-orange-300',
                  isAllDay: true,
                  startDate: part.invoiceDate,
                  endDate: part.invoiceDate,
                  status: part.paidDate ? 'paid' : 'pending',
                  projectId: proj.id,
                  stageKey: stage.key,
                  partStageId: part.id,
                  sourceId: proj.id,
                  sourceTable: 'projects'
                });
              }

              if (part.paidDate === dateStr) {
                events.push({
                  id: `paid-${proj.id}-${stage.key}-part-${part.id}`,
                  eventType: 'invoicing',
                  title: `${proj.projectCode || proj.name}: ${stage.name} — ${partLabel} Paid`,
                  category: 'invoicing',
                  subType: 'paid',
                  color: 'bg-emerald-50 text-emerald-800 border-emerald-300',
                  isAllDay: true,
                  startDate: part.paidDate,
                  endDate: part.paidDate,
                  status: 'paid',
                  projectId: proj.id,
                  stageKey: stage.key,
                  partStageId: part.id,
                  sourceId: proj.id,
                  sourceTable: 'projects'
                });
              }
            });
          }
        });
      });
    }
    
    // ========================================
    // 6. CUSTOM CALENDAR EVENTS
    // ========================================
    if (categoryFilters.events) {
      const dayEvents = activeCalendarEvents.filter(e => {
        const eventDate = e.date || e.startDate;
        return eventDate === dateStr;
      });
      
      events.push(...dayEvents.map(e => ({
        id: e.id,
        eventType: 'events',
        title: e.title || e.description || 'Untitled Event',
        description: e.description,
        category: e.category || 'events',
        color: e.color || 'bg-gray-100 text-gray-800 border-gray-300',
        isAllDay: e.isAllDay === true,
        startDate: e.startDate || e.date,
        endDate: e.endDate || e.startDate || e.date,
        startTime: e.startTime,
        endTime: e.endTime,
        status: 'active',
        sourceId: e.id,
        sourceTable: 'calendar_events',
        source: e.source || { table: 'calendar_events', id: e.id },
        isEditable: e.isEditable !== undefined ? e.isEditable : true
      })));
    }
    
    // ========================================
    // 7. MICROSOFT CALENDAR EVENTS
    // ========================================
    // MS Calendar events now have a 'category' field set during sync:
    // - 'msCalendar' for default calendar (meetings/appointments)
    // - 'holidays', 'leaves', 'pendingLeaves', 'milestones', 'invoicing', 'anniversaries' for BRIO calendars
    
    // 7a. Show generic MS Calendar events (meetings/appointments) under msCalendar filter
    if (categoryFilters.msCalendar && msCalendarConnected) {
      const genericMsEvents = msCalendarEvents.filter(e => {
        if (!e || !e.start) return false;
        // Only show events with category 'msCalendar' (generic appointments)
        if (e.category !== 'msCalendar') return false;
        
        try {
          const startDate = new Date(e.start);
          const endDate = e.end ? new Date(e.end) : startDate;
          
          if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return false;
          
          const eventStartYmd = dateToYmd(startDate);
          // Graph all-day events: end is exclusive (next day) → subtract 1 for inclusive UI
          let eventEndYmd = dateToYmd(endDate);
          if (e.isAllDay && e.end) {
            eventEndYmd = dateToYmd(addDays(endDate, -1));
          }
          
          return dateStr >= eventStartYmd && dateStr <= eventEndYmd;
        } catch (error) {
          return false;
        }
      });
      
      events.push(...genericMsEvents.map(e => {
        try {
          const startDate = new Date(e.start);
          const endDate = e.end ? new Date(e.end) : startDate;
          
          const startYmd = dateToYmd(startDate);
          // Graph all-day events: end is exclusive → subtract 1 for inclusive UI
          let endYmd = dateToYmd(endDate);
          if (e.isAllDay && e.end) {
            endYmd = dateToYmd(addDays(endDate, -1));
          }
          
          return {
            id: `ms-${e.id}`,
            eventType: 'msCalendar',
            title: e.subject || 'MS Calendar Event',
            description: e.location,
            category: 'msCalendar',
            color: 'bg-indigo-100 text-indigo-800 border-indigo-300',
            isAllDay: e.isAllDay || false,
            startDate: startYmd,
            endDate: endYmd,
            startTime: !e.isAllDay ? startDate.toTimeString().slice(0, 5) : null,
            endTime: !e.isAllDay ? endDate.toTimeString().slice(0, 5) : null,
            status: 'synced',
            sourceId: e.id,
            sourceTable: 'ms_calendar',
            source: 'microsoft',
            organizer: e.organizer,
            attendees: e.attendees,
            categories: e.categories
          };
        } catch (error) {
          return null;
        }
      }).filter(Boolean));
    }
    
    // 7b. Events people typed straight into a BRIO calendar.
    //
    // Those calendars are push targets: nearly everything in them was written
    // by this app from the data already rendered above, so drawing the pulled
    // copies alongside the generated ones double-drew every synced entry. That
    // is why the blanket overlay was removed.
    //
    // But staff do add real appointments there by hand ("Adrienne in office",
    // "meeting Louk Hotel Vietnam"), and with no overlay at all those were
    // invisible in this app — visible only in Outlook. So render exactly the
    // events this app did NOT create: id absent from ms_synced_items. Anything
    // the app made is already on the calendar from the database, so a duplicate
    // is impossible by construction rather than by matching titles.
    //
    // Gated on msLedgerLoaded. If the ledger has not been read, or its query
    // failed, every event looks untracked and the whole calendar would double
    // up — so in that case show nothing here and leave the database view alone.
    if (msCalendarConnected && msLedgerLoaded && msCalendarEvents.length > 0) {
      const trackedIds = new Set(msSyncedEventIds);

      VALID_MS_EVENT_TYPES.forEach(brioCategory => {
        if (!categoryFilters[brioCategory]) return;

        const untracked = msCalendarEvents.filter(e => {
          if (!e || !e.start || e.category !== brioCategory) return false;
          if (e.category === 'msCalendar') return false; // handled by 7a
          if (trackedIds.has(e.id)) return false;        // this app's own output

          try {
            const startDate = new Date(e.start);
            const endDate = e.end ? new Date(e.end) : startDate;
            if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return false;

            const eventStartYmd = dateToYmd(startDate);
            let eventEndYmd = dateToYmd(endDate);
            if (e.isAllDay && e.end) eventEndYmd = dateToYmd(addDays(endDate, -1));

            return dateStr >= eventStartYmd && dateStr <= eventEndYmd;
          } catch (error) {
            return false;
          }
        });

        events.push(...untracked.map(e => {
          try {
            const startDate = new Date(e.start);
            const endDate = e.end ? new Date(e.end) : startDate;

            const startYmd = dateToYmd(startDate);
            let endYmd = dateToYmd(endDate);
            if (e.isAllDay && e.end) endYmd = dateToYmd(addDays(endDate, -1));

            return {
              id: `ms-manual-${e.id}`,
              eventType: brioCategory,
              // Flagged so it reads as somebody's own entry rather than app
              // output, and so a future orphan sweep has a visible signal.
              title: `${e.subject || 'Outlook entry'} (added in Outlook)`,
              description: e.location,
              category: brioCategory,
              color: 'bg-slate-100 text-slate-800 border-slate-400',
              isAllDay: e.isAllDay || false,
              startDate: startYmd,
              endDate: endYmd,
              startTime: !e.isAllDay ? startDate.toTimeString().slice(0, 5) : null,
              endTime: !e.isAllDay ? endDate.toTimeString().slice(0, 5) : null,
              status: 'manual',
              sourceId: e.id,
              sourceTable: 'ms_calendar',
              source: 'microsoft',
              msCalendarId: e.msCalendarId,
              msCalendarName: e.msCalendarName,
              organizer: e.organizer,
              attendees: e.attendees,
              categories: e.categories
            };
          } catch (error) {
            return null;
          }
        }).filter(Boolean));
      });
    }
    // ========================================
    // DEDUPE: Build set of MS-synced birthday/anniversary keys so generated
    // system events (sections 8 & 9) don't duplicate them.
    //
    // Section 7b only renders events this app did NOT create, so nothing it
    // adds should ever suppress a generated birthday — hence the status check
    // below. The set is therefore empty in normal operation and stays purely as
    // a guard: if anyone reintroduces an overlay of app-created events, this
    // catches the duplicate instead of letting it reach the calendar. Keep the
    // generated titles matching the MS-push format for that to keep working.
    // ========================================
    const normTitle = (t) => (t || '').toLowerCase().trim()
      .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, '') // strip emoji
      .replace(/\s+/g, ' ').trim();

    const msSyncedEventKeys = new Set();
    events.forEach(ev => {
      if (ev.source !== 'microsoft') return;
      if (ev.status === 'manual') return; // somebody's own entry, not app output
      if (ev.eventType !== 'birthdays' && ev.eventType !== 'anniversaries') return;
      const key = `${ev.eventType}|${normTitle(ev.title)}|${ev.startDate}`;
      msSyncedEventKeys.add(key);
    });

    // ========================================
    // 8. ANNIVERSARIES (work anniversaries + join dates + leaving dates)
    // ========================================
    if (categoryFilters.anniversaries) {
      const dateYMD = parseYMD(dateStr);
      const currentYear = dateYMD ? dateYMD.year : new Date().getFullYear();
      
      // Only show anniversaries for active employees
      activeEmployeesForSystemEvents.forEach(user => {
        // --- 8a. Work Anniversaries (yearly recurring, existing behavior) ---
        if (user.joinDate) {
          const joinYMD = parseYMD(user.joinDate);
          if (joinYMD) {
            // Timezone-safe: build YYYY-MM-DD string without Date object
            const anniversaryThisYear = ymdLocal(currentYear, joinYMD.month0, joinYMD.day);
            
            if (anniversaryThisYear === dateStr && currentYear > joinYMD.year) {
              const yearsOfService = currentYear - joinYMD.year;
              // Title must match the MS-push format (App.js ~4177) exactly, or the
              // msSyncedEventKeys dedup below misses and the native + Outlook-synced
              // copies both render as duplicates.
              const title = `${user.name} - ${yearsOfService} Year${yearsOfService > 1 ? 's' : ''} Anniversary`;
              const dedupeKey = `anniversaries|${normTitle(title)}|${dateStr}`;
              if (!msSyncedEventKeys.has(dedupeKey)) {
                events.push({
                  id: `anniversary-${user.id}-${currentYear}`,
                  eventType: 'anniversaries',
                  title,
                  category: 'anniversaries',
                  color: 'brio-event-anniversaries',
                  isAllDay: true,
                  startDate: dateStr,
                  endDate: dateStr,
                  status: 'system',
                  userId: user.id,
                  yearsOfService: yearsOfService,
                  sourceId: user.id,
                  sourceTable: 'users'
                });
              }
            }
          }
        }
        
        // --- 8b. Join Date (one-time event) ---
        if (user.joinDate) {
          // Timezone-safe: just split on 'T', no Date conversion
          const joinDateStr = typeof user.joinDate === 'string'
            ? user.joinDate.split('T')[0]
            : '';
          
          if (joinDateStr === dateStr) {
            const title = `${user.name} Joining`;
            const dedupeKey = `anniversaries|${normTitle(title)}|${dateStr}`;
            if (!msSyncedEventKeys.has(dedupeKey)) {
              events.push({
                id: `joining-${user.id}`,
                eventType: 'anniversaries',
                title,
                category: 'anniversaries',
                color: 'brio-event-anniversaries',
                isAllDay: true,
                startDate: dateStr,
                endDate: dateStr,
                status: 'system',
                userId: user.id,
                sourceId: user.id,
                sourceTable: 'users'
              });
            }
          }
        }
        
        // --- 8c. Leaving Date / Last Day (one-time event) ---
        if (user.leavingDate) {
          // Timezone-safe: just split on 'T', no Date conversion
          const leaveDateStr = typeof user.leavingDate === 'string'
            ? user.leavingDate.split('T')[0]
            : '';
          
          if (leaveDateStr === dateStr) {
            const title = `${user.name} Last Day`;
            const dedupeKey = `anniversaries|${normTitle(title)}|${dateStr}`;
            if (!msSyncedEventKeys.has(dedupeKey)) {
              events.push({
                id: `lastday-${user.id}`,
                eventType: 'anniversaries',
                title,
                category: 'anniversaries',
                color: 'brio-event-anniversaries',
                isAllDay: true,
                startDate: dateStr,
                endDate: dateStr,
                status: 'system',
                userId: user.id,
                sourceId: user.id,
                sourceTable: 'user_profiles'
              });
            }
          }
        }
      });
    }
    
    // ========================================
    // 9. BIRTHDAYS (yearly recurring from user_profiles.dob)
    // ========================================
    if (categoryFilters.birthdays) {
      const dateYMD = parseYMD(dateStr);
      const currentYear = dateYMD ? dateYMD.year : new Date().getFullYear();
      
      // Only show birthdays for active employees
      activeEmployeesForSystemEvents.forEach(user => {
        if (!user.dob) return;
        
        // Timezone-safe: parse dob string without Date object
        const dobYMD = parseYMD(user.dob);
        if (!dobYMD) return;
        
        let birthMonth0 = dobYMD.month0;
        let birthDay = dobYMD.day;
        
        // Feb 29 handling: show on Feb 28 in non-leap years
        if (birthMonth0 === 1 && birthDay === 29) {
          const isLeapYear = (currentYear % 4 === 0 && currentYear % 100 !== 0) || (currentYear % 400 === 0);
          if (!isLeapYear) {
            birthDay = 28;
          }
        }
        
        // Timezone-safe: build YYYY-MM-DD string without Date object
        const birthdayThisYear = ymdLocal(currentYear, birthMonth0, birthDay);
        
        if (birthdayThisYear === dateStr) {
          const title = `${user.name} Birthday`;
          const dedupeKey = `birthdays|${normTitle(title)}|${dateStr}`;
          if (!msSyncedEventKeys.has(dedupeKey)) {
            events.push({
              id: `birthday-${user.id}-${currentYear}`,
              eventType: 'birthdays',
              title,
              category: 'birthdays',
              color: 'brio-event-birthdays',
              isAllDay: true,
              startDate: dateStr,
              endDate: dateStr,
              status: 'system',
              userId: user.id,
              sourceId: user.id,
              sourceTable: 'user_profiles'
            });
          }
        }
      });
    }
    
    // ========================================
    // 10. CLIENT BIRTHDAYS (yearly recurring from clients.dob)
    // ========================================
    if (categoryFilters.clientBirthdays) {
      const dateYMD = parseYMD(dateStr);
      const currentYear = dateYMD ? dateYMD.year : new Date().getFullYear();

      // Collect all client-related people with birthdays:
      // primary contact (client.name + client.dob) + additionalContacts[].dob
      (activeClients || []).forEach(client => {
        const contacts = [];
        // Primary contact
        if (client.dob) contacts.push({ name: client.name || 'Client', dob: client.dob, key: `${client.id}` });
        // Additional contacts from the People tab
        if (Array.isArray(client.additionalContacts)) {
          client.additionalContacts.forEach((c, i) => {
            if (c.dob) contacts.push({ name: c.name || `Contact ${i + 1}`, dob: c.dob, key: `${client.id}-c${i}` });
          });
        }

        contacts.forEach(({ name, dob, key }) => {
          const dobYMD = parseYMD(dob);
          if (!dobYMD) return;
          let birthMonth0 = dobYMD.month0;
          let birthDay = dobYMD.day;
          if (birthMonth0 === 1 && birthDay === 29) {
            const isLeapYear = (currentYear % 4 === 0 && currentYear % 100 !== 0) || (currentYear % 400 === 0);
            if (!isLeapYear) birthDay = 28;
          }
          const birthdayThisYear = ymdLocal(currentYear, birthMonth0, birthDay);
          if (birthdayThisYear === dateStr) {
            events.push({
              id: `client-birthday-${key}-${currentYear}`,
              eventType: 'clientBirthdays',
              title: `${name} Birthday`,
              category: 'clientBirthdays',
              color: 'brio-event-client-birthdays',
              isAllDay: true,
              startDate: dateStr,
              endDate: dateStr,
              status: 'system',
              sourceId: client.id,
              sourceTable: 'clients'
            });
          }
        });
      });
    }

    // ========================================
    // 11. CLIENT ANNIVERSARIES (yearly recurring from clients.anniversary)
    // ========================================
    if (categoryFilters.clientAnniversaries) {
      const dateYMD = parseYMD(dateStr);
      const currentYear = dateYMD ? dateYMD.year : new Date().getFullYear();

      // Collect all client-related people with anniversaries:
      // primary contact (client.name + client.anniversary) + additionalContacts[].anniversary
      (activeClients || []).forEach(client => {
        const contacts = [];
        if (client.anniversary) contacts.push({ name: client.name || 'Client', anniversary: client.anniversary, key: `${client.id}` });
        if (Array.isArray(client.additionalContacts)) {
          client.additionalContacts.forEach((c, i) => {
            if (c.anniversary) contacts.push({ name: c.name || `Contact ${i + 1}`, anniversary: c.anniversary, key: `${client.id}-c${i}` });
          });
        }

        contacts.forEach(({ name, anniversary, key }) => {
          const annivYMD = parseYMD(anniversary);
          if (!annivYMD) return;
          const anniversaryThisYear = ymdLocal(currentYear, annivYMD.month0, annivYMD.day);
          if (anniversaryThisYear === dateStr && currentYear > annivYMD.year) {
            const years = currentYear - annivYMD.year;
            events.push({
              id: `client-anniversary-${key}-${currentYear}`,
              eventType: 'clientAnniversaries',
              title: `${name} ${years} Year Anniversary`,
              category: 'clientAnniversaries',
              color: 'brio-event-client-anniversaries',
              isAllDay: true,
              startDate: dateStr,
              endDate: dateStr,
              status: 'system',
              sourceId: client.id,
              sourceTable: 'clients'
            });
          }
        });
      });
    }

    // ========================================
    // DEDUPE: MS-synced duplicates of DB-authoritative leaves
    // ----------------------------------------
    // time_off is the source of truth for leaves and pendingLeaves; companies
    // often also sync the same leaves into a "BRIO Leaves" Outlook calendar,
    // which then arrives via MS Calendar sync and duplicates the DB-rendered
    // entry. When both exist for the same person/dates, drop the MS-sourced
    // copy and keep the DB one (it carries accurate status/type/supervisor
    // metadata used by edit and filter logic).
    // ========================================
    const normLeaveKey = (ev) => {
      const t = normTitle(ev.title)
        .replace(/\s*\(pending\)\s*/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      return `${ev.eventType}|${t}|${ev.startDate}|${ev.endDate}`;
    };
    const dbLeaveKeys = new Set();
    events.forEach(ev => {
      if (ev.eventType !== 'leaves' && ev.eventType !== 'pendingLeaves') return;
      if (ev.sourceTable === 'ms_calendar') return;
      dbLeaveKeys.add(normLeaveKey(ev));
    });
    const deduped = events.filter(ev => {
      if (ev.eventType !== 'leaves' && ev.eventType !== 'pendingLeaves') return true;
      if (ev.sourceTable !== 'ms_calendar') return true;
      return !dbLeaveKeys.has(normLeaveKey(ev));
    });

    return deduped;
  };

  // Alias for backward compatibility (used in multiple places)
  const getAllDayEvents = getUnifiedCalendarEvents;
  
  // Push local calendar event to Microsoft Calendar (legacy - uses default calendar)
  const pushEventToMsCalendarLegacy = async (event) => {
    try {
      const validToken = await getValidToken();
      if (!validToken) {
        alert('Please connect to Microsoft Calendar first');
        return false;
      }

      // Prepare event data for Microsoft Graph API
      const startDateTime = event.startTime 
        ? `${event.startDate || event.date}T${event.startTime}:00`
        : `${event.startDate || event.date}T09:00:00`;
      
      const endDateTime = event.endTime
        ? `${event.endDate || event.startDate || event.date}T${event.endTime}:00`
        : `${event.endDate || event.startDate || event.date}T10:00:00`;

      const msEvent = {
        subject: event.title || event.description || 'Untitled Event',
        start: {
          dateTime: startDateTime,
          timeZone: 'UTC'
        },
        end: {
          dateTime: endDateTime,
          timeZone: 'UTC'
        },
        isAllDay: event.isAllDay || false,
        body: {
          contentType: 'text',
          content: event.description || event.notes || ''
        }
      };
      if (msEvent.isAllDay) {
        msEvent.isReminderOn = false; // no 23:45 alarm the night before
      }

      const legacyCategoryName = getCategoryNameForEventType(event.category);
      if (legacyCategoryName) {
        msEvent.categories = [legacyCategoryName];
      }

      const response = await msGraphFetch('https://graph.microsoft.com/v1.0/me/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(msEvent)
      });

      if (!response.ok) {
        throw new Error('Failed to create event in Microsoft Calendar');
      }

      const createdEvent = await response.json();
      
      // Update local event to link with MS Calendar
      setCalendarEvents(calendarEvents.map(e => 
        e.id === event.id 
          ? { ...e, msCalendarEventId: createdEvent.id, syncedToMs: true }
          : e
      ));

      return true;
    } catch (error) {
      console.error('Error pushing event to MS Calendar:', error);
      return false;
    }
  };

  // ========================================================================
  // CALENDAR EVENT EDIT & DELETE FUNCTIONS
  // ========================================================================
  
  // Check if an event type is editable (user-created events only)
  const isEventEditable = (event) => {
    // If event has explicit isEditable flag (from Supabase), use it
    if (event.isEditable === true) {
      return true;
    }
    // Also check if source.table is calendar_events (Supabase events)
    if (event.source?.table === 'calendar_events') {
      return true;
    }
    // Only custom calendar events are editable
    // Holidays, leaves, milestones, invoicing, anniversaries, birthdays, MS Calendar events are read-only
    const editableTypes = ['events'];
    return editableTypes.includes(event.eventType || event.category);
  };
  
  // Delete a calendar event (soft delete - marks as deleted)
  const deleteCalendarEvent = async (eventId) => {
    const event = activeCalendarEvents.find(e => e.id === eventId);
    if (!event) {
      console.error('Event not found:', eventId);
      return false;
    }
    
    // Check if event is editable
    if (!isEventEditable(event)) {
      alert('This event type cannot be deleted. It is managed by the system.');
      return false;
    }
    
    if (!window.confirm(`Delete event "${event.title}"?\n\nThis action cannot be undone.`)) {
      return false;
    }
    
    try {
      // If event is synced to MS Calendar, delete it there first
      if (event.msCalendarEventId && msCalendarConnected) {
        try {
            console.log('🗑️ Deleting MS Calendar event:', event.msCalendarEventId);
            await msGraphFetch(`https://graph.microsoft.com/v1.0/me/events/${event.msCalendarEventId}`, {
              method: 'DELETE'
            });
            console.log('✅ MS Calendar event deleted');
        } catch (msError) {
            console.warn('Failed to delete from MS Calendar:', msError);
            // Continue with local delete even if MS delete fails
        }
      }
      
      // Delete based on mode
      if (useSupabaseDB) {
        // Supabase mode: soft delete via hook
        const result = await supabaseCalendarEvents.deleteCalendarEvent(eventId);
        if (!result.success) {
          throw new Error(result.error || 'Failed to delete event');
        }
        // Refetch to stay authoritative
        await supabaseCalendarEvents.fetchCalendarEvents();
      } else {
        // localStorage mode: hard delete from state
        setCalendarEvents(prev => prev.filter(e => e.id !== eventId));
      }
      
      // Close the day events modal if open
      if (showDayEventsModal) {
        setShowDayEventsModal({
          ...showDayEventsModal,
          events: showDayEventsModal.events.filter(e => e.id !== eventId && e.sourceId !== eventId)
        });
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Failed to delete event. Please try again.');
      return false;
    }
  };
  
  // Update an existing calendar event
  const updateCalendarEvent = async (eventId, updates) => {
    const event = activeCalendarEvents.find(e => e.id === eventId);
    if (!event) {
      console.error('Event not found:', eventId);
      return false;
    }
    
    // Check if event is editable
    if (!isEventEditable(event)) {
      alert('This event type cannot be edited. It is managed by the system.');
      return false;
    }
    
    try {
      // If event is synced to MS Calendar, update it there too
      if (event.msCalendarEventId && msCalendarConnected) {
          try {
            const isAllDay = updates.isAllDay ?? event.isAllDay;
            const startDateStr = updates.startDate || updates.date || event.startDate || event.date;
            const endDateStr = updates.endDate || updates.startDate || updates.date || event.endDate || event.startDate || event.date;
            
            let startObj, endObj;
            
            if (isAllDay) {
              // All-day events MUST use "date" property (not "dateTime") per MS Graph API
              const endDatePlusOne = new Date(endDateStr + 'T12:00:00Z'); // Use noon to avoid timezone issues
              endDatePlusOne.setDate(endDatePlusOne.getDate() + 1);
              const endDatePlusOneStr = endDatePlusOne.toISOString().split('T')[0];
              startObj = { date: startDateStr, timeZone: 'UTC' };
              endObj = { date: endDatePlusOneStr, timeZone: 'UTC' };
            } else {
              // Timed events use "dateTime" property
              const startTimeStr = updates.startTime || event.startTime || '09:00';
              const endTimeStr = updates.endTime || event.endTime || '10:00';
              startObj = { dateTime: `${startDateStr}T${startTimeStr}:00`, timeZone: 'UTC' };
              endObj = { dateTime: `${endDateStr}T${endTimeStr}:00`, timeZone: 'UTC' };
            }
            
            await msGraphFetch(`https://graph.microsoft.com/v1.0/me/events/${event.msCalendarEventId}`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                subject: updates.title || event.title,
                start: startObj,
                end: endObj,
                isAllDay: isAllDay,
                body: {
                  contentType: 'text',
                  content: updates.description || event.description || ''
                }
              })
            });
            console.log('✅ MS Calendar event updated:', event.msCalendarEventId);
          } catch (msError) {
            console.warn('Failed to update in MS Calendar:', msError);
            // Continue with local update even if MS update fails
          }
      }
      
      // Update based on mode
      if (useSupabaseDB) {
        // Supabase mode: update via hook
        const result = await supabaseCalendarEvents.updateCalendarEvent(eventId, updates);
        if (!result.success) {
          throw new Error(result.error || 'Failed to update event');
        }
        // Refetch to stay authoritative
        await supabaseCalendarEvents.fetchCalendarEvents();
      } else {
        // localStorage mode: update local state
        const updatedEvent = {
          ...event,
          ...updates,
          updatedAt: new Date().toISOString()
        };
        setCalendarEvents(prev => prev.map(e => e.id === eventId ? updatedEvent : e));
      }
      
      return true;
    } catch (error) {
      console.error('Error updating event:', error);
      alert('Failed to update event. Please try again.');
      return false;
    }
  };
  
  // Open edit modal for an event
  const openEditEventModal = (event) => {
    if (!isEventEditable(event)) {
      alert('This event type cannot be edited. It is managed by the system.');
      return;
    }
    
    // Find the original event from activeCalendarEvents (not the unified view version)
    const originalEvent = activeCalendarEvents.find(e => e.id === event.id || e.id === event.sourceId);
    if (!originalEvent) {
      alert('Cannot edit this event. Original event not found.');
      return;
    }
    
    setEditingEvent(originalEvent);
    setEventForm({
      title: originalEvent.title || '',
      category: originalEvent.category || 'events',
      date: originalEvent.date || originalEvent.startDate || '',
      startDate: originalEvent.startDate || originalEvent.date || '',
      endDate: originalEvent.endDate || originalEvent.startDate || originalEvent.date || '',
      startTime: originalEvent.startTime || '',
      endTime: originalEvent.endTime || '',
      isAllDay: originalEvent.isAllDay || false,
      description: originalEvent.description || ''
    });
    setShowEventModal(true);
  };

  // Import all MS Calendar events to local calendar
  const importMsCalendarEvents = async () => {
    // Only real appointments from the DEFAULT Outlook calendar can be imported.
    // The sync also pulls the dedicated "BRIO — ..." calendars, tagging those
    // events with their BRIO category (holidays, leaves, milestones, invoicing,
    // anniversaries, birthdays, events). Those are this app's own output: it
    // pushed them there from holidays, time_off_requests, project stage dates
    // and user profiles. Copying them into calendar_events would turn every
    // synced entry into a second, custom copy of something the calendar
    // already draws from its own data — and unlike the pushed originals, those
    // copies have no ms_synced_items row, so no future sync could clean them up.
    const importable = (msCalendarEvents || []).filter(e => e && e.category === 'msCalendar');

    if (importable.length === 0) {
      alert(
        (msCalendarEvents || []).length > 0
          ? 'Nothing to import. The synced events all belong to the BRIO calendars, which this app maintains itself.'
          : 'No Microsoft Calendar events to import. Please sync first.'
      );
      return;
    }

    try {
      const newEvents = [];

      importable.forEach(msEvent => {
        // Check if event already imported
        const exists = activeCalendarEvents.some(e => e.msCalendarEventId === msEvent.id);
        if (exists) return;

        // Parse dates and times properly
        const startDate = msEvent.start ? new Date(msEvent.start) : new Date();
        const endDate = msEvent.end ? new Date(msEvent.end) : new Date();
        
        const eventStartDate = startDate.toISOString().split('T')[0];
        const eventEndDate = endDate.toISOString().split('T')[0];
        
        // Extract times (HH:MM format)
        const startTime = !msEvent.isAllDay ? startDate.toTimeString().slice(0, 5) : null;
        const endTime = !msEvent.isAllDay ? endDate.toTimeString().slice(0, 5) : null;
        
        newEvents.push({
          id: Date.now() + Math.random(),
          title: msEvent.subject || 'Untitled Event',
          description: msEvent.location || '',
          date: eventStartDate,
          startDate: eventStartDate,
          endDate: eventEndDate,
          startTime: startTime,  // Will be null for all-day events
          endTime: endTime,      // Will be null for all-day events
          isAllDay: msEvent.isAllDay === true, // Explicit boolean check
          category: 'events',
          color: 'bg-indigo-100 text-indigo-800 border-indigo-300',
          msCalendarEventId: msEvent.id,
          syncedFromMs: true,
          createdBy: currentUser.id
        });
      });

      if (newEvents.length > 0) {
        // Create in Supabase or localStorage based on mode
        if (useSupabaseDB) {
          for (const event of newEvents) {
            await supabaseCalendarEvents.createCalendarEvent(event);
          }
          await supabaseCalendarEvents.fetchCalendarEvents();
        } else {
          setCalendarEvents([...calendarEvents, ...newEvents]);
        }
        
        // Show detailed import summary
        const allDayCount = newEvents.filter(e => e.isAllDay).length;
        const timedCount = newEvents.length - allDayCount;
        alert(`Imported ${newEvents.length} events from Microsoft Calendar!\n\n✓ Timed events: ${timedCount}\n✓ All-day events: ${allDayCount}`);
      } else {
        alert('All Microsoft Calendar events are already imported.');
      }
    } catch (error) {
      console.error('Error importing MS Calendar events:', error);
      alert('Failed to import events. Please try again.');
    }
  };

  // Export all local calendar events to MS Calendar
  const exportAllEventsToMsCalendar = async () => {
    const unsyncedEvents = activeCalendarEvents.filter(e => !e.msCalendarEventId && !e.syncedToMs);
    
    if (unsyncedEvents.length === 0) {
      alert('All local events are already synced to Microsoft Calendar.');
      return;
    }

    if (!window.confirm(`Export ${unsyncedEvents.length} local events to Microsoft Calendar?`)) {
      return;
    }

    let successCount = 0;
    let failCount = 0;

    for (const event of unsyncedEvents) {
      const success = await pushEventToMsCalendarLegacy(event);
      if (success) successCount++;
      else failCount++;
    }

    alert(`Export complete!\n✓ Success: ${successCount}\n✗ Failed: ${failCount}`);
    
    // Refresh MS Calendar to show new events
    if (successCount > 0) {
      syncMsCalendar();
    }
  };
  
  // Check if date is today
  const isToday = (dateStr) => {
    return dateStr === dateToYmd(new Date());
  };
  
  // === WEEKLY CALENDAR VIEW (Inline JSX) ===
  const renderWeeklyView = (weekDays) => {
    const weekStart = dateToYmd(weekDays[0]);
    const weekEnd = dateToYmd(weekDays[6]);
    
    // Get all multi-day events (leaves, holidays, etc. that span multiple days)
    const getMultiDayEvents = () => {
      const multiDayItems = [];
      
      // 1. Approved Leaves (multi-day)
      if (categoryFilters.leaves) {
        const leaves = activeTimeOff.filter(to => 
          to.status === 'approved' &&
          to.startDate <= weekEnd &&
          to.endDate >= weekStart &&
          to.startDate !== to.endDate // Only multi-day leaves
        );
        
        const leaveTypeConfig = {
          sick: { color: 'bg-red-100 text-red-800 border-red-300' },
          vacation: { color: 'bg-blue-100 text-blue-800 border-blue-300' },
          emergency: { color: 'bg-orange-100 text-orange-800 border-orange-300' },
          // Legacy: any leftover pre-migration "personal" rows render as vacation.
          personal: { color: 'bg-blue-100 text-blue-800 border-blue-300' },
          wfh: { color: 'bg-green-100 text-green-800 border-green-300' }
        };
        
        leaves.forEach(leave => {
          const user = activeUsers.find(u => u.id === leave.userId);
          const config = leaveTypeConfig[leave.type] || { color: 'bg-gray-100 text-gray-800 border-gray-300' };
          multiDayItems.push({
            id: `leave-${leave.id}`,
            eventType: 'leaves',
            title: `${user?.name || 'Unknown'} - ${leave.type.charAt(0).toUpperCase() + leave.type.slice(1)}`,
            startDate: leave.startDate,
            endDate: leave.endDate,
            color: config.color,
          });
        });
      }
      
      // 2. Pending Leaves (multi-day) - Admin/Supervisor only
      if (categoryFilters.pendingLeaves && (isAdmin || currentUser.role === 'supervisor')) {
        const pendingLeaves = activeTimeOff.filter(to => 
          (to.status === 'pending' || to.status === 'pending_supervisor' || to.status === 'pending_admin') &&
          to.startDate <= weekEnd &&
          to.endDate >= weekStart &&
          to.startDate !== to.endDate
        );
        
        pendingLeaves.forEach(leave => {
          const user = activeUsers.find(u => u.id === leave.userId);
          // For supervisors, only show their team members
          // Uses isSupervisorOfRequest which checks HFB weekly_team_assignments
          if (currentUser.role === 'supervisor') {
            const isSupervisorOfLeaveUser = isSupervisorOfRequest(currentUser.id, leave);
            
            if (DEBUG_TIMEOFF) {
              console.log('[TimeOff] Week view pending leave filter:', {
                currentUserId: currentUser.id,
                leaveUserId: leave.userId,
                leaveUserName: user?.name,
                leaveStartDate: leave.startDate,
                isSupervisorOfLeaveUser
              });
            }
            
            if (!isSupervisorOfLeaveUser) return;
          }
          
          multiDayItems.push({
            id: `pending-leave-${leave.id}`,
            eventType: 'pendingLeaves',
            title: `${user?.name || 'Unknown'} - ${leave.type} (Pending)`,
            startDate: leave.startDate,
            endDate: leave.endDate,
            color: 'bg-yellow-100 text-yellow-800 border-yellow-400 border-dashed',
          });
        });
      }
      
      // 3. Multi-day custom calendar events
      if (categoryFilters.events) {
        const multiDayCalEvents = activeCalendarEvents.filter(e => 
          e.startDate && e.endDate && 
          e.startDate !== e.endDate &&
          e.startDate <= weekEnd &&
          e.endDate >= weekStart
        );
        
        multiDayCalEvents.forEach(e => {
          multiDayItems.push({
            id: e.id,
            eventType: 'events',
            title: e.title || e.description || 'Event',
            startDate: e.startDate,
            endDate: e.endDate,
            color: e.color || 'bg-gray-100 text-gray-800 border-gray-300',
            isEditable: e.isEditable !== undefined ? e.isEditable : true
          });
        });
      }
      
      return multiDayItems;
    };
    
    const multiDayEvents = getMultiDayEvents();
    
    // Calculate current time position for indicator
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    
    // AUDIT[01]: Calendar grid uses 20+ Tailwind color classes (bg-slate-50, text-slate-600, bg-blue-50, bg-blue-600, etc.) — entire section needs BRIO token migration
    return (
      <div className="bg-white rounded-lg shadow overflow-hidden" style={{ height: 'calc(100vh - 350px)', minHeight: '600px' }}>
        {/* Header with day names and dates */}
        <div className="grid grid-cols-8 border-b bg-slate-50 sticky top-0 z-20">
          <div className="p-3 text-xs font-semibold text-slate-600 border-r bg-slate-100">TIME</div>
          {weekDays.map((day, idx) => {
            const dateStr = dateToYmd(day);
            const today = isToday(dateStr);
            return (
              <div key={idx} className={`p-3 text-center border-r ${today ? 'bg-blue-50' : ''}`}>
                <div className="text-xs text-slate-600 uppercase">{day.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                <div className={`text-2xl font-bold mt-1 ${today ? 'bg-blue-600 text-white w-10 h-10 rounded-full flex items-center justify-center mx-auto' : 'text-slate-800'}`}>
                  {day.getDate()}
                </div>
              </div>
            );
          })}
        </div>
        
        {/* All-day events row */}
        <div className="grid grid-cols-8 border-b bg-slate-50 sticky" style={{ top: '73px', zIndex: 19, minHeight: '80px', maxHeight: '140px' }}>
          <div className="p-2 text-xs font-semibold text-slate-600 border-r bg-slate-100 flex items-start">
            ALL DAY
          </div>
          <div className="col-span-7 relative">
            <div className="grid grid-cols-7 h-full">
              {weekDays.map((day, dayIdx) => {
                const dateStr = dateToYmd(day);
                // Get all events for this day, then filter to only all-day events
                const allEvents = getAllDayEvents(dateStr);
                const singleDayEvents = allEvents.filter(e => {
                  const isMultiDay = e.endDate && e.startDate !== e.endDate;
                  const isTrueAllDay = e.isAllDay === true;
                  return !isMultiDay && isTrueAllDay;
                });
                
                return (
                  <div key={dayIdx} className="border-r p-1 overflow-y-auto flex flex-col" style={{ maxHeight: '140px', minHeight: '60px' }}>
                    {singleDayEvents.map(event => {
                      const brioStyle = getBrioEventStyle(event.eventType);
                      const ttText = `${event.icon ? event.icon + ' ' : ''}${event.title}${event.status && event.status !== 'system' && event.status !== 'active' && event.status !== 'synced' ? ` (${event.status})` : ''}`;
                      return (
                        <div
                          key={event.id}
                          className="flex-shrink-0 truncate"
                          style={{
                            fontSize: '11px',
                            lineHeight: '1.2',
                            padding: '1px 5px',
                            marginBottom: '2px',
                            cursor: (event.isEditable === true || event.source?.table === 'calendar_events') ? 'pointer' : 'default',
                            border: `1px ${brioStyle.borderStyle} ${brioStyle.borderColor}`,
                            borderRadius: 0,
                            backgroundColor: brioStyle.backgroundColor
                          }}
                          title={ttText}
                          {...getCalTooltipProps(`wk-allday-${event.id}`, ttText)}
                          onDoubleClick={() => {
                            if (event.isEditable === true || event.source?.table === 'calendar_events') {
                              openEditEventModal(event);
                            }
                          }}
                        >
                          {event.icon && `${event.icon} `}{event.title}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
            
            {/* Multi-day events spanning across days */}
            <div className="absolute top-1 left-0 right-0 pointer-events-none">
              {multiDayEvents.map((event, idx) => {
                const eventStartDate = new Date(Math.max(new Date(event.startDate), new Date(weekStart)));
                const eventEndDate = new Date(Math.min(new Date(event.endDate), new Date(weekEnd)));
                
                const startDayIndex = Math.floor((eventStartDate - new Date(weekStart)) / 86400000);
                const endDayIndex = Math.floor((eventEndDate - new Date(weekStart)) / 86400000);
                const spanDays = endDayIndex - startDayIndex + 1;
                
                if (spanDays > 1) {
                  const brioStyle = getBrioEventStyle(event.eventType);
                  const ttText = `${event.icon ? event.icon + ' ' : ''}${event.title}`;
                  return (
                    <div
                      key={event.id}
                      className="absolute font-medium pointer-events-auto truncate"
                      style={{
                        left: `${(startDayIndex / 7) * 100}%`,
                        width: `${(spanDays / 7) * 100}%`,
                        top: `${idx * 22 + 2}px`,
                        fontSize: '11px',
                        lineHeight: '1.2',
                        padding: '1px 5px',
                        borderRadius: 0,
                        cursor: (event.isEditable === true || event.source?.table === 'calendar_events') ? 'pointer' : 'default',
                        border: `1px ${brioStyle.borderStyle} ${brioStyle.borderColor}`,
                        backgroundColor: brioStyle.backgroundColor
                      }}
                      title={ttText}
                      {...getCalTooltipProps(`wk-multi-${event.id}`, ttText)}
                      onDoubleClick={() => {
                        if (event.isEditable === true || event.source?.table === 'calendar_events') {
                          openEditEventModal(event);
                        }
                      }}
                    >
                      {event.icon && `${event.icon} `}{event.title}
                    </div>
                  );
                }
                return null;
              })}
            </div>
          </div>
        </div>
        
        {/* Scrollable hourly timeline */}
        <div className="overflow-y-auto" style={{ height: 'calc(100% - 220px)' }}>
          <div className="grid grid-cols-8 relative">
            {/* Events overlay layer - spans all hours */}
            <div className="absolute top-0 left-0 right-0 bottom-0 grid grid-cols-8 pointer-events-none" style={{ zIndex: 5 }}>
              <div></div> {/* Empty space for time labels column */}
              {weekDays.map((day, dayIdx) => {
                const dateStr = dateToYmd(day);

                // Get all timed events for this day
                const allEvents = getAllDayEvents(dateStr);
                const dayTimedEvents = allEvents.filter(e => {
                  return e.isAllDay !== true && e.startTime;
                });
                
                // === OVERLAP COLUMN ALGORITHM ===
                // Step 1: Sort by start time, then end time
                const sortedEvents = [...dayTimedEvents].sort((a, b) => {
                  const aStart = a.startTime || '00:00';
                  const bStart = b.startTime || '00:00';
                  if (aStart !== bStart) return aStart.localeCompare(bStart);
                  const aEnd = a.endTime || '23:59';
                  const bEnd = b.endTime || '23:59';
                  return aEnd.localeCompare(bEnd);
                });
                
                // Step 2: Build overlap clusters and assign columns
                const eventsWithLayout = sortedEvents.map(event => {
                  const [startHour, startMin] = event.startTime.split(':').map(Number);
                  const [endHour, endMin] = event.endTime ? event.endTime.split(':').map(Number) : [startHour + 1, 0];
                  return {
                    ...event,
                    _startMinutes: startHour * 60 + startMin,
                    _endMinutes: endHour * 60 + endMin,
                    _columnIndex: 0,
                    _columnCount: 1
                  };
                });
                
                // Step 3: Greedy column assignment within clusters
                const columns = []; // Each column tracks the end time of its last event
                
                eventsWithLayout.forEach(event => {
                  // Find first column where this event fits (no overlap)
                  let placed = false;
                  for (let col = 0; col < columns.length; col++) {
                    if (columns[col] <= event._startMinutes) {
                      event._columnIndex = col;
                      columns[col] = event._endMinutes;
                      placed = true;
                      break;
                    }
                  }
                  if (!placed) {
                    event._columnIndex = columns.length;
                    columns.push(event._endMinutes);
                  }
                });
                
                // Step 4: Determine column count for each event (max columns in its time range)
                eventsWithLayout.forEach(event => {
                  // Find all events that overlap with this one
                  const overlapping = eventsWithLayout.filter(other => 
                    event._startMinutes < other._endMinutes && event._endMinutes > other._startMinutes
                  );
                  // Column count is the max columnIndex + 1 among overlapping events
                  const maxCol = Math.max(...overlapping.map(e => e._columnIndex));
                  event._columnCount = maxCol + 1;
                });
                
                return (
                  <div key={dayIdx} className="relative" style={{ height: '100%' }}>
                    {eventsWithLayout.map((event) => {
                      // Calculate absolute position from top (in pixels)
                      const topPx = (event._startMinutes / 60) * 60; // 60px per hour
                      
                      // Calculate height based on duration
                      const durationMinutes = event._endMinutes - event._startMinutes;
                      const heightPx = Math.max(20, (durationMinutes / 60) * 60); // 60px per hour, min 20px
                      
                      // Calculate width and left position for side-by-side layout
                      const widthPercent = 100 / event._columnCount;
                      const leftPercent = event._columnIndex * widthPercent;
                      
                      const brioStyle = getBrioEventStyle(event.eventType);
                      const ttText = `${event.icon ? event.icon + ' ' : ''}${event.title} (${event.startTime}${event.endTime ? `–${event.endTime}` : ''})`;

                      return (
                        <div
                          key={event.id}
                          className="absolute overflow-hidden pointer-events-auto"
                          style={{
                            top: `${topPx}px`,
                            height: `${heightPx}px`,
                            left: `${leftPercent}%`,
                            width: `${widthPercent}%`,
                            fontSize: '10px',
                            lineHeight: '1.2',
                            padding: '1px 4px',
                            borderRadius: 0,
                            cursor: 'pointer',
                            border: `1px ${brioStyle.borderStyle} ${brioStyle.borderColor}`,
                            backgroundColor: brioStyle.backgroundColor,
                            boxSizing: 'border-box'
                          }}
                          title={ttText}
                          {...getCalTooltipProps(`wk-timed-${event.id}`, ttText)}
                          onClick={(e) => {
                            e.stopPropagation();
                            // Could open edit modal here
                          }}
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            if (event.isEditable === true || event.source?.table === 'calendar_events') {
                              openEditEventModal(event);
                            }
                          }}
                        >
                          <div className="font-semibold truncate">{event.startTime}</div>
                          <div className="truncate">
                            {event.icon && `${event.icon} `}
                            {event.title}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
            
            {/* 24 hour rows */}
            {Array.from({ length: 24 }, (_, hour) => (
              <React.Fragment key={hour}>
                <div className="border-r border-b p-2 text-xs text-slate-600 bg-slate-50 text-right pr-3" style={{ height: '60px', fontSize: '11px' }}>
                  {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
                </div>
                {weekDays.map((day, idx) => {
                  const dateStr = dateToYmd(day);
                  const today = isToday(dateStr);
                  const isCurrentHour = today && hour === currentHour;
                  
                  return (
                    <div 
                      key={idx} 
                      className={`border-r border-b relative cursor-pointer hover:bg-blue-50 transition-colors ${today ? 'bg-blue-50 bg-opacity-20' : ''}`}
                      style={{ height: '60px' }}
                      onClick={() => {
                        setEventForm({
                          date: dateStr,
                          startTime: `${hour.toString().padStart(2, '0')}:00`,
                          endTime: `${(hour + 1).toString().padStart(2, '0')}:00`,
                          isAllDay: false,
                          category: 'events',
                          title: '',
                          description: ''
                        });
                        setShowEventModal(true);
                      }}
                    >
                      {/* Current time indicator (red line) */}
                      {isCurrentHour && (
                        <div 
                          className="absolute left-0 right-0 z-10"
                          style={{ 
                            top: `${(currentMinute / 60) * 100}%`,
                          }}
                        >
                          <div className="relative">
                            <div className="w-2 h-2 bg-red-500 rounded-full absolute -left-1 -top-1"></div>
                            <div className="border-t-2 border-red-500"></div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    );
  };
  
  // === MONTHLY CALENDAR VIEW (Inline JSX) ===
  const renderMonthlyView = (monthDays) => {
    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="grid grid-cols-7 gap-px bg-slate-200">
          {/* Day headers */}
          {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(d => (
            <div key={d} className="text-center font-semibold py-3 text-sm bg-slate-50 text-slate-700">
              {d}
            </div>
          ))}
          
          {/* Calendar days */}
          {monthDays.map((day, idx) => {
            // Handle null entries (leading blank cells for proper weekday alignment)
            if (day === null) {
              return (
                <div 
                  key={`blank-${idx}`} 
                  className="bg-slate-100 p-3 min-h-32 border-b"
                  style={{ minHeight: '140px' }}
                />
              );
            }
            
            const dateStr = dateToYmd(day);
            const allEvents = getAllDayEvents(dateStr);
            const today = isToday(dateStr);
            
            const maxVisible = 6; // Compact event boxes fit more per cell
            const visibleEvents = allEvents.slice(0, maxVisible);
            const hiddenCount = allEvents.length - maxVisible;

            const isCurrentMonth = day.getMonth() === calDate.getMonth();

            return (
              <div
                key={idx}
                className={`bg-white p-2 min-h-32 border-b ${!isCurrentMonth ? 'bg-slate-50' : ''} ${today ? 'ring-2 ring-blue-500 ring-inset' : ''}`}
                style={{ minHeight: '140px' }}
              >
                <div className={`text-sm font-semibold mb-1 ${today ? 'bg-blue-600 text-white w-7 h-7 rounded-full flex items-center justify-center' : !isCurrentMonth ? 'text-slate-400' : 'text-slate-800'}`}>
                  {day.getDate()}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  {visibleEvents.map(event => {
                    const brioStyle = getBrioEventStyle(event.eventType);
                    const ttText = `${event.icon ? event.icon + ' ' : ''}${event.title}${event.status && event.status !== 'system' && event.status !== 'active' && event.status !== 'synced' ? ` (${event.status})` : ''}`;
                    return (
                      <div
                        key={event.id}
                        className="truncate cursor-pointer hover:shadow transition-shadow"
                        style={{
                          fontSize: '11px',
                          lineHeight: '1.2',
                          padding: '1px 5px',
                          border: `1px ${brioStyle.borderStyle} ${brioStyle.borderColor}`,
                          borderRadius: 0,
                          backgroundColor: brioStyle.backgroundColor
                        }}
                        title={ttText}
                        {...getCalTooltipProps(`mo-${dateStr}-${event.id}`, ttText)}
                        onDoubleClick={() => {
                          if (event.isEditable === true || event.source?.table === 'calendar_events') {
                            openEditEventModal(event);
                          }
                        }}
                      >
                        {event.icon && `${event.icon} `}{event.title}
                      </div>
                    );
                  })}
                  
                  {hiddenCount > 0 && (
                    <button
                      onClick={() => setShowDayEventsModal({ date: dateStr, events: allEvents, day: day })}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium hover:underline"
                    >
                      + {hiddenCount} more
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const getDaysInView = () => {
    const days = [];
    const start = new Date(calDate);
    if (calView === 'week') {
      start.setDate(start.getDate() - start.getDay());
      for (let i = 0; i < 7; i++) days.push(new Date(start.getTime() + i*86400000));
    } else {
      // FIXED: Properly calculate first day of month and add leading blank cells
      const year = calDate.getFullYear();
      const month = calDate.getMonth();
      
      // Get the first day of the month (avoid timezone issues by using year, month, day constructor)
      const firstDayOfMonth = new Date(year, month, 1);
      const firstDayWeekday = firstDayOfMonth.getDay(); // 0 = Sunday, 6 = Saturday
      
      // Add null entries for leading blank cells (days from previous month)
      for (let i = 0; i < firstDayWeekday; i++) {
        days.push(null);
      }
      
      // Add all days of the current month
      const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
      for (let day = 1; day <= lastDayOfMonth; day++) {
        days.push(new Date(year, month, day));
      }
    }
    return days;
  };

  // === SHARED NAVIGATION DATA (used by both desktop and mobile) ===
  const subNavItemsByCategory = {
    time: [
      { id: 'donut-tracker', label: 'Donut Tracker' },
      { id: 'tracker', label: 'Time Log' },
      ...(canAccessPage(currentUser.role, 'timesheet-overview') ? [{ id: 'timesheet-overview', label: 'Timesheet Overview' }] : []),
      ...(canAccessPage(currentUser.role, 'freelancer-management') ? [{ id: 'freelancer-management', label: 'Freelancer Management' }] : []),
      { id: 'calendar', label: 'Calendar' },
      ...(isAdmin ? [{ id: 'ms-calendar', label: 'MS Calendar' }] : []),
      { id: 'time-off-requests', label: 'Time Off Requests' },
      ...(isAdmin ? [
        { id: 'leave-policy', label: 'Leave Policy' },
        { id: 'leave-entitlement', label: 'Leave Entitlement' },
        { id: 'leave-summary', label: 'Leave Summary' }
      ] : [])
    ],
    projects: [
      { id: 'manage-projects', label: 'Projects' },
      ...(canAccessPage(currentUser.role, 'projects-overview') ? [
        { id: 'projects-overview', label: 'Projects Overview' }
      ] : []),
      { id: 'project-map', label: 'Map' },
      { id: 'clients', label: 'Clients' },
      ...(isAdmin ? [{ id: 'inquiries', label: 'Inquiries', badge: inquiriesNeedsReviewCount }] : []),
      { id: 'stages', label: 'Stages' },
      ...(canAccessPage(currentUser.role, 'contacts-directory') ? [
        { id: 'contacts-directory', label: 'Contacts' }
      ] : []),
      ...(canAccessPage(currentUser.role, 'contracts-scope') ? [
        { id: 'contracts-scope', label: 'Scope' }
      ] : []),
      ...(canAccessPage(currentUser.role, 'contracts') ? [
        { id: 'contracts', label: 'Contracts' }
      ] : []),
      ...(canAccessPage(currentUser.role, 'tracking') ? [
        { id: 'tracking', label: 'Tracking' }
      ] : []),
      ...(canAccessPage(currentUser.role, 'invoices') ? [
        { id: 'invoices', label: 'Invoices', badge: pendingPaymentCount }
      ] : []),
      ...((isAdmin || currentUser?.role === 'supervisor' || hasAnyTimelineAccess) ? [
        { id: 'timelines', label: 'Timelines' }
      ] : []),
      { id: 'mom-directory', label: 'M.O.M' },
    ],
    team: [
      { id: 'hybrid-focus-board', label: 'Team Allocation' },
      { id: 'project-tasks', label: 'Project Tasks' },
      { id: 'teams', label: 'Teams' },
      { id: 'team-members', label: 'Team Members' },
      { id: 'admin-team-overview', label: 'Team Overview' },
      { id: 'hiring', label: 'Hiring' },
      { id: 'role-framework', label: 'Roles & Responsibilities' },
      { id: 'travel', label: 'Travel' },
    ].filter(item => canAccessPage(currentUser.role, item.id)),
    accounting: [
      { id: 'expense-analytics', label: 'Expense Analytics' },
      { id: 'expense-admin', label: 'Expense Administration' },
      { id: 'expense-claims', label: 'Expense Claims' },
      { id: 'tax-reconciliation', label: 'Tax Reconciliation' },
      { id: 'project-planning', label: 'Project Planning' },
      { id: 'salary-matrix', label: 'Salary Matrix IN' },
      { id: 'salary-matrix-nl', label: 'Salary Matrix NL' },
      { id: 'markup-factor', label: 'Markup Factor' },
      { id: 'settings', label: 'Settings' }
    ].filter(item => canAccessPage(currentUser.role, item.id)),
    reports: [
      { id: 'projectProfitability', label: 'Project Profitability' },
      { id: 'salaryCost', label: 'Salary Cost' },
      { id: 'projectCostLedger', label: 'Project Cost Ledger' },
      { id: 'profitability', label: 'Variance Analysis' },
      { id: 'vofStatement', label: 'VOF Statement' }
    ].filter(item => canAccessPage(currentUser.role, item.id)),
    analytics: [
      { id: 'overview', label: 'Overview' },
      { id: 'timesheets', label: 'Timesheets' },
      { id: 'allocation', label: 'Teams' },
      { id: 'financial-overview', label: 'Financial' },
      { id: 'analytics-projects', label: 'Projects' },
      { id: 'invoicing', label: 'Invoicing' },
      { id: 'cashflow', label: 'Cashflow' },
      { id: 'salary', label: 'Salaries' },
      { id: 'stage-intelligence', label: 'Stages' },
      { id: 'capacity-intelligence', label: 'Capacity' }
    ].filter(item => canAccessPage(currentUser.role, item.id)),
    comms: [
      { id: 'comms-practice', label: 'Practice' },
      { id: 'comms-projects', label: 'Projects' },
      { id: 'comms-social',   label: 'Social' },
      { id: 'comms-calendar', label: 'Calendar' },
      { id: 'comms-contacts', label: 'Contacts' },
      { id: 'comms-inbox',    label: 'Inbox' },
      { id: 'comms-pitches',  label: 'Pitches' },
    ]
  };

  // Main category definitions with icons for mobile nav
  const mainCategoryConfig = [
    { id: 'time', label: 'Time', icon: Clock, defaultSubPage: 'tracker' },
    { id: 'projects', label: 'Projects', icon: Briefcase, defaultSubPage: 'manage-projects' },
    { id: 'team', label: 'Team', icon: Users, defaultSubPage: (isAdmin || currentUser.role === 'supervisor') ? 'teams' : 'project-tasks' },
    { id: 'accounting', label: 'Accounting', icon: Receipt, defaultSubPage: isAdmin ? 'expense-analytics' : 'expense-claims' },
    ...((isAdmin || currentUser.role === 'supervisor') ? [{ id: 'reports', label: 'Reports', icon: BarChart3, defaultSubPage: null }] : []),
    ...(isAdmin ? [{ id: 'analytics', label: 'Analytics', icon: TrendingUp, defaultSubPage: null }] : []),
    ...((isAdmin || currentUser.role === 'supervisor') ? [{ id: 'radar', label: 'Radar', icon: Radio, defaultSubPage: null }] : []),
    ...(canAccessPage(currentUser.role, 'comms') ? [{ id: 'comms', label: 'Comms', icon: Newspaper, defaultSubPage: 'comms-practice' }] : []),
    ...(isAdmin ? [{ id: 'styleguide', label: 'SG', icon: Edit2, defaultSubPage: null }] : []),
  ];

  // Mobile bottom bar: these four sections get their own button, the rest go
  // under More, so the bar never holds more than five buttons.
  const MOBILE_PRIMARY_CATEGORIES = ['time', 'projects', 'team', 'accounting'];
  const mobilePrimaryCategories = mainCategoryConfig.filter(c => MOBILE_PRIMARY_CATEGORIES.includes(c.id));
  const mobileMoreCategories = mainCategoryConfig.filter(c => !MOBILE_PRIMARY_CATEGORIES.includes(c.id));
  const selectMobileCategory = (cat) => {
    setMainCategory(cat.id);
    if (cat.defaultSubPage !== null) {
      setSubPage(cat.defaultSubPage);
    }
    setMobileMoreSheetOpen(false);
  };

  // Get the current subpage label for mobile header display
  const getCurrentSubpageLabel = () => {
    const items = subNavItemsByCategory[mainCategory] || [];
    const activeKey = mainCategory === 'reports' ? (form.reportTab || 'projectProfitability')
      : mainCategory === 'analytics' ? (form.analyticsTab || 'overview')
      : subPage;
    const found = items.find(item => item.id === activeKey);
    return found ? found.label : (mainCategoryConfig.find(c => c.id === mainCategory)?.label || '');
  };

  // Get the current main category label
  const getCurrentCategoryLabel = () => {
    return mainCategoryConfig.find(c => c.id === mainCategory)?.label || '';
  };

  return (
    <div
      className="min-h-screen bg-white brio-app-wrapper"
      style={{
        // Published for full-screen page overlays (useFullScreenPage in
        // BrioComponents, and the local copy in ExpenseClaimsPage): they pin
        // themselves to the viewport, so without these they would paint over
        // the fixed nav rows. Both go to 0 when the nav is hidden (Donut
        // Tracker in browser fullscreen), giving a true edge-to-edge surface.
        '--brio-nav-height': `${(subPage === 'donut-tracker' && isFullscreen) ? 0 : navHeight}px`,
        '--brio-nav-bottom-height':
          isMobile && !(subPage === 'donut-tracker' && isFullscreen)
            ? 'calc(56px + env(safe-area-inset-bottom, 0px))'
            : '0px',
      }}
    >
      <CalendarTooltipPortal active={calTooltipActive} />
      {/* FIXED NAVIGATION - TWO ROWS */}
      {/* Hidden when Donut Tracker is in fullscreen mode */}
      {!(subPage === 'donut-tracker' && isFullscreen) && (
      <div
        ref={navRef}
        className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm"
        style={{ borderBottom: '1px solid #000000' }}
      >
        {/* === MOBILE TOP BAR === */}
        {isMobile && (
          <div className="px-4 py-3 flex items-center justify-between" style={{ minHeight: 52 }}>
            <div className="flex items-center gap-2 min-w-0">
              <BrioMark size={20} className="flex-shrink-0 brio-text-gold" />
              <span className="text-sm font-bold brio-section-title truncate">{getCurrentCategoryLabel()}</span>
            </div>
            <div className="flex items-center gap-2">
              {/* Subpage selector chip */}
              {(subNavItemsByCategory[mainCategory] || []).length > 1 && (
                <button
                  onClick={() => setMobileSubpageSheetOpen(true)}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium"
                  style={{ background: '#f5f5f5', border: '1px solid #e0e0e0', maxWidth: 180 }}
                >
                  <span className="truncate">{getCurrentSubpageLabel()}</span>
                  <ChevronDown className="w-3 h-3 flex-shrink-0" />
                </button>
              )}
              {/* Sign out - icon only on mobile */}
              <button
                onClick={async () => {
                  try {
                    await signOut();
                  } catch (err) {
                    alert('Failed to sign out: ' + err.message);
                  }
                }}
                className="p-2 rounded-full"
                style={{ background: 'transparent', border: 'none', color: '#666' }}
                title="Sign out"
                aria-label="Sign out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* === DESKTOP NAVIGATION === */}
        {!isMobile && (
        <>
        {/* ROW 1: Header + Main Categories */}
        <div className="border-b brio-border-light">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              {/* Left: Brand + Authenticated User + Role. The Timelines and Donut Tracker pages pin an
                  info icon at the window's top left (16px to 30px); on screens narrower than this row's
                  1280px the logo reaches that corner, so it moves right just enough to clear it. */}
              <div className="flex items-center gap-3" style={{ marginLeft: 'max(0px, calc(16px - max(0px, (100vw - 1280px) / 2)))' }}>
                <BrioMark size={32} className="brio-text-gold" />
                <div>
                  <h1 className="text-xl font-bold brio-section-title">BRIO Atlas</h1>
                  {/* Show authenticated user info with their actual role */}
                  {session?.user?.email && mappedUser ? (
                    <p className="text-sm font-medium brio-text-gold" title="Authenticated via Supabase - this identity controls database permissions">
                      {mappedUser.name}
                    </p>
                  ) : session?.user?.email ? (
                    <p className="text-sm text-amber-600">
                      Loading profile...
                    </p>
                  ) : (
                    <p className="text-sm text-red-600">Not authenticated</p>
                  )}
                </div>
              </div>
              
              {/* Right: Demo User Selector + Sign Out + Main Categories */}
              <div className="flex items-center gap-3">
                {/* Sign Out Button */}
                <button
                  onClick={async () => {
                    try {
                      await signOut();
                      console.log('✅ Signed out');
                    } catch (err) {
                      console.error('Sign out failed:', err);
                      alert('Failed to sign out: ' + err.message);
                    }
                  }}
                  className="brio-btn brio-btn-secondary brio-flex brio-items-center brio-gap-xs"
                  title="Sign out of Supabase"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">Sign out</span>
                </button>
                
                {/* Main Categories */}
                <div className="flex gap-1">
                  {/* TIME */}
                  <button
                    onClick={() => { setMainCategory('time'); setSubPage('tracker'); }}
                    className="px-5 py-2 text-sm transition-all"
                    style={{
                      fontWeight: mainCategory === 'time' ? 400 : 200,
                      color: mainCategory === 'time' ? '#000000' : '#666666',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: mainCategory === 'time' ? '2px solid #000000' : '2px solid transparent',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}
                  >
                    TIME
                    {(isAdmin || currentUser?.role === 'supervisor') && pendingTimeOffCount > 0 && (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: 'var(--brio-orange)',
                        color: '#ffffff',
                        fontSize: '9px',
                        fontWeight: 700,
                        lineHeight: 1,
                        padding: '0 3px'
                      }}>
                        {pendingTimeOffCount}
                      </span>
                    )}
                  </button>
                  
                  {/* PROJECTS */}
                  <button 
                    onClick={() => { setMainCategory('projects'); setSubPage('manage-projects'); }} 
                    className="px-5 py-2 text-sm transition-all"
                    style={{
                      fontWeight: mainCategory === 'projects' ? 400 : 200,
                      color: mainCategory === 'projects' ? '#000000' : '#666666',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: mainCategory === 'projects' ? '2px solid #000000' : '2px solid transparent'
                    }}
                  >
                    PROJECTS
                  </button>
                  
                  {/* TEAM - Visible to all roles; users see only Project Tasks */}
                    <button 
                      onClick={() => { 
                        setMainCategory('team'); 
                        // Users default to project-tasks; admin/supervisor to teams
                        setSubPage((isAdmin || currentUser.role === 'supervisor') ? 'teams' : 'project-tasks'); 
                      }} 
                      className="px-5 py-2 text-sm transition-all"
                      style={{
                        fontWeight: mainCategory === 'team' ? 400 : 200,
                        color: mainCategory === 'team' ? '#000000' : '#666666',
                        background: 'transparent',
                        border: 'none',
                        borderBottom: mainCategory === 'team' ? '2px solid #000000' : '2px solid transparent'
                      }}
                    >
                      TEAM
                    </button>
                  
                  {/* ACCOUNTING - All roles (nav items filtered by canAccessPage) */}
                  <button
                    onClick={() => { setMainCategory('accounting'); setSubPage(isAdmin ? 'expense-analytics' : 'expense-claims'); }}
                    className="px-5 py-2 text-sm transition-all"
                    style={{
                      fontWeight: mainCategory === 'accounting' ? 400 : 200,
                      color: mainCategory === 'accounting' ? '#000000' : '#666666',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: mainCategory === 'accounting' ? '2px solid #000000' : '2px solid transparent'
                    }}
                  >
                    ACCOUNTING
                  </button>
                  
                  {/* REPORTS - Admin + Supervisor (supervisors see Project Profitability only) */}
                  {(isAdmin || currentUser?.role === 'supervisor') && (
                    <button
                      onClick={() => { setMainCategory('reports'); setSubPage(null); }}
                      className="px-5 py-2 text-sm transition-all"
                      style={{
                        fontWeight: mainCategory === 'reports' ? 400 : 200,
                        color: mainCategory === 'reports' ? '#000000' : '#666666',
                        background: 'transparent',
                        border: 'none',
                        borderBottom: mainCategory === 'reports' ? '2px solid #000000' : '2px solid transparent'
                      }}
                    >
                      REPORTS
                    </button>
                  )}
                  
                  {/* ANALYTICS - Admin only */}
                  {isAdmin && (
                    <button
                      onClick={() => { setMainCategory('analytics'); setSubPage(null); }}
                      className="px-5 py-2 text-sm transition-all"
                      style={{
                        fontWeight: mainCategory === 'analytics' ? 400 : 200,
                        color: mainCategory === 'analytics' ? '#000000' : '#666666',
                        background: 'transparent',
                        border: 'none',
                        borderBottom: mainCategory === 'analytics' ? '2px solid #000000' : '2px solid transparent'
                      }}
                    >
                      ANALYTICS
                    </button>
                  )}
                  {/* RADAR - Admin and Supervisor */}
                  {(isAdmin || currentUser.role === 'supervisor') && (
                    <button
                      onClick={() => { setMainCategory('radar'); setSubPage(null); }}
                      className="px-5 py-2 text-sm transition-all"
                      style={{
                        fontWeight: mainCategory === 'radar' ? 400 : 200,
                        color: mainCategory === 'radar' ? '#000000' : '#666666',
                        background: 'transparent',
                        border: 'none',
                        borderBottom: mainCategory === 'radar' ? '2px solid #000000' : '2px solid transparent',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px'
                      }}
                    >
                      RADAR
                      {pendingPaymentCount > 0 && (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          backgroundColor: 'var(--brio-orange)',
                          color: '#ffffff',
                          fontSize: '9px',
                          fontWeight: 700,
                          lineHeight: 1,
                          padding: '0 3px'
                        }}>
                          {pendingPaymentCount}
                        </span>
                      )}
                    </button>
                  )}
                  {/* COMMS - Admin, Supervisor, and users with temp access */}
                  {canAccessPage(currentUser.role, 'comms') && (
                    <button
                      onClick={() => { setMainCategory('comms'); setSubPage('comms-practice'); }}
                      className="px-5 py-2 text-sm transition-all"
                      style={{
                        fontWeight: mainCategory === 'comms' ? 400 : 200,
                        color: mainCategory === 'comms' ? '#9F902A' : '#666666',
                        background: 'transparent',
                        border: 'none',
                        borderBottom: mainCategory === 'comms' ? '2px solid #9F902A' : '2px solid transparent'
                      }}
                    >
                      COMMS
                    </button>
                  )}
                  {/* STYLE GUIDE - Admin only (temporary) */}
                  {isAdmin && (
                    <button
                      onClick={() => { setMainCategory('styleguide'); setSubPage(null); }}
                      className="px-5 py-2 text-sm transition-all"
                      style={{
                        fontWeight: mainCategory === 'styleguide' ? 400 : 200,
                        color: mainCategory === 'styleguide' ? '#9F902A' : '#666666',
                        background: 'transparent',
                        border: 'none',
                        borderBottom: mainCategory === 'styleguide' ? '2px solid #9F902A' : '2px solid transparent',
                        fontStyle: 'italic',
                      }}
                    >
                      SG
                    </button>
                  )}
                </div>

                {/* Project Filter Icon Button */}
                <>
                    <button
                      onClick={() => setShowFilterMenu(!showFilterMenu)}
                      className="p-2 transition-all flex items-center justify-center"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: projectStatusFilter !== 'default'
                          ? PROJECT_STATUS_COLORS[projectStatusFilter]
                          : 'var(--brio-grey)'
                      }}
                      title="Project Filter"
                      aria-label="Filter projects by status"
                    >
                      <Filter className="w-4 h-4" />
                    </button>
                    
                    {/* Filter Modal - BRIO styled */}
                    {showFilterMenu && (
                      <BrioModal
                        title="Project Status Filter"
                        onClose={() => setShowFilterMenu(false)}
                        width="sm"
                        footer={
                          <BrioActionGroup>
                            <button
                              onClick={() => setShowFilterMenu(false)}
                              className="brio-btn brio-btn-secondary"
                            >
                              Close
                            </button>
                          </BrioActionGroup>
                        }
                      >
                        <div className="space-y-4">
                          <div>
                            <label className="brio-modal-label">Filter by Status</label>
                            <select
                              value={projectStatusFilter}
                              onChange={(e) => {
                                setProjectStatusFilter(e.target.value);
                                setShowFilterMenu(false);
                              }}
                              className="brio-modal-select"
                            >
                              <option value="default">All Projects (Default)</option>
                              <option value="active">Active Only</option>
                              <option value="paused">Paused Only</option>
                              <option value="completed">Completed Only</option>
                              <option value="archived">Archived Only</option>
                            </select>
                          </div>
                          
                          <div className="pt-3" style={{ borderTop: '1px solid var(--brio-border)' }}>
                            <p className="brio-modal-hint brio-mb-xs">Status Colors:</p>
                            <div className="flex flex-wrap gap-3">
                              <div className="flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full" style={{ background: PROJECT_STATUS_COLORS.active }}></span>
                                <span style={{ fontSize: '11px', color: 'var(--brio-grey)' }}>Active</span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full" style={{ background: PROJECT_STATUS_COLORS.paused }}></span>
                                <span style={{ fontSize: '11px', color: 'var(--brio-grey)' }}>Paused</span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full" style={{ background: PROJECT_STATUS_COLORS.completed }}></span>
                                <span style={{ fontSize: '11px', color: 'var(--brio-grey)' }}>Completed</span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full" style={{ background: PROJECT_STATUS_COLORS.archived }}></span>
                                <span style={{ fontSize: '11px', color: 'var(--brio-grey)' }}>Archived</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </BrioModal>
                    )}
                </>
              </div>
            </div>
          </div>
        </div>
        
        {/* ROW 2: Sub-menu (Context-sensitive) - Desktop only */}
        {(() => {
          const items = subNavItemsByCategory[mainCategory] || [];
          if (items.length === 0) return null;

          const getActiveKey = () => {
            if (mainCategory === 'reports') {
              const defaultReport = form.reportTab || 'projectProfitability';
              return canAccessPage(currentUser.role, defaultReport) ? defaultReport : (items[0]?.id || defaultReport);
            }
            if (mainCategory === 'analytics') {
              const defaultAnalytics = form.analyticsTab || 'overview';
              return canAccessPage(currentUser.role, defaultAnalytics) ? defaultAnalytics : (items[0]?.id || defaultAnalytics);
            }
            return subPage;
          };
          const getOnSelect = () => {
            if (mainCategory === 'reports') return (key) => setForm({...form, reportTab: key});
            if (mainCategory === 'analytics') return (key) => setForm({...form, analyticsTab: key});
            return setSubPage;
          };

          return (
            <BrioPageSubHeader
              items={items}
              activeKey={getActiveKey()}
              onSelect={getOnSelect()}
              variant="global"
              sticky={false}
            />
          );
        })()}
        </>
        )}
      </div>
      )}

      {/* === MOBILE BOTTOM NAV BAR === */}
      {isMobile && !(subPage === 'donut-tracker' && isFullscreen) && (
        <div
          className="fixed bottom-0 left-0 right-0 z-50 bg-white"
          style={{ borderTop: '1px solid #000000', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <div className="flex justify-around items-center" style={{ height: 56 }}>
            {[
              ...mobilePrimaryCategories,
              ...(mobileMoreCategories.length > 0 ? [{ id: '__more', label: 'More', icon: MoreHorizontal }] : []),
            ].map((cat) => {
              const Icon = cat.icon;
              const isActive = cat.id === '__more'
                ? mobileMoreSheetOpen || mobileMoreCategories.some(c => c.id === mainCategory)
                : mainCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => {
                    if (cat.id === '__more') {
                      setMobileMoreSheetOpen(true);
                    } else {
                      selectMobileCategory(cat);
                    }
                  }}
                  className="flex flex-col items-center justify-center flex-1 py-1"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: isActive ? '#000000' : '#999999',
                    minWidth: 0,
                  }}
                  aria-label={cat.label}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <Icon className="w-5 h-5" style={{ strokeWidth: isActive ? 2.5 : 1.5 }} />
                  <span
                    className="mt-0.5 truncate"
                    style={{
                      fontSize: 10,
                      fontWeight: isActive ? 600 : 300,
                      letterSpacing: '0.02em',
                      maxWidth: '100%',
                    }}
                  >
                    {cat.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* === MOBILE MORE SECTIONS BOTTOM SHEET === */}
      {isMobile && mobileMoreSheetOpen && (
        <div className="fixed inset-0 z-[60]" onClick={() => setMobileMoreSheetOpen(false)}>
          <div className="absolute inset-0 bg-black" style={{ opacity: 0.3 }} />
          <div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl"
            style={{
              maxHeight: '70vh',
              overflowY: 'auto',
              paddingBottom: 'env(safe-area-inset-bottom, 16px)',
              animation: 'mobileSheetSlideUp 0.2s ease-out',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 rounded-full" style={{ background: '#ddd' }} />
            </div>
            <div className="px-5 pb-2">
              <p className="text-xs font-medium" style={{ color: '#999', letterSpacing: '0.05em' }}>
                MORE
              </p>
            </div>
            <div className="pb-4">
              {mobileMoreCategories.map((cat) => {
                const Icon = cat.icon;
                const isActive = mainCategory === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => selectMobileCategory(cat)}
                    className="w-full text-left px-5 py-3 flex items-center gap-3"
                    style={{
                      background: isActive ? '#f5f5f5' : 'transparent',
                      border: 'none',
                      borderLeft: isActive ? '3px solid #000' : '3px solid transparent',
                      color: isActive ? '#000000' : '#444444',
                      fontWeight: isActive ? 500 : 300,
                      fontSize: 15,
                    }}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" style={{ strokeWidth: isActive ? 2.5 : 1.5 }} />
                    {cat.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* === MOBILE SUBPAGE BOTTOM SHEET === */}
      {isMobile && mobileSubpageSheetOpen && (
        <div className="fixed inset-0 z-[60]" onClick={() => setMobileSubpageSheetOpen(false)}>
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black" style={{ opacity: 0.3 }} />
          {/* Sheet */}
          <div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl"
            style={{
              maxHeight: '70vh',
              overflowY: 'auto',
              paddingBottom: 'env(safe-area-inset-bottom, 16px)',
              animation: 'mobileSheetSlideUp 0.2s ease-out',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Sheet handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 rounded-full" style={{ background: '#ddd' }} />
            </div>
            {/* Sheet title */}
            <div className="px-5 pb-2">
              <p className="text-xs font-medium" style={{ color: '#999', letterSpacing: '0.05em' }}>
                {getCurrentCategoryLabel().toUpperCase()}
              </p>
            </div>
            {/* Subpage items */}
            <div className="pb-4">
              {(subNavItemsByCategory[mainCategory] || []).map((item) => {
                const activeKey = mainCategory === 'reports' ? (form.reportTab || 'projectProfitability')
                  : mainCategory === 'analytics' ? (form.analyticsTab || 'overview')
                  : subPage;
                const isActive = item.id === activeKey;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      if (mainCategory === 'reports') {
                        setForm({ ...form, reportTab: item.id });
                      } else if (mainCategory === 'analytics') {
                        setForm({ ...form, analyticsTab: item.id });
                      } else {
                        setSubPage(item.id);
                      }
                      setMobileSubpageSheetOpen(false);
                    }}
                    className="w-full text-left px-5 py-3 flex items-center gap-3"
                    style={{
                      background: isActive ? '#f5f5f5' : 'transparent',
                      border: 'none',
                      borderLeft: isActive ? '3px solid #000' : '3px solid transparent',
                      color: isActive ? '#000000' : '#444444',
                      fontWeight: isActive ? 500 : 300,
                      fontSize: 15,
                    }}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div
        className={(subPage === 'donut-tracker' || subPage === 'project-map') ? '' : (isMobile ? 'px-3 py-3' : 'max-w-7xl mx-auto px-6 py-8')}
        style={{
          paddingTop: (subPage === 'donut-tracker' && isFullscreen) ? 0 : navHeight,
          paddingBottom: isMobile ? 72 : undefined,
        }}
      >
        
        {/* DONUT TRACKER VIEW - Default landing page */}
        {view === 'donut-tracker' && (
          <div style={{ backgroundColor: '#F5F5F5', minHeight: '100vh' }}>
          <DonutTimeTracker
            useSupabaseDB={useSupabaseDB}
            useSupabaseAllocations={useSupabaseDB}
            currentUser={currentUser}
            navHeight={navHeight}
            siteVisitBackendHours={settingsHook.siteVisitBackendHours}
            wheelPickerSpeed={settingsHook.wheelPickerSpeed}
            selectedUserId={filters?.userId && filters.userId !== 'all' ? filters.userId : currentUser.id}
            activeTimeEntries={activeTimeEntries}
            activeProjects={activeProjects}
            activeUsers={activeUsers}
            activeTasks={activeTasks}
            allocations={activeAllocations}
            holidays={userHolidays}
            invoices={useSupabaseDB ? supabaseInvoices.invoices : []}
            isProjectBillable={isProjectBillable}
            onCreateTimeEntry={async (entry) => {
              if (useSupabaseDB) {
                return await supabaseTimeEntries.createTimeEntry(entry);
              } else {
                // Local mode handled by setTimeEntries in component
                return { success: true };
              }
            }}
            onUpdateTimeEntry={async (id, updates) => {
              if (useSupabaseDB) {
                // Optimistic UI update: update HOOK state immediately
                supabaseTimeEntries.optimisticUpdate(id, updates);
                
                // Then perform the Supabase update (no refetch in updateTimeEntry)
                const result = await supabaseTimeEntries.updateTimeEntry(id, updates);
                
                if (!result.success) {
                  // If update failed, refetch to restore correct state
                  console.error('[App] Update failed, refetching...', result.error);
                  supabaseTimeEntries.refetch();
                }
                
                return result;
              } else {
                setTimeEntries(prev => prev.map(e => e.id === id ? { ...e, ...updates } : e));
                return { success: true };
              }
            }}
            onDeleteTimeEntry={async (id) => {
              if (useSupabaseDB) {
                // Optimistic UI update: remove from HOOK state immediately (this is what activeTimeEntries reads from)
                supabaseTimeEntries.optimisticDelete(id);
                
                // TEMP debug log - remove after confirming single delete
                console.log('[App] Delete entry optimistic update on hook state', { id });
                
                // Then perform the Supabase delete (no refetch needed - we already updated state)
                const result = await supabaseTimeEntries.deleteTimeEntryNoRefetch(id);
                
                if (!result.success) {
                  // If delete failed, refetch to restore correct state
                  console.error('[App] Delete failed, refetching...', result.error);
                  supabaseTimeEntries.refetch();
                }
                
                return result;
              } else {
                setTimeEntries(prev => prev.filter(e => e.id !== id));
                return { success: true };
              }
            }}
            onRefetch={useSupabaseDB ? supabaseTimeEntries.refetch : undefined}
            setTimeEntries={setTimeEntries}
            timeEntries={timeEntries}
          />
          </div>
        )}
        
        {view === 'tracker' && (
          <TrackerPage
            currentUser={currentUser}
            activeUsers={activeUsers}
            activeClients={activeClients}
            activeProjects={activeProjects}
            activeTasks={activeTasks}
            activeTeams={activeTeams}
            timeEntries={activeTimeEntries}
            selected={selected}
            filters={filters}
            currentPage={currentPage}
            entriesPerPage={entriesPerPage}
            useSupabaseDB={useSupabaseDB}
            supabaseTimeEntries={supabaseTimeEntries}
            setModal={setModal}
            setFilters={setFilters}
            setSelected={setSelected}
            setTimeEntries={setTimeEntries}
            setCurrentPage={setCurrentPage}
            setEditing={setEditing}
            filterEntries={filterEntries}
            getProjectsForDropdown={getProjectsForDropdown}
            exportCSV={exportCSV}
            importCSV={importCSV}
            getStorageInfo={getStorageInfo}
            cleanupOldEntries={cleanupOldEntries}
            calcRev={calcRev}
            fmtTime={fmtTime}
            fmtCurr={fmtCurr}
            fmtProject={fmtProject}
          />
        )}
        
        {view === 'calendar' && (
          <CalendarPage
            renderWeeklyView={renderWeeklyView}
            renderMonthlyView={renderMonthlyView}
            calDate={calDate}
            setCalDate={setCalDate}
            calView={calView}
            setCalView={setCalView}
            calendarEvents={calendarEvents}
            setCalendarEvents={setCalendarEvents}
            categoryFilters={categoryFilters}
            setCategoryFilters={setCategoryFilters}
            activeUsers={activeUsers}
            currentUser={currentUser}
            timeOff={activeTimeOff}
            teams={teams}
            holidays={userHolidays}
            msCalendarEvents={msCalendarEvents}
            msCalendarConnected={msCalendarConnected}
            showEventModal={showEventModal}
            setShowEventModal={setShowEventModal}
            showDayEventsModal={showDayEventsModal}
            setShowDayEventsModal={setShowDayEventsModal}
            eventForm={eventForm}
            setEventForm={setEventForm}
            editingEvent={editingEvent}
            setEditingEvent={setEditingEvent}
            getDaysInView={getDaysInView}
          />
        )}

        {/* Reports Dashboard - Admin + Supervisor (supervisors see Project Profitability only) */}
        {view === 'dashboard' && (isAdmin || currentUser?.role === 'supervisor') && (
          <div className="brio-analytics-font">

            {/* Reports Content - Constrained to 875px centered */}
            <BrioFrame>
              <div className="space-y-6" style={{ paddingTop: 'var(--brio-space-m)', paddingBottom: 'var(--brio-space-m)' }}>
            
            {/* Profitability Analysis Report */}
            {(form.reportTab || 'projectProfitability') === 'profitability' && canAccessPage(currentUser.role, 'profitability') && (
              <>
            {/* Permission Denied - Show graceful message for profitability report */}
            {profitViewError && isViewPermissionDenied(profitViewError) && (
              <BrioAnalyticsSurface padding="m" className="text-center">
                <div className="brio-analytics-value" style={{ color: 'var(--brio-orange)', marginBottom: '8px' }}>⚠️</div>
                <p className="brio-analytics-body">You don't have access to Reports with this account.</p>
                <p className="brio-analytics-muted" style={{ marginTop: '8px' }}>Contact your administrator for access.</p>
              </BrioAnalyticsSurface>
            )}
            
            {/* Loading State for profitability report */}
            {profitViewLoading && !profitViewError && (
              <BrioAnalyticsSurface padding="m" className="text-center">
                <div style={{ color: 'var(--brio-gold)', marginBottom: '8px' }}>
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
                </div>
                <p className="brio-analytics-body">Loading profitability data...</p>
              </BrioAnalyticsSurface>
            )}
            
            {/* Variance Analysis - Admin Only */}
            {(!profitViewError || !isViewPermissionDenied(profitViewError)) && isAdmin && (
            <>
                      <p className="brio-analytics-muted brio-mt-xs">Compare planned vs actual hours and costs to identify project budget variances</p>

                      {/* Project Profitability Breakdown */}
                      {(() => {
                        const code = "4a";
                        const tableProps = getTablePropsFromCode(code);
                        return (
                      <BrioAnalyticsSurface padding="m">
                        <BrioByCode code={code}>
                          <BrioTable {...tableProps}>
                            <thead>
                              <tr>
                                <th>Project</th>
                                <th className="text-right">Labour Cost</th>
                                <th className="text-right">Expenses</th>
                                <th className="text-right">Cost Variance</th>
                                <th className="text-right">Profit</th>
                                <th className="text-right">Margin</th>
                              </tr>
                            </thead>
                            <tbody>
                          {getSortedProjects().map(project => {
                            const realisedProfit = getProjectRealisedProfit(project.id);
                            const costVariance = getProjectCostVariance(project.id);
                            const labourCost = getProjectActualLabourCost(project.id).labourCostTotal;
                            const expenses = getProjectExpenses(project.id);
                            const client = activeClients.find(c => c.id === project.clientId);

                            return (
                              <tr key={project.id}>
                                <td>
                                  <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: project.color }}/>
                                    <div>
                                      <div className="brio-analytics-body" style={{ fontWeight: 400 }}>{project.projectCode || project.name}</div>
                                      <div className="brio-analytics-muted" style={{ fontStyle: 'italic' }}>
                                        {client?.organisation || client?.name} • Paid: {fmtCurr(paidIncomeByProject[project.id] || 0)}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="text-right">
                                  <div className="brio-analytics-body">{fmtCurr(labourCost)}</div>
                                </td>
                                <td className="text-right">
                                  <div className="brio-analytics-body">{fmtCurr(expenses)}</div>
                                </td>
                                <td className="text-right">
                                  <div className="brio-analytics-body" style={{ color: costVariance.variance > 0 ? 'var(--brio-red)' : 'var(--brio-gold)' }}>
                                    {costVariance.variance >= 0 ? '+' : ''}{fmtCurr(costVariance.variance)}
                                  </div>
                                  {Math.abs(costVariance.variance) > 0 && (
                                    <div className="brio-analytics-muted" style={{ color: costVariance.variance > 0 ? 'var(--brio-red)' : 'var(--brio-green)' }}>
                                      {costVariance.variance > 0 ? '⚠️ Over budget' : '✓ Under budget'}
                                    </div>
                                  )}
                                </td>
                                <td className="text-right">
                                  <div className="brio-analytics-body" style={{ fontWeight: 400, color: realisedProfit >= 0 ? 'var(--brio-gold)' : 'var(--brio-red)' }}>
                                    {fmtCurr(realisedProfit)}
                                  </div>
                                </td>
                                <td className="text-right">
                                  <div className="brio-analytics-body">
                                    {(() => {
                                      const totalFee = paidIncomeByProject[project.id] || 0;
                                      return totalFee > 0 ? ((realisedProfit / totalFee) * 100).toFixed(1) : 0;
                                    })()}%
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                            </tbody>
                          </BrioTable>
                        </BrioByCode>
                      </BrioAnalyticsSurface>
                        );
                      })()}

                      {/* Salary Variance & Alerts */}
                      <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-xl font-bold mb-4" style={{ fontSize: '15px', fontWeight: 400, fontFamily: 'Raleway, sans-serif' }}>
                          ⏱️ Salary Variance & Capacity Signals
                        </h3>
                        <p className="text-sm mb-4" style={{ fontSize: '13px', fontWeight: 200, fontStyle: 'italic', color: '#666666' }}>
                          Monitoring overtime patterns (10% tolerance, 3-month persistence required)
                        </p>

                        <div className="space-y-2">
                          {activeUsers.map(user => {
                            const currentMonth = new Date().toISOString().substring(0, 7);
                            const alert = getSalaryVarianceAlerts(user.id, currentMonth);
                            const variance = calculateSalaryVariance(user.id, currentMonth);
                            const withTolerance = applyToleranceBand(variance);

                            if (!alert && withTolerance && withTolerance.withinTolerance) return null;

                            return (
                              <div
                                key={user.id}
                                className={`border rounded-lg p-3 ${
                                  alert?.level === 'red' ? 'bg-red-50 border-red-200' :
                                  alert?.level === 'orange' ? 'bg-orange-50 border-orange-200' :
                                  alert?.level === 'yellow' ? 'bg-yellow-50 border-yellow-200' :
                                  'border-slate-200'
                                }`}
                                style={{ fontFamily: 'Raleway, sans-serif' }}
                              >
                                <div className="flex justify-between items-start">
                                  <div>
                                    <div className="font-medium" style={{ fontSize: '13px', fontWeight: 400 }}>
                                      {alert?.level === 'red' && '🔴 '}
                                      {alert?.level === 'orange' && '🟠 '}
                                      {alert?.level === 'yellow' && '🟡 '}
                                      {user.name}
                                    </div>
                                    <div className="text-xs" style={{ fontSize: '13px', fontWeight: 200, fontStyle: 'italic', color: '#666666' }}>
                                      {user.jobRole} • {variance?.recordedHours.toFixed(1)}h recorded vs {variance?.contractedHours || 176}h contracted
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <div className="font-bold" style={{ fontSize: '13px', fontWeight: 400, color: '#FF6B6B' }}>
                                      +{withTolerance ? fmtCurr(withTolerance.excessVariance) : fmtCurr(0)}
                                    </div>
                                    <div className="text-xs" style={{ fontSize: '13px', fontWeight: 300, color: '#666666' }}>
                                      excess variance
                                    </div>
                                  </div>
                                </div>
                                {alert && (
                                  <div className="mt-2 text-xs" style={{ fontSize: '13px', fontWeight: 300 }}>
                                    {alert.message}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
            </>
            )}
            </>
            )}
            
            {/* Salary Cost Report — uses SalaryCostPage component */}
            {form.reportTab === 'salaryCost' && isAdmin && (
              <SalaryCostPage
                activeUsers={activeUsers}
                activeTeams={activeTeams}
                brioColors={brioColors}
                getInternalCostRate={getInternalCostRate}
                getClientBillingRate={getClientBillingRate}
                getEmployerBurdenShare={getEmployerBurdenShare}
                fmtCurr={fmtCurr}
                currSymbol={currSymbol}
                isAdmin={isAdmin}
              />
            )}
            
            {/* Project Profitability Report */}
            {(form.reportTab || 'projectProfitability') === 'projectProfitability' && (isAdmin || currentUser?.role === 'supervisor') && canAccessPage(currentUser.role, 'projectProfitability') && (
              <ProjectProfitabilityPage
                activeProjects={filteredProjects}
                activeClients={activeClients}
                activeUsers={activeUsers}
                activeTimeEntries={activeTimeEntries}
                allTimeEntriesLoaded={supabaseTimeEntries.allLoaded}
                timeEntriesLoadProgress={supabaseTimeEntries.loadAllProgress}
                fetchAllTimeEntries={supabaseTimeEntries.fetchAllTimeEntries}
                activeExpenses={activeExpenses}
                activeOfficeOverheads={activeOfficeOverheads}
                monthlyOfficeOverheads={supabaseOfficeOverheadsMonthly.overheads}
                monthlyOfficeOverheadsLoading={supabaseOfficeOverheadsMonthly.loading}
                calcMonthlyCost={calcMonthlyTrueCostPM}
                isEmployerBurdenCategory={isEmployerBurdenCategory}
                fullLedgerLoaded={fullLedgerLoaded}
                fullSalaryLedger={fullSalaryLedger}
                getUserProfileAtDate={getUserProfileAtDate}
                getEffectiveContractedHours={getEffectiveContractedHoursPM}
                computeOverheadAllocation={computeOverheadAllocation}
                fmtCurr={fmtCurr}
                currSymbol={currSymbol}
                settingsHook={settingsHook}
                isAdmin={isAdmin}
                getSupervisedProjects={getSupervisedProjects}
                projectStatusFilter={projectStatusFilter}
                setProjectStatusFilter={setProjectStatusFilter}
                refreshProjectLoadedCosts={refreshProjectLoadedCosts}
              />
            )}

            {/* VOF hours statement (intercompany ABVOF to SBPL) */}
            {form.reportTab === 'vofStatement' && isAdmin && canAccessPage(currentUser.role, 'vofStatement') && (
              <VofStatementPage currentUser={currentUser} />
            )}

            {/* Project Cost Ledger Report */}
            {form.reportTab === 'projectCostLedger' && isAdmin && canAccessPage(currentUser.role, 'projectCostLedger') && (
              <ProjectCostLedgerPage
                activeProjects={activeProjects}
                activeClients={activeClients}
                activeUsers={activeUsers}
                activeTimeEntries={activeTimeEntries}
                activeExpenses={activeExpenses}
                activeOfficeOverheads={activeOfficeOverheads}
                monthlyOfficeOverheads={supabaseOfficeOverheadsMonthly.overheads}
                monthlyOfficeOverheadsLoading={supabaseOfficeOverheadsMonthly.loading}
                calcMonthlyCost={calcMonthlyTrueCostPM}
                isEmployerBurdenCategory={isEmployerBurdenCategory}
                getUserProfileAtDate={getUserProfileAtDate}
                getEffectiveContractedHours={getEffectiveContractedHoursPM}
                computeOverheadAllocation={computeOverheadAllocation}
                fmtCurr={fmtCurr}
                currSymbol={currSymbol}
                settingsHook={settingsHook}
                isAdmin={isAdmin}
                projectStatusFilter={projectStatusFilter}
                setProjectStatusFilter={setProjectStatusFilter}
              />
            )}
              </div>
            </BrioFrame>
          </div>
        )}
        
        {view === 'team' && isAdmin && (
          <div className="space-y-6">
            {/* Teams Section */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold">Teams</h2>
                  <p className="text-sm text-slate-600 mt-1">Manage teams, colors, and supervisors (single source of truth for resource allocation)</p>
                </div>
                <button onClick={() => setModal('addTeam')} className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2">
                  <Plus className="w-4 h-4"/>Add Team
                </button>
              </div>

              <div className="space-y-3">
                {activeTeams.map(team => {
                  const teamMembers = activeUsers.filter(u => u.teamId === team.id);
                  const supervisors = activeUsers.filter(u => normalizedIncludes(team.supervisorIds, u.id));
                  
                  return (
                    <div key={team.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div 
                              className="w-6 h-6 rounded-full border-4" 
                              style={{ 
                                borderColor: team.color,
                                borderStyle: 'solid'
                              }}
                              title="Team Color (used in resource allocation)"
                            />
                            <div className="font-bold text-lg">{team.name}</div>
                            <span className="px-3 py-1 rounded text-sm font-medium bg-slate-100 text-slate-700">
                              {teamMembers.length} {teamMembers.length === 1 ? 'member' : 'members'}
                            </span>
                          </div>
                          
                          {supervisors.length > 0 && (
                            <div className="text-sm text-slate-600 mb-2">
                              <strong>Supervisors:</strong> {supervisors.map(s => s.name).join(', ')}
                            </div>
                          )}
                          
                          <div className="text-sm text-slate-600">
                            <strong>Members:</strong> {teamMembers.length > 0 ? teamMembers.map(m => m.name).join(', ') : 'No members assigned'}
                          </div>
                        </div>
                        
                        <button 
                          onClick={() => {
                            // Get current project IDs linked to this team
                            const teamProjectIds = supabaseTeamProjects.getProjectIdsForTeam(team.id);
                            setForm({
                              editTeamId: team.id,
                              teamName: team.name,
                              teamColor: team.color || team.outlineColor || '#3b82f6',
                              teamSupervisors: team.supervisorIds || [],
                              teamProjects: teamProjectIds || []
                            });
                            setModal('editTeam');
                          }}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 p-2 rounded"
                        >
                          <Edit2 className="w-4 h-4"/>
                        </button>
                      </div>
                      
                      {/* Team Member Breakdown by Role */}
                      <div className="mt-3 pt-3 border-t">
                        <div className="text-xs font-semibold text-slate-600 mb-2">Role Breakdown:</div>
                        <div className="flex flex-wrap gap-2">
                          {jobRoles.map(role => {
                            const count = teamMembers.filter(m => m.jobRole === role).length;
                            if (count === 0) return null;
                            return (
                              <div 
                                key={role} 
                                className="px-2 py-1 rounded text-xs font-medium"
                                style={{ 
                                  backgroundColor: roleColors[role] + '33',
                                  color: roleColors[role],
                                  border: `1px solid ${roleColors[role]}`
                                }}
                              >
                                {count} {role}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Team Members Section */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold">Team Members</h2>
                  <p className="text-sm text-slate-600 mt-1">Manage team members, roles, and project assignments</p>
                </div>
                <button onClick={() => setModal('addUser')} className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2">
                  <Plus className="w-4 h-4"/>Add Team Member
                </button>
              </div>
              
              {/* Employment Status Filter */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Filter by Employment Status:</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setFilters({...filters, employmentStatus: 'all'})}
                    className={`px-4 py-2 rounded-lg text-sm transition-all ${
                      (filters.employmentStatus || 'all') === 'all'
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    All Employees ({activeUsers.length})
                  </button>
                  <button
                    onClick={() => setFilters({...filters, employmentStatus: 'active'})}
                    className={`px-4 py-2 rounded-lg text-sm transition-all ${
                      filters.employmentStatus === 'active'
                        ? 'bg-green-600 text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    ✓ Active Only ({activeUsers.filter(u => u.isActive !== false).length})
                  </button>
                  <button
                    onClick={() => setFilters({...filters, employmentStatus: 'inactive'})}
                    className={`px-4 py-2 rounded-lg text-sm transition-all ${
                      filters.employmentStatus === 'inactive'
                        ? 'bg-slate-600 text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    ✗ Inactive Only ({activeUsers.filter(u => u.isActive === false).length})
                  </button>
                </div>
              </div>
              
              <div className="space-y-3">
                {activeUsers
                  .filter(u => {
                    // Apply employment status filter
                    const status = filters.employmentStatus || 'all';
                    if (status === 'active') return u.isActive !== false;
                    if (status === 'inactive') return u.isActive === false;
                    return true; // 'all'
                  })
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(u => {
                  // Project ownership is defined via team_projects; supervisedProjects derived from team_projects
                  const supervisedProjects = u.role === 'supervisor' 
                    ? activeProjects.filter(p => {
                        const userSupervisedTeamIds = activeTeams
                          .filter(t => normalizedIncludes(t.supervisorIds, u.id))
                          .map(t => t.id);
                        const userSupervisedProjectIds = [];
                        userSupervisedTeamIds.forEach(teamId => {
                          const pids = supabaseTeamProjects.getProjectIdsForTeam(teamId);
                          userSupervisedProjectIds.push(...pids);
                        });
                        return normalizedIncludes(userSupervisedProjectIds, p.id);
                      })
                    : [];
                  const userTeam = activeTeams.find(t => t.id === u.teamId);
                  
                  return (
                  <div key={u.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          {/* Visual Marker: Circle with role color fill and team color solid outline */}
                          {u.jobRole && userTeam && (
                            <div 
                              className="w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-bold border-4"
                              style={{ 
                                backgroundColor: roleColors[u.jobRole],
                                borderColor: userTeam.color,
                                borderStyle: 'solid'
                              }}
                              title={`${u.jobRole} - ${userTeam.name}`}
                            >
                              {u.name.split(' ').map(n => n[0]).join('')}
                            </div>
                          )}
                          <div>
                            <div className="font-bold text-lg">{u.name}</div>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                (u.role==='admin'||u.role==='admin-pa')?'bg-purple-100 text-purple-700':
                                u.role==='supervisor'?'bg-blue-100 text-blue-700':
                                'bg-slate-100 text-slate-700'
                              }`}>
                                {u.role}
                              </span>
                              {u.jobRole && (
                                <span 
                                  className="px-2 py-0.5 rounded text-xs font-medium text-white"
                                  style={{ backgroundColor: roleColors[u.jobRole] }}
                                >
                                  {u.jobRole} - L{u.jobLevel}
                                </span>
                              )}
                              {userTeam && (
                                <span 
                                  className="px-2 py-0.5 rounded text-xs font-medium border-2"
                                  style={{ 
                                    borderColor: userTeam.color,
                                    borderStyle: 'solid',
                                    color: userTeam.color
                                  }}
                                >
                                  {userTeam.name}
                                </span>
                              )}
                              {u.isActive === false && (
                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-200 text-slate-600">
                                  ✗ Inactive
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-sm text-slate-600 mb-1">{u.email}</div>
                        {u.role === 'supervisor' && supervisedProjects.length > 0 && (
                          <div className="mt-2 text-xs text-blue-600 bg-blue-50 inline-block px-2 py-1 rounded">
                            Supervises: {supervisedProjects.map(p => p.projectCode || '').filter(Boolean).join(', ')}
                          </div>
                        )}
                      </div>
                      <button 
                        type="button"
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const currentYear = new Date().getFullYear();
                          const yearBonuses = u.bonuses?.[currentYear] || { q1: 0, q2: 0, q3: 0, q4: 0 };
                          // Fetch profile_intro from user_profiles table
                          let profileIntro = '';
                          try {
                            const { data: profileRow } = await supabase
                              .from('user_profiles').select('profile_intro').eq('user_id', u.id).maybeSingle();
                            if (profileRow) profileIntro = profileRow.profile_intro || '';
                          } catch (err) { console.warn('[EditUser] Could not fetch profile_intro:', err); }
                          setForm({
                            editUserId: u.id,
                            userName: u.name,
                            userEmail: u.email,
                            userRole: u.role,
                            jobRole: u.jobRole,
                            jobLevel: u.jobLevel,
                            costRate: u.costRate,
                            markup: u.markup,
                            contractedHours: u.contractedHours || 176,
                            teamId: u.teamId || null,
                            joinDate: u.joinDate || '',
                            isActive: u.isActive !== false, // Default to true
                            officeLocation: u.officeLocation || 'NL',
                            bonusYear: currentYear,
                            bonusQ1: yearBonuses.q1 || 0,
                            bonusQ2: yearBonuses.q2 || 0,
                            bonusQ3: yearBonuses.q3 || 0,
                            bonusQ4: yearBonuses.q4 || 0,
                            profileIntro,
                          });
                          setModal('editUser');
                        }} 
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 p-2 rounded cursor-pointer"
                        title="Edit user"
                      >
                        <Edit2 className="w-4 h-4"/>
                      </button>
                    </div>
                    
                    {(() => {
                      const currentYear = new Date().getFullYear();
                      const internalCost = getInternalCostRate(u.id, currentYear);
                      
                      return (
                        <div className="grid grid-cols-3 gap-4 bg-gradient-to-br from-slate-50 to-slate-100 p-3 rounded-lg">
                          <div>
                            <div className="text-xs text-slate-600 mb-1">Monthly Salary</div>
                            <div className="font-bold text-slate-800">{fmtCurr(u.costRate)}</div>
                          </div>
                          <div>
                            <div className="text-xs text-slate-600 mb-1">Internal Cost Rate</div>
                            <div className="font-bold text-blue-600">
                              {fmtCurr(internalCost.hourlyInternalCost && !isNaN(internalCost.hourlyInternalCost) ? Math.round(internalCost.hourlyInternalCost) : 0)}/h
                            </div>
                            <div className="text-xs text-slate-500 italic mt-0.5">
                              {internalCost.totalAnnualBonus > 0 ? (
                                <>
                                  ({fmtCurr(u.costRate)} + {fmtCurr(Math.round(internalCost.monthlyBonusEquivalent))}) × {internalCost.salaryMarkupFactor.toFixed(3)} ÷ 176h
                                </>
                              ) : (
                                <>
                                  {fmtCurr(u.costRate)} × {internalCost.salaryMarkupFactor.toFixed(3)} ÷ 176h
                                </>
                              )}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-slate-600 mb-1">Contracted Hours</div>
                            <div className="font-bold text-slate-800">{internalCost.contractedHours}h/month</div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold">Salary Matrix</h2>
                  <p className="text-sm text-slate-600 mt-1">Monthly salary by job role and level (0-20). Markup is calculated office-wide in Accounting.</p>
                </div>
                {!isEditingMatrix ? (
                  <button 
                    onClick={() => {
                      setIsEditingMatrix(true);
                      setTempSalaryMatrix(JSON.parse(JSON.stringify(salaryMatrix))); // Deep copy
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
                  >
                    <Edit2 className="w-4 h-4"/>
                    Edit Matrix
                  </button>
                ) : (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <label className="block text-xs font-medium mb-1 text-slate-700">Effective Date</label>
                        <BrioDatePicker
                          value={form.matrixEffectiveDate || new Date().toISOString().split('T')[0]}
                          onChange={(v) => setForm({...form, matrixEffectiveDate: v})}
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs font-medium mb-1 text-slate-700">Change Reason (optional)</label>
                        <input 
                          type="text" 
                          value={form.matrixChangeReason || ''} 
                          onChange={(e) => setForm({...form, matrixChangeReason: e.target.value})}
                          placeholder="e.g., Annual revision"
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        />
                      </div>
                    </div>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-xs text-blue-800">
                        💡 New matrix version will be created. Calculations from this date onwards will use the new matrix.
                      </p>
                    </div>
                    
                    <div className="flex gap-2">
                      <button 
                        onClick={async () => {
                          const effectiveDate = form.matrixEffectiveDate || new Date().toISOString().split('T')[0];
                          
                          // Create new matrix history entry
                          const newMatrixRevision = {
                            id: Date.now(),
                            effectiveDate: effectiveDate,
                            changedBy: currentUser.email,
                            changeReason: form.matrixChangeReason || 'Matrix update',
                            matrix: JSON.parse(JSON.stringify(tempSalaryMatrix))
                          };
                          
                          if (useSupabaseDB) {
                            // Supabase mode: write to database
                            const result = await supabaseSalaryMatrix.createSalaryMatrixRevision(newMatrixRevision);
                            if (result.success) {
                              // Update local state from Supabase results
                              setSalaryMatrixHistory(result.history);
                              setSalaryMatrix(tempSalaryMatrix);
                              console.log('✅ Salary matrix saved to Supabase');
                            } else {
                              alert(`Error saving salary matrix: ${result.error}`);
                              return;
                            }
                          } else {
                            // LocalStorage mode: update local state only
                            setSalaryMatrixHistory([...salaryMatrixHistory, newMatrixRevision]);
                            setSalaryMatrix(tempSalaryMatrix);
                          }
                          
                          // Reset editing state
                          setIsEditingMatrix(false);
                          setTempSalaryMatrix(null);
                          setForm({});
                          
                          alert(`✓ Salary matrix updated!\nEffective from: ${effectiveDate}\nCost calculations will use the new matrix from this date onwards.`);
                        }}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700"
                      >
                        ✓ Update Matrix
                      </button>
                      <button 
                        onClick={() => {
                          setIsEditingMatrix(false);
                          setTempSalaryMatrix(null);
                          setForm({});
                        }}
                        className="bg-slate-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-slate-700"
                      >
                        <X className="w-4 h-4"/>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {isEditingMatrix && (
                <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800 font-medium">
                    ⚠️ <strong>Edit Mode Active:</strong> Make your changes, then click "Update Matrix" to save or "Cancel" to discard changes.
                  </p>
                </div>
              )}

              <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  💡 <strong>How it works:</strong> Set monthly salaries for each job role and level. Internal cost rates (which include bonuses and markup) are automatically calculated and shown in the Team Members section.
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-slate-100 border-b-2 border-slate-300">
                      <th className="p-3 text-left font-semibold sticky left-0 bg-slate-100 z-10">Job Role</th>
                      {Array.from({ length: 21 }, (_, i) => (
                        <th key={i} className="p-2 text-center font-semibold whitespace-nowrap">
                          L{i}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {jobRoles.map((jobRole, roleIdx) => (
                      <tr key={jobRole} className={`border-b hover:bg-slate-50 ${roleIdx % 2 === 0 ? 'bg-white' : 'bg-slate-25'}`}>
                        <td className="p-3 font-medium sticky left-0 bg-white z-10 border-r">
                          {jobRole}
                          {['Support', 'Model Maker', 'Visualiser', 'Others'].includes(jobRole) && <span className="text-xs text-slate-500 ml-2">(Non-billable)</span>}
                        </td>
                        {Array.from({ length: 21 }, (_, level) => {
                          const currentMatrix = isEditingMatrix ? tempSalaryMatrix : salaryMatrix;
                          const data = currentMatrix[jobRole]?.[level] || { salary: 0, markup: 0 };
                          const _costRate = Math.round(data.salary / 176);
                          
                          return (
                            <td key={level} className="p-1 border-r whitespace-nowrap">
                              <div className="min-w-[100px]">
                                <input
                                  type="number"
                                  value={data.salary || ''}
                                  onChange={(e) => {
                                    if (isEditingMatrix) {
                                      setTempSalaryMatrix(prev => {
                                        const roleData = prev[jobRole] || Array.from({ length: 21 }, () => ({ salary: 0, markup: 0 }));
                                        const updated = Array.isArray(roleData)
                                          ? roleData.map((item, idx) => idx === level ? { ...item, salary: parseFloat(e.target.value) || 0 } : item)
                                          : Object.fromEntries(
                                              Object.keys(roleData).map(k => [k, parseInt(k) === level ? { ...roleData[k], salary: parseFloat(e.target.value) || 0 } : roleData[k]])
                                            );
                                        return { ...prev, [jobRole]: updated };
                                      });
                                    }
                                  }}
                                  className={`w-full px-3 py-1.5 text-sm border rounded ${isEditingMatrix ? 'bg-white' : 'bg-slate-50 cursor-not-allowed'}`}
                                  placeholder="0"
                                  title={`Monthly Salary (${currSymbol})`}
                                  disabled={!isEditingMatrix}
                                  style={{ minWidth: '110px' }}
                                />
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 text-xs text-slate-600 bg-slate-50 p-3 rounded">
                <p className="font-semibold mb-1">Note:</p>
                <p>• Each cell shows the <strong>Monthly Salary ({currSymbol})</strong> for that job role and level</p>
                <p className="mt-2">• Markup is calculated office-wide in the Accounting page and applies to all employees equally</p>
                <p>• Internal cost rate includes salary + bonuses and is shown in the Team Members section above</p>
              </div>
            </div>
          </div>
        )}

        {/* Team Allocation - Weekly team/project allocation planning */}
        {/* SAFETY NET: HFB needs special container to prevent page-level scrolling */}
        {/* This ensures sticky footer works in normal mode (not just fullscreen) */}
        {/* HFB middle region owns scrolling; page overflow must be hidden */}
        {view === 'hybrid-focus-board' && (isAdmin || currentUser.role === 'supervisor') && (() => {
          // HFB-only "top guard": ensure HFB never renders under the nav on first paint
          // even if navHeight measurement hasn't completed yet
          const HFB_NAV_FALLBACK = 140; // Safe fallback for two-row nav (header + subheader)
          const hfbNavHeight = Math.max(navHeight, HFB_NAV_FALLBACK);
          const hfbExtraTopPad = hfbNavHeight - navHeight; // 0 once measured correctly
          
          return (
            <div 
              style={{ 
                // Fixed height based on viewport minus navigation (with safe fallback)
                height: `calc(100vh - ${hfbNavHeight}px)`,
                overflow: 'hidden',
                minHeight: 0,
                // Extra top padding guards against first-render flash
                paddingTop: hfbExtraTopPad,
                // Compensate for wrapper px-6/py-8 padding (left/right/bottom only)
                // Top margin removed to prevent slipping under subheader
                margin: '0 -24px -32px -24px'
              }}
            >
            <HybridFocusBoard
              currentUser={currentUser}
              users={activeUsers}
              projects={getFilteredProjects()}
              teams={activeTeams}
              onUpdateTeamSupervisors={async (teamId, supervisorIds) => {
                // DEBUG: Instrumentation to prove supervisor persistence flow
                const DEBUG_SUPERVISOR_SYNC = true;
                
                if (DEBUG_SUPERVISOR_SYNC) {
                  console.log('[App->HFB] onUpdateTeamSupervisors called:');
                  console.log('  - teamId:', teamId);
                  console.log('  - supervisorIds (to save):', supervisorIds);
                  console.log('  - useSupabaseDB:', useSupabaseDB);
                }
                
                if (useSupabaseDB) {
                  // Persist to Supabase via useSupabaseTeams hook
                  if (DEBUG_SUPERVISOR_SYNC) {
                    console.log('[App->HFB] Calling supabaseTeams.updateTeam()...');
                  }
                  
                  const result = await supabaseTeams.updateTeam(teamId, { supervisorIds });
                  
                  if (DEBUG_SUPERVISOR_SYNC) {
                    console.log('[App->HFB] supabaseTeams.updateTeam result:', result);
                  }
                  
                  if (!result.success) {
                    console.error('[App->HFB] Failed to update supervisors:', result.error);
                    return { success: false, error: result.error };
                  }
                  
                  // Refetch to ensure app state is in sync
                  if (DEBUG_SUPERVISOR_SYNC) {
                    console.log('[App->HFB] Refetching teams...');
                  }
                  
                  await supabaseTeams.refetch();
                  
                  if (DEBUG_SUPERVISOR_SYNC) {
                    // Verify the update persisted
                    const updatedTeam = supabaseTeams.teams.find(t => String(t.id) === String(teamId));
                    console.log('[App->HFB] AFTER refetch, team.supervisorIds:', updatedTeam?.supervisorIds);
                    console.log('[App->HFB] activeTeams will update via useSupabaseDB check');
                  }
                  
                  return { success: true };
                } else {
                  // Local mode: update local teams state
                  if (DEBUG_SUPERVISOR_SYNC) {
                    console.log('[App->HFB] Local mode - updating local teams state');
                  }
                  
                  setTeams(teams.map(t => 
                    String(t.id) === String(teamId) 
                      ? { ...t, supervisorIds } 
                      : t
                  ));
                  return { success: true };
                }
              }}
            />
            </div>
          );
        })()}

        {/* Project Tasks - Weekly to-do lists per project */}
        {view === 'project-tasks' && (
          <ProjectTasksPage
            currentUser={currentUser}
            projects={getFilteredProjects()}
            users={activeUsers}
            priorityOptions={settingsHook.priorityOptions}
            taskCategories={settingsHook.taskCategories}
          />
        )}

        {/* ACCOUNTING PAGE - Project & Office Profitability System */}
        {view === 'accounting' && isAdmin && (
          <div className="space-y-6" style={{ fontFamily: 'Raleway, sans-serif' }}>
            
            {/* Header */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold mb-2" style={{ fontSize: '15px', fontWeight: 400 }}>
                Accounting & Financial Inputs
              </h2>
              <p className="text-sm" style={{ fontSize: '13px', fontWeight: 200, fontStyle: 'italic', color: '#666666' }}>
                Project expenses, office overheads, and employee contracted hours
              </p>
            </div>

          </div>
        )}



        {/* ============================================================================ */}
        {/* ANALYTICS VIEW */}
        {/* ============================================================================ */}
        {view === 'analytics' && isAdmin && (
          <div className="max-w-[1800px] mx-auto">
            {/* Permission Denied - Show graceful message */}
            {analyticsViewError && isViewPermissionDenied(analyticsViewError) && (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <div className="text-amber-600 text-lg mb-2">⚠️</div>
                <p className="text-slate-700">You don't have access to Analytics with this account.</p>
                <p className="text-sm text-slate-500 mt-2">Contact your administrator for access.</p>
              </div>
            )}
            
            {/* Loading State */}
            {analyticsViewLoading && !analyticsViewError && (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <div className="text-blue-600 mb-2">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
                </div>
                <p className="text-slate-600">Loading analytics data...</p>
              </div>
            )}
            
            {/* Normal Render - Only when no permission error */}
            {!analyticsViewError || !isViewPermissionDenied(analyticsViewError) ? (
              <AnalyticsDashboard
                timeEntries={(() => {
                  // Apply project filter with robust String comparison
                  let entries = [...activeTimeEntries];
                  if (filters.projectId !== 'all') {
                    entries = entries.filter(e => String(e.projectId) === String(filters.projectId));
                  }
                  return entries.map(e => ({
                    ...e,
                    revenue: calcRev(e),
                    cost: calcLedgerCost(e)
                  }));
                })()}
                projects={activeProjects.map(p => ({
                  ...p,
                  area: parseFloat(p.projectSize) || p.area || 0,
                  category: p.category || 'commercial',
                  location: p.location || 'IN',
                  teamLead: p.teamLead || 'Unassigned',
                  stage: p.stage || 'design',
                  consultantCost: p.consultantCost || 0,
                  travelCost: p.travelCost || 0,
                  presentationCost: p.presentationCost || 0
                }))}
                users={activeUsers}
                clients={activeClients}
                invoices={invoices}
                allProjectInvoices={useSupabaseDB ? supabaseInvoices.allInvoices : []}
                invoicesHook={useSupabaseDB ? supabaseInvoices : null}
                allocations={activeAllocations.map(a => ({
                  ...a,
                  percentage: a.allocatedHours ? (a.allocatedHours / 176) * 100 : 0
                }))}
                activeExpenses={activeExpenses}
                activeOfficeOverheads={activeOfficeOverheads}
                // Pass view data for future use (when analytics component supports it)
                viewRows={analyticsViewRows}
                viewLoading={analyticsViewLoading}
                viewError={analyticsViewError}
                stageStatusHook={useSupabaseDB ? supabaseStageStatus : null}
                settingsHook={settingsHook}
                essentialProfitPercentage={settingsHook.essentialProfitPercentage}
                computeOverheadAllocation={computeOverheadAllocation}
                fmtCurr={fmtCurr}
                currSymbol={currSymbol}
                projectStatusFilter={projectStatusFilter}
                allTimeEntriesLoaded={supabaseTimeEntries.allLoaded}
                activeSection={form.analyticsTab || 'overview'}
                onSectionChange={(key) => setForm({...form, analyticsTab: key})}
              />
            ) : null}
          </div>
        )}
        {view === 'project-map' && (
          <ProjectMapPage
            useSupabaseDB={useSupabaseDB}
            activeProjects={getFilteredProjects()}
            navHeight={navHeight}
            /* Map layers follow the same RBAC as their own pages: inquiries
               are admin-only, team home towns are admin + supervisor. */
            canViewInquiries={canAccessPage(currentUser.role, 'inquiries')}
            canViewTeam={canAccessPage(currentUser.role, 'team-members')}
          />
        )}
        {/* ACCOUNTING SETTINGS PAGE — admin by default; supervisors / users
            with an active 'settings' temp-access grant also reach it via
            canAccessPage. */}
        {view === 'settings' && canAccessPage(currentUser.role, 'settings') && (
          <AccountingSettingsPage settingsHook={settingsHook} activeOfficeOverheads={activeOfficeOverheads} radarSettingsHook={radarSettingsHook} scopeTemplatesHook={scopeTemplatesHook} />
        )}
        {/* STYLE GUIDE PAGE (admin only, temporary) */}
        {view === 'style-guide' && isAdmin && (
          <StyleGuidePage />
        )}

        {/* ============================================================================ */}
        {/* RADAR VIEW — Operational command center */}
        {/* ============================================================================ */}
        {view === 'radar' && canAccessPage(currentUser.role, 'radar') && (
          <RadarDashboardPage
            projects={activeProjects}
            clients={activeClients}
            users={activeUsers}
            invoices={useSupabaseDB ? supabaseInvoices.allInvoices : []}
            timeEntries={activeTimeEntries}
            userLastEntryDates={supabaseTimeEntries.userLastEntryDates}
            userCurrentMonthHours={supabaseTimeEntries.userCurrentMonthHours}
            timeOff={activeTimeOff}
            onRefreshRadarData={() => Promise.all([
              supabaseTimeEntries.fetchUserLastEntryDates(),
              supabaseTimeEntries.fetchUserMonthHours(),
            ])}
            inquiries={useSupabaseDB ? supabaseInquiries.inquiries : []}
            candidates={hiringHook.candidates || []}
            interviews={hiringHook.interviews || []}
            radarSettingsHook={radarSettingsHook}
            invoicesHook={supabaseInvoices}
            currentUser={currentUser}
            isAdmin={isAdmin}
            fmtCurr={fmtCurr}
            customEmailTemplates={settingsHook.emailTemplates}
            onNavigate={(target, params) => {
              // Navigate to the target page with optional params
              const navMap = {
                'invoices': { mainCategory: 'projects', subPage: 'invoices' },
                'manage-projects': { mainCategory: 'projects', subPage: 'manage-projects' },
                'clients': { mainCategory: 'projects', subPage: 'clients' },
                'inquiries': { mainCategory: 'projects', subPage: 'inquiries' },
                'timeline': { mainCategory: 'projects', subPage: 'tracking' },
                'team-members': { mainCategory: 'team', subPage: 'team-members' },
                'hiring': { mainCategory: 'team', subPage: 'hiring' },
                // Press folder drift. CommsPage holds its open project in
                // component state rather than in the route, so this lands on
                // the projects list — where the drift banner names the affected
                // projects and is one click from the press kit itself.
                'comms-projects': { mainCategory: 'comms', subPage: 'comms-projects' },
              };
              const nav = navMap[target];
              if (nav) {
                if (target === 'timeline' && params) {
                  if (params.projectId) setSelectedTimelineProject(params.projectId);
                  if (params.openInvoicePipeline) setPendingTimelineAction(params);
                }
                if (target === 'hiring' && params) {
                  setPendingHiringAction(params);
                }
                setMainCategory(nav.mainCategory);
                setSubPage(nav.subPage);
              }
            }}
          />
        )}

        {/* ============================================================================ */}
        {/* COMMS & OUTREACH VIEW */}
        {/* ============================================================================ */}
        {mainCategory === 'comms' && canAccessPage(currentUser.role, 'comms') && (
          <CommsPage
            currentUser={currentUser}
            isAdmin={isAdmin}
            subPage={subPage || 'comms-practice'}
            setSubPage={setSubPage}
            openForInquiry={pitchDeckInquiry}
            onOpenVendorRegister={canAccessPage(currentUser.role, 'contacts-directory')
              ? () => { setMainCategory('projects'); setSubPage('contacts-directory'); }
              : undefined}
          />
        )}

        {view === 'timeline' && canAccessPage(currentUser.role, 'timeline') && (
          <TimelinePage
            currentUser={currentUser}
            activeClients={activeClients}
            activeProjects={activeProjects}
            activeTasks={activeTasks}
            projects={projects}
            selectedTimelineProject={selectedTimelineProject}
            getSupervisedProjects={getSupervisedProjects}
            getSortedProjects={getSortedProjects}
            fmtProject={fmtProject}
            fmtCurr={fmtCurr}
            useSupabaseDB={useSupabaseDB}
            supabaseProjects={supabaseProjects}
            stageStatusHook={useSupabaseDB ? supabaseStageStatus : null}
            stageEntriesHook={useSupabaseDB ? supabaseStageEntries : null}
            invoicesHook={useSupabaseDB ? supabaseInvoices : null}
            feeRevisionsHook={useSupabaseDB ? supabaseFeeRevisions : null}
            settingsHook={settingsHook}
            setSelectedTimelineProject={setSelectedTimelineProject}
            setProjects={setProjects}
            customEmailTemplates={settingsHook.emailTemplates}
            pendingTimelineAction={pendingTimelineAction}
            clearPendingTimelineAction={() => setPendingTimelineAction(null)}
          />
        )}

        {/* ==================== INVOICES VIEW ==================== */}
        {view === 'invoices' && canAccessPage(currentUser.role, 'invoices') && (
          <InvoicesPage
            currentUser={currentUser}
            activeClients={activeClients}
            activeProjects={activeProjects}
            activeTasks={activeTasks}
            projects={projects}
            selectedTimelineProject={selectedTimelineProject}
            getSupervisedProjects={getSupervisedProjects}
            getSortedProjects={getSortedProjects}
            fmtProject={fmtProject}
            fmtCurr={fmtCurr}
            useSupabaseDB={useSupabaseDB}
            supabaseProjects={supabaseProjects}
            invoicesHook={useSupabaseDB ? supabaseInvoices : null}
            feeRevisionsHook={useSupabaseDB ? supabaseFeeRevisions : null}
            settingsHook={settingsHook}
            setProjects={setProjects}
            setSelectedTimelineProject={setSelectedTimelineProject}
          />
        )}
        
        {/* ==================== MS CALENDAR VIEW ==================== */}
        {view === 'ms-calendar' && isAdmin && (
          <MsCalendarPage
            renderWeeklyView={renderWeeklyView}
            renderMonthlyView={renderMonthlyView}
            calDate={calDate}
            setCalDate={setCalDate}
            calView={calView}
            setCalView={setCalView}
            msCalendarConnected={msCalendarConnected}
            msAuthNeedsReconnect={msAuthNeedsReconnect}
            msCalendarEvents={msCalendarEvents}
            msCalendarName={msCalendarName}
            msSyncInProgress={msSyncInProgress}
            msAutoSync={msAutoSync}
            setMsAutoSync={setMsAutoSync}
            syncMsCalendar={syncMsCalendar}
            initiateMsCalendarAuth={initiateMsCalendarAuth}
            disconnectMsCalendar={disconnectMsCalendar}
            currentUser={currentUser}
            msWebhookEnabled={msWebhookEnabled}
            msWebhookSubscription={msWebhookSubscription}
            msSyncSettings={msSyncSettings}
            timeEntries={timeEntries}
            bulkPushInProgress={bulkPushInProgress}
            useSupabaseDB={useSupabaseDB}
            supabaseTimeEntries={supabaseTimeEntries}
            setModal={setModal}
            setTimeEntries={setTimeEntries}
            initializeMicrosoftCalendars={initializeMicrosoftCalendars}
            seedMsCategoryColors={seedMsCategoryColors}
            recolorMsCalendars={recolorMsCalendars}
            backfillMsCategoryColors={backfillMsCategoryColors}
            bulkPushEventsToMicrosoft={bulkPushEventsToMicrosoft}
            createWebhookSubscription={createWebhookSubscription}
            deleteWebhookSubscription={deleteWebhookSubscription}
            fmtTime={fmtTime}
          />
        )}
        {/* ==================== END MS CALENDAR VIEW ==================== */}
        
        
        {view === 'timeoff' && (
          <TimeOffRequestsPage
            currentUser={currentUser}
            activeUsers={activeUsers}
            activeHolidays={activeHolidays}
            timeOff={activeTimeOff}
            useSupabaseDB={useSupabaseDB}
            supabaseHolidays={supabaseHolidays}
            supabaseTimeOff={supabaseTimeOff}
            setModal={setModal}
            setForm={setForm}
            setTimeOff={setTimeOff}
            setConfirmCancel={setConfirmCancel}
            setEditLeave={setEditLeave}
            setConfirmReject={setConfirmReject}
            isSupervisorOfRequest={isSupervisorOfRequest}
            checkRequestRoutability={checkRequestRoutability}
            getWeekIdFromDateStr={getWeekIdFromDateStr}
          />
        )}
        
        {/* ========== NEW TIME CATEGORY PAGES ========== */}
        
        {/* Leave Policy Page - Admin only */}
        {view === 'leave-policy' && isAdmin && (
          <LeavePolicyPage
            selectedLeaveYear={selectedLeaveYear}
            leavePolicySaving={leavePolicySaving}
            leavePolicyDirty={leavePolicyDirty}
            setSelectedLeaveYear={setSelectedLeaveYear}
            handleSaveLeavePolicy={handleSaveLeavePolicy}
            getLeavePolicy={getLeavePolicy}
            updateLeavePolicyMatrix={updateLeavePolicyMatrix}
            safeNumericInputValue={safeNumericInputValue}
            noticePolicySaving={noticePolicySaving}
            noticePolicyDirty={noticePolicyDirty}
            handleSaveNoticePolicy={handleSaveNoticePolicy}
            getNoticePolicy={getNoticePolicy}
            updateNoticePolicyMatrix={updateNoticePolicyMatrix}
          />
        )}

        {/* Leave Entitlement Page - Admin only */}
        {view === 'leave-entitlement' && isAdmin && (
          <LeaveEntitlementPage
            activeUsers={activeUsers}
            selectedLeaveYear={selectedLeaveYear}
            employeeLeaveEntitlement={employeeLeaveEntitlement}
            form={form}
            setForm={setForm}
            recalculateAllEntitlements={recalculateAllEntitlements}
            getEmployeeEntitlement={getEmployeeEntitlement}
            setManualLeaveOverride={setManualLeaveOverride}
            removeManualLeaveOverride={removeManualLeaveOverride}
            getEmployeeNoticeEntitlement={getEmployeeNoticeEntitlement}
            setManualNoticeOverride={setManualNoticeOverride}
            removeManualNoticeOverride={removeManualNoticeOverride}
          />
        )}

        {/* Leave Summary Page - Admin only */}
        {view === 'leave-summary' && isAdmin && (
          <LeaveSummaryPage
            activeUsers={activeUsers}
            selectedLeaveYear={selectedLeaveYear}
            leaveAuditLog={leaveAuditLog}
            form={form}
            setForm={setForm}
            setSelectedLeaveYear={setSelectedLeaveYear}
            getYearlyLeaveSummary={getYearlyLeaveSummary}
          />
        )}
        
        {view === 'timesheet-overview' && canAccessPage(currentUser.role, 'timesheet-overview') && (
          <TimesheetOverviewPage
            currentUser={currentUser}
            activeUsers={activeUsers}
            activeProjects={activeProjects}
            activeTimeEntries={activeTimeEntries}
            useSupabaseDB={useSupabaseDB}
            activeTeams={activeTeams}
            activeHolidays={activeHolidays}
            activeTimeOff={activeTimeOff}
            getProjectIdsForTeam={supabaseTeamProjects?.getProjectIdsForTeam}
            dataLoading={timesheetDataLoading}
          />
        )}

        {view === 'freelancer-management' && canAccessPage(currentUser.role, 'freelancer-management') && (
          <FreelancerManagementPage
            currentUser={currentUser}
            activeUsers={activeUsers}
            activeProjects={activeProjects}
          />
        )}

        {/* ========== END NEW TIME CATEGORY PAGES ========== */}
        
        {view === 'manage' && (
          <div className="space-y-6" style={{ fontFamily: 'Raleway, sans-serif' }}>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold brio-section-title">Projects</h3>
                {isAdmin && (
                  <button 
                    onClick={() => setModal('addProject')} 
                    className="px-4 py-2 rounded-lg flex items-center gap-2 brio-btn-gold"
                  >
                    <Plus className="w-4 h-4"/>Add Project
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {getSortedProjects().map(p => {
                  const c = activeClients.find(x => x.id === p.clientId);
                  return (
                    <div key={p.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50" style={{ borderColor: '#e0e0e0' }}>
                      <div className="flex items-center gap-3">
                        <div className="w-4 h-4 rounded-full" style={{backgroundColor:p.color}}/>
                        <div style={{ fontFamily: 'Raleway, sans-serif' }}>
                          <div className="font-medium flex items-center gap-2" style={{ fontWeight: 400, fontSize: '13px' }}>
                            {fmtProject(p)}
                            <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getProjectStatusStyle(p.status).bg} ${getProjectStatusStyle(p.status).text} ${getProjectStatusStyle(p.status).border}`}>
                              {getProjectStatusStyle(p.status).label}
                            </span>
                            {isAdmin && !hasSupervisorsAssigned(p) && (
                              <span
                                className="px-2 py-0.5 rounded text-xs font-medium border border-amber-300 bg-amber-50 text-amber-700 flex items-center gap-1"
                                title="Assign supervisors in Edit Project so this project is visible on Timeline / Invoices."
                              >
                                ⚠ No supervisors
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-slate-600" style={{ fontSize: '13px', fontWeight: 200, fontStyle: 'italic', color: '#666666' }}>{c?.name}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {isAdmin && (
                          <>
                            <div className="text-sm text-slate-600" style={{ fontSize: '13px', fontWeight: 300, color: '#666666', fontFamily: 'Raleway, sans-serif' }}>
                              Total Design Fee: {(() => {
                                const baseFee = p.budget || 0;
                                const retainerConfig = p.retainerConfig || { monthlyAmount: 0, totalMonths: 12, status: 'inactive' };
                                const ccRetainer = retainerConfig.status === 'active' 
                                  ? (retainerConfig.monthlyAmount || 0) * (retainerConfig.totalMonths || 12)
                                  : 0;
                                const totalFee = baseFee + ccRetainer;
                                return fmtCurr(totalFee);
                              })()}
                            </div>
                            <div className="text-sm text-slate-600" style={{ fontSize: '13px', fontWeight: 300, color: '#666666', fontFamily: 'Raleway, sans-serif' }}>
                              Size: {p.projectSize ? `${p.projectSize.toLocaleString()} m²` : 'N/A'}
                            </div>
                            <button 
                              onClick={() => {
                                const retainerConfig = p.retainerConfig || {
                                  monthlyAmount: 0,
                                  startDate: null,
                                  totalMonths: 12,
                                  status: 'inactive'
                                };
                                // Auto-suggest type if category is missing
                                const existingCategory = p.category || '';
                                const suggestedCategory = existingCategory || suggestProjectTypeFromName(p.name);
                                // Determine initial color - keep existing color, or auto-pick if category available
                                const initialColor = p.color || (suggestedCategory ? autoPickProjectColor({ type: suggestedCategory, projectId: p.id, projectName: p.name }) : '#3b82f6');
                                // Auto-color enabled by default when opening modal
                                const autoColorEnabled = true;
                                setForm({
                                  editProjectId: p.id,
                                  projectName: p.name,
                                  projectCode: p.projectCode || '',
                                  clientId: p.clientId,
                                  category: suggestedCategory,
                                  status: p.status || 'active',
                                  color: initialColor,
                                  budget: p.budget,
                                  projectSize: p.projectSize || 0,
                                  targetConstructionBudget: p.targetConstructionBudget || 0,
                                  finalConstructionBudget: p.finalConstructionBudget || 0,
                                  supervisors: p.supervisors || [],
                                  budgetAllocation: p.budgetAllocation || {},
                                  retainerMonthlyAmount: retainerConfig.monthlyAmount,
                                  retainerStartDate: retainerConfig.startDate,
                                  retainerTotalMonths: retainerConfig.totalMonths,
                                  retainerStatus: retainerConfig.status,
                                  autoColorEnabled: autoColorEnabled
                                });
                                setModal('editProject');
                              }}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 p-2 rounded"
                              title="Edit project"
                            >
                              <Edit2 className="w-4 h-4"/>
                            </button>
{/* DISABLED: Projects cannot be deleted - change status instead */}
                            <button 
                              onClick={() => {
                                alert('Projects cannot be deleted. Change the project status to "Completed" or "On Hold" instead.');
                              }}
                              className="text-slate-300 cursor-not-allowed p-2 rounded"
                              title="Projects cannot be deleted - change status instead"
                              disabled
                            >
                              <Trash2 className="w-4 h-4"/>
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {!isAdmin && (
                <p className="text-sm text-slate-500 mt-4">All users can add time to any project. Contact admin to create new projects.</p>
              )}
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold" style={{ fontSize: '15px', fontWeight: 400, lineHeight: '18px' }}>Clients</h3>
                {isAdmin && (
                  <button onClick={() => setModal('addClient')} className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2">
                    <Plus className="w-4 h-4"/>Add Client
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {activeClients.map(c => (
                  <div key={c.id} className="p-3 border rounded-lg hover:bg-slate-50">
                    <div className="font-medium">{c.name}</div>
                    <div className="text-sm text-slate-600">{c.email}</div>
                  </div>
                ))}
              </div>
              {!isAdmin && (
                <p className="text-sm text-slate-500 mt-4">Contact admin to add new clients.</p>
              )}
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold" style={{ fontSize: '15px', fontWeight: 400, lineHeight: '18px' }}>Stages</h3>
                <div className="flex gap-2">
                  {isAdmin && (
                    <>
                      <button onClick={async () => {
                        const defaultStages = [
                          { id: 1, name: 'Advance' },
                          { id: 2, name: 'CD AD' },
                          { id: 3, name: 'CD ID' },
                          { id: 4, name: 'SD AD' },
                          { id: 5, name: 'SD ID' },
                          { id: 6, name: 'DD AD' },
                          { id: 7, name: 'DD ID' },
                          { id: 8, name: 'AoC AD' },
                          { id: 9, name: 'AoC ID' },
                          { id: 10, name: 'TD AD' },
                          { id: 11, name: 'TD ID' },
                          { id: 12, name: 'GFC 1' },
                          { id: 13, name: 'GFC 2' },
                          { id: 14, name: 'GFC 3' },
                          { id: 15, name: 'Completion' },
                          { id: 16, name: 'CC' }
                        ];
                        // Keep custom stages (ID > 16) and add default stages
                        const customStages = activeTasks.filter(t => t.id > 16);
                        
                        if (useSupabaseDB) {
                          // Delete existing default stages (id <= 16) and recreate
                          for (const stage of activeTasks.filter(t => t.id <= 16)) {
                            await supabaseTasks.deleteTask(stage.id);
                          }
                          for (const stage of defaultStages) {
                            await supabaseTasks.createTask(stage);
                          }
                        } else {
                          setTasks([...defaultStages, ...customStages]);
                        }
                        setForm({importMessage: 'Default stages have been restored!'});
                        setModal('importSuccess');
                      }} className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
                        Reset to Default
                      </button>
                      <button onClick={() => setModal('addTask')} className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2">
                        <Plus className="w-4 h-4"/>Add Stage
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                {activeTasks.map(t => {
                  // Map task IDs to full names from standard budget allocation table
                  const stageFullNames = {
                    1: 'Advance',
                    2: 'CD AD (Concept Design - Architecture)',
                    3: 'CD ID (Concept Design - Interior)',
                    4: 'SD AD (Schematic Design - Architecture)',
                    5: 'SD ID (Schematic Design - Interior)',
                    6: 'DD AD (Design Development - Architecture)',
                    7: 'DD ID (Design Development - Interior)',
                    8: 'AoC AD (Awarding of Contractor - Architecture)',
                    9: 'AoC ID (Awarding of Contractor - Interior)',
                    10: 'TD AD (Tender Design - Architecture)',
                    11: 'TD ID (Tender Design - Interior)',
                    12: 'GFC 1 (Good for Construction 1)',
                    13: 'GFC 2 (Good for Construction 2)',
                    14: 'GFC 3 (Good for Construction 3)',
                    15: 'Completion',
                    16: 'CC (Construction Coordination)'
                  };
                  
                  const displayName = stageFullNames[t.id] || t.name;
                  
                  return (
                    <div key={t.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
                      <div className="font-medium">{displayName}</div>
                      {isAdmin && t.id > 16 && (
                        <button onClick={() => {
                          setForm({
                            deleteStageId: t.id,
                            deleteStageName: t.name
                          });
                          setModal('confirmDeleteStage');
                        }} className="text-red-600 hover:text-red-700 p-2">
                          <Trash2 className="w-4 h-4"/>
                        </button>
                      )}
                      {isAdmin && t.id <= 16 && (
                        <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">Default</span>
                      )}
                    </div>
                  );
                })}
              </div>
              {!isAdmin && (
                <p className="text-sm text-slate-500 mt-4">All users can add time to any stage. Contact admin to create new stages.</p>
              )}
            </div>
          </div>
        )}
        
        {/* ========== NEW PROJECTS CATEGORY PAGES ========== */}
        
        {/* Manage Projects Page */}
        {view === 'manage-projects' && (
          <ManageProjectsPage
            activeClients={activeClients}
            activeUsers={activeUsers}
            activeProjects={activeProjects}
            currentUser={currentUser}
            isAdmin={isAdmin}
            BRIO_PROJECT_TYPES={BRIO_PROJECT_TYPES}
            getSortedProjects={getSortedProjects}
            getProjectStatusStyle={getProjectStatusStyle}
            suggestProjectTypeFromName={suggestProjectTypeFromName}
            autoPickProjectColor={autoPickProjectColor}
            hasSupervisorsAssigned={hasSupervisorsAssigned}
            normalizeId={normalizeId}
            modal={modal}
            form={form}
            setModal={setModal}
            setForm={setForm}
            useSupabaseDB={useSupabaseDB}
            supabaseProjects={supabaseProjects}
            projects={projects}
            setProjects={setProjects}
            fmtCurr={fmtCurr}
            fmtProject={fmtProject}
            currSymbol={currSymbol}
            feeRevisionsHook={useSupabaseDB ? supabaseFeeRevisions : null}
          />
        )}

        {/* Projects Overview — one-page review table (admin + supervisor) */}
        {view === 'projects-overview' && canAccessPage(currentUser.role, 'projects-overview') && (
          <ProjectsOverviewPage
            activeProjects={activeProjects}
            activeClients={activeClients}
            activeUsers={activeUsers}
            BRIO_PROJECT_TYPES={BRIO_PROJECT_TYPES}
            getProjectStatusStyle={getProjectStatusStyle}
            currentUser={currentUser}
            isAdmin={isAdmin}
            canManageProject={canManageProject}
            feeRevisionsHook={useSupabaseDB ? supabaseFeeRevisions : null}
            updateProject={useSupabaseDB
              ? supabaseProjects.updateProject
              : async (id, patch) => {
                  setProjects(prev => prev.map(p => (p.id === id ? { ...p, ...patch } : p)));
                  return { success: true };
                }}
          />
        )}

        {/* Clients Page */}
        {view === 'clients' && (
          <ClientsPage
            activeClients={activeClients}
            currentUser={currentUser}
            setModal={setModal}
            useSupabaseDB={useSupabaseDB}
            supabaseClients={supabaseClients}
            clients={clients}
            setClients={setClients}
            projects={activeProjects}
          />
        )}

        {/* Contacts Directory — vendors, contractors, suppliers */}
        {view === 'contacts-directory' && canAccessPage(currentUser.role, 'contacts-directory') && (
          <ContactsDirectoryPage
            currentUser={currentUser}
            isAdmin={isAdmin}
            activeProjects={activeProjects}
            activeUsers={activeUsers}
            vendorsHook={supabaseVendors}
            readOnly={getContactsAccessLevel(currentUser) === 'read'}
            canManageAccess={(isAdmin || currentUser?.role === 'supervisor') && getContactsAccessLevel(currentUser) === 'full'}
            contactsAccess={contactsAccessMap}
            onSaveContactsAccess={(map) => settingsHook.updateSetting('contactsAccess', map)}
          />
        )}

        {/* Inquiries Page (admin / admin-pa only) */}
        {view === 'inquiries' && isAdmin && (
          <InquiriesPage
            currentUser={currentUser}
            isAdmin={isAdmin}
            activeClients={activeClients}
            activeProjects={activeProjects}
            useSupabaseDB={useSupabaseDB}
            supabaseClients={supabaseClients}
            supabaseInquiries={supabaseInquiries}
            supabaseProjects={supabaseProjects}
            fmtCurr={fmtCurr}
            priorityOptions={settingsHook.priorityOptions}
            contractScopesHook={contractScopesHook}
            projectLoadedCosts={supabaseProjectLoadedCosts.byProjectId}
            projectLoadedCostsOldest={supabaseProjectLoadedCosts.oldestComputedAt}
            projectLoadedCostsLoading={supabaseProjectLoadedCosts.loading}
            refreshProjectLoadedCosts={refreshProjectLoadedCosts}
            onOpenPitchDeck={inquiryId => {
              setPitchDeckInquiry(inquiryId);
              setMainCategory('comms');
              setSubPage('comms-pitches');
            }}
          />
        )}

        {/* Timelines Page — managers, or a non-manager with a per-project grant
            (scoped to their granted projects inside TimelinesPage). */}
        {view === 'timelines' && (isAdmin || currentUser?.role === 'supervisor' || hasAnyTimelineAccess) && (
          <TimelinesPage
            activeProjects={activeProjects}
            activeUsers={activeUsers}
            activeClients={activeClients}
            activeTasks={activeTasks}
            allocations={activeAllocations}
            activeTeams={activeTeams}
            activeTeamProjects={supabaseTeamProjects.teamProjects}
            currentUser={currentUser}
            supabaseProjects={supabaseProjects}
            contractScopesHook={contractScopesHook}
            scopeTemplatesHook={scopeTemplatesHook}
            projectStatusFilter={projectStatusFilter}
            invoicesHook={useSupabaseDB ? supabaseInvoices : null}
            onNavigateToTracking={({ projectId, stageKey, stageName, invoiceDate, triggerContext } = {}) => {
              if (projectId) setSelectedTimelineProject(projectId);
              if (triggerContext) {
                // Full trigger context from TimelinesPage — fire the workflow modal on TimelinePage
                setPendingTimelineAction({ triggerRecommendedAction: true, ...triggerContext });
              } else if (stageKey) {
                setPendingTimelineAction({ triggerInvoiceDate: true, stageKey, stageName, invoiceDate });
              }
              setMainCategory('projects');
              setSubPage('tracking');
            }}
          />
        )}

        {/* M.O.M Directory Page */}
        {view === 'mom-directory' && (
          <MOMDirectoryPage
            activeProjects={activeProjects}
            activeTasks={activeTasks}
            currentUser={currentUser}
            projectStatusFilter={projectStatusFilter}
            isAdmin={isAdmin}
            isSupervisor={currentUser?.role === 'supervisor'}
            supabaseProjects={supabaseProjects}
          />
        )}

        {/* Stages Page */}
        {view === 'stages' && (
          <StagesPage
            activeTasks={activeTasks}
            tasks={tasks}
            currentUser={currentUser}
            useSupabaseDB={useSupabaseDB}
            supabaseTasks={supabaseTasks}
            setTasks={setTasks}
            setModal={setModal}
            setForm={setForm}
          />
        )}
        
        {/* Contracts & Scope Page */}
        {view === 'contracts-scope' && canAccessPage(currentUser.role, 'contracts-scope') && (
          <ContractsScopePage
            activeProjects={activeProjects}
            filteredProjects={filteredProjects}
            activeUsers={activeUsers}
            currentUser={currentUser}
            isAdmin={isAdmin}
            contractScopesHook={contractScopesHook}
            scopeTemplatesHook={scopeTemplatesHook}
            supabaseInquiries={supabaseInquiries}
            settingsHook={settingsHook}
          />
        )}

        {/* Contracts Page */}
        {view === 'contracts' && canAccessPage(currentUser.role, 'contracts') && (
          <ContractsPage
            currentUser={currentUser}
            isAdmin={isAdmin}
            settingsHook={settingsHook}
            scopeTemplatesHook={scopeTemplatesHook}
            contractScopesHook={contractScopesHook}
            activeTasks={activeTasks}
            onNavigate={(page) => { setMainCategory('projects'); setSubPage(page); }}
          />
        )}

        {/* ========== END NEW PROJECTS CATEGORY PAGES ========== */}
        
        {/* ========== NEW TEAM CATEGORY PAGES ========== */}
        
        {/* Teams Page */}
        {view === 'teams' && canAccessPage(currentUser.role, 'teams') && (
          <TeamsPage
            teams={teams}
            activeTeams={activeTeams}
            activeUsers={activeUsers}
            activeProjects={activeProjects}
            useSupabaseDB={useSupabaseDB}
            supabaseTeams={supabaseTeams}
            supabaseTeamProjects={supabaseTeamProjects}
            setModal={setModal}
            setForm={setForm}
            currentUser={currentUser}
          />
        )}

        {/* Team Members Page */}
        {view === 'team-members' && canAccessPage(currentUser.role, 'team-members') && (
          <TeamMembersPage
            activeUsers={activeUsers}
            activeProjects={activeProjects}
            activeTeams={activeTeams}
            brioColors={brioColors}
            hfbAllocations={hfbAllocations}
            currentUser={currentUser}
            filters={filters}
            setFilters={setFilters}
            modal={modal}
            form={form}
            setModal={setModal}
            setForm={setForm}
            fmtCurr={fmtCurr}
            supabaseUsers={supabaseUsers}
            useSupabaseDB={useSupabaseDB}
            users={users}
            setUsers={setUsers}
            getSalaryFromMatrix={getSalaryFromMatrix}
            jobRoles={jobRoles}
            calculateSalaryMarkupFactor={calculateSalaryMarkupFactor}
            rateHistory={rateHistory}
            setRateHistory={setRateHistory}
            teams={teams}
            setTeams={setTeams}
            activeTimeEntries={activeTimeEntries}
            activeTasks={activeTasks}
            salaryMatrixHistoryNL={salaryMatrixHistoryNL}
            getInternalCostRate={getInternalCostRate}
          />
        )}

        {/* Admin Team Overview Page */}
        {view === 'admin-team-overview' && isAdmin && (
          <AdminTeamOverviewPage
            currentUser={currentUser}
            activeUsers={activeUsers}
            activeTeams={activeTeams}
            supabaseUsers={supabaseUsers}
            getEmployeeNoticeEntitlement={getEmployeeNoticeEntitlement}
            selectedLeaveYear={selectedLeaveYear}
            getSalaryFromMatrix={getSalaryFromMatrix}
            getSalaryFromMatrixHistory={getSalaryFromMatrixHistory}
            getSalaryFromNLMatrixHistory={getSalaryFromNLMatrixHistory}
            activeTimeEntries={activeTimeEntries}
            activeProjects={activeProjects}
            getInternalCostRate={getInternalCostRate}
          />
        )}

        {/* Hiring Page */}
        {view === 'hiring' && canAccessPage(currentUser.role, 'hiring') && (
          <HiringPage
            currentUser={currentUser}
            activeUsers={activeUsers}
            activeProjects={activeProjects}
            activeTeams={activeTeams}
            useSupabaseDB={useSupabaseDB}
            hiringHook={hiringHook}
            settingsHook={settingsHook}
            customEmailTemplates={settingsHook.emailTemplates}
            pendingHiringAction={pendingHiringAction}
            clearPendingHiringAction={() => setPendingHiringAction(null)}
          />
        )}

        {/* Roles & Responsibilities Framework */}
        {view === 'role-framework' && (
          <RoleFrameworkPage currentUser={currentUser} />
        )}

        {/* Travel log (everyone: own trips; admins: everyone's) */}
        {view === 'travel' && isAdmin && (
          <TravelPage currentUser={currentUser} isAdmin={isAdmin} activeUsers={activeUsers} />
        )}

        {/* ========== END NEW TEAM CATEGORY PAGES ========== */}

        {/* ========== NEW ACCOUNTING CATEGORY PAGES ========== */}

        {/* Expense Claims Page (all roles) */}
        {view === 'expense-claims' && (
          <ExpenseClaimsPage
            currentUser={currentUser}
            isAdmin={isAdmin}
            activeUsers={activeUsers}
            activeProjects={activeProjects}
            getSortedProjects={getSortedProjects}
            fmtProject={fmtProject}
            reimbursementsHook={supabaseReimbursements}
            supabaseExpenses={supabaseExpenses}
          />
        )}

        {/* Expense Administration Page (admin + supervisor) */}
        {view === 'expense-admin' && (isAdmin || currentUser?.role === 'supervisor') && (
          <ExpenseAdminPage
            currentUser={currentUser}
            isAdmin={isAdmin}
            activeUsers={activeUsers}
            activeProjects={activeProjects}
            getSortedProjects={getSortedProjects}
            getSupervisedProjects={getSupervisedProjects}
            fmtProject={fmtProject}
            activeExpenses={activeExpenses}
            projectExpenses={projectExpenses}
            setProjectExpenses={setProjectExpenses}
            activeOfficeOverheads={activeOfficeOverheads}
            officeOverheads={officeOverheads}
            setOfficeOverheads={setOfficeOverheads}
            form={form}
            setForm={setForm}
            useSupabaseDB={useSupabaseDB}
            supabaseExpenses={supabaseExpenses}
            supabaseOfficeOverheads={supabaseOfficeOverheads}
            reimbursementsHook={supabaseReimbursements}
            invoicesHook={useSupabaseDB ? supabaseInvoices : null}
            fmtCurr={fmtCurr}
            currSymbol={currSymbol}
            onNavigate={(page) => { setMainCategory('accounting'); setSubPage(page); }}
          />
        )}

        {/* Expense Analytics Page (admin only) */}
        {view === 'expense-analytics' && isAdmin && (
          <ExpenseAnalyticsPage
            activeProjects={activeProjects}
            activeUsers={activeUsers}
            activeExpenses={activeExpenses}
            activeOfficeOverheads={activeOfficeOverheads}
            fmtProject={fmtProject}
            reimbursementsHook={supabaseReimbursements}
            invoicesHook={useSupabaseDB ? supabaseInvoices : null}
            onNavigate={(page) => { setMainCategory('accounting'); setSubPage(page); }}
          />
        )}

        {/* Tax Reconciliation Page (admin only) */}
        {view === 'tax-reconciliation' && isAdmin && (
          <TaxReconciliationPage
            activeProjects={activeProjects}
            activeExpenses={activeExpenses}
            fmtProject={fmtProject}
            supabaseExpenses={supabaseExpenses}
          />
        )}

        {/* Project Planning Page */}
        {view === 'project-planning' && isAdmin && (
          <ProjectPlanningPage
            getSortedProjects={getSortedProjects}
            activeClients={activeClients}
            activeTimeEntries={activeTimeEntries}
            projects={projects}
            setProjects={setProjects}
            form={form}
            setForm={setForm}
            useSupabaseDB={useSupabaseDB}
            supabaseProjects={supabaseProjects}
            fmtCurr={fmtCurr}
            currSymbol={currSymbol}
          />
        )}
        
        {/* Markup Factor Page */}
        {view === 'markup-factor' && isAdmin && (
          <MarkupFactorPage
            activeUsers={activeUsers}
            activeOfficeOverheads={activeOfficeOverheads}
            computeOverheadAllocation={computeOverheadAllocation}
            settingsHook={settingsHook}
            form={form}
            getEffectiveSalaryMarkup={getEffectiveSalaryMarkup}
            getEffectiveSalary={getEffectiveSalary}
            getInternalCostRate={getInternalCostRate}
            calculateSalaryMarkupFactor={calculateSalaryMarkupFactor}
            fmtCurr={fmtCurr}
            currSymbol={currSymbol}
            setMainCategory={setMainCategory}
            setSubPage={setSubPage}
            isAdmin={isAdmin}
          />
        )}
        
        
        {/* Salary Matrix Page */}
        {view === 'salary-matrix' && isAdmin && (
          <SalaryMatrixPage
            salaryMatrix={salaryMatrix}
            setSalaryMatrix={setSalaryMatrix}
            salaryMatrixHistory={salaryMatrixHistory}
            setSalaryMatrixHistory={setSalaryMatrixHistory}
            isEditingMatrix={isEditingMatrix}
            setIsEditingMatrix={setIsEditingMatrix}
            tempSalaryMatrix={tempSalaryMatrix}
            setTempSalaryMatrix={setTempSalaryMatrix}
            form={form}
            setForm={setForm}
            currentUser={currentUser}
            jobRoles={jobRoles}
            useSupabaseDB={useSupabaseDB}
            supabaseSalaryMatrix={supabaseSalaryMatrix}
            currSymbol={currSymbol}
          />
        )}
        
        {/* NL Salary Matrix Page */}
        {view === 'salary-matrix-nl' && isAdmin && (
          <SalaryMatrixNLPage
            salaryMatrixHistoryNL={salaryMatrixHistoryNL}
            setSalaryMatrixHistoryNL={setSalaryMatrixHistoryNL}
            currentUser={currentUser}
            useSupabaseDB={useSupabaseDB}
            supabaseSalaryMatrixNL={supabaseSalaryMatrixNL}
          />
        )}

        {/* ========== END NEW ACCOUNTING CATEGORY PAGES ========== */}
      </div>

      {modal === 'addTime' && (() => {
        // Get current week start (Monday)
        const weekStart = form.weekStart ? new Date(form.weekStart) : (() => {
          const today = new Date();
          const day = today.getDay();
          const diff = day === 0 ? -6 : 1 - day; // Adjust to Monday
          const monday = new Date(today);
          monday.setDate(today.getDate() + diff);
          return monday;
        })();

        // Generate week days (Mon-Sun)
        const weekDays = Array.from({ length: 7 }, (_, i) => {
          const date = new Date(weekStart);
          date.setDate(weekStart.getDate() + i);
          return date;
        });

        // Get user's allocated projects — uses same week-based HFB allocation
        // logic as the Donut Time Tracker so both views show the same projects.
        // Also includes ALL projects the user has ever logged time on, so
        // previously-worked-on projects remain accessible across weeks.
        const userProjects = (() => {
          const weekStartStr = timesheetWeekStart;
          const userIdStr = String(currentUser.id);

          // Use Supabase weekly_project_allocations when available
          if (useSupabaseDB && timesheetAllocationsHook.allocations) {
            const hfbAllocs = timesheetAllocationsHook.allocations.filter(
              a => String(a.userId) === userIdStr && String(a.weekStart) === weekStartStr
            );
            // Combine allocation project IDs with historical project IDs.
            // Only include historical projects that are still active — imported
            // time entries on inactive projects should not resurface those projects.
            const visibleProjectIds = new Set(hfbAllocs.map(a => String(a.projectId)));
            const activeProjectIdSet = new Set(activeProjects.filter(p => p.status === 'active').map(p => String(p.id)));
            for (const pid of userHistoricalProjectIds) {
              if (activeProjectIdSet.has(pid)) visibleProjectIds.add(pid);
            }
            if (visibleProjectIds.size > 0) {
              return activeProjects.filter(p => visibleProjectIds.has(String(p.id)));
            }
            // Floating user (no team) gets all projects
            const teamId = currentUser.teamId ?? currentUser.team_id;
            const isFloating = !teamId || teamId === '' || teamId === 0 || teamId === '0';
            if (isFloating) return activeProjects;
            // Team user with no allocations and no history: empty
            return [];
          }

          // Fallback: legacy activeAllocations (non-week-based)
          const userAllocations = activeAllocations.filter(a => a.userId === currentUser.id);
          const userProjectIds = [...new Set(userAllocations.map(a => a.projectId))];
          return userProjectIds.length > 0
            ? activeProjects.filter(p => normalizedIncludes(userProjectIds, p.id))
            : activeProjects;
        })();

        // Initialize timesheet data if not exists
        if (!form.timesheetData) {
          const initialData = {};
          
          // Get week date range
          const weekEndDate = new Date(weekStart);
          weekEndDate.setDate(weekStart.getDate() + 6);
          const weekStartStr = weekStart.toISOString().split('T')[0];
          const weekEndStr = weekEndDate.toISOString().split('T')[0];
          
          // Get existing entries for this week
          const weekEntries = activeTimeEntries.filter(e => 
            e.userId === currentUser.id && 
            e.date >= weekStartStr && 
            e.date <= weekEndStr
          );
          
          userProjects.forEach(p => {
            initialData[p.id] = {};
            weekDays.forEach(day => {
              const dateKey = day.toISOString().split('T')[0];
              
              // Check if there's an existing entry for this project and date
              const existingEntry = weekEntries.find(e => 
                e.projectId === p.id && 
                e.date === dateKey
              );
              
              if (existingEntry) {
                // Detect site visit entries (CC stage + 'Site Visit' description)
                const isSavedSiteVisit = String(existingEntry.taskId) === '16' && existingEntry.description === 'Site Visit';
                // Load existing entry data
                initialData[p.id][dateKey] = {
                  hours: (existingEntry.duration / 3600).toString(),
                  taskId: isSavedSiteVisit ? 'site' : existingEntry.taskId.toString(),
                  billable: existingEntry.billable,
                  entryId: existingEntry.id // Store entry ID to update instead of creating new
                };
              } else {
                // Empty cell — default billable based on project client
                initialData[p.id][dateKey] = {
                  hours: '',
                  taskId: '',
                  billable: isProjectBillable(p.id)
                };
              }
            });
          });
          setForm({ ...form, weekStart: weekStart.toISOString().split('T')[0], timesheetData: initialData });
        }

        const timesheetData = form.timesheetData || {};

        // Calculate totals
        const dayTotals = weekDays.map(day => {
          const dateKey = day.toISOString().split('T')[0];
          let total = 0;
          userProjects.forEach(p => {
            const hours = parseFloat(timesheetData[p.id]?.[dateKey]?.hours || 0);
            total += hours;
          });
          return total;
        });

        const projectTotals = userProjects.map(p => {
          let total = 0;
          weekDays.forEach(day => {
            const dateKey = day.toISOString().split('T')[0];
            const hours = parseFloat(timesheetData[p.id]?.[dateKey]?.hours || 0);
            total += hours;
          });
          return total;
        });

        const grandTotal = dayTotals.reduce((sum, t) => sum + t, 0);

        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-7xl max-h-[95vh] flex flex-col">
              {/* Header */}
              <div className="flex justify-between items-center p-4 border-b bg-slate-50 rounded-t-lg">
                <div>
                  <h3 className="text-lg font-bold">Weekly Timesheet</h3>
                  <p className="text-sm text-slate-600">
                    Week of {weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {weekDays[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const newStart = new Date(weekStart);
                      newStart.setDate(weekStart.getDate() - 7);
                      setForm({ ...form, weekStart: newStart.toISOString().split('T')[0], timesheetData: null });
                    }}
                    className="p-2 hover:bg-slate-200 rounded"
                    title="Previous week"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => {
                      const newStart = new Date(weekStart);
                      newStart.setDate(weekStart.getDate() + 7);
                      setForm({ ...form, weekStart: newStart.toISOString().split('T')[0], timesheetData: null });
                    }}
                    className="p-2 hover:bg-slate-200 rounded"
                    title="Next week"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  <button onClick={() => { setModal(null); setForm({}); }} className="text-slate-400 hover:text-slate-600 p-2">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Toolbar */}
              {userProjects.length > 0 && (
                <div className="px-4 py-3 border-b bg-white flex items-center gap-2">
                  <button
                    onClick={() => {
                      // Get last week's start date
                      const lastWeekStart = new Date(weekStart);
                      lastWeekStart.setDate(weekStart.getDate() - 7);
                      const lastWeekEnd = new Date(lastWeekStart);
                      lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
                      
                      // Get entries from last week for current user
                      const lastWeekEntries = activeTimeEntries.filter(e => {
                        if (e.userId !== currentUser.id) return false;
                        const entryDate = new Date(e.date);
                        return entryDate >= lastWeekStart && entryDate <= lastWeekEnd;
                      });
                      
                      if (lastWeekEntries.length === 0) {
                        alert('No entries found for last week.');
                        return;
                      }
                      
                      // Convert last week's entries to current week's timesheet data
                      const newTimesheetData = { ...timesheetData };
                      
                      lastWeekEntries.forEach(entry => {
                        // Find the corresponding day in current week
                        const lastWeekDate = new Date(entry.date);
                        const dayOfWeek = lastWeekDate.getDay();
                        const dayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Monday = 0, Sunday = 6
                        
                        const currentWeekDate = new Date(weekStart);
                        currentWeekDate.setDate(weekStart.getDate() + dayOffset);
                        const dateKey = currentWeekDate.toISOString().split('T')[0];
                        
                        // Only copy if project is in user's current allocations
                        const userProjectIds = userProjects.map(p => p.id);
                        if (normalizedIncludes(userProjectIds, entry.projectId)) {
                          if (!newTimesheetData[entry.projectId]) {
                            newTimesheetData[entry.projectId] = {};
                          }
                          
                          newTimesheetData[entry.projectId][dateKey] = {
                            hours: (entry.duration / 3600).toString(),
                            taskId: entry.taskId.toString(),
                            billable: entry.billable
                          };
                        }
                      });
                      
                      setForm({ ...form, timesheetData: newTimesheetData });
                      alert(`Copied ${lastWeekEntries.length} entries from last week!`);
                    }}
                    className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200 flex items-center gap-2"
                  >
                    <Clock className="w-4 h-4" />
                    Copy Last Week
                  </button>
                </div>
              )}

              {/* Timesheet Grid */}
              <div className="flex-1 overflow-auto p-4">
                {userProjects.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Briefcase className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>You are not allocated to any projects.</p>
                    <p className="text-sm mt-2">Contact your supervisor to be assigned to projects.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-sm">
                      <thead>
                        <tr className="bg-slate-100 border-b-2 border-slate-300">
                          <th className="p-3 text-left font-semibold sticky left-0 bg-slate-100 z-10 min-w-[200px]">Project</th>
                          {weekDays.map((day, idx) => (
                            <th key={idx} className="p-2 text-center font-semibold min-w-[140px]">
                              <div>{['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][idx]}</div>
                              <div className="text-xs font-normal text-slate-600">{day.getDate()}/{day.getMonth() + 1}</div>
                            </th>
                          ))}
                          <th className="p-2 text-center font-semibold bg-slate-200 min-w-[80px]">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userProjects.map((project, pIdx) => {
                          const client = activeClients.find(c => c.id === project.clientId);
                          return (
                            <tr key={project.id} className={`border-b ${pIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                              <td className="p-3 sticky left-0 bg-inherit z-10 border-r">
                                <div className="flex items-center gap-2">
                                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: project.color }} />
                                  <div>
                                    <div className="font-medium">{project.projectCode || ''}</div>
                                    <div className="text-xs text-slate-600">{client?.name}</div>
                                  </div>
                                </div>
                              </td>
                              {weekDays.map((day, dIdx) => {
                                const dateKey = day.toISOString().split('T')[0];
                                const cellData = timesheetData[project.id]?.[dateKey] || { hours: '', taskId: '', billable: isProjectBillable(project.id) };
                                
                                return (
                                  <td key={dIdx} className="p-1 border-r">
                                    <div className="space-y-1">
                                      <input
                                        type="number"
                                        step="0.5"
                                        min="0"
                                        max="24"
                                        placeholder="8"
                                        value={cellData.hours}
                                        readOnly={cellData.taskId === 'site'}
                                        onFocus={(e) => {
                                          // If empty, set to 8 hours by default
                                          if (!cellData.hours || cellData.hours === '') {
                                            const newData = { ...timesheetData };
                                            if (!newData[project.id]) newData[project.id] = {};
                                            if (!newData[project.id][dateKey]) newData[project.id][dateKey] = { hours: '8', taskId: '', billable: isProjectBillable(project.id) };
                                            else {
                                              newData[project.id][dateKey] = { ...newData[project.id][dateKey], hours: '8' };
                                            }
                                            setForm({ ...form, timesheetData: newData });
                                          }
                                        }}
                                        onChange={(e) => {
                                          const newData = { ...timesheetData };
                                          if (!newData[project.id]) newData[project.id] = {};
                                          if (!newData[project.id][dateKey]) newData[project.id][dateKey] = { hours: '', taskId: '', billable: isProjectBillable(project.id) };
                                          newData[project.id][dateKey].hours = e.target.value;
                                          setForm({ ...form, timesheetData: newData });
                                        }}
                                        className="w-full px-2 py-1 text-sm border rounded"
                                      />
                                      <select
                                        value={cellData.taskId}
                                        onChange={(e) => {
                                          const newData = { ...timesheetData };
                                          if (!newData[project.id]) newData[project.id] = {};
                                          if (!newData[project.id][dateKey]) newData[project.id][dateKey] = { hours: '', taskId: '', billable: isProjectBillable(project.id) };
                                          newData[project.id][dateKey].taskId = e.target.value;
                                          // Automatically set billable to true when stage is selected
                                          if (e.target.value) {
                                            newData[project.id][dateKey].billable = true;
                                          }
                                          // Site Visit: auto-set hours to configured backend hours
                                          if (e.target.value === 'site') {
                                            newData[project.id][dateKey].hours = String(settingsHook.siteVisitBackendHours);
                                          }
                                          setForm({ ...form, timesheetData: newData });
                                        }}
                                        className="w-full px-2 py-1 text-xs border rounded"
                                        disabled={!cellData.hours}
                                      >
                                        <option value="">Stage</option>
                                        <option value="1">Advance</option>
                                        <option value="2">CD AD</option>
                                        <option value="3">CD ID</option>
                                        <option value="4">SD AD</option>
                                        <option value="5">SD ID</option>
                                        <option value="6">DD AD</option>
                                        <option value="7">DD ID</option>
                                        <option value="8">AoC AD</option>
                                        <option value="9">AoC ID</option>
                                        <option value="10">TD AD</option>
                                        <option value="11">TD ID</option>
                                        <option value="12">GFC 1</option>
                                        <option value="13">GFC 2</option>
                                        <option value="14">GFC 3</option>
                                        <option value="15">Completion</option>
                                        <option value="16">CC</option>
                                        <option value="site">SITE</option>
                                      </select>
                                      <label className="flex items-center gap-1 text-xs">
                                        <input
                                          type="checkbox"
                                          checked={cellData.billable}
                                          onChange={(e) => {
                                            const newData = { ...timesheetData };
                                            if (!newData[project.id]) newData[project.id] = {};
                                            if (!newData[project.id][dateKey]) newData[project.id][dateKey] = { hours: '', taskId: '', billable: isProjectBillable(project.id) };
                                            newData[project.id][dateKey].billable = e.target.checked;
                                            setForm({ ...form, timesheetData: newData });
                                          }}
                                          className="w-3 h-3"
                                          disabled={!cellData.hours}
                                        />
                                        <span className="text-slate-600">Bill</span>
                                      </label>
                                    </div>
                                  </td>
                                );
                              })}
                              <td className="p-2 text-center font-bold bg-slate-100">
                                {projectTotals[pIdx].toFixed(1)}h
                              </td>
                            </tr>
                          );
                        })}
                        {/* Total Row */}
                        <tr className="bg-slate-200 border-t-2 border-slate-400 font-bold">
                          <td className="p-3 sticky left-0 bg-slate-200 z-10">Total</td>
                          {dayTotals.map((total, idx) => (
                            <td key={idx} className="p-2 text-center border-r">
                              {total.toFixed(1)}h
                            </td>
                          ))}
                          <td className="p-2 text-center bg-slate-300">
                            {grandTotal.toFixed(1)}h
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Footer */}
              {userProjects.length > 0 && (
                <div className="p-4 border-t bg-slate-50 flex justify-between items-center rounded-b-lg">
                  <div className="text-sm text-slate-600">
                    Total hours this week: <span className="font-bold text-slate-900">{grandTotal.toFixed(1)}h</span>
                  </div>
                  <button
                    disabled={timesheetSaving}
                    onClick={async () => {
                      // A second click while a save is running would re-read the same
                      // cells (still without entryIds) and insert every row again.
                      if (timesheetSavingRef.current) return;
                      timesheetSavingRef.current = true;
                      setTimesheetSaving(true);
                      try {
                      // Create or update entries for all cells with hours
                      const newEntries = [];
                      const updatedEntries = [];
                      const deletedEntryIds = [];
                      let createCount = 0;
                      let updateCount = 0;
                      
                      userProjects.forEach(project => {
                        weekDays.forEach(day => {
                          const dateKey = day.toISOString().split('T')[0];
                          const cellData = timesheetData[project.id]?.[dateKey];
                          
                          if (cellData && cellData.hours && parseFloat(cellData.hours) > 0) {
                            const hours = parseFloat(cellData.hours);
                            const duration = hours * 3600; // Convert to seconds
                            const rdur = round(duration / 60) * 60; // Round based on settings
                            
                            // Site visit: map virtual 'site' task to CC (16) with configured hours
                            const isSiteVisit = cellData.taskId === 'site';
                            const resolvedTaskId = isSiteVisit ? 16 : (parseInt(cellData.taskId) || 1);
                            const resolvedDuration = isSiteVisit ? Math.round(settingsHook.siteVisitBackendHours * 3600) : rdur;
                            const resolvedDescription = isSiteVisit ? 'Site Visit' : `Week of ${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

                            // A cell without an entryId can still have a saved row behind it
                            // (form opened before an earlier save landed). Update that row
                            // instead of inserting a duplicate for the same project and day.
                            const targetEntryId = cellData.entryId || activeTimeEntries.find(e =>
                              normalizeId(e.userId) === normalizeId(currentUser.id) &&
                              normalizeId(e.projectId) === normalizeId(project.id) &&
                              e.date === dateKey
                            )?.id;

                            if (targetEntryId) {
                              // Update existing entry
                              const existingEntry = activeTimeEntries.find(e => normalizeId(e.id) === normalizeId(targetEntryId));
                              if (existingEntry) {
                                updatedEntries.push({
                                  ...existingEntry,
                                  taskId: isSiteVisit ? 16 : (parseInt(cellData.taskId) || existingEntry.taskId),
                                  duration: resolvedDuration,
                                  description: resolvedDescription,
                                  billable: cellData.billable
                                });
                                updateCount++;
                              }
                            } else {
                              // Create new entry
                              newEntries.push({
                                id: Date.now() + createCount,
                                userId: currentUser.id,
                                projectId: project.id,
                                taskId: resolvedTaskId,
                                description: resolvedDescription,
                                duration: resolvedDuration,
                                date: dateKey,
                                billable: cellData.billable !== undefined ? cellData.billable : isProjectBillable(project.id),
                                notes: '',
                                approved: false
                              });
                              createCount++;
                            }
                          } else if (cellData && cellData.entryId && (!cellData.hours || parseFloat(cellData.hours) === 0)) {
                            // Mark entry for deletion if hours cleared
                            deletedEntryIds.push(cellData.entryId);
                          }
                        });
                      });
                      
                      if (newEntries.length === 0 && updatedEntries.length === 0 && deletedEntryIds.length === 0) {
                        alert('No changes to save. Please add hours to at least one cell.');
                        return;
                      }
                      
                      if (useSupabaseDB) {
                        // Handle Supabase operations
                        let errors = [];
                        
                        // Delete entries
                        if (deletedEntryIds.length > 0) {
                          const deleteResult = await supabaseTimeEntries.bulkDeleteTimeEntries(deletedEntryIds);
                          if (!deleteResult.success) errors.push('Delete: ' + deleteResult.error);
                        }
                        
                        // Update entries - only send DB-compatible fields
                        for (const entry of updatedEntries) {
                          const updatePayload = {
                            taskId: entry.taskId,
                            duration: entry.duration,
                            billable: entry.billable,
                            description: entry.description,
                            notes: entry.notes
                          };
                          const updateResult = await supabaseTimeEntries.updateTimeEntry(entry.id, updatePayload);
                          if (!updateResult.success) errors.push('Update: ' + updateResult.error);
                        }
                        
                        // Create entries
                        for (const entry of newEntries) {
                          const createResult = await supabaseTimeEntries.createTimeEntry(entry);
                          if (!createResult.success) errors.push('Create: ' + createResult.error);
                        }
                        
                        if (errors.length > 0) {
                          alert('Some operations failed: ' + errors.join(', '));
                        }
                        
                        // Refetch time entries to ensure UI shows persisted data
                        await supabaseTimeEntries.refetch();
                      } else {
                        // Apply updates to localStorage
                        let updatedTimeEntries = timeEntries.filter(e => !normalizedIncludes(deletedEntryIds, e.id));
                        
                        // Update existing entries
                        updatedTimeEntries = updatedTimeEntries.map(e => {
                          const updated = updatedEntries.find(u => normalizeId(u.id) === normalizeId(e.id));
                          return updated || e;
                        });
                        
                        // Add new entries
                        updatedTimeEntries = [...newEntries, ...updatedTimeEntries];
                        
                        setTimeEntries(updatedTimeEntries);
                      }
                      
                      const messages = [];
                      if (createCount > 0) messages.push(`Created ${createCount} new ${createCount === 1 ? 'entry' : 'entries'}`);
                      if (updateCount > 0) messages.push(`Updated ${updateCount} ${updateCount === 1 ? 'entry' : 'entries'}`);
                      if (deletedEntryIds.length > 0) messages.push(`Deleted ${deletedEntryIds.length} ${deletedEntryIds.length === 1 ? 'entry' : 'entries'}`);
                      
                      alert(`Success! ${messages.join(', ')}.`);
                      setModal(null);
                      setForm({});
                      } finally {
                        timesheetSavingRef.current = false;
                        setTimesheetSaving(false);
                      }
                    }}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-4 h-4" />
                    {timesheetSaving ? 'Saving…' : 'Submit Timesheet'}
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {editing && (
        <BrioModal
          title="Edit Entry"
          onClose={() => setEditing(null)}
          width="md"
          footer={
            <button onClick={updateEntry} className="brio-btn brio-btn-primary" style={{ width: '100%' }}>Update</button>
          }
        >
          <div className="space-y-3">
            <input type="text" value={editing.description} onChange={(e) => setEditing({...editing, description:e.target.value})} className="brio-modal-input"/>
            <div className="grid grid-cols-2 gap-2">
              <input type="number" placeholder="Hours" value={editing.hours} onChange={(e) => setEditing({...editing, hours:e.target.value})} className="brio-modal-input"/>
              <input type="number" placeholder="Minutes" value={editing.minutes} onChange={(e) => setEditing({...editing, minutes:e.target.value})} className="brio-modal-input"/>
            </div>
            <textarea value={editing.notes} onChange={(e) => setEditing({...editing, notes:e.target.value})} className="brio-modal-input" rows="2"/>
          </div>
        </BrioModal>
      )}

      {/* MS Calendar Settings Modal */}
      {modal === 'msCalendarSettings' && (
        <BrioModal
          title="Microsoft Calendar Settings"
          onClose={() => setModal(null)}
          width="md"
          footer={
            <button 
              onClick={() => {
                setModal(null);
                alert('Settings saved!');
              }}
              className="brio-btn brio-btn-primary" style={{ width: '100%' }}
            >
              Save Settings
            </button>
          }
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Sync Past Days</label>
              <input 
                type="number" 
                value={msSyncSettings.syncPastDays}
                onChange={(e) => setMsSyncSettings({...msSyncSettings, syncPastDays: parseInt(e.target.value)})}
                className="brio-modal-input"
              />
              <p className="text-xs text-slate-500 mt-1">How many days in the past to sync</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Sync Future Days</label>
              <input 
                type="number" 
                value={msSyncSettings.syncFutureDays}
                onChange={(e) => setMsSyncSettings({...msSyncSettings, syncFutureDays: parseInt(e.target.value)})}
                className="brio-modal-input"
              />
              <p className="text-xs text-slate-500 mt-1">How many days in the future to sync</p>
            </div>

            <div className="border-t pt-4">
              <label className="flex items-center gap-2 mb-3">
                <input 
                  type="checkbox" 
                  checked={msSyncSettings.autoCreateEntries}
                  onChange={(e) => setMsSyncSettings({...msSyncSettings, autoCreateEntries: e.target.checked})}
                  className="w-4 h-4"
                />
                <span className="font-medium">Auto-create time entries from events</span>
              </label>

              {msSyncSettings.autoCreateEntries && (
                <div className="space-y-3 pl-6">
                  <div>
                    <label className="block text-sm font-medium mb-1">Default Project</label>
                    <select 
                      value={msSyncSettings.defaultProjectId || ''}
                      onChange={(e) => setMsSyncSettings({...msSyncSettings, defaultProjectId: parseInt(e.target.value)})}
                      className="brio-modal-select"
                    >
                      <option value="">Select Project</option>
                      {activeProjects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Default Task</label>
                    <select 
                      value={msSyncSettings.defaultTaskId || ''}
                      onChange={(e) => setMsSyncSettings({...msSyncSettings, defaultTaskId: parseInt(e.target.value)})}
                      className="brio-modal-select"
                    >
                      <option value="">Select Task</option>
                      {activeTasks.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Only Sync Categories (Optional)</label>
                    <input 
                      type="text" 
                      placeholder="e.g., Billable, Client Meeting"
                      value={msSyncSettings.onlyBillableCategories.join(', ')}
                      onChange={(e) => setMsSyncSettings({
                        ...msSyncSettings, 
                        onlyBillableCategories: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                      })}
                      className="brio-modal-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">Leave empty to sync all events, or specify comma-separated categories</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </BrioModal>
      )}

      {modal === 'addTimeOff' && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          style={{ WebkitTapHighlightColor: 'transparent' }}
          onClick={(e) => { if (e.target === e.currentTarget) { setModal(null); setForm({}); resetDateRangePicker(); } }}
        >
          <div 
            className="bg-white rounded-lg max-w-md w-full max-h-[90vh] flex flex-col"
            style={{ touchAction: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Fixed header */}
            <div className="flex justify-between items-center px-6 pt-6 mb-4 flex-shrink-0">
              <h3 className="text-lg font-bold">Request Time Off</h3>
              <button type="button" onClick={() => {setModal(null); setForm({}); resetDateRangePicker();}} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5"/>
              </button>
            </div>
            {/* Scrollable content area — button is OUTSIDE this div */}
            <div className="px-6 overflow-y-auto flex-1 min-h-0" style={{ WebkitOverflowScrolling: 'touch' }}>
            <div className="space-y-3">
              {isAdmin && (
                <select value={form.userId||currentUser.id} onChange={(e) => setForm({...form, userId:e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                  {activeUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              )}
              <select value={form.timeOffType||''} onChange={(e) => setForm({...form, timeOffType:e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                <option value="">Select Type</option>
                <option value="vacation">Vacation</option>
                <option value="sick">Sick Leave</option>
                <option value="emergency">Emergency Leave</option>
                <option value="wfh">Work from Home</option>
              </select>
              
              {/* Inline Calendar Range Picker */}
              <div className="border rounded-lg p-3 bg-slate-50">
                <div className="text-sm font-medium text-slate-700 mb-2">Select Date Range</div>
                
                {/* Month Navigation */}
                <div className="flex items-center justify-between mb-2">
                  <button 
                    type="button"
                    onClick={() => setDateRangeMonth(new Date(dateRangeMonth.getFullYear(), dateRangeMonth.getMonth() - 1, 1))}
                    className="p-1 hover:bg-slate-200 rounded text-slate-600"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-sm font-semibold text-slate-800">
                    {dateRangeMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </span>
                  <button 
                    type="button"
                    onClick={() => setDateRangeMonth(new Date(dateRangeMonth.getFullYear(), dateRangeMonth.getMonth() + 1, 1))}
                    className="p-1 hover:bg-slate-200 rounded text-slate-600"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Weekday Headers */}
                <div className="grid grid-cols-7 gap-0.5 mb-1">
                  {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map(day => (
                    <div key={day} className="text-center text-xs font-medium text-slate-500 py-1">
                      {day}
                    </div>
                  ))}
                </div>
                
                {/* Calendar Grid */}
                <div 
                  className="grid grid-cols-7 gap-0.5 select-none"
                  onPointerLeave={() => {
                    // Don't reset hover when leaving grid if dragging - let global handler catch it
                  }}
                >
                  {(() => {
                    const days = buildCalendarGrid(dateRangeMonth.getFullYear(), dateRangeMonth.getMonth());
                    const previewRange = getDatePickerPreviewRange();
                    const todayISO = dateToISO(new Date());
                    
                    return days.map((dayInfo, idx) => {
                      if (!dayInfo) {
                        return <div key={`empty-${idx}`} className="h-8" />;
                      }
                      
                      const { iso, day } = dayInfo;
                      const isInPreview = previewRange && isInRange(iso, previewRange.start, previewRange.end);
                      const isRangeStart = previewRange && iso === previewRange.start;
                      const isRangeEnd = previewRange && iso === previewRange.end;
                      const isToday = iso === todayISO;
                      const isPast = iso < todayISO;
                      
                      return (
                        <button
                          key={iso}
                          type="button"
                          className={`
                            h-8 text-xs font-medium transition-colors relative
                            ${isPast ? 'text-slate-300' : 'text-slate-700 hover:bg-blue-100'}
                            ${isInPreview && !isRangeStart && !isRangeEnd ? 'bg-blue-100' : ''}
                            ${isRangeStart ? 'bg-blue-500 text-white rounded-l-full' : ''}
                            ${isRangeEnd ? 'bg-blue-500 text-white rounded-r-full' : ''}
                            ${isRangeStart && isRangeEnd ? 'rounded-full' : ''}
                            ${isToday && !isInPreview ? 'ring-1 ring-blue-400 ring-inset' : ''}
                          `}
                          onPointerDown={(e) => {
                            e.preventDefault();
                            setDateRangeDragging(true);
                            setDateRangeDragAnchor(iso);
                            setDateRangeDragHover(iso);
                          }}
                          onPointerEnter={() => {
                            if (dateRangeDragging) {
                              setDateRangeDragHover(iso);
                            }
                          }}
                          onPointerUp={() => {
                            if (dateRangeDragging) {
                              commitDateRange(dateRangeDragAnchor, iso);
                              resetDateRangePicker();
                            }
                          }}
                        >
                          {day}
                        </button>
                      );
                    });
                  })()}
                </div>
                
                {/* Selected Range Display */}
                {(form.timeOffStart || form.timeOffEnd) && (
                  <div className="mt-2 pt-2 border-t border-slate-200 text-xs text-slate-600">
                    <span className="font-medium">Selected: </span>
                    {form.timeOffStart === form.timeOffEnd 
                      ? form.timeOffStart
                      : `${form.timeOffStart || '...'} → ${form.timeOffEnd || '...'}`
                    }
                  </div>
                )}
              </div>
              
              {/* Manual Date Entry Fallback */}
              <details className="text-xs">
                <summary className="cursor-pointer text-slate-500 hover:text-slate-700">
                  ✏️ Edit dates manually
                </summary>
                <div className="flex gap-2 mt-2">
                  <BrioDatePicker
                    placeholder="Start"
                    value={form.timeOffStart||''}
                    onChange={(v) => setForm({...form, timeOffStart:v})}
                  />
                  <BrioDatePicker
                    placeholder="End"
                    value={form.timeOffEnd||''}
                    onChange={(v) => setForm({...form, timeOffEnd:v})}
                  />
                </div>
              </details>
              
              {/* Validation Info Box - Collapsible FAQ */}
              <details className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <summary className="font-semibold text-sm text-blue-900 cursor-pointer hover:text-blue-700 flex items-center gap-2">
                  📋 Leave Request Guidelines FAQ
                  <span className="text-xs text-blue-600 font-normal">(click to expand)</span>
                </summary>
                <div className="mt-3 space-y-3 text-xs text-slate-700">
                  <div>
                    <div className="font-semibold text-slate-900 mb-1.5">1. How much notice is required?</div>
                    <ul className="ml-4 space-y-1">
                      <li>• <strong>Vacation (1–2 working days):</strong> Minimum 5 days' notice required</li>
                      <li>• <strong>Vacation (3–5 working days):</strong> Minimum 7 days' notice required</li>
                      <li>• <strong>Vacation (6+ working days):</strong> Minimum 21 days' notice required</li>
                      <li>• <strong>Emergency Leave:</strong> Can be requested within 5 days of the start date; must be submitted before 08:00 IST on the day the leave starts</li>
                      <li>• <strong>Sick Leave:</strong> No advance notice required</li>
                      <li>• <strong>Work From Home (WFH):</strong> No advance notice required</li>
                    </ul>
                  </div>

                  <div>
                    <div className="font-semibold text-slate-900 mb-1.5">2. How much time can I take off?</div>
                    <ul className="ml-4 space-y-1">
                      <li>• <strong>Vacation:</strong> Maximum of 15 working days per request</li>
                      <li>• <strong>Emergency Leave:</strong> Maximum of 2 working days per request, and 5 working days per leave year (counted as vacation)</li>
                      <li>• <strong>Sick Leave:</strong> As required (no fixed limit)</li>
                      <li>• <strong>Work From Home:</strong> As required; WFH exceeding 5 days will be treated as leave</li>
                    </ul>
                  </div>

                  <div>
                    <div className="font-semibold text-slate-900 mb-1.5">3. What is deducted from my leave balance?</div>
                    <ul className="ml-4 space-y-1">
                      <li>• <strong>Vacation:</strong> Fully deducted from your annual leave entitlement</li>
                      <li>• <strong>Emergency Leave:</strong> Fully deducted from your annual leave entitlement</li>
                      <li>• <strong>Sick Leave:</strong> Deductible beyond 5 sick days per year</li>
                      <li>• <strong>Work From Home:</strong> Deductible beyond 5 WFH days per year</li>
                    </ul>
                  </div>

                  <div>
                    <div className="font-semibold text-slate-900 mb-1.5">4. Can I take back-to-back vacation?</div>
                    <ul className="ml-4 space-y-1">
                      <li>• <strong>No.</strong> There must be a minimum gap of 5 working days between Vacation periods. (Emergency Leave is exempt from this rule.)</li>
                    </ul>
                  </div>

                  <div>
                    <div className="font-semibold text-slate-900 mb-1.5">5. What happens when I reach the Emergency Leave cap?</div>
                    <ul className="ml-4 space-y-1">
                      <li>• Once your approved + pending Emergency Leave for the year reaches 5 working days, further Emergency requests still go through and follow the normal route — supervisor approval first, then admin sign-off — but are flagged as over the cap.</li>
                    </ul>
                  </div>
                  
                  {isAdmin && (
                    <div className="pt-2 mt-2 border-t border-blue-300">
                      <div className="text-purple-700 font-semibold text-xs">👑 Admin Override</div>
                      <div className="text-purple-600 text-xs mt-0.5">You can approve any request regardless of notice requirements.</div>
                    </div>
                  )}
                </div>
              </details>
              
              {/* Leave Balance & Validation Info */}
              {form.timeOffStart && form.timeOffEnd && form.timeOffType && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg p-4 text-sm">
                  <div className="font-bold text-blue-900 mb-3 flex items-center gap-2">
                    <span className="text-lg">ℹ️</span>
                    <span>Your Leave Summary</span>
                  </div>
                  {(() => {
                    try {
                      const workingDays = countWorkingDays(form.timeOffStart, form.timeOffEnd);
                      const start = new Date(form.timeOffStart);
                      const _end = new Date(form.timeOffEnd);
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      start.setHours(0, 0, 0, 0);
                      const daysUntilLeave = Math.ceil((start - today) / (1000 * 60 * 60 * 24));
                      
                      const requestingUserId = parseInt(form.userId) || currentUser.id;
                      const year = new Date(form.timeOffStart).getFullYear();
                      const entitlement = getEmployeeEntitlement(requestingUserId, year);
                      const leaveTaken = calculateLeaveTaken(requestingUserId, year);
                      
                      // Handle case where entitlement is not found
                      if (!entitlement) {
                        return (
                          <div className="text-amber-600 text-xs">
                            ⚠️ Could not calculate leave entitlement for this user. Please ensure the user is active.
                          </div>
                        );
                      }
                      
                      const remaining = entitlement.finalTotalLeave - leaveTaken.total;

                      // Notice rules per type.
                      // Vacation: tier-driven via getVacationNoticeRequirement.
                      // Emergency: must be ≤5d out AND before 08:00 IST cutoff on the start day.
                      // Sick / WFH: no notice required.
                      let advanceNoticeRequired = 0;
                      let noticeTierLabel = '';
                      let noticeStatus = '✓';
                      let noticeMessage = '';
                      const isVacationType = form.timeOffType === 'vacation' || form.timeOffType === 'personal';
                      const isEmergencyType = form.timeOffType === 'emergency';
                      if (isVacationType) {
                        const tier = getVacationNoticeRequirement(workingDays);
                        advanceNoticeRequired = tier.days;
                        noticeTierLabel = tier.label;
                        noticeStatus = daysUntilLeave >= advanceNoticeRequired ? '✓' : '✗';
                      } else if (isEmergencyType) {
                        const istNow = getNowInIST();
                        const inWindow = daysUntilLeave <= 5;
                        const pastCutoff = (istNow.date === form.timeOffStart && istNow.hour >= 8)
                          || istNow.date > form.timeOffStart;
                        const emergencyOk = inWindow && !pastCutoff;
                        noticeStatus = emergencyOk ? '✓' : '✗';
                        if (!inWindow) noticeMessage = 'Emergency Leave window is the 5 days before the start date.';
                        else if (pastCutoff) noticeMessage = 'Past the 08:00 IST same-day cutoff.';
                      }

                      // Max length per request: 15 for vacation, 2 for emergency.
                      const maxAllowed = isEmergencyType ? EMERGENCY_LEAVE_MAX_LENGTH : 15;
                      const exceedsMax = (isVacationType || isEmergencyType) && workingDays > maxAllowed;

                      // Emergency annual cap (5 working days per leave year).
                      const emergencyUsed = isEmergencyType ? getEmergencyUsageForYear(requestingUserId, year) : 0;
                      const emergencyTotalAfter = emergencyUsed + workingDays;
                      const emergencyCapReached = isEmergencyType && emergencyTotalAfter > EMERGENCY_LEAVE_ANNUAL_CAP;

                      const balanceAfter = remaining - workingDays;
                      const balanceStatus = balanceAfter >= 0 ? '✓' : '✗';
                      
                      return (
                        <div className="space-y-3">
                          {/* Request Details */}
                          <div className="bg-white rounded-lg p-3 border border-blue-200">
                            <div className="font-semibold text-blue-900 mb-2">📅 This Request:</div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <span className="text-slate-600">Dates:</span>
                                <div className="font-medium">{form.timeOffStart} to {form.timeOffEnd}</div>
                              </div>
                              <div>
                                <span className="text-slate-600">Working Days:</span>
                                <div className={`font-bold ${exceedsMax ? 'text-red-600' : 'text-blue-600'}`}>
                                  {workingDays} days
                                  {exceedsMax && <span className="ml-1">⚠️</span>}
                                </div>
                                {exceedsMax && !isAdmin && (
                                  <div className="text-red-600 text-xs mt-1">
                                    Exceeds {maxAllowed}-day limit
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Maximum Days Warning - vacation > 15 days, or emergency > 2 days */}
                          {exceedsMax && (
                            <div className={`rounded-lg p-3 border-2 ${isAdmin ? 'bg-purple-50 border-purple-300' : 'bg-red-50 border-red-300'}`}>
                              <div className={`font-semibold mb-2 flex items-center gap-2 ${isAdmin ? 'text-purple-700' : 'text-red-700'}`}>
                                {isAdmin ? '👑 Admin Override: Maximum Days' : '✗ Maximum Days Exceeded'}
                              </div>
                              <div className="text-xs space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Requested:</span>
                                  <span className="font-bold">{workingDays} working days</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Maximum allowed:</span>
                                  <span className="font-bold">{maxAllowed} working days</span>
                                </div>
                                {isAdmin ? (
                                  <div className="text-purple-700 font-medium mt-2 pt-2 border-t border-purple-200">
                                    ✓ As admin, you can submit this request despite exceeding the {maxAllowed}-day limit
                                  </div>
                                ) : (
                                  <div className="text-red-700 font-medium mt-2 pt-2 border-t border-red-200">
                                    ⚠️ This request will be blocked — maximum is {maxAllowed} working days per {isEmergencyType ? 'Emergency Leave' : 'Vacation'} request
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Advance Notice — Vacation: tier-based; Emergency: 5-day window + 08:00 IST cutoff */}
                          {isVacationType && (
                            <div className={`rounded-lg p-3 border-2 ${noticeStatus === '✓' ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
                              <div className="font-semibold mb-2 flex items-center gap-2">
                                <span className={noticeStatus === '✓' ? 'text-green-700' : 'text-red-700'}>
                                  {noticeStatus} Advance Notice
                                </span>
                              </div>
                              <div className="text-xs space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Vacation length:</span>
                                  <span className="font-bold">{noticeTierLabel}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Days until leave starts:</span>
                                  <span className="font-bold">{daysUntilLeave} days</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Required notice:</span>
                                  <span className="font-bold">{advanceNoticeRequired} days</span>
                                </div>
                                {daysUntilLeave < advanceNoticeRequired && !isAdmin && (
                                  <div className="text-red-700 font-medium mt-2 pt-2 border-t border-red-200">
                                    ⚠️ Insufficient notice — request will be blocked. Use Emergency Leave if short-notice and unavoidable.
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {isEmergencyType && (
                            <div className={`rounded-lg p-3 border-2 ${noticeStatus === '✓' ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
                              <div className="font-semibold mb-2 flex items-center gap-2">
                                <span className={noticeStatus === '✓' ? 'text-green-700' : 'text-red-700'}>
                                  {noticeStatus} Emergency Leave Window
                                </span>
                              </div>
                              <div className="text-xs space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Days until leave starts:</span>
                                  <span className="font-bold">{daysUntilLeave} days</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Window:</span>
                                  <span className="font-bold">Within 5 days of start; before 08:00 IST on start day</span>
                                </div>
                                {noticeStatus === '✗' && !isAdmin && (
                                  <div className="text-red-700 font-medium mt-2 pt-2 border-t border-red-200">
                                    ⚠️ {noticeMessage} Request will be blocked.
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Emergency Annual Cap — advisory signal */}
                          {isEmergencyType && (
                            <div className={`rounded-lg p-3 border-2 ${emergencyCapReached ? 'bg-amber-50 border-amber-300' : 'bg-green-50 border-green-300'}`}>
                              <div className="font-semibold mb-2 flex items-center gap-2">
                                <span className={emergencyCapReached ? 'text-amber-700' : 'text-green-700'}>
                                  {emergencyCapReached ? '⚠ Emergency Cap' : '✓ Emergency Cap'}
                                </span>
                              </div>
                              <div className="text-xs space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Emergency days used this year:</span>
                                  <span className="font-bold">{emergencyUsed} / {EMERGENCY_LEAVE_ANNUAL_CAP}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-600">After this request:</span>
                                  <span className="font-bold">{emergencyTotalAfter} / {EMERGENCY_LEAVE_ANNUAL_CAP}</span>
                                </div>
                                {emergencyCapReached && !isAdmin && (
                                  <div className="text-amber-700 font-medium mt-2 pt-2 border-t border-amber-200">
                                    This request is over the yearly cap. It still needs supervisor approval first, then admin sign-off.
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Leave Balance — Vacation / Emergency only */}
                          {(isVacationType || isEmergencyType) && (
                            <div className={`rounded-lg p-3 border-2 ${balanceAfter >= 0 ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
                              <div className="font-semibold mb-2 flex items-center gap-2">
                                <span className={balanceAfter >= 0 ? 'text-green-700' : 'text-red-700'}>
                                  {balanceStatus} Leave Balance
                                </span>
                              </div>
                              <div className="text-xs space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Annual entitlement ({year}):</span>
                                  <span className="font-bold">{entitlement.finalTotalLeave} days</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Already taken:</span>
                                  <span className="font-medium">{leaveTaken.total} days</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Currently available:</span>
                                  <span className="font-bold text-blue-600">{remaining.toFixed(1)} days</span>
                                </div>
                                <div className="flex justify-between pt-2 border-t border-slate-200">
                                  <span className="text-slate-600">After this request:</span>
                                  <span className={`font-bold ${balanceAfter >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {balanceAfter.toFixed(1)} days {balanceAfter < 0 && '(EXCEEDS!)'}
                                  </span>
                                </div>
                                {balanceAfter < 0 && (
                                  <div className="text-red-700 font-medium mt-2 pt-2 border-t border-red-200">
                                    ⚠️ Exceeds available balance - request will be auto-rejected
                                  </div>
                                )}
                                {balanceAfter >= 0 && balanceAfter < 2 && (
                                  <div className="text-orange-700 font-medium mt-2 pt-2 border-orange-200">
                                    ⚠️ Warning: Less than 2 days will remain
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Continuous Vacation / Gap Check — Vacation only (Emergency exempt) */}
                          {isVacationType && (() => {
                            const continuousCheck = validateContinuousLeave(
                              requestingUserId,
                              form.timeOffStart,
                              form.timeOffEnd,
                              form.timeOffType,
                              isAdmin
                            );

                            // Find nearby leaves to show gap information
                            const userLeaves = activeTimeOff.filter(leave =>
                              leave.userId === requestingUserId &&
                              (leave.type === 'vacation' || leave.type === 'personal') &&
                              (leave.status === 'approved' || leave.status === 'pending')
                            );
                            
                            const start = new Date(form.timeOffStart);
                            const end = new Date(form.timeOffEnd);
                            
                            // Find closest leave before and after
                            let closestBefore = null;
                            let closestAfter = null;
                            let minGapBefore = Infinity;
                            let minGapAfter = Infinity;
                            
                            userLeaves.forEach(leave => {
                              const leaveStart = new Date(leave.startDate);
                              const leaveEnd = new Date(leave.endDate);
                              
                              if (leaveEnd < start) {
                                const gap = countWorkingDaysBetween(leaveEnd, start);
                                if (gap < minGapBefore) {
                                  minGapBefore = gap;
                                  closestBefore = { ...leave, gap };
                                }
                              } else if (leaveStart > end) {
                                const gap = countWorkingDaysBetween(end, leaveStart);
                                if (gap < minGapAfter) {
                                  minGapAfter = gap;
                                  closestAfter = { ...leave, gap };
                                }
                              }
                            });
                            
                            const hasNearbyLeaves = closestBefore || closestAfter;
                            const isValid = continuousCheck.valid || isAdmin;
                            
                            return (
                              <div className={`rounded-lg p-3 border-2 ${
                                isAdmin && !continuousCheck.valid 
                                  ? 'bg-purple-50 border-purple-300' 
                                  : isValid 
                                    ? 'bg-green-50 border-green-300' 
                                    : 'bg-red-50 border-red-300'
                              }`}>
                                <div className="font-semibold mb-2 flex items-center gap-2">
                                  <span className={
                                    isAdmin && !continuousCheck.valid
                                      ? 'text-purple-700'
                                      : isValid 
                                        ? 'text-green-700' 
                                        : 'text-red-700'
                                  }>
                                    {isAdmin && !continuousCheck.valid 
                                      ? '👑 Admin Override: Holiday Gaps' 
                                      : isValid 
                                        ? '✓ Holiday Gap Requirements' 
                                        : '✗ Holiday Gap Violation'}
                                  </span>
                                </div>
                                <div className="text-xs space-y-1">
                                  {hasNearbyLeaves ? (
                                    <>
                                      {closestBefore && (
                                        <div className="mb-2 pb-2 border-b border-slate-200">
                                          <div className="text-slate-600 mb-1">Previous Leave:</div>
                                          <div className="flex justify-between">
                                            <span className="text-slate-600">Dates:</span>
                                            <span className="font-medium">{closestBefore.startDate} to {closestBefore.endDate}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-slate-600">Gap to new request:</span>
                                            <span className={`font-bold ${closestBefore.gap >= 5 ? 'text-green-600' : 'text-red-600'}`}>
                                              {closestBefore.gap} working days {closestBefore.gap >= 5 ? '✓' : '✗'}
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                      {closestAfter && (
                                        <div className="mb-2 pb-2 border-b border-slate-200">
                                          <div className="text-slate-600 mb-1">Next Leave:</div>
                                          <div className="flex justify-between">
                                            <span className="text-slate-600">Dates:</span>
                                            <span className="font-medium">{closestAfter.startDate} to {closestAfter.endDate}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-slate-600">Gap from new request:</span>
                                            <span className={`font-bold ${closestAfter.gap >= 5 ? 'text-green-600' : 'text-red-600'}`}>
                                              {closestAfter.gap} working days {closestAfter.gap >= 5 ? '✓' : '✗'}
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                      <div className="flex justify-between pt-1">
                                        <span className="text-slate-600">Minimum required gap:</span>
                                        <span className="font-bold">5 working days</span>
                                      </div>
                                    </>
                                  ) : (
                                    <div className="text-green-700">
                                      ✓ No other vacation found — no gap conflicts
                                    </div>
                                  )}
                                  
                                  {isAdmin && !continuousCheck.valid ? (
                                    <div className="text-purple-700 font-medium mt-2 pt-2 border-t border-purple-200">
                                      👑 As admin, you can submit this request despite gap violations
                                    </div>
                                  ) : !continuousCheck.valid ? (
                                    <div className="text-red-700 font-medium mt-2 pt-2 border-t border-red-200">
                                      ⚠️ This request will be blocked - minimum 5 working days gap required
                                    </div>
                                  ) : hasNearbyLeaves ? (
                                    <div className="text-green-700 font-medium mt-2 pt-2 border-t border-green-200">
                                      ✓ All gap requirements met
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            );
                          })()}

                          {/* Sick Leave Note */}
                          {form.timeOffType === 'sick' && (
                            <div className="bg-green-50 border-2 border-green-300 rounded-lg p-3">
                              <div className="font-semibold text-green-700 mb-2 flex items-center gap-2">
                                ✓ Sick Leave
                              </div>
                              <div className="text-xs space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Sick leave used this year:</span>
                                  <span className="font-bold">{leaveTaken.sick || 0} days</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Free sick days remaining:</span>
                                  <span className="font-bold text-green-600">{Math.max(0, 5 - (leaveTaken.sick || 0))} days</span>
                                </div>
                                {workingDays > (5 - (leaveTaken.sick || 0)) && (
                                  <div className="text-orange-700 font-medium mt-2 pt-2 border-t border-orange-200">
                                    ⚠️ {Math.max(0, workingDays - (5 - (leaveTaken.sick || 0)))} days will count against your annual leave
                                  </div>
                                )}
                                <div className="text-green-800 mt-2 pt-2 border-t border-green-200">
                                  Policy: Deductible beyond 5 sick days per year.
                                </div>
                                <div className="text-amber-800 mt-2 pt-2 border-t border-green-200">
                                  Note: Sick leave is intended for health-related absences. For short-notice personal matters, please use Emergency Leave (max 2 working days per request, max 5 per year).
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Work from Home Note */}
                          {form.timeOffType === 'wfh' && (
                            <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-3">
                              <div className="font-semibold text-blue-700 mb-2 flex items-center gap-2">
                                ✓ Work from Home
                              </div>
                              <div className="text-xs space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-slate-600">WFH days used this year:</span>
                                  <span className="font-bold">{leaveTaken.wfh || 0} days</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Free WFH days remaining:</span>
                                  <span className="font-bold text-blue-600">{Math.max(0, 5 - (leaveTaken.wfh || 0))} days</span>
                                </div>
                                {workingDays > (5 - (leaveTaken.wfh || 0)) && (
                                  <div className="text-orange-700 font-medium mt-2 pt-2 border-t border-orange-200">
                                    ⚠️ {Math.max(0, workingDays - (5 - (leaveTaken.wfh || 0)))} days will count against your annual leave
                                  </div>
                                )}
                                <div className="text-blue-800 mt-2 pt-2 border-t border-blue-200">
                                  Policy: Deductible beyond 5 WFH days per year.
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Admin Override */}
                          {isAdmin && (
                            <div className="bg-purple-50 border-2 border-purple-300 rounded-lg p-3">
                              <div className="font-semibold text-purple-700 mb-1 flex items-center gap-2">
                                👑 Admin Override Active
                              </div>
                              <div className="text-xs text-purple-800">
                                As an admin, all validation rules are bypassed. This request will be automatically approved.
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    } catch (error) {
                      return (
                        <div className="text-red-600 text-xs">
                          Unable to calculate leave details. Please ensure all fields are filled correctly.
                        </div>
                      );
                    }
                  })()}
                </div>
              )}
            </div>{/* end space-y-3 */}
            </div>{/* end scroll area */}
            {/* Sticky footer — outside scroll container for reliable iPad Safari taps */}
            <div className="px-6 pb-6 pt-3 flex-shrink-0">
              <button type="button" onClick={async () => {
                // iPad Safari diagnostic — enable with: window.__BRIO_DEBUG__ = true
                if (typeof window !== 'undefined' && window.__BRIO_DEBUG__) {
                  console.log('[TimeOff Submit] handler fired', { ua: navigator.userAgent.slice(0, 80) });
                }
                
                // Validate required fields
                if(!form.timeOffStart || !form.timeOffEnd || !form.timeOffType) { 
                  
                  alert('⚠️ Please fill all fields\n\nRequired:\n- Leave Type\n- Start Date\n- End Date'); 
                  return; 
                }
                
                
                // Validate dates
                const start = new Date(form.timeOffStart);
                const end = new Date(form.timeOffEnd);
                
                if (end < start) {
                  alert('⚠️ Invalid Dates\n\nEnd date must be after start date.');
                  return;
                }
                
                // Calculate working days
                const workingDays = countWorkingDays(form.timeOffStart, form.timeOffEnd);
                const calendarDays = Math.ceil((end - start) / 86400000) + 1;
                
                const requestingUserId = parseInt(form.userId) || currentUser.id;
                const adminOverride = isAdmin; // FIX: was `const isAdmin = isAdmin` — TDZ crash on Safari/iPadOS
                const year = new Date(form.timeOffStart).getFullYear();
                
                // 1. Validate advance notice / Emergency window (unless admin)
                if (!adminOverride) {
                  const noticeValidation = validateAdvanceNotice(
                    form.timeOffStart,
                    form.timeOffType,
                    workingDays,
                    false
                  );
                  if (!noticeValidation.valid) {
                    alert(noticeValidation.message);
                    return;
                  }
                }

                // 1b. Emergency Leave per-request length cap (2 working days).
                if (form.timeOffType === 'emergency') {
                  const emergencyLength = validateEmergencyLength(workingDays, adminOverride);
                  if (!emergencyLength.valid) {
                    alert(emergencyLength.message);
                    return;
                  }
                }

                // 2. Validate maximum continuous holiday and gap requirements (vacation only)
                const continuousValidation = validateContinuousLeave(
                  requestingUserId,
                  form.timeOffStart,
                  form.timeOffEnd,
                  form.timeOffType,
                  adminOverride
                );
                if (!continuousValidation.valid) {
                  alert(continuousValidation.message);
                  return;
                }
                
                // 3. Check leave balance (for vacation/personal)
                
                const balanceCheck = checkLeaveBalance(
                  requestingUserId,
                  form.timeOffType,
                  workingDays,
                  year
                );
                
                
                if (!balanceCheck.valid) {
                  
                  if (balanceCheck.autoReject) {
                    
                    // Auto-reject and show message
                    alert(balanceCheck.message + '\n\n❌ Request automatically rejected.');
                    
                    // Still create the request but mark as rejected
                    // Note: Don't include 'id' for Supabase - DB auto-generates it
                    const rejectedRequestPayload = {
                      userId: requestingUserId,
                      type: form.timeOffType,
                      startDate: form.timeOffStart,
                      endDate: form.timeOffEnd,
                      status: 'rejected',
                      supervisorApproval: 'rejected',
                      adminApproval: 'rejected',
                      days: calendarDays,
                      workingDays: workingDays,
                      requestedDate: new Date().toISOString().split('T')[0],
                      rejectionReason: 'Exceeds available leave balance'
                    };
                    
                    if (useSupabaseDB) {
                      const result = await supabaseTimeOff.createTimeOffRequest(rejectedRequestPayload);
                      if (!result.success) {
                        alert('Error saving request: ' + result.error);
                      }
                      // Don't call setTimeOff - hook refetches automatically
                    } else {
                      // localStorage mode - need client-generated id
                      setTimeOff([...timeOff, { id: Date.now(), ...rejectedRequestPayload }]);
                    }
                    
                    setModal(null);
                    setForm({});
                    return;
                  }
                }
                
                // Show warning if close to limit
                if (balanceCheck.warning) {
                  if (!window.confirm(balanceCheck.warning + '\n\nDo you want to proceed?')) {
                    return;
                  }
                }
                
                // Success - create the request
                let successMessage = `✓ Leave Request Submitted\n\nType: ${form.timeOffType}\nDates: ${form.timeOffStart} to ${form.timeOffEnd}\nWorking Days: ${workingDays}\n`;

                if (adminOverride) {
                  successMessage += '\nStatus: Automatically approved (Admin)';
                } else if (currentUser.role === 'supervisor') {
                  successMessage += '\nStatus: Pending admin approval';
                } else if (balanceCheck.emergencyCapExceeded) {
                  successMessage += '\nStatus: Pending supervisor approval (Emergency Leave cap reached — flagged for supervisor and admin)';
                } else {
                  successMessage += '\nStatus: Pending supervisor approval';
                }
                
                // ================================================================
                // ANTI-REGRESSION GUARDRAIL: Check routability before submission
                // ================================================================
                // This is a SOFT GUARD - warn but do NOT block submission
                const routingCheck = checkRequestRoutability(requestingUserId, form.timeOffStart);
                let routingNote = '';
                
                if (routingCheck.isUnroutable) {
                  // Log warning for debugging
                  console.warn('[TimeOff Guard] Submitting unroutable request:', {
                    userId: requestingUserId,
                    startDate: form.timeOffStart,
                    weekId: routingCheck.weekId,
                    reason: 'No HFB team assignment found'
                  });
                  
                  // Show NON-blocking warning to user
                  alert(`⚠️ Routing Warning\n\n${routingCheck.warningMessage}\n\nThe request will still be submitted, but a supervisor may not see it until team assignments are updated.`);
                  
                  // Add system note to reason field for persistence
                  routingNote = `[System] Unroutable week ${routingCheck.weekId}: no team assignment. `;
                }
                
                // Note: Don't include 'id' for Supabase - DB auto-generates it
                const supervisorApprovalNow = (adminOverride || currentUser.role === 'supervisor');
                // Approval route: everyone below supervisor level starts at the
                // supervisor step and only reaches an admin once a supervisor has
                // approved. Supervisors' own requests still go straight to admin,
                // and an admin entering leave records it as already approved.
                // An over-cap Emergency request is flagged, not fast-tracked.
                let computedStatus;
                if (adminOverride) {
                  computedStatus = 'approved';
                } else if (currentUser.role === 'supervisor') {
                  computedStatus = 'pending_admin';
                } else {
                  computedStatus = 'pending_supervisor';
                }
                // "Approved by" names whoever actually signed the request off. A
                // supervisor's own request skips the supervisor step rather than being
                // approved by anyone, so the column stays empty until an admin approves.
                const selfSubmitted = String(requestingUserId) === String(currentUser.id);
                const recordApprover = supervisorApprovalNow && (!selfSubmitted || computedStatus === 'approved');
                const combinedReason = [
                  routingNote || null,
                  (!adminOverride && balanceCheck.emergencyCapExceeded)
                    ? '[System] Emergency Leave annual cap reached; needs supervisor approval, then admin sign-off.'
                    : null,
                ].filter(Boolean).join(' ');
                const newRequestPayload = {
                  userId: requestingUserId,
                  type: form.timeOffType,
                  startDate: form.timeOffStart,
                  endDate: form.timeOffEnd,
                  status: computedStatus,
                  supervisorApproval: supervisorApprovalNow ? 'approved' : 'pending',
                  adminApproval: adminOverride ? 'approved' : 'pending',
                  days: calendarDays,
                  workingDays: workingDays,
                  requestedDate: new Date().toISOString().split('T')[0],
                  approvedBy: recordApprover ? currentUser.id : null,
                  approvedAt: recordApprover ? new Date().toISOString() : null,
                  ...(combinedReason ? { reason: combinedReason } : {})
                };
                
                if (useSupabaseDB) {
                  const result = await supabaseTimeOff.createTimeOffRequest(newRequestPayload);
                  if (!result.success) {
                    alert('Error saving request: ' + result.error);
                    return;
                  }
                  // Don't call setTimeOff - hook refetches automatically
                } else {
                  // localStorage mode - need client-generated id
                  setTimeOff([...timeOff, { id: Date.now(), ...newRequestPayload }]);
                }
                
                setModal(null);
                setForm({});
                alert(successMessage);
              }} className="w-full bg-blue-600 text-white py-2 rounded-lg font-medium active:bg-blue-700" style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'rgba(0,0,0,0.1)' }}>Submit Request</button>
            </div>
          </div>
        </div>
      )}

      {modal === 'addHoliday' && (
        <BrioModal
          title="Add Holiday"
          onClose={() => {setModal(null); setForm({});}}
          width="sm"
          footer={
            <button onClick={async () => {
              if(!form.holidayName || !form.holidayDate) { alert('Fill all fields'); return; }
              const holidayData = {name:form.holidayName, date:form.holidayDate, office:form.holidayOffice || null};
              const result = await supabaseHolidays.createHoliday(holidayData);
              if (!result.success) {
                alert('Error creating holiday: ' + result.error);
                return;
              }
              setModal(null); 
              setForm({});
            }} className="brio-btn brio-btn-primary" style={{ width: '100%' }}>Add Holiday</button>
          }
        >
          <div className="space-y-3">
            <input type="text" placeholder="Holiday Name" value={form.holidayName||''} onChange={(e) => setForm({...form, holidayName:e.target.value})} className="brio-modal-input"/>
            <BrioDatePicker value={form.holidayDate||''} onChange={(v) => setForm({...form, holidayDate:v})} />
            <select value={form.holidayOffice||''} onChange={(e) => setForm({...form, holidayOffice:e.target.value || null})} className="brio-select" style={{width:'100%'}}>
              <option value="">All offices</option>
              <option value="NL">Rotterdam (NL)</option>
              <option value="IN">Mumbai (IN)</option>
            </select>
          </div>
        </BrioModal>
      )}

      {modal === 'editHoliday' && (
        <BrioModal
          title="Edit Holiday"
          onClose={() => {setModal(null); setForm({});}}
          width="sm"
          footer={
            <button onClick={async () => {
              if(!form.holidayName || !form.holidayDate) { alert('Fill all fields'); return; }
              const result = await supabaseHolidays.updateHoliday(form.holidayId, {
                name: form.holidayName,
                date: form.holidayDate,
                office: form.holidayOffice || null,
              });
              if (!result.success) {
                alert('Error updating holiday: ' + result.error);
                return;
              }
              setModal(null);
              setForm({});
            }} className="brio-btn brio-btn-primary" style={{ width: '100%' }}>Save Changes</button>
          }
        >
          <div className="space-y-3">
            <input type="text" placeholder="Holiday Name" value={form.holidayName||''} onChange={(e) => setForm({...form, holidayName:e.target.value})} className="brio-modal-input"/>
            <BrioDatePicker value={form.holidayDate||''} onChange={(v) => setForm({...form, holidayDate:v})} />
            <select value={form.holidayOffice||''} onChange={(e) => setForm({...form, holidayOffice:e.target.value || null})} className="brio-select" style={{width:'100%'}}>
              <option value="">All offices</option>
              <option value="NL">Rotterdam (NL)</option>
              <option value="IN">Mumbai (IN)</option>
            </select>
          </div>
        </BrioModal>
      )}


      {/* addProject modal moved to ManageProjectsPage.jsx */}

      {/* editProject modal moved to ManageProjectsPage.jsx */}

      {/* addClient modal moved to ClientsPage.jsx */}

      {modal === 'addTask' && (
        <BrioModal
          title="Add Stage"
          onClose={() => {setModal(null); setForm({});}}
          width="sm"
          footer={
            <button onClick={async () => {
              if(!form.taskName) { 
                setForm({errorMessage: 'Enter stage name'});
                setModal('importError');
                return; 
              }
              const taskData = {id:Date.now(), name:form.taskName};
              if (useSupabaseDB) {
                const result = await supabaseTasks.createTask(taskData);
                if (!result.success) {
                  alert('Error creating stage: ' + result.error);
                  return;
                }
              } else {
                setTasks([...tasks, taskData]);
              }
              setModal(null); 
              setForm({});
            }} className="brio-btn brio-btn-primary" style={{ width: '100%' }}>Add Stage</button>
          }
        >
          <div className="space-y-3">
            <div>
              <input type="text" placeholder="Stage Name (e.g., CD AD, SD ID, GFC 1)" value={form.taskName||''} onChange={(e) => setForm({...form, taskName:e.target.value})} className="brio-modal-input"/>
              <p className="text-xs text-slate-500 mt-1">Stages are independent and can be used across all projects</p>
            </div>
          </div>
        </BrioModal>
      )}

      {modal === 'addAllocation' && (
        <BrioModal
          title="Add Resource Allocation"
          onClose={() => {setModal(null); setForm({});}}
          width="md"
          footer={
            <button onClick={async () => {
              if(!form.allocProjectId || !form.allocHours) { 
                alert('Select project and enter hours'); 
                return; 
              }

              const newAllocation = {
                id: Date.now(),
                userId: form.allocUserId,
                projectId: form.allocProjectId,
                weekStart: form.allocWeekStart,
                allocatedHours: parseFloat(form.allocHours),
                notes: form.allocNotes || ''
              };

              if (useSupabaseDB) {
                // Supabase mode: create via API
                const result = await supabaseAllocations.createAllocation({
                  userId: newAllocation.userId,
                  projectId: newAllocation.projectId,
                  weekStart: newAllocation.weekStart,
                  allocatedHours: newAllocation.allocatedHours,
                  notes: newAllocation.notes
                });
                if (result.success) {
                  await supabaseAllocations.refetch();
                  setModal(null);
                  setForm({});
                } else {
                  alert('Failed to create allocation: ' + (result.error || 'Unknown error'));
                }
              } else {
                // localStorage mode: update local state
                setAllocations(prev => [...prev, newAllocation]);
                setModal(null);
                setForm({});
              }
            }} className="brio-btn brio-btn-primary" style={{ width: '100%' }}>
              Add Allocation
            </button>
          }
        >
          <div className="space-y-3">
            {(() => {
              const user = activeUsers.find(u => u.id === form.allocUserId);
              const userTeam = activeTeams.find(t => t.id === user?.teamId);
              return (
                <>
                  {/* User Info */}
                  {user && (
                    <div className="bg-slate-50 rounded-lg p-3 flex items-center gap-3">
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center text-white text-xs font-bold border-4 flex-shrink-0"
                        style={{ 
                          backgroundColor: roleColors[user.jobRole],
                          borderColor: userTeam?.color,
                          borderStyle: 'solid'
                        }}
                      >
                        {user.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <div className="font-semibold">{user.name}</div>
                        <div className="text-sm text-slate-600">{user.jobRole} - {userTeam?.name}</div>
                      </div>
                    </div>
                  )}

                  {/* Week Info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="text-sm font-semibold text-blue-800">
                      Week: {formatWeek(form.allocWeekStart)}
                    </div>
                    <div className="text-xs text-blue-600 mt-1">
                      Current allocation: {getUserWeekAllocated(form.allocUserId, form.allocWeekStart)}h / 40h
                    </div>
                  </div>

                  {/* Project Selection */}
                  <div>
                    <select 
                      value={form.allocProjectId||''} 
                      onChange={(e) => setForm({...form, allocProjectId:parseInt(e.target.value)})} 
                      className="brio-modal-select"
                    >
                      <option value="">Select Project</option>
                      {getSortedProjects().map(p => {
                        const client = activeClients.find(c => c.id === p.clientId);
                        return (
                          <option key={p.id} value={p.id}>
                            {fmtProject(p)}{client ? ` \u2013 ${client.name}` : ''}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  {/* Hours */}
                  <div>
                    <input 
                      type="number" 
                      min="0" 
                      max="40"
                      step="0.5"
                      placeholder="Allocated Hours (e.g., 8)" 
                      value={form.allocHours||''} 
                      onChange={(e) => setForm({...form, allocHours:e.target.value})} 
                      className="brio-modal-input"
                    />
                  </div>

                  {/* Notes */}
                  <div>
                    <textarea 
                      value={form.allocNotes||''} 
                      onChange={(e) => setForm({...form, allocNotes:e.target.value})} 
                      className="brio-modal-input"
                      rows="2"
                      placeholder="Notes (optional) - Special considerations..."
                    />
                  </div>

                  {/* Warning if over-allocated */}
                  {form.allocHours && (
                    (() => {
                      const currentAlloc = getUserWeekAllocated(form.allocUserId, form.allocWeekStart);
                      const newTotal = currentAlloc + parseFloat(form.allocHours);
                      if (newTotal > 40) {
                        return (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <p className="text-sm text-red-800 font-semibold">
                              ⚠️ Over-allocation Warning
                            </p>
                            <p className="text-xs text-red-700 mt-1">
                              This will allocate {newTotal}h / 40h ({((newTotal/40)*100).toFixed(0)}%)
                            </p>
                          </div>
                        );
                      }
                      return null;
                    })()
                  )}
                </>
              );
            })()}
          </div>
        </BrioModal>
      )}

      {modal === 'editAllocation' && (
        <BrioModal
          title="Edit Resource Allocation"
          onClose={() => {setModal(null); setForm({});}}
          width="md"
          footer={
            (() => {
              const currentProject = activeProjects.find(p => p.id === form.allocProjectId);
              return (
                <div className="flex gap-2 w-full">
                  <button 
                    type="button"
                    onClick={async () => {
                    if(!form.allocProjectId || !form.allocHours) { 
                      alert('Select project and enter hours'); 
                      return; 
                    }

                    if (useSupabaseDB) {
                      const result = await supabaseAllocations.updateAllocation(form.editAllocId, {
                        projectId: form.allocProjectId,
                        allocatedHours: parseFloat(form.allocHours),
                        notes: form.allocNotes || ''
                      });
                      if (result.success) {
                        await supabaseAllocations.refetch();
                        setModal(null);
                        setForm({});
                      } else {
                        alert('Failed to update allocation: ' + (result.error || 'Unknown error'));
                      }
                    } else {
                      setAllocations(prev => prev.map(a => a.id === form.editAllocId ? {
                        ...a,
                        projectId: form.allocProjectId,
                        allocatedHours: parseFloat(form.allocHours),
                        notes: form.allocNotes || ''
                      } : a));
                      
                      setModal(null);
                      setForm({});
                    }
                  }} className="brio-btn brio-btn-primary" style={{ flex: 1 }}>
                    Update Allocation
                  </button>

                  <button 
                    type="button"
                    onClick={() => {
                      setForm({
                        ...form,
                        confirmDeleteAllocId: form.editAllocId,
                        confirmDeleteProject: currentProject?.name,
                        confirmDeleteHours: form.allocHours
                      });
                      setModal('confirmDeleteAllocation');
                    }} 
                    className="brio-btn brio-btn-danger"
                  >
                    <Trash2 className="w-4 h-4"/>
                  </button>
                </div>
              );
            })()
          }
        >
          <div className="space-y-3">
            {(() => {
              const user = activeUsers.find(u => u.id === form.allocUserId);
              const userTeam = activeTeams.find(t => t.id === user?.teamId);
              
              return (
                <>
                  {/* User Info */}
                  {user && (
                    <div className="bg-slate-50 rounded-lg p-3 flex items-center gap-3">
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center text-white text-xs font-bold border-4 flex-shrink-0"
                        style={{ 
                          backgroundColor: roleColors[user.jobRole],
                          borderColor: userTeam?.color,
                          borderStyle: 'solid'
                        }}
                      >
                        {user.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <div className="font-semibold">{user.name}</div>
                        <div className="text-sm text-slate-600">{user.jobRole} - {userTeam?.name}</div>
                      </div>
                    </div>
                  )}

                  {/* Week Info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="text-sm font-semibold text-blue-800">
                      Week: {formatWeek(form.allocWeekStart)}
                    </div>
                    <div className="text-xs text-blue-600 mt-1">
                      Current allocation: {getUserWeekAllocated(form.allocUserId, form.allocWeekStart)}h / 40h
                    </div>
                  </div>

                  {/* Project Selection */}
                  <div>
                    <select 
                      value={form.allocProjectId||''} 
                      onChange={(e) => setForm({...form, allocProjectId:parseInt(e.target.value)})} 
                      className="brio-modal-select"
                    >
                      <option value="">Select Project</option>
                      {getSortedProjects().map(p => {
                        const client = activeClients.find(c => c.id === p.clientId);
                        return (
                          <option key={p.id} value={p.id}>
                            {fmtProject(p)}{client ? ` \u2013 ${client.name}` : ''}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  {/* Hours */}
                  <div>
                    <input 
                      type="number" 
                      min="0" 
                      max="40"
                      step="0.5"
                      placeholder="Allocated Hours (e.g., 8)" 
                      value={form.allocHours||''} 
                      onChange={(e) => setForm({...form, allocHours:e.target.value})} 
                      className="brio-modal-input"
                    />
                  </div>

                  {/* Notes */}
                  <div>
                    <textarea 
                      value={form.allocNotes||''} 
                      onChange={(e) => setForm({...form, allocNotes:e.target.value})} 
                      className="brio-modal-input"
                      rows="2"
                      placeholder="Notes (optional) - Special considerations..."
                    />
                  </div>

                  {/* Warning if over-allocated */}
                  {form.allocHours && (
                    (() => {
                      const originalAlloc = activeAllocations.find(a => a.id === form.editAllocId);
                      const currentAlloc = getUserWeekAllocated(form.allocUserId, form.allocWeekStart) - (originalAlloc?.allocatedHours || 0);
                      const newTotal = currentAlloc + parseFloat(form.allocHours);
                      if (newTotal > 40) {
                        return (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <p className="text-sm text-red-800 font-semibold">
                              ⚠️ Over-allocation Warning
                            </p>
                            <p className="text-xs text-red-700 mt-1">
                              This will allocate {newTotal}h / 40h ({((newTotal/40)*100).toFixed(0)}%)
                            </p>
                          </div>
                        );
                      }
                      return null;
                    })()
                  )}
                </>
              );
            })()}
          </div>
        </BrioModal>
      )}

      {modal === 'confirmDeleteAllocation' && (
        <BrioModal
          title="Confirm Delete Allocation"
          titleDanger
          onClose={() => {setModal('editAllocation');}}
          width="sm"
          footer={
            <div className="flex gap-3 w-full">
              <button 
                onClick={() => {setModal('editAllocation');}} 
                className="brio-btn brio-btn-secondary" style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button 
                onClick={async () => {
                  const allocId = form.confirmDeleteAllocId;
                  
                  if (useSupabaseDB) {
                    // Supabase mode: delete via API
                    const result = await supabaseAllocations.deleteAllocation(allocId);
                    if (result.success) {
                      await supabaseAllocations.refetch();
                      setModal(null);
                      setForm({});
                    } else {
                      alert('Failed to delete allocation: ' + (result.error || 'Unknown error'));
                    }
                  } else {
                    // localStorage mode: update local state
                    setAllocations(prev => prev.filter(a => a.id !== allocId));
                    setModal(null);
                    setForm({});
                  }
                }} 
                className="brio-btn brio-btn-danger" style={{ flex: 1 }}
              >
                Delete Allocation
              </button>
            </div>
          }
        >
          <div className="mb-2">
            <p className="text-slate-700 mb-3">
              Are you sure you want to delete this allocation?
            </p>
            <div className="bg-slate-50 rounded-lg p-3 space-y-1">
              <div className="text-sm"><strong>Project:</strong> {form.confirmDeleteProject}</div>
              <div className="text-sm"><strong>Hours:</strong> {form.confirmDeleteHours}h</div>
            </div>
            <p className="text-sm text-red-600 font-medium mt-3">
              ⚠️ This action cannot be undone.
            </p>
          </div>
        </BrioModal>
      )}

      {modal === 'addTeam' && (
        <BrioModal
          title="Add Team"
          onClose={() => {setModal(null); setForm({});}}
          width="md"
          footer={
            <button onClick={async () => {
              if(!form.teamName) { 
                alert('Enter team name'); 
                return; 
              }
              
              // Generate a unique code from team name if not provided
              const generateTeamCode = (name, suffix = '') => {
                // Create code from uppercase initials/first letters + random suffix
                const base = name
                  .split(/\s+/)
                  .map(w => w.charAt(0).toUpperCase())
                  .join('')
                  .substring(0, 4) || 'TEAM';
                const rand = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
                return `${base}-${rand}${suffix}`;
              };
              
              const newTeam = {
                name: form.teamName,
                code: generateTeamCode(form.teamName),
                color: form.teamColor || brioColors.teams[0],
                outlineColor: form.teamColor || brioColors.teams[0],
                supervisorIds: [],  // Not stored in Supabase teams table
                memberIds: [],
                isActive: true
              };
              
              if (useSupabaseDB) {
                // Write to Supabase with retry on unique constraint violation
                let result = await supabaseTeams.createTeam(newTeam);
                
                // If unique constraint violation (23505), retry with different code
                if (!result.success && result.code === '23505') {
                  console.log('[App] Team code collision, retrying with new code...');
                  newTeam.code = generateTeamCode(form.teamName, 'R');
                  result = await supabaseTeams.createTeam(newTeam);
                }
                
                if (result.success) {
                  console.log('[App] Team created in Supabase:', result.data);
                } else {
                  console.error('[App] Failed to create team:', result.error);
                  alert('Failed to create team: ' + result.error);
                  return;
                }
              } else {
                // Local storage mode - add with Date.now() ID
                newTeam.id = Date.now();
                setTeams([...teams, newTeam]);
              }
              
              setModal(null); 
              setForm({});
            }} className="brio-btn brio-btn-primary" style={{ width: '100%' }}>Create Team</button>
          }
        >
          <div className="space-y-3">
            <div>
              <input 
                type="text" 
                placeholder="Team Name (e.g., Design Team)" 
                value={form.teamName||''} 
                onChange={(e) => setForm({...form, teamName:e.target.value})} 
                className="brio-modal-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Team Outline Color</label>
              <div className="flex gap-2 items-center mb-2">
                <input 
                  type="color" 
                  value={form.teamColor || brioColors.teams[0]} 
                  onChange={(e) => setForm({...form, teamColor:e.target.value})} 
                  className="w-20 h-10 rounded-lg"
                />
                <span className="text-xs text-slate-600">Desaturated colors recommended</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                {brioColors.teams.map((color, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setForm({...form, teamColor: color})}
                    className="w-8 h-8 rounded border-2 transition-all"
                    style={{ 
                      backgroundColor: color,
                      borderColor: form.teamColor === color ? '#000000' : '#d1d5db'
                    }}
                    title={`Preset ${idx + 1}`}
                  />
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Team Supervisors</label>
              <div className="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-2">
                {activeUsers.filter(u => u.role === 'admin' || u.role === 'admin-pa' || u.role === 'supervisor').map(u => (
                  <label key={u.id} className="flex items-center gap-2 text-sm">
                    <input 
                      type="checkbox" 
                      checked={normalizedIncludes(form.teamSupervisors || [], u.id)}
                      onChange={(e) => {
                        const current = form.teamSupervisors || [];
                        setForm({
                          ...form, 
                          teamSupervisors: e.target.checked 
                            ? [...current, u.id]
                            : current.filter(id => normalizeId(id) !== normalizeId(u.id))
                        });
                      }}
                      className="w-4 h-4"
                    />
                    {u.name} ({u.role})
                  </label>
                ))}
              </div>
            </div>
          </div>
        </BrioModal>
      )}

      {modal === 'editTeam' && (
        <BrioModal
          title="Edit Team"
          onClose={() => {setModal(null); setForm({});}}
          width="lg"
          footer={
            <BrioActionGroup>
              <button onClick={async () => {
                if(window.confirm(`Delete team "${form.teamName}"? Users will remain but lose team assignment.`)) {
                  // Unassign users from this team
                  if (useSupabaseDB) {
                    for (const user of activeUsers) {
                      if (user.teamId === form.editTeamId) {
                        await supabaseUsers.updateUser(user.id, { teamId: null });
                      }
                    }
                    // Delete team from Supabase
                    const deleteResult = await supabaseTeams.deleteTeam(form.editTeamId);
                    if (!deleteResult.success) {
                      alert('Failed to delete team: ' + (deleteResult.error || 'Unknown error'));
                      return;
                    }
                    // Refetch teams to ensure UI is in sync
                    await supabaseTeams.refetch();
                  } else {
                    setUsers(users.map(u => u.teamId === form.editTeamId ? {...u, teamId: null} : u));
                    // Remove team from local state
                    setTeams(teams.filter(t => t.id !== form.editTeamId));
                  }
                  setModal(null);
                  setForm({});
                }
              }} className="brio-btn brio-btn-danger">Delete Team</button>
              <button onClick={async () => {
                if(!form.teamName) { 
                  alert('Enter team name'); 
                  return; 
                }
                
                const selectedSupervisors = form.teamSupervisors || [];
                const selectedProjects = form.teamProjects || [];
                
                // Update team in Supabase if enabled
                if (useSupabaseDB) {
                  console.log('[EditTeam] Updating team in Supabase:', {
                    teamId: form.editTeamId,
                    name: form.teamName,
                    color: form.teamColor,
                    supervisorIds: selectedSupervisors,
                    projectIds: selectedProjects
                  });
                  
                  const result = await supabaseTeams.updateTeam(form.editTeamId, {
                    name: form.teamName,
                    color: form.teamColor,
                    supervisorIds: selectedSupervisors  // uuid[] - pass as-is, no parseInt
                  });
                  
                  if (!result.success) {
                    alert('Failed to update team: ' + (result.error || 'Unknown error'));
                    return;
                  }
                  
                  // Update team_projects links
                  const projectsResult = await supabaseTeamProjects.setProjectsForTeam(form.editTeamId, selectedProjects);
                  if (!projectsResult.success) {
                    console.error('[EditTeam] Failed to update team projects:', projectsResult.error);
                    // Don't block - team update succeeded, projects are secondary
                  }
                  
                  console.log('[EditTeam] Team updated in Supabase, result:', result);
                  // Refetch teams to ensure UI is in sync
                  await supabaseTeams.refetch();
                }
                
                // Update local state for teams (will be overwritten by Supabase refetch if enabled)
                setTeams(teams.map(t => t.id === form.editTeamId ? {
                  ...t,
                  name: form.teamName,
                  color: form.teamColor,
                  outlineColor: form.teamColor,
                  supervisorIds: selectedSupervisors
                } : t));
                
                setModal(null); 
                setForm({});
              }} className="brio-btn brio-btn-primary">Update Team</button>
            </BrioActionGroup>
          }
        >
          <BrioAnalyticsSurface padding="m">
            <div className="space-y-4">
              <div>
                <input 
                  type="text" 
                  placeholder="Team Name"
                  value={form.teamName||''} 
                  onChange={(e) => setForm({...form, teamName:e.target.value})} 
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Team Outline Color</label>
                <div className="flex items-center gap-3 mb-2">
                  <input 
                    type="color" 
                    value={form.teamColor || brioColors.teams[0]} 
                    onChange={(e) => setForm({...form, teamColor:e.target.value})} 
                    className="h-10 w-20 rounded-lg"
                  />
                  <div 
                    className="w-8 h-8 rounded-full"
                    style={{ 
                      backgroundColor: '#8A7040',
                      border: `4px solid ${form.teamColor || brioColors.teams[0]}`
                    }}
                    title="Preview: ochre role fill with team outline"
                  />
                  <span className="text-xs text-slate-600">Preview with role color</span>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {brioColors.teams.map((color, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setForm({...form, teamColor: color})}
                      className="w-8 h-8 rounded border-2 transition-all"
                      style={{ 
                        backgroundColor: color,
                        borderColor: form.teamColor === color ? '#000000' : '#d1d5db'
                      }}
                      title={`Preset ${idx + 1}`}
                    />
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Team Supervisors</label>
                <div className="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-3 bg-slate-50">
                  {activeUsers.filter(u => u.role === 'admin' || u.role === 'admin-pa' || u.role === 'supervisor').map(u => (
                    <label key={u.id} className="flex items-center gap-2 text-sm hover:bg-white p-2 rounded cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={normalizedIncludes(form.teamSupervisors || [], u.id)}
                        onChange={(e) => {
                          const current = form.teamSupervisors || [];
                          setForm({
                            ...form, 
                            teamSupervisors: e.target.checked 
                              ? [...current, u.id]
                              : current.filter(id => normalizeId(id) !== normalizeId(u.id))
                          });
                        }}
                        className="w-4 h-4"
                      />
                      <span className="font-medium">{u.name}</span>
                      <span className="text-slate-500">({u.role})</span>
                    </label>
                  ))}
                  {activeUsers.filter(u => u.role === 'admin' || u.role === 'admin-pa' || u.role === 'supervisor').length === 0 && (
                    <p className="text-sm text-slate-500 text-center py-2">No admins or supervisors available</p>
                  )}
                </div>
              </div>
              
              {/* Team Projects Section */}
              <div>
                <label className="block text-sm font-medium mb-2">Team Projects</label>
                <p className="text-xs text-slate-500 mb-2">Projects linked to this team. Team supervisors can see and manage these projects.</p>
                <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3 bg-slate-50">
                  {activeProjects.length > 0 ? (
                    <>
                      {/* Search/filter input */}
                      <input
                        type="text"
                        placeholder="Search projects..."
                        value={form.projectSearch || ''}
                        onChange={(e) => setForm({...form, projectSearch: e.target.value})}
                        className="w-full px-2 py-1 text-sm border rounded mb-2"
                      />
                      {activeProjects
                        .filter(p => !form.projectSearch || p.name.toLowerCase().includes(form.projectSearch.toLowerCase()))
                        .sort((a, b) => (a.projectCode || a.name || '').localeCompare(b.projectCode || b.name || ''))
                        .map(p => (
                        <label key={p.id} className="flex items-center gap-2 text-sm hover:bg-white p-2 rounded cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={normalizedIncludes(form.teamProjects || [], p.id)}
                            onChange={(e) => {
                              const current = form.teamProjects || [];
                              setForm({
                                ...form, 
                                teamProjects: e.target.checked 
                                  ? [...current, p.id]
                                  : current.filter(id => normalizeId(id) !== normalizeId(p.id))
                              });
                            }}
                            className="w-4 h-4"
                          />
                          <span 
                            className="w-3 h-3 rounded-full flex-shrink-0" 
                            style={{ backgroundColor: p.color || '#3b82f6' }}
                          />
                          <span className="font-medium">{p.name}</span>
                          <span className="text-slate-400 text-xs">{p.projectCode || ''}</span>
                        </label>
                      ))}
                    </>
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-2">No projects available</p>
                  )}
                </div>
                <div className="mt-2 text-xs text-slate-500">
                  {(form.teamProjects || []).length} project(s) selected
                </div>
              </div>
            </div>
          </BrioAnalyticsSurface>
        </BrioModal>
      )}

      {/* addUser modal moved to TeamMembersPage.jsx */}

      {/* editUser modal moved to TeamMembersPage.jsx */}

      {/* Add Project Expense Modal */}

      {modal === 'confirmDelete' && (
        <BrioModal
          title="Confirm Delete"
          titleDanger
          onClose={() => setModal(null)}
          width="sm"
          footer={
            <div className="flex gap-3 w-full">
              <button 
                onClick={() => setModal(null)} 
                className="brio-btn brio-btn-secondary" style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button 
                onClick={async () => {
                  if (useSupabaseDB) {
                    const result = await supabaseTimeEntries.bulkDeleteTimeEntries(selected);
                    if (!result.success) {
                      alert('Error deleting entries: ' + result.error);
                      return;
                    }
                  } else {
                    setTimeEntries(timeEntries.filter(e => !normalizedIncludes(selected, e.id)));
                  }
                  setSelected([]);
                  setModal(null);
                }} 
                className="brio-btn brio-btn-danger" style={{ flex: 1 }}
              >
                Delete
              </button>
            </div>
          }
        >
          <div className="mb-2">
            <p className="text-slate-700 mb-2">
              Are you sure you want to delete <strong>{selected.length}</strong> selected time {selected.length === 1 ? 'entry' : 'entries'}?
            </p>
            <p className="text-sm text-red-600">
              ⚠️ This action cannot be undone.
            </p>
          </div>
        </BrioModal>
      )}

      {modal === 'confirmDeleteUser' && (
        <BrioModal
          title="Confirm Delete User"
          titleDanger
          onClose={() => { setModal('editUser'); setForm(f => ({ ...f, deleteConfirmInput: '' })); }}
          width="sm"
          footer={
            <div className="flex gap-3 w-full">
              <button
                onClick={() => { setModal('editUser'); setForm(f => ({ ...f, deleteConfirmInput: '' })); }}
                className="brio-btn brio-btn-secondary" style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                disabled={(form.deleteConfirmInput || '').trim().toLowerCase() !== (form.userName || '').trim().toLowerCase()}
                onClick={async () => {
                  const userId = form.editUserId;

                  if (activeUsers.length <= 1) {
                    setModal('editUser');
                    return;
                  }

                  // Prevent deleting the authenticated user
                  if (mappedUser && currentUser.id === userId) {
                    alert('You cannot delete your own authenticated account. Contact an administrator.');
                    return;
                  }

                  // REMOVED: Legacy user switching on delete - no longer needed with Supabase auth
                  // Identity is AUTHORITATIVE from mappedUser, cannot be changed by UI actions

                  // Delete time entries for this user
                  if (useSupabaseDB) {
                    const userEntryIds = activeTimeEntries.filter(e => e.userId === userId).map(e => e.id);
                    if (userEntryIds.length > 0) {
                      await supabaseTimeEntries.bulkDeleteTimeEntries(userEntryIds);
                    }
                    // Delete time off requests for this user
                    const userTimeOffIds = activeTimeOff.filter(to => to.userId === userId).map(to => to.id);
                    for (const toId of userTimeOffIds) {
                      await supabaseTimeOff.deleteTimeOffRequest(toId);
                    }
                    // Delete user from Supabase
                    const result = await supabaseUsers.deleteUser(userId);
                    if (!result.success) {
                      alert('Error deleting user: ' + result.error);
                      return;
                    }
                  } else {
                    setTimeEntries(timeEntries.filter(e => e.userId !== userId));
                    setUsers(users.filter(x => x.id !== userId));
                    setTimeOff(timeOff.filter(to => to.userId !== userId));
                  }

                  setRateHistory(rateHistory.filter(h => h.userId !== userId));
                  setRepeatingEntries(repeatingEntries.filter(r => r.userId !== userId));

                  setModal(null);
                  setForm({});
                }}
                className="brio-btn brio-btn-danger" style={{ flex: 1, opacity: (form.deleteConfirmInput || '').trim().toLowerCase() === (form.userName || '').trim().toLowerCase() ? 1 : 0.5 }}
              >
                Delete User
              </button>
            </div>
          }
        >
          <div className="mb-2">
            <p className="text-slate-700 mb-3">
              Are you sure you want to delete user <strong>{form.userName}</strong>?
            </p>
            <p className="text-sm text-slate-600 mb-2">This will permanently delete:</p>
            <ul className="text-sm text-slate-600 list-disc list-inside space-y-1 mb-3">
              <li>The user account</li>
              <li>All their time entries</li>
              <li>All their time off requests</li>
              <li>All their rate history</li>
              <li>All their repeating entries</li>
            </ul>
            <p className="text-sm text-slate-600 mb-2">
              Type <strong>{form.userName}</strong> below to confirm:
            </p>
            <input
              autoFocus
              type="text"
              value={form.deleteConfirmInput || ''}
              onChange={e => setForm(f => ({ ...f, deleteConfirmInput: e.target.value }))}
              placeholder={form.userName}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:border-red-400"
            />
          </div>
        </BrioModal>
      )}

      {modal === 'importProgress' && (
        <BrioModal
          title={<span className="flex items-center gap-3"><RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />Importing CSV</span>}
          onClose={() => {}}
          width="sm"
        >
          <div className="space-y-3">
            <p className="text-sm text-slate-600">{form.importMessage}</p>
            
            <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
              <div 
                className="h-full bg-blue-600 transition-all duration-300 ease-out"
                style={{ width: `${form.importProgress || 0}%` }}
              />
            </div>
            
            <p className="text-sm text-center font-medium text-slate-700">
              {form.importProgress || 0}%
            </p>
          </div>
          
          <p className="text-xs text-slate-500 mt-4 text-center">
            Please wait... Large imports may take a few moments.
          </p>
        </BrioModal>
      )}

      {modal === 'importSuccess' && (
        <BrioModal
          title={<span className="text-green-600">Import Successful</span>}
          onClose={() => {setModal(null); setForm({});}}
          width="lg"
          footer={
            <button 
              onClick={() => {setModal(null); setForm({});}} 
              className="brio-btn brio-btn-primary" style={{ width: '100%', backgroundColor: '#16a34a' }} /* AUDIT[01]: #16a34a overrides brio-btn-primary gold — use var(--brio-green) or a new .brio-btn-success variant */
            >
              Close
            </button>
          }
        >
          <div className="mb-2">
            <pre className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 p-4 rounded-lg border">
              {form.importMessage}
            </pre>
          </div>
        </BrioModal>
      )}

      {modal === 'importError' && (
        <BrioModal
          title="Import Error"
          titleDanger
          onClose={() => {setModal(null); setForm({});}}
          width="lg"
          footer={
            <button 
              onClick={() => {setModal(null); setForm({});}} 
              className="brio-btn brio-btn-danger" style={{ width: '100%' }}
            >
              Close
            </button>
          }
        >
          <div className="mb-2">
            <pre className="text-sm text-slate-700 whitespace-pre-wrap bg-red-50 p-4 rounded-lg border border-red-200">
              {form.errorMessage}
            </pre>
          </div>
        </BrioModal>
      )}

      {modal === 'confirmDeleteStage' && (
        <BrioModal
          title="Confirm Delete Stage"
          titleDanger
          onClose={() => {setModal(null); setForm({});}}
          width="sm"
          footer={
            <div className="flex gap-3 w-full">
              <button 
                onClick={() => {setModal(null); setForm({});}} 
                className="brio-btn brio-btn-secondary" style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button 
                onClick={async () => {
                  if (useSupabaseDB) {
                    const result = await supabaseTasks.deleteTask(form.deleteStageId);
                    if (!result.success) {
                      alert('Error deleting stage: ' + result.error);
                      return;
                    }
                  } else {
                    setTasks(tasks.filter(x => x.id !== form.deleteStageId));
                  }
                  setModal(null);
                  setForm({});
                }} 
                className="brio-btn brio-btn-danger" style={{ flex: 1 }}
              >
                Delete Stage
              </button>
            </div>
          }
        >
          <div className="mb-2">
            <p className="text-slate-700 mb-2">
              Are you sure you want to delete stage <strong>{form.deleteStageName}</strong>?
            </p>
            <p className="text-sm text-red-600">
              ⚠️ This action cannot be undone.
            </p>
          </div>
        </BrioModal>
      )}

      {/* Add Project Expense Modal */}
      {modal === 'addProjectExpense' && (
        <BrioModal
          title="Add Project Expense"
          onClose={() => {setModal(null); setForm({});}}
          width="md"
          footer={
            <BrioActionGroup align="end">
              <button 
                onClick={async () => {
                  if(!form.expenseProjectId || !form.expenseCategory || !form.expenseDescription || !form.expenseAmount) {
                    alert('Please fill all required fields');
                    return;
                  }
                  
                  const expenseData = {
                    id: Date.now(),
                    projectId: parseInt(form.expenseProjectId),
                    category: form.expenseCategory,
                    description: form.expenseDescription,
                    amount: parseFloat(form.expenseAmount),
                    date: form.expenseDate || new Date().toISOString().split('T')[0],
                    status: form.expenseStatus || 'planned',
                    officeLocation: form.expenseOfficeLocation || 'NL',
                    foreignVendor: !!form.expenseForeignVendor,
                  };
                  
                  if (useSupabaseDB) {
                    const result = await supabaseExpenses.createExpense(expenseData);
                    if (!result.success) {
                      alert('Error creating expense: ' + result.error);
                      return;
                    }
                  } else {
                    setProjectExpenses([...projectExpenses, expenseData]);
                  }
                  
                  setModal(null);
                  setForm({});
                }} 
                className="brio-btn brio-btn-primary"
              >
                Add Expense
              </button>
            </BrioActionGroup>
          }
        >
          <div className="space-y-3">
            <select 
              value={form.expenseProjectId||''} 
              onChange={(e) => setForm({...form, expenseProjectId:e.target.value})} 
              className="brio-modal-select"
            >
              <option value="">Select Project</option>
              {[...activeProjects].sort((a, b) => (a.projectCode || a.name || '').localeCompare(b.projectCode || b.name || '')).map(p => <option key={p.id} value={p.id}>{fmtProject(p)}</option>)}
            </select>

            <select
              value={form.expenseCategory||''} 
              onChange={(e) => setForm({...form, expenseCategory:e.target.value})} 
              className="brio-modal-select"
            >
              <option value="">Select Category</option>
              <option value="Consultants">Consultants</option>
              <option value="Travel">Travel</option>
              <option value="Materials">Materials (Prints, Models, Renders)</option>
              <option value="Miscellaneous">Miscellaneous (Permits, Software, etc.)</option>
            </select>
            
            <input 
              type="text" 
              placeholder="Description" 
              value={form.expenseDescription||''} 
              onChange={(e) => setForm({...form, expenseDescription:e.target.value})} 
              className="brio-modal-input"
            />
            
            <input 
              type="number" 
              placeholder={`Amount (${currSymbol})`} 
              value={form.expenseAmount||''} 
              onChange={(e) => setForm({...form, expenseAmount:e.target.value})} 
              className="brio-modal-input"
            />
            
            <BrioDatePicker
              value={form.expenseDate||new Date().toISOString().split('T')[0]}
              onChange={(v) => setForm({...form, expenseDate:v})}
            />
            
            <select 
              value={form.expenseStatus||'planned'} 
              onChange={(e) => setForm({...form, expenseStatus:e.target.value})} 
              className="brio-modal-select"
            >
              <option value="planned">Planned</option>
              <option value="committed">Committed</option>
              <option value="invoiced">Invoiced</option>
              <option value="paid">Paid</option>
            </select>
            
            <div>
              <label className="block text-sm font-medium mb-1">Office Location</label>
              <select
                value={form.expenseOfficeLocation || 'NL'}
                onChange={(e) => setForm({...form, expenseOfficeLocation: e.target.value})}
                className="brio-modal-select"
              >
                <option value="NL">NL</option>
                <option value="IN">IN</option>
              </select>
            </div>

            <label
              className="flex items-center gap-2 text-sm cursor-pointer"
              title="Vendor established outside the office country (triggers reverse charge for SaaS, ads, professional services)"
            >
              <input
                type="checkbox"
                checked={!!form.expenseForeignVendor}
                onChange={(e) => setForm({...form, expenseForeignVendor: e.target.checked})}
              />
              Foreign vendor (cross-border B2B)
            </label>
          </div>
        </BrioModal>
      )}

      {/* Add Office Overhead Modal */}
      {modal === 'addOverhead' && (
        <BrioModal
          title="Add Office Overhead"
          onClose={() => {setModal(null); setForm({});}}
          width="md"
          footer={
            <BrioActionGroup align="end">
              <button 
                onClick={() => {
                  if(!form.overheadCategory || !form.overheadSubcategory || !form.overheadAmount) {
                    alert('Please fill all required fields');
                    return;
                  }
                  
                  setOfficeOverheads([...officeOverheads, {
                    id: Date.now(),
                    yearMonth: form.overheadMonth || new Date().toISOString().substring(0,7),
                    category: form.overheadCategory,
                    subcategory: form.overheadSubcategory,
                    amount: parseFloat(form.overheadAmount),
                    officeLocation: form.overheadOfficeLocation || 'NL'
                  }]);
                  
                  setModal(null);
                  setForm({});
                }} 
                className="brio-btn brio-btn-primary"
              >
                Add Overhead
              </button>
            </BrioActionGroup>
          }
        >
          <div className="space-y-3">
            <BrioMonthPicker
              value={form.overheadMonth||new Date().toISOString().substring(0,7)}
              onChange={(v) => setForm({...form, overheadMonth:v})}
            />
            
            <select 
              value={form.overheadCategory||''} 
              onChange={(e) => setForm({...form, overheadCategory:e.target.value})} 
              className="brio-modal-select"
            >
              <option value="">Select Category</option>
              <option value="Premises">Premises (Rent, Maintenance)</option>
              <option value="Energy & Utilities">Energy & Utilities</option>
              <option value="Office Operations">Office Operations</option>
              <option value="Equipment & IT">Equipment & IT</option>
              <option value="HR">Human Resources</option>
              <option value="Professional">Professional Services</option>
              <option value="Marketing">Marketing & Business Dev</option>
              <option value="Finance">Finance & Admin</option>
            </select>
            
            <input 
              type="text" 
              placeholder="Subcategory (e.g., Rent, Electricity)" 
              value={form.overheadSubcategory||''} 
              onChange={(e) => setForm({...form, overheadSubcategory:e.target.value})} 
              className="brio-modal-input"
            />
            
            <input 
              type="number" 
              placeholder={`Amount (${currSymbol})`} 
              value={form.overheadAmount||''} 
              onChange={(e) => setForm({...form, overheadAmount:e.target.value})} 
              className="brio-modal-input"
            />
            
            <div>
              <label className="block text-sm font-medium mb-1">Office Location</label>
              <select 
                value={form.overheadOfficeLocation || 'NL'}
                onChange={(e) => setForm({...form, overheadOfficeLocation: e.target.value})} 
                className="brio-modal-select"
              >
                <option value="NL">NL</option>
                <option value="IN">IN</option>
              </select>
            </div>
          </div>
        </BrioModal>
      )}

      {/* Salary Variance Detail Modal */}
      {modal === 'salaryVarianceDetail' && form.salaryVarianceUserId && (
        <BrioModal
          title="Salary Variance Details"
          onClose={() => {setModal(null); setForm({});}}
          width="lg"
        >
          {(() => {
            const user = activeUsers.find(u => u.id === form.salaryVarianceUserId);
            if (!user) return null;
            
            const persistence = checkPersistence(user.id, form.salaryVarianceMonth);
            const months = persistence.months;
            
            return (
              <div className="space-y-4">
                {/* User Info */}
                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                  <div 
                    className="w-8 h-8 rounded-full flex-shrink-0"
                    style={{ 
                      backgroundColor: brioColors.roles[user.jobRole] || '#D4C4A0',
                      border: `4px solid ${activeTeams.find(t => t.id === user.teamId)?.outlineColor || '#5B7A99'}`,
                      width: '32px',
                      height: '32px'
                    }}
                  />
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 400 }}>{user.name}</div>
                    <div style={{ fontSize: '13px', fontWeight: 200, fontStyle: 'italic', color: '#666666' }}>
                      {user.jobRole} - L{user.jobLevel}
                    </div>
                  </div>
                </div>
                
                {/* 3-Month History */}
                <div>
                  <h4 style={{ fontSize: '13px', fontWeight: 400, marginBottom: '12px' }}>
                    3-Month Variance History
                  </h4>
                  <div className="space-y-3">
                    {months.map((month, idx) => {
                      const isCurrentMonth = idx === 2;
                      return (
                        <div 
                          key={month.yearMonth} 
                          className={`p-4 border rounded-lg ${isCurrentMonth ? 'border-blue-500 bg-blue-50' : ''}`}
                          style={{ borderColor: isCurrentMonth ? '#3b82f6' : '#e0e0e0' }}
                        >
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <div style={{ fontSize: '13px', fontWeight: 400 }}>
                                {month.yearMonth} {isCurrentMonth && '(Current)'}
                              </div>
                              <div style={{ fontSize: '13px', fontWeight: 200, fontStyle: 'italic', color: '#666666' }}>
                                {month.interpretation === 'unpaid_overtime' ? 'Unpaid overtime detected' :
                                 month.interpretation === 'under_utilisation' ? 'Under-utilisation' : 'On target'}
                              </div>
                            </div>
                            {month.excessVariance > 0 && (
                              /* AUDIT[08]: Tailwind badge with bg-red/orange/yellow — use BrioBadge variant="danger/warning/pending" */
                              <span className={`px-2 py-1 rounded text-xs ${
                                persistence.confirmed && isCurrentMonth ? 'bg-red-100 text-red-700' :
                                idx === 1 ? 'bg-orange-100 text-orange-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {persistence.confirmed && isCurrentMonth ? '🔴 Confirmed' :
                                 idx === 1 ? '🟠 Warning' : '🟡 Detected'}
                              </span>
                            )}
                          </div>
                          
                          {/* AUDIT[02]: fontWeight:300 on all labels below — use 200 (light) or 400 (regular). AUDIT[01]: hardcoded #666666 — use var(--brio-grey) */}
                          <div className="grid grid-cols-2 gap-4" style={{ fontSize: '13px' }}>
                            <div>
                              <div style={{ fontWeight: 300, color: '#666666' }}>Contracted Hours</div>
                              <div style={{ fontWeight: 400 }}>{month.contractedHours}h</div>
                            </div>
                            <div>
                              <div style={{ fontWeight: 300, color: '#666666' }}>Recorded Hours</div>
                              <div style={{ fontWeight: 400 }}>{month.recordedHours.toFixed(1)}h</div>
                            </div>
                            <div>
                              <div style={{ fontWeight: 300, color: '#666666' }}>Theoretical Cost</div>
                              <div style={{ fontWeight: 400 }}>{fmtCurr(Math.round(month.theoreticalHourlyCost))}/h</div>
                            </div>
                            <div>
                              <div style={{ fontWeight: 300, color: '#666666' }}>Fixed Monthly Salary</div>
                              <div style={{ fontWeight: 400 }}>{fmtCurr(month.fixedMonthlySalaryCost)}</div>
                            </div>
                            <div>
                              <div style={{ fontWeight: 300, color: '#666666' }}>Recorded Hours Cost</div>
                              <div style={{ fontWeight: 400 }}>{fmtCurr(month.recordedHoursCost)}</div>
                            </div>
                            <div>
                              <div style={{ fontWeight: 300, color: '#666666' }}>Variance</div>
                              <div style={{ 
                                fontWeight: 400,
                                color: month.variance > 0 ? '#7F7321' : month.variance < 0 ? '#9F902A' : '#000000'
                              }}>
                                {fmtCurr(month.variance)}
                              </div>
                            </div>
                            <div>
                              <div style={{ fontWeight: 300, color: '#666666' }}>Tolerance (10%)</div>
                              <div style={{ fontWeight: 400 }}>{fmtCurr(month.toleranceCost)}</div>
                            </div>
                            <div>
                              <div style={{ fontWeight: 300, color: '#666666' }}>Excess Variance</div>
                              <div style={{ 
                                fontWeight: 400,
                                color: month.excessVariance > 0 ? '#7F7321' : '#9F902A'
                              }}>
                                {month.excessVariance > 0 ? fmtCurr(month.excessVariance) : '—'}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Persistence Status */}
                <div className={`p-4 rounded-lg ${
                  persistence.confirmed ? 'bg-red-50 border border-red-200' :
                  months.filter(m => m.excessVariance > 0).length === 2 ? 'bg-orange-50 border border-orange-200' :
                  months.filter(m => m.excessVariance > 0).length === 1 ? 'bg-yellow-50 border border-yellow-200' :
                  'bg-green-50 border border-green-200'
                }`}>
                  <div style={{ fontSize: '13px', fontWeight: 400, marginBottom: '8px' }}>
                    {persistence.confirmed ? '🔴 Confirmed - Capacity Adjustment Applied' :
                     months.filter(m => m.excessVariance > 0).length === 2 ? '🟠 Persistence Warning (2/3 months)' :
                     months.filter(m => m.excessVariance > 0).length === 1 ? '🟡 Monitoring (1/3 months)' :
                     '✅ Within Tolerance'}
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: 300, color: '#666666' }}>
                    {persistence.confirmed 
                      ? `Confirmed excess variance of ${fmtCurr(persistence.confirmedExcessVariance)} is deducted from capacity-adjusted profit.`
                      : `Excess variance must persist for 3 consecutive months to affect capacity-adjusted profit.`
                    }
                  </div>
                </div>
                
                {/* Explanation */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p style={{ fontSize: '13px', fontWeight: 300 }}>
                    <strong>How it works:</strong> Variance = (Recorded Hours × Hourly Cost) - Fixed Salary. 
                    A 10% tolerance band applies. Only excess variance persisting for 3 consecutive months 
                    affects capacity-adjusted profit as a management indicator.
                  </p>
                </div>
              </div>
            );
          })()}
        </BrioModal>
      )}


      {/* Edit Approved Leave Modal */}
      {editLeave && (
        <BrioModal
          title="Edit Approved Leave"
          onClose={() => setEditLeave(null)}
          width="md"
          footer={
            <BrioActionGroup align="end">
              <button
                type="button"
                onClick={() => setEditLeave(null)}
                className="brio-btn brio-btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  // Validate dates
                  if (!editLeave.startDate || !editLeave.endDate) {
                    alert('Please select both start and end dates.');
                    return;
                  }

                  const start = new Date(editLeave.startDate);
                  const end = new Date(editLeave.endDate);
                  
                  if (end < start) {
                    alert('End date must be after start date.');
                    return;
                  }

                  // Calculate working days
                  const workingDays = countWorkingDays(editLeave.startDate, editLeave.endDate);

                  // Validate advance notice (unless admin)
                  if (!isAdmin) {
                    const validation = validateAdvanceNotice(editLeave.startDate, editLeave.type, workingDays, false);
                    if (!validation.valid) {
                      alert(validation.message);
                      return;
                    }
                    
                    // Validate continuous leave and gap requirements
                    const continuousValidation = validateContinuousLeave(
                      editLeave.userId,
                      editLeave.startDate,
                      editLeave.endDate,
                      editLeave.type,
                      false,
                      editLeave.id // Pass the ID of the leave being edited to exclude it from checks
                    );
                    
                    if (!continuousValidation.valid) {
                      alert(continuousValidation.message);
                      return;
                    }
                  }

                  // Update the leave request - RESET APPROVALS
                  const editLeaveUser = activeUsers.find(x => String(x.id) === String(editLeave.userId));
                  const editLeaveUserIsSupervisor = editLeaveUser?.role === 'supervisor';
                  const updateData = {
                    type: editLeave.type,
                    startDate: editLeave.startDate,
                    endDate: editLeave.endDate,
                    workingDays: workingDays,
                    // Reset approvals since leave changed; supervisors skip to pending_admin
                    status: editLeaveUserIsSupervisor ? 'pending_admin' : 'pending_supervisor',
                    supervisorApproval: editLeaveUserIsSupervisor ? 'approved' : 'pending',
                    adminApproval: 'pending',
                    requestedDate: new Date().toISOString().split('T')[0],
                    approvedBy: null,
                    approvedAt: null
                  };
                  
                  if (useSupabaseDB) {
                    const result = await supabaseTimeOff.updateTimeOffRequest(editLeave.id, updateData);
                    if (!result.success) {
                      alert('Error updating request: ' + result.error);
                      return;
                    }
                  } else {
                    const updatedTimeOff = timeOff.map(t => {
                      if (t.id === editLeave.id) {
                        return { ...t, ...updateData };
                      }
                      return t;
                    });
                    setTimeOff(updatedTimeOff);
                  }

                  setEditLeave(null);
                  alert('✓ Leave updated!\n\nThe request has been sent back for approval with the new details.');
                }}
                className="brio-btn brio-btn-primary"
              >
                Submit Changes
              </button>
            </BrioActionGroup>
          }
        >
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
            <div className="text-sm font-semibold mb-2">Current Leave:</div>
            <div className="text-sm text-slate-700">
              <div><strong>Type:</strong> {editLeave.originalType}</div>
              <div><strong>Dates:</strong> {editLeave.originalStartDate} to {editLeave.originalEndDate}</div>
              <div><strong>Days:</strong> {editLeave.workingDays || editLeave.days} working days</div>
              <div className="text-orange-600 font-medium mt-2">⚠️ Editing will reset approvals</div>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Leave Type</label>
              <select
                value={editLeave.type}
                onChange={(e) => setEditLeave({...editLeave, type: e.target.value})}
                className="brio-modal-select"
              >
                <option value="sick">Sick Leave</option>
                <option value="vacation">Vacation</option>
                <option value="emergency">Emergency Leave</option>
                <option value="wfh">Work from Home</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Start Date</label>
              <BrioDatePicker
                value={editLeave.startDate}
                onChange={(v) => setEditLeave({...editLeave, startDate: v})}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">End Date</label>
              <BrioDatePicker
                value={editLeave.endDate}
                onChange={(v) => setEditLeave({...editLeave, endDate: v})}
              />
            </div>

            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm">
              <strong>⚠️ Leave Requirements:</strong>
              <ul className="mt-2 space-y-1 text-xs">
                <li>• Vacation (1–2 days): 5 days advance notice</li>
                <li>• Vacation (3–5 days): 7 days advance notice</li>
                <li>• Vacation (6+ days): 21 days advance notice</li>
                <li>• Maximum continuous vacation: 15 working days at one go</li>
                <li>• Gap between vacation periods: Minimum 5 working days required</li>
                <li>• Emergency Leave: must start within 5 days, before 08:00 IST on the start day; max 2 days/request and 5 days/year (past the yearly cap, supervisor approval is skipped)</li>
                <li>• Sick leave: No advance notice required (deductible beyond 5 sick days per year)</li>
                <li>• Work from Home: No advance notice required (deductible beyond 5 WFH days per year)</li>
              </ul>
              <div className="mt-2 text-orange-600 font-medium">
                Note: Editing will reset approvals and require re-approval.
              </div>
            </div>
          </div>
        </BrioModal>
      )}

      {/* Custom Confirmation Modal for Cancel Leave */}
      {confirmCancel && (
        <BrioModal
          title="Cancel Approved Leave?"
          titleDanger
          onClose={() => setConfirmCancel(null)}
          width="sm"
          footer={
            <BrioActionGroup align="end">
              <button
                type="button"
                onClick={() => setConfirmCancel(null)}
                className="brio-btn brio-btn-secondary"
              >
                Keep Leave
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (useSupabaseDB) {
                    const result = await supabaseTimeOff.deleteTimeOffRequest(confirmCancel.leaveId);
                    if (!result.success) {
                      alert('Error cancelling leave: ' + result.error);
                      return;
                    }
                  } else {
                    setTimeOff(timeOff.filter(t => t.id !== confirmCancel.leaveId));
                  }
                  setConfirmCancel(null);
                  alert('✓ Leave cancelled successfully.');
                }}
                className="brio-btn brio-btn-danger"
              >
                Yes, Cancel Leave
              </button>
            </BrioActionGroup>
          }
        >
          <div className="text-sm text-slate-700 space-y-2">
            <p>You are about to cancel the following approved leave:</p>
            <div className="bg-slate-50 rounded p-3 border">
              <div><strong>Type:</strong> {confirmCancel.type}</div>
              <div><strong>Dates:</strong> {confirmCancel.startDate} to {confirmCancel.endDate}</div>
              <div><strong>Working Days:</strong> {confirmCancel.workingDays}</div>
            </div>
            <p className="text-red-700 font-medium">⚠️ This will permanently remove this leave from your records.</p>
          </div>
        </BrioModal>
      )}

      {/* Custom Confirmation Modal for Reject */}
      {confirmReject && (
        <BrioModal
          title="Confirm Rejection"
          titleDanger
          onClose={() => setConfirmReject(null)}
          width="sm"
          footer={
            <BrioActionGroup align="end">
              <button
                type="button"
                onClick={() => setConfirmReject(null)}
                className="brio-btn brio-btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  // Fixed: was `window.confirmReject` but should be `confirmReject`
                  const updateData = confirmReject.type === 'supervisor'
                    ? {
                        supervisorApproval: 'rejected',
                        status: 'rejected',
                        adminApproval: 'n/a',
                        approvedBy: null,
                        approvedAt: null
                      }
                    : {
                        adminApproval: 'rejected',
                        status: 'rejected',
                        approvedBy: null,
                        approvedAt: null
                      };
                  
                  if (useSupabaseDB) {
                    const result = await supabaseTimeOff.updateTimeOffRequest(confirmReject.requestId, updateData);
                    if (!result.success) {
                      alert('Error rejecting request: ' + result.error);
                      return;
                    }
                  } else {
                    const newTimeOff = [...timeOff];
                    const index = newTimeOff.findIndex(t => t.id === confirmReject.requestId);
                    
                    if (index !== -1) {
                      newTimeOff[index] = { ...newTimeOff[index], ...updateData };
                      setTimeOff(newTimeOff);
                    }
                  }
                  
                  setConfirmReject(null);
                  alert('✗ Leave request rejected.');
                }}
                className="brio-btn brio-btn-danger"
              >
                Yes, Reject Request
              </button>
            </BrioActionGroup>
          }
        >
          <p className="text-slate-700 mb-2">
            {confirmReject.overrideApproval
              ? 'Are you sure you want to deny this previously approved leave request?'
              : 'Are you sure you want to reject this leave request?'}
          </p>
          {confirmReject.userName && (
            <p className="text-sm text-slate-600 mb-4">
              Employee: <strong>{confirmReject.userName}</strong>
            </p>
          )}
          {confirmReject.overrideApproval && confirmReject.approverName && (
            <p className="text-sm text-slate-600 mb-4">
              Previously approved by: <strong>{confirmReject.approverName}</strong>
            </p>
          )}
          {confirmReject.type === 'supervisor' && (
            <p className="text-sm text-orange-600">
              ⚠️ This rejection is final and will not require admin approval.
            </p>
          )}
        </BrioModal>
      )}

      {/* Add/Edit Event Modal */}
      {showEventModal && (
        <BrioModal
          title={editingEvent ? 'Edit Calendar Event' : 'Add Calendar Event'}
          onClose={() => { setShowEventModal(false); setEventForm({}); setEditingEvent(null); }}
          width="md"
          footer={
            <BrioActionGroup align="end">
              {editingEvent && (editingEvent.isEditable === true || editingEvent.source?.table === 'calendar_events') && (
                <button 
                  onClick={async () => {
                    const deleted = await deleteCalendarEvent(editingEvent.id);
                    if (deleted) {
                      setShowEventModal(false);
                      setEventForm({});
                      setEditingEvent(null);
                    }
                  }} 
                  className="brio-btn brio-btn-danger"
                >
                  Delete Event
                </button>
              )}
              <button 
                onClick={async () => {
                  if (!eventForm.title || !eventForm.date) {
                    alert('Please fill in title and date');
                    return;
                  }
                  
                  // Validate times for non-all-day events
                  if (!eventForm.isAllDay && (!eventForm.startTime || !eventForm.endTime)) {
                    alert('Please fill in start and end times');
                    return;
                  }
                  
                  const categoryColors = {
                    events: 'bg-gray-100 text-gray-800 border-gray-300',
                    milestones: 'bg-green-50 text-green-800 border-green-300',
                    invoicing: 'bg-orange-50 text-orange-800 border-orange-300'
                  };
                  
                  if (editingEvent) {
                    // UPDATE existing event
                    const updates = {
                      title: eventForm.title,
                      category: eventForm.category || 'events',
                      date: eventForm.date,
                      startDate: eventForm.startDate || eventForm.date,
                      endDate: eventForm.endDate || eventForm.date,
                      startTime: eventForm.isAllDay ? null : eventForm.startTime,
                      endTime: eventForm.isAllDay ? null : eventForm.endTime,
                      isAllDay: eventForm.isAllDay === true,
                      description: eventForm.description || '',
                      color: categoryColors[eventForm.category || 'events']
                    };
                    
                    const success = await updateCalendarEvent(editingEvent.id, updates);
                    if (success) {
                      setShowEventModal(false);
                      setEventForm({});
                      setEditingEvent(null);
                      alert('✓ Event updated successfully!');
                    }
                  } else {
                    // CREATE new event
                    const newEventData = {
                      title: eventForm.title,
                      category: eventForm.category || 'events',
                      date: eventForm.date,
                      startDate: eventForm.startDate || eventForm.date,
                      endDate: eventForm.endDate || eventForm.date,
                      startTime: eventForm.isAllDay ? null : eventForm.startTime,
                      endTime: eventForm.isAllDay ? null : eventForm.endTime,
                      isAllDay: eventForm.isAllDay === true,
                      description: eventForm.description || '',
                      color: categoryColors[eventForm.category || 'events'],
                      createdBy: currentUser.id
                    };
                    
                    if (useSupabaseDB) {
                      // Supabase mode: create via hook
                      const result = await supabaseCalendarEvents.createCalendarEvent(newEventData);
                      if (result.success) {
                        const createdEventId = result.data?.id;
                        
                        // Push to Microsoft Calendar if connected
                        if (msCalendarConnected && createdEventId) {
                          try {
                            const validToken = await getValidToken();
                            if (validToken) {
                              // Map category to event type for MS calendar lookup
                              const eventType = ['leaves', 'milestones', 'invoicing', 'events', 'holidays'].includes(newEventData.category) 
                                ? newEventData.category 
                                : 'events';
                              
                              const msCalendarId = await getMsCalendarIdForEventType(eventType);
                              if (msCalendarId) {
                                const msEvent = await pushEventToMsCalendar(newEventData, msCalendarId, validToken);
                                
                                // Update Supabase record with MS event ID
                                if (msEvent?.id) {
                                  await supabaseCalendarEvents.updateCalendarEvent(createdEventId, {
                                    msCalendarEventId: msEvent.id
                                  });
                                  console.log('✅ Supabase updated with MS event ID:', msEvent.id);
                                }
                              } else {
                                console.warn('⚠️ No MS calendar mapping found. Run "Initialize Calendars" first.');
                              }
                            }
                          } catch (msError) {
                            console.error('⚠️ Failed to push to MS Calendar:', msError);
                            // Don't block - event was created locally
                          }
                        }
                        
                        // Refetch to stay authoritative
                        await supabaseCalendarEvents.refetch();
                        setShowEventModal(false);
                        setEventForm({});
                        alert('✓ Event created successfully!');
                      } else {
                        alert('Failed to create event: ' + (result.error || 'Unknown error'));
                      }
                    } else {
                      // localStorage mode: add to local state
                      const newEvent = {
                        ...newEventData,
                        id: `event-${Date.now()}`,
                        createdAt: new Date().toISOString()
                      };
                      setCalendarEvents([...calendarEvents, newEvent]);
                      setShowEventModal(false);
                      setEventForm({});
                      alert('✓ Event created successfully!');
                    }
                  }
                }} 
                className="brio-btn brio-btn-primary"
              >
                {editingEvent ? 'Update Event' : 'Add Event'}
              </button>
            </BrioActionGroup>
          }
        >
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Event Title</label>
              <input 
                type="text" 
                placeholder="Meeting, Deadline, etc." 
                value={eventForm.title || ''} 
                onChange={(e) => setEventForm({...eventForm, title: e.target.value})} 
                className="brio-modal-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Category</label>
              <select 
                value={eventForm.category || 'events'} 
                onChange={(e) => setEventForm({...eventForm, category: e.target.value})} 
                className="brio-modal-select"
              >
                <option value="events">General Event</option>
                <option value="milestones">Milestone</option>
                <option value="invoicing">Invoicing</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Date</label>
              <BrioDatePicker
                value={eventForm.date || ''}
                onChange={(v) => setEventForm({...eventForm, date: v})}
              />
            </div>
            
            <div>
              <label className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  checked={eventForm.isAllDay === true} 
                  onChange={(e) => setEventForm({...eventForm, isAllDay: e.target.checked})} 
                  className="w-4 h-4"
                />
                <span className="text-sm">All-day event</span>
              </label>
            </div>
            
            {!eventForm.isAllDay && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Start Time</label>
                  <input 
                    type="time" 
                    value={eventForm.startTime || ''} 
                    onChange={(e) => setEventForm({...eventForm, startTime: e.target.value})} 
                    className="brio-modal-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">End Time</label>
                  <input 
                    type="time" 
                    value={eventForm.endTime || ''} 
                    onChange={(e) => setEventForm({...eventForm, endTime: e.target.value})} 
                    className="brio-modal-input"
                  />
                </div>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium mb-1">Description (optional)</label>
              <textarea 
                value={eventForm.description || ''} 
                onChange={(e) => setEventForm({...eventForm, description: e.target.value})} 
                className="brio-modal-input"
                rows="3"
                placeholder="Add notes..."
              />
            </div>
          </div>
        </BrioModal>
      )}

      {/* Day Events Modal (for "+ more" in monthly view) */}
      {showDayEventsModal && (
        <BrioModal
          title={showDayEventsModal.day?.toLocaleDateString('en-US', { 
            weekday: 'long', 
            month: 'long', 
            day: 'numeric', 
            year: 'numeric' 
          })}
          onClose={() => setShowDayEventsModal(null)}
          width="md"
          footer={
            <BrioActionGroup align="end">
              <button
                onClick={() => setShowDayEventsModal(null)}
                className="brio-btn brio-btn-secondary"
              >
                Close
              </button>
            </BrioActionGroup>
          }
        >
          <p className="text-sm text-slate-600 mb-3">
            {showDayEventsModal.events?.length || 0} events
          </p>
          <div className="space-y-2">
            {showDayEventsModal.events?.map(event => {
              const canEdit = isEventEditable(event);
              const brioStyle = getBrioEventStyle(event.eventType);
              return (
                <div 
                  key={event.id}
                  className="p-3 rounded-lg"
                  style={{
                    border: `2px ${brioStyle.borderStyle} ${brioStyle.borderColor}`,
                    backgroundColor: brioStyle.backgroundColor
                  }}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-medium">
                        {event.icon && `${event.icon} `}{event.title}
                      </div>
                      {event.description && (
                        <div className="text-sm mt-1 opacity-80">{event.description}</div>
                      )}
                      <div className="text-xs mt-2 opacity-60">
                        Category: {event.category}
                        {!canEdit && <span className="ml-2 italic">(Read-only)</span>}
                      </div>
                    </div>
                    
                    {/* Edit/Delete buttons - only for editable events */}
                    {canEdit ? (
                      <div className="flex gap-1 ml-2">
                        <button
                          onClick={() => {
                            setShowDayEventsModal(null);
                            openEditEventModal(event);
                          }}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                          title="Edit event"
                        >
                          <Edit2 className="w-4 h-4"/>
                        </button>
                        <button
                          onClick={async () => {
                            const deleted = await deleteCalendarEvent(event.id || event.sourceId);
                            if (deleted) {
                              // Refresh the modal events list
                              const updatedEvents = showDayEventsModal.events.filter(
                                e => e.id !== event.id && e.sourceId !== event.id
                              );
                              if (updatedEvents.length === 0) {
                                setShowDayEventsModal(null);
                              } else {
                                setShowDayEventsModal({
                                  ...showDayEventsModal,
                                  events: updatedEvents
                                });
                              }
                            }
                          }}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                          title="Delete event"
                        >
                          <Trash2 className="w-4 h-4"/>
                        </button>
                      </div>
                    ) : (
                      <div className="ml-2 text-xs text-slate-400" title="System-managed events cannot be edited">
                        🔒
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            
            {(!showDayEventsModal.events || showDayEventsModal.events.length === 0) && (
              <div className="text-center py-8 text-slate-500">
                No events on this day
              </div>
            )}
          </div>
        </BrioModal>
      )}
    </div>
  );
}
